"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { dropCategories, dropProducts, type DropProduct } from "./drop-data";
import { DressingEquivalenceModule } from "./dressing-equivalence-module";
import { DormantProductsModule } from "./dormant-products-module";
import { DriveModule } from "./drive-module";
import { PerfadomModule } from "./perfadom-module";
import { compressFileForUpload } from "./file-compression";
import { PricingAssistantModule } from "./pricing-assistant-module";
import { PrescriptionRightsModule } from "./prescription-rights-module";
import { AdviceSheetModule } from "./advice-sheet-module";
import { MerchandisingModule } from "./merchandising-module";
import { ProductsToOrderModule } from "./products-to-order-module";
import { PromoPosterModule } from "./promo-poster-module";
import { ChallengeModule, ChallengeTvPage, type ChallengeSummary } from "./challenge-module";
import { CatalogAutocomplete, extractGtinFromScan, resolveCatalogEan, type CatalogSuggestion } from "./catalog-autocomplete";
import {
  TeamHandoverModule,
  isTeamHandoverVisible,
  normalizeTeamHandoverData,
  type TeamHandoverData,
  type TeamHandoverMessage,
} from "./team-handover-module";

type Tool = {
  name: string;
  description: string;
  category: "Comptoir" | "Organisation" | "Gestion stock" | "Back-office";
  icon: string;
  color: string;
  badge?: string;
};

const tools: Tool[] = [
  { name: "Fiche conseil patient", description: "Créer, personnaliser et envoyer", category: "Comptoir", icon: "📝", color: "sky" },
  { name: "Mémo conseil patient", description: "Conseils prêts à imprimer", category: "Comptoir", icon: "🗒️", color: "apricot" },
  { name: "Ventes associées", description: "Recommandations par famille", category: "Comptoir", icon: "💡", color: "lilac" },
  { name: "Droits de prescription", description: "Qui peut prescrire quoi ?", category: "Comptoir", icon: "⚖️", color: "mint", badge: "NEW" },
  { name: "Nombre de gouttes", description: "Conservation, flacons et lentilles", category: "Comptoir", icon: "💧", color: "sky", badge: "206" },
  { name: "Équivalences pansements", description: "Alternatives par famille, plaie et format", category: "Comptoir", icon: "🩹", color: "rose", badge: "NEW" },
  { name: "Assistant prix", description: "Prix conseillé, taux de marque et zone de chalandise", category: "Back-office", icon: "✨", color: "apricot", badge: "IA" },
  { name: "BRI", description: "Listings labo, analyse IA, archivage et règlements", category: "Back-office", icon: "📄", color: "mint", badge: "NEW" },
  { name: "PharmaMerch", description: "Analyser les rayons et créer les actions merchandising", category: "Back-office", icon: "🟩", color: "mint", badge: "IA" },
  { name: "Création Affiche promo", description: "Créer et imprimer étiquettes, offres et affiches promotionnelles", category: "Back-office", icon: "🏷️", color: "apricot", badge: "NEW" },
  { name: "Nettoyer une ordonnance", description: "Rendre le document lisible", category: "Comptoir", icon: "📸", color: "sky" },
  { name: "Patients à suivre", description: "Relances et échéances", category: "Comptoir", icon: "📞", color: "rose", badge: "2" },
  { name: "PERFADOM", description: "Créer et suivre un dossier", category: "Back-office", icon: "🩺", color: "mint", badge: "3" },
  { name: "Location MAD", description: "Parc, locations, ordonnances et retours", category: "Back-office", icon: "🛏️", color: "sand" },
  { name: "Missions récurrentes", description: "Rituels de l’équipe", category: "Organisation", icon: "🔁", color: "lilac", badge: "4" },
  { name: "Produits à commander", description: "Demandes consolidées", category: "Gestion stock", icon: "📦", color: "apricot", badge: "6" },
  { name: "Produits dormants", description: "Stocks immobiles et comparaison entre officines", category: "Gestion stock", icon: "🌙", color: "mint", badge: "NEW" },
  { name: "Stock & étiquettes", description: "Contrôles et impressions", category: "Gestion stock", icon: "🏷️", color: "sky" },
  { name: "Produits à écouler", description: "Priorités et déclencheurs comptoir", category: "Gestion stock", icon: "📣", color: "sand" },
  { name: "Laboratoires", description: "Fiches, rendez-vous, opérations et règlements", category: "Organisation", icon: "🧪", color: "rose", badge: "NEW" },
  { name: "Passation équipe", description: "Informations à reprendre entre équipes", category: "Organisation", icon: "🤲", color: "apricot", badge: "LIVE" },
  { name: "Formation équipe", description: "Parcours et progression", category: "Organisation", icon: "🎓", color: "mint" },
  { name: "Une semaine, un conseil", description: "Animation du comptoir", category: "Organisation", icon: "✨", color: "apricot" },
  { name: "Promotions", description: "Campagnes et affiches", category: "Organisation", icon: "🎯", color: "lilac", badge: "6" },
  { name: "Challenge équipe", description: "Objectifs, progression et classement réseau", category: "Organisation", icon: "🏁", color: "apricot", badge: "LIVE" },
  { name: "To-do & livraisons", description: "Actions du jour en temps réel", category: "Organisation", icon: "⚡", color: "sky" },
  { name: "Suivi adrénaline", description: "Lots, péremptions et alertes vaccin", category: "Back-office", icon: "💉", color: "rose" },
  { name: "Indus CPAM", description: "Notifications, décisions et remboursements", category: "Back-office", icon: "💶", color: "apricot" },
  { name: "Courses", description: "Besoins communs et achats du quotidien", category: "Organisation", icon: "🛒", color: "mint" },
  { name: "Produits manquants à contrôler au quotidien", description: "Contrôles à 11 h et 15 h avec alertes", category: "Gestion stock", icon: "🔎", color: "apricot" },
  { name: "Génériques manquants", description: "Molécules, laboratoires et retours", category: "Gestion stock", icon: "💊", color: "sky" },
  { name: "Bloc-notes opérateur", description: "Mes rappels et priorités personnelles", category: "Organisation", icon: "📌", color: "sand" },
  { name: "Drive équipe", description: "Documents, procédures et formulaires", category: "Organisation", icon: "🗂️", color: "sky", badge: "NEW" },
  { name: "Écran équipe TV", description: "Le direct utile du back-office", category: "Organisation", icon: "📺", color: "mint", badge: "LIVE" },
];

const toolCategories: Tool["category"][] = ["Organisation", "Back-office", "Gestion stock", "Comptoir"];
const portalThemes = [
  { id: "jade", label: "Jade", color: "#168b70" },
  { id: "ocean", label: "Azur", color: "#3178d6" },
  { id: "sunrise", label: "Solaire", color: "#e28a2d" },
  { id: "graphite", label: "Graphite", color: "#43545b" },
] as const;

const PORTAL_LIVE_REFRESH_MS = 60_000;
const PORTAL_TV_REFRESH_MS = 2 * 60_000;
const TITULAIRE_ACCOUNT_EMAIL = "vincent.brasseur2@gmail.com";
const DAILY_TASKS_SYNC_EVENT = "portail-pharma:daily-tasks-updated";
const DAILY_TASKS_SYNC_STORAGE_KEY = "portail-pharma:daily-tasks-updated-at";

function publishDailyTasksUpdate() {
  window.dispatchEvent(new Event(DAILY_TASKS_SYNC_EVENT));
  try {
    window.localStorage.setItem(DAILY_TASKS_SYNC_STORAGE_KEY, String(Date.now()));
  } catch {
    // La sauvegarde PocketBase reste la source de vérité si le stockage local est bloqué.
  }
}

function subscribeToDailyTasksUpdates(callback: () => void | Promise<void>) {
  const refresh = () => void callback();
  const refreshFromStorage = (event: StorageEvent) => {
    if (event.key === DAILY_TASKS_SYNC_STORAGE_KEY) refresh();
  };
  window.addEventListener(DAILY_TASKS_SYNC_EVENT, refresh);
  window.addEventListener("storage", refreshFromStorage);
  return () => {
    window.removeEventListener(DAILY_TASKS_SYNC_EVENT, refresh);
    window.removeEventListener("storage", refreshFromStorage);
  };
}

function startVisiblePolling(callback: () => void | Promise<void>, interval: number) {
  // Seul l'écran TV, explicitement laissé ouvert pour l'équipe, conserve un
  // rafraîchissement. Les modules métier se rechargent à leur ouverture et
  // après leurs écritures, jamais en boucle en arrière-plan.
  if (interval !== PORTAL_TV_REFRESH_MS) return 0;
  return window.setInterval(() => {
    if (document.visibilityState === "visible") void callback();
  }, interval);
}

function ModuleToolsLink() {
  return <div className="module-shortcuts"><a className="module-tools-shortcut" href="/?tools=1" title="Ouvrir les applications"><span>🧰</span><b>Applications</b></a><a className="module-laboratory-shortcut" href="/?tool=Laboratoires" title="Ouvrir les laboratoires"><span>🧪</span><b>Laboratoires</b></a></div>;
}

const focusActions = [
  { id: 1, level: "urgent", title: "Contrôler la chaîne du froid", meta: "Mission récurrente · avant 10:00", owner: "Équipe matin", icon: "!" },
  { id: 2, level: "patient", title: "Rappeler 2 patients", meta: "Suivi traitement · échéance aujourd’hui", owner: "Comptoir", icon: "◎" },
  { id: 3, level: "stock", title: "Valider 6 produits à commander", meta: "Demandes regroupées des 3 pharmacies", owner: "Stock", icon: "▦" },
  { id: 4, level: "labo", title: "Préparer le rendez-vous laboratoire", meta: "Mustela · 14:30 · Pharmacie du Square", owner: "Titulaire", icon: "□" },
];

const pharmacies = [
  "Pharmacie du Square",
  "Pharmacie Ragadan",
  "Pharmacie la Laurentine",
  "Vue groupe · 3 pharmacies",
];

const pharmacyShortLabels: Record<string, string> = {
  "Pharmacie du Square": "Square",
  "Pharmacie Ragadan": "Ragadan",
  "Pharmacie la Laurentine": "Laurentine",
  "Vue groupe · 3 pharmacies": "Les 3 pharmacies",
};

const memoPharmacies = {
  Ragadan: {
    name: "PHARMACIE DU RAGADAN",
    address: "324 bd de Provence, 06700 Saint-Laurent-du-Var",
    email: "phieduragadan@gmail.com",
    website: "www.pharmacieduragadan.com",
    phone: "04 93 07 25 69",
    hours: ["Lundi à vendredi : 08h45–13h00 et 14h00–19h15", "Samedi : 09h00–12h30", "Dimanche : fermé"],
    logo: "https://static.wixstatic.com/media/34a034_131d78513c7c426a8ba42df033cd14ac~mv2.png/v1/fill/w_330,h_112,al_c,q_90/34a034_131d78513c7c426a8ba42df033cd14ac~mv2.png",
    logoClass: "ragadan",
  },
  Square: {
    name: "PHARMACIE DU SQUARE",
    address: "613 av. de la Libération, 06700 Saint-Laurent-du-Var",
    email: "phiedusquare@gmail.com",
    website: "www.pharmaciedusquare06700.com",
    phone: "04 93 31 25 75",
    hours: ["Lundi à vendredi : 08h45–19h30", "Samedi : 08h45–12h30 et 15h00–19h30", "Dimanche : fermé"],
    logo: "https://static.wixstatic.com/media/94467b_f79856a87ddb4fb29c8bce97f08f3f68~mv2.png/v1/fill/w_354,h_176,al_c,q_90/94467b_f79856a87ddb4fb29c8bce97f08f3f68~mv2.png",
    logoClass: "square",
  },
  Laurentine: {
    name: "PHARMACIE LAURENTINE",
    address: "650 av. du Général de Gaulle, 06700 Saint-Laurent-du-Var",
    email: "pharmacielaurentine@gmail.com",
    website: "www.pharmacielaurentine.fr",
    phone: "04 93 31 00 55",
    hours: ["Lundi à samedi : 08h30–19h30", "Ouverture continue", "Dimanche : fermé"],
    logo: "https://static.wixstatic.com/media/34a034_b079e0aeca814ce5978e0321c2724598~mv2.png/v1/fill/w_250,h_168,al_c,q_90/logo%20transp.png",
    logoClass: "laurentine",
  },
} as const;

type MemoPharmacyKey = keyof typeof memoPharmacies;

function formatMemoDate(value: string) {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
}

function MemoCard({
  side,
  pharmacy,
  date,
  patient,
  advice,
  advisor,
  blank,
}: {
  side: "front" | "back";
  pharmacy: MemoPharmacyKey;
  date: string;
  patient: string;
  advice: string;
  advisor: string;
  blank: boolean;
}) {
  const profile = memoPharmacies[pharmacy];
  if (side === "front") {
    return (
      <article className="memo-card memo-card-front">
        <div className="memo-card-brand">
          <img className={`memo-pharmacy-logo ${profile.logoClass}`} src={profile.logo} alt="" />
          <span><strong>{profile.name}</strong><small>DES PHARMACIENS PLUS PROCHES DE VOUS</small></span>
        </div>
        <div className="memo-rule" />
        <div className="memo-identity">
          <p><strong>Patient</strong><span>{blank ? "" : patient}</span></p>
          <p><strong>Date</strong><span>{blank ? "" : formatMemoDate(date)}</span></p>
        </div>
        <h3>CONSEIL(S) / MÉDICAMENT(S)</h3>
        <div className={`memo-advice ${advice && !blank ? "filled" : ""}`}>
          {!blank && advice ? <p>{advice}</p> : Array.from({ length: 7 }, (_, index) => <i key={index} />)}
        </div>
        {!blank && advisor && <p className="memo-advisor">Conseillé par {advisor}</p>}
        <footer>À VOTRE ÉCOUTE, POUR VOTRE SANTÉ</footer>
      </article>
    );
  }

  return (
    <article className="memo-card memo-card-back">
      <div className="memo-back-brand">
        <img className={`memo-pharmacy-logo ${profile.logoClass}`} src={profile.logo} alt={`Logo ${profile.name}`} />
        <strong>{profile.name}</strong>
        <small>DES PHARMACIENS PLUS PROCHES DE VOUS</small>
      </div>
      <div className="memo-rule" />
      <div className="memo-contact">
        <p><span>●</span>{profile.address}</p>
        <p><span>✉</span>{profile.email}</p>
        <p><span>◎</span>{profile.website}</p>
        <p><span>☎</span>{profile.phone}</p>
      </div>
      <div className="memo-hours">
        <strong>Horaires d’ouverture</strong>
        {profile.hours.map((line) => <span key={line}>{line}</span>)}
      </div>
      <footer>À VOTRE ÉCOUTE, POUR VOTRE SANTÉ</footer>
    </article>
  );
}

function MemoModule({ onBack }: { onBack: () => void }) {
  const [pharmacy, setPharmacy] = useState<MemoPharmacyKey>("Ragadan");
  const [date, setDate] = useState("2026-07-23");
  const [patient, setPatient] = useState("");
  const [advice, setAdvice] = useState("");
  const [advisor, setAdvisor] = useState("Gaëlle");
  const [cardsPerSheet, setCardsPerSheet] = useState(4);
  const [blank, setBlank] = useState(false);
  const [printMode, setPrintMode] = useState<"single" | "sheet" | null>(null);

  useEffect(() => {
    const resetPrintMode = () => setPrintMode(null);
    window.addEventListener("afterprint", resetPrintMode);
    return () => window.removeEventListener("afterprint", resetPrintMode);
  }, []);

  const printMemo = (mode: "single" | "sheet") => {
    setPrintMode(mode);
    window.setTimeout(() => window.print(), 80);
  };

  const copies = printMode === "single" ? 1 : cardsPerSheet;
  const pharmacyOperators = missionOperators.filter((operator) => operator.pharmacy === pharmacy);
  const printGroups = Array.from({ length: Math.ceil(copies / 4) }, (_, groupIndex) =>
    Array.from({ length: Math.min(4, copies - groupIndex * 4) }),
  );

  return (
    <main className={`memo-app print-${printMode ?? "none"}`}>
      <header className="module-header">
        <button type="button" className="module-back" onClick={onBack}>← <span>Retour à l’accueil</span></button>
        <div className="wordmark module-wordmark">
          <span className="wordmark-symbol"><i /><i /><i /></span>
          <span><strong>saint-laurent</strong><small>pharmacies</small></span>
        </div>
        <div className="module-header-actions"><ModuleToolsLink /><span className="module-status"><i /> Module opérationnel</span></div>
      </header>

      <section className="memo-page">
        <div className="memo-hero">
          <div>
            <span className="memo-kicker">COMPTOIR · REMPLACEMENT POST-IT</span>
            <h1>Mémo conseil patient</h1>
            <p>Préparez un conseil clair, vérifiez le rendu et imprimez les cartons recto-verso sans ressaisie.</p>
          </div>
          <span className="memo-format">70 × 105 mm</span>
        </div>

        <div className="memo-layout">
          <section className="memo-form-card">
            <div className="memo-section-title">
              <span>01</span>
              <div><h2>Préparer le carton</h2><p>L’aperçu se met à jour automatiquement.</p></div>
            </div>

            <div className="memo-form-grid">
              <label>
                <span>Pharmacie</span>
                <select value={pharmacy} onChange={(event) => {
                  const nextPharmacy = event.target.value as MemoPharmacyKey;
                  setPharmacy(nextPharmacy);
                  setAdvisor(missionOperators.find((operator) => operator.pharmacy === nextPharmacy)?.name ?? "");
                }}>
                  <option value="Ragadan">Ragadan</option>
                  <option value="Square">Square</option>
                  <option value="Laurentine">Laurentine</option>
                </select>
              </label>
              <label>
                <span>Date</span>
                <input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
              </label>
              <label className="full">
                <span>Nom du patient conseillé</span>
                <input value={patient} onChange={(event) => setPatient(event.target.value)} placeholder="Ex. Mme Dupont" disabled={blank} />
              </label>
              <label className="full">
                <span>Conseil(s) / médicament(s)</span>
                <textarea
                  value={advice}
                  onChange={(event) => setAdvice(event.target.value.slice(0, 420))}
                  placeholder="Ex. Doliprane 1 g si douleur, max 3/jour. Lavage nasal matin et soir. Revoir le médecin si la fièvre persiste."
                  disabled={blank}
                />
                <small>{advice.length}/420 caractères</small>
              </label>
              <label>
                <span>Conseillé par</span>
                <select value={advisor} onChange={(event) => setAdvisor(event.target.value)} disabled={blank}>
                  {pharmacyOperators.map((operator) => <option key={operator.name} value={operator.name}>{operator.name} · {operator.role}</option>)}
                </select>
              </label>
              <label>
                <span>Cartons par planche</span>
                <select value={cardsPerSheet} onChange={(event) => setCardsPerSheet(Number(event.target.value))}>
                  <option value={4}>4 cartons / page</option>
                  <option value={8}>8 cartons / 2 pages</option>
                </select>
              </label>
            </div>

            <label className="memo-check">
              <input type="checkbox" checked={blank} onChange={(event) => setBlank(event.target.checked)} />
              <span><strong>Planche vierge à remplir à la main</strong><small>Le patient, la date et le conseil restent vides.</small></span>
            </label>

            <div className="memo-guidance"><span>i</span><p>Pour un recto-verso propre, choisissez « bord court » dans les réglages de l’imprimante.</p></div>

            <div className="memo-actions">
              <button type="button" className="memo-primary" onClick={() => printMemo("single")}>Imprimer ce mémo</button>
              <button type="button" className="memo-secondary" onClick={() => printMemo("sheet")}>Imprimer la planche recto-verso</button>
            </div>
          </section>

          <section className="memo-preview-card">
            <div className="memo-preview-heading">
              <div><span>02</span><div><h2>Aperçu</h2><p>Le recto et le verso à taille réelle.</p></div></div>
              <span className="memo-duplex">Recto + verso</span>
            </div>
            <div className="memo-preview-stage">
              <div><span>RECTO</span><MemoCard side="front" pharmacy={pharmacy} date={date} patient={patient} advice={advice} advisor={advisor} blank={blank} /></div>
              <div><span>VERSO</span><MemoCard side="back" pharmacy={pharmacy} date={date} patient={patient} advice={advice} advisor={advisor} blank={blank} /></div>
            </div>
          </section>
        </div>
      </section>

      <section className="print-zone" aria-hidden="true">
        {printGroups.map((group, groupIndex) => (
          <div className="print-pair" key={groupIndex}>
            <div className="print-page">
              {group.map((_, index) => <MemoCard key={`front-${index}`} side="front" pharmacy={pharmacy} date={date} patient={patient} advice={advice} advisor={advisor} blank={blank} />)}
            </div>
            <div className="print-page">
              {group.map((_, index) => <MemoCard key={`back-${index}`} side="back" pharmacy={pharmacy} date={date} patient={patient} advice={advice} advisor={advisor} blank={blank} />)}
            </div>
          </div>
        ))}
      </section>
    </main>
  );
}

type MissionOperator = {
  name: string;
  pharmacy: "Square" | "Ragadan" | "Laurentine";
  role: "Titulaire" | "Pharmacien" | "Préparateur" | "Apprentie" | "Rayonniste";
  birthDate?: string;
};

let missionOperators: MissionOperator[] = [
  { name: "Cécile F", pharmacy: "Square", role: "Rayonniste" },
  { name: "Céline B", pharmacy: "Square", role: "Préparateur" },
  { name: "Elise G", pharmacy: "Square", role: "Pharmacien" },
  { name: "Fiona", pharmacy: "Square", role: "Préparateur" },
  { name: "Florian B", pharmacy: "Square", role: "Titulaire" },
  { name: "Sylvain P", pharmacy: "Square", role: "Préparateur" },
  { name: "Vincent B", pharmacy: "Square", role: "Titulaire" },
  { name: "Yasmine", pharmacy: "Square", role: "Apprentie" },
  { name: "Florian C", pharmacy: "Ragadan", role: "Pharmacien", birthDate: "1996-01-31" },
  { name: "Gaëlle", pharmacy: "Ragadan", role: "Préparateur", birthDate: "1998-10-20" },
  { name: "Julie", pharmacy: "Ragadan", role: "Apprentie" },
  { name: "Karen", pharmacy: "Ragadan", role: "Préparateur", birthDate: "1972-03-27" },
  { name: "Karine", pharmacy: "Ragadan", role: "Préparateur", birthDate: "1977-06-23" },
  { name: "Anissa", pharmacy: "Laurentine", role: "Préparateur", birthDate: "1997-05-11" },
  { name: "Magali", pharmacy: "Laurentine", role: "Préparateur", birthDate: "1990-01-24" },
  { name: "MC", pharmacy: "Laurentine", role: "Préparateur", birthDate: "1994-05-06" },
  { name: "Sandrine", pharmacy: "Laurentine", role: "Préparateur", birthDate: "1979-12-26" },
  { name: "Tracy", pharmacy: "Laurentine", role: "Apprentie", birthDate: "1994-02-07" },
  { name: "Val", pharmacy: "Laurentine", role: "Préparateur", birthDate: "1992-10-05" },
];

function applySharedOperators(rows: Array<{ name?: string; pharmacy?: string; role?: string; birthDate?: string }>) {
  const normalized = rows.flatMap((row) => {
    if (!row.name || !["Square", "Ragadan", "Laurentine"].includes(row.pharmacy ?? "")) return [];
    const role: MissionOperator["role"] = row.role === "Pharmacien Titulaire" ? "Titulaire"
      : row.role === "Préparateur(trice)" ? "Préparateur"
      : row.role === "Rayonniste" ? "Rayonniste"
      : "Pharmacien";
    return [{ name: row.name, pharmacy: row.pharmacy as MissionOperator["pharmacy"], role, birthDate: row.birthDate || undefined }];
  });
  if (normalized.length) missionOperators = normalized;
}

type MissionFrequency = "twice_daily" | "daily" | "weekly" | "monthly";
type MissionPriority = "normal" | "important" | "urgent";

type RecurringMission = {
  id: number;
  title: string;
  description: string;
  priority: MissionPriority;
  frequency: MissionFrequency;
  schedule: string;
  pharmacies: string[];
  assignees: string[];
  active: boolean;
  createdAt: number;
  updatedAt: number;
};

type MissionCompletion = {
  id: number;
  missionId: number;
  pharmacy: string;
  completedBy: string;
  periodKey: string;
  completedAt: number;
};

type MissionData = { missions: RecurringMission[]; completions: MissionCompletion[] };

function parseStringList(value: unknown) {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value !== "string") return [];
  try { return JSON.parse(value) as string[]; } catch { return []; }
}

function normalizeMission(row: Record<string, unknown>): RecurringMission {
  return {
    id: Number(row.id),
    title: String(row.title ?? ""),
    description: String(row.description ?? ""),
    priority: String(row.priority ?? "normal") as MissionPriority,
    frequency: String(row.frequency ?? "daily") as MissionFrequency,
    schedule: String(row.schedule ?? "09:00"),
    pharmacies: parseStringList(row.pharmacies),
    assignees: parseStringList(row.assignees),
    active: Boolean(row.active),
    createdAt: Number(row.createdAt ?? row.created_at ?? 0),
    updatedAt: Number(row.updatedAt ?? row.updated_at ?? 0),
  };
}

function normalizeCompletion(row: Record<string, unknown>): MissionCompletion {
  return {
    id: Number(row.id),
    missionId: Number(row.missionId ?? row.mission_id),
    pharmacy: String(row.pharmacy ?? ""),
    completedBy: String(row.completedBy ?? row.completed_by ?? ""),
    periodKey: String(row.periodKey ?? row.period_key ?? ""),
    completedAt: Number(row.completedAt ?? row.completed_at ?? 0),
  };
}

function normalizeMissionData(payload: { missions?: Record<string, unknown>[]; completions?: Record<string, unknown>[] }): MissionData {
  return {
    missions: (payload.missions ?? []).map(normalizeMission),
    completions: (payload.completions ?? []).map(normalizeCompletion),
  };
}

const missionFrequencyLabels: Record<MissionFrequency, string> = {
  twice_daily: "2× par jour",
  daily: "Quotidienne",
  weekly: "Hebdomadaire",
  monthly: "Mensuelle",
};

function mondayWeekKey(date: Date) {
  const copy = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = copy.getDay() || 7;
  copy.setDate(copy.getDate() - day + 1);
  return localDateIso(copy);
}

function timeReached(time: string, now: Date) {
  const [hours, minutes] = time.split(":").map(Number);
  return now.getHours() * 60 + now.getMinutes() >= hours * 60 + minutes;
}

function missionPeriod(mission: RecurringMission, now = new Date()) {
  const dateKey = localDateIso(now);
  if (mission.frequency === "twice_daily") {
    const times = mission.schedule.split(",").filter(Boolean).sort();
    const reached = times.filter((time) => timeReached(time, now));
    if (!reached.length) return { due: false, key: `${dateKey}-${times[0] ?? "09:00"}`, label: `À ${times[0] ?? "09:00"}` };
    const slot = reached[reached.length - 1];
    return { due: true, key: `${dateKey}-${slot}`, label: slot === times[0] ? `Passage du matin · ${slot}` : `Passage du soir · ${slot}` };
  }
  if (mission.frequency === "daily") {
    const time = mission.schedule || "09:00";
    return { due: now.getDay() !== 0 && timeReached(time, now), key: dateKey, label: `Aujourd’hui · ${time}` };
  }
  const [dayValue, time = "09:00"] = mission.schedule.split(",");
  if (mission.frequency === "weekly") {
    const scheduledDay = Number(dayValue);
    const currentDay = now.getDay() || 7;
    return { due: currentDay >= scheduledDay && (currentDay > scheduledDay || timeReached(time, now)), key: mondayWeekKey(now), label: `Cette semaine · ${time}` };
  }
  const scheduledDate = Number(dayValue);
  return { due: now.getDate() >= scheduledDate && (now.getDate() > scheduledDate || timeReached(time, now)), key: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`, label: `Ce mois · jour ${scheduledDate}` };
}

function missionOccurrences(data: MissionData, now = new Date()) {
  return data.missions.flatMap((mission) => {
    const period = missionPeriod(mission, now);
    if (!period.due) return [];
    return mission.pharmacies.map((pharmacy) => ({
      mission,
      pharmacy,
      periodKey: period.key,
      periodLabel: period.label,
      completion: data.completions.find((item) => item.missionId === mission.id && item.pharmacy === pharmacy && item.periodKey === period.key),
    }));
  });
}

type DailyTaskKind = "task" | "delivery";
type DailyTaskStatus = "open" | "completed";

type DailyTask = {
  id: number;
  kind: DailyTaskKind;
  title: string;
  details: string;
  priority: MissionPriority;
  pharmacies: string[];
  createdBy: string;
  assignedTo: string;
  displayDate: string;
  dueTime: string;
  recipient: string;
  phone: string;
  address: string;
  status: DailyTaskStatus;
  completedBy: string;
  completedAt: number | null;
  createdAt: number;
  updatedAt: number;
  sourceRef?: string;
};

type DailyTaskNote = {
  id: number;
  taskId: number;
  author: string;
  body: string;
  createdAt: number;
};

type DailyTaskData = { tasks: DailyTask[]; notes: DailyTaskNote[]; syncedAt: number };
type DailyTaskForm = Omit<DailyTask, "id" | "status" | "completedBy" | "completedAt" | "createdAt" | "updatedAt">;
const niftyTaskMatch = (task: DailyTask) => String(task.sourceRef || "").match(/^nifty-(start|return):(Square|Ragadan|Laurentine):(.+)$/);

async function markNiftyCampaignTaskDone(task: DailyTask) {
  const match = niftyTaskMatch(task);
  if (!match) return;
  const [, phase, pharmacy, campaignId] = match;
  const response = await fetch(`/api/nifty-pricing?pharmacy=${encodeURIComponent(pharmacy)}`, { cache: "no-store" });
  const payload = await response.json() as { items?: Array<Record<string, unknown>>; error?: string };
  if (!response.ok) throw new Error(payload.error || "Lecture des prix Nifty impossible.");
  const items = (payload.items ?? []).filter((row) => {
    if (String((row.campaign as Record<string, unknown> | undefined)?.id || "") !== campaignId) return false;
    return phase === "start" ? String(row.status || "") === "ready" : String(row.status || "") === "applied";
  });
  // La tâche conserve elle-même la trace complète des prix. Si le laboratoire
  // a déjà retiré la campagne du fichier Nifty, sa validation doit rester
  // possible et le rappel rejoint normalement l’historique de la To-do.
  if (!items.length) return;
  const saved = await fetch("/api/nifty-pricing", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      pharmacy,
      items: items.map((row) => ({
        ean: row.ean,
        baselinePrice: row.baseline_price || row.sale_price,
        proposedPrice: row.proposed_price,
        returnPrice: row.return_price || row.baseline_price || row.sale_price,
        status: phase === "start" ? "applied" : "returned",
        note: row.note || (phase === "start" ? "Période Nifty mise en place depuis la To-do." : "Prix remis après la campagne Nifty depuis la To-do."),
      })),
    }),
  });
  const savedPayload = await saved.json() as { error?: string };
  if (!saved.ok) throw new Error(savedPayload.error || "Validation du retour des prix Nifty impossible.");
}

function normalizeDailyTask(row: Record<string, unknown>): DailyTask {
  return {
    id: Number(row.id),
    kind: String(row.kind ?? "task") as DailyTaskKind,
    title: String(row.title ?? ""),
    details: String(row.details ?? ""),
    priority: String(row.priority ?? "normal") as MissionPriority,
    pharmacies: parseStringList(row.pharmacies),
    createdBy: String(row.createdBy ?? row.created_by ?? ""),
    assignedTo: String(row.assignedTo ?? row.assigned_to ?? ""),
    displayDate: String(row.displayDate ?? row.display_date ?? ""),
    dueTime: String(row.dueTime ?? row.due_time ?? ""),
    recipient: String(row.recipient ?? ""),
    phone: String(row.phone ?? ""),
    address: String(row.address ?? ""),
    status: String(row.status ?? "open") as DailyTaskStatus,
    completedBy: String(row.completedBy ?? row.completed_by ?? ""),
    completedAt: row.completedAt || row.completed_at ? Number(row.completedAt ?? row.completed_at) : null,
    createdAt: Number(row.createdAt ?? row.created_at ?? 0),
    updatedAt: Number(row.updatedAt ?? row.updated_at ?? 0),
    sourceRef: String(row.sourceRef ?? row.source_ref ?? ""),
  };
}

function normalizeDailyTaskData(payload: { tasks?: Record<string, unknown>[]; notes?: Record<string, unknown>[]; syncedAt?: number }): DailyTaskData {
  return {
    tasks: (payload.tasks ?? []).map(normalizeDailyTask),
    notes: (payload.notes ?? []).map((row) => ({
      id: Number(row.id),
      taskId: Number(row.taskId ?? row.task_id),
      author: String(row.author ?? ""),
      body: String(row.body ?? ""),
      createdAt: Number(row.createdAt ?? row.created_at ?? 0),
    })),
    syncedAt: Number(payload.syncedAt ?? Date.now()),
  };
}

function dueDailyTasks(data: DailyTaskData, now = new Date()) {
  const today = localDateIso(now);
  return data.tasks.filter((task) => task.status === "open" && task.displayDate <= today);
}

function DailyTaskModal({
  kind,
  initial,
  operators,
  initialPharmacy,
  initialOperator,
  saving,
  onClose,
  onSave,
}: {
  kind: DailyTaskKind;
  initial: DailyTask | null;
  operators: MissionOperator[];
  initialPharmacy: MissionOperator["pharmacy"];
  initialOperator: string;
  saving: boolean;
  onClose: () => void;
  onSave: (form: DailyTaskForm) => void;
}) {
  const chosenKind = initial?.kind ?? kind;
  const [form, setForm] = useState<DailyTaskForm>(() => initial ? {
    kind: initial.kind,
    title: initial.title,
    details: initial.details,
    priority: initial.priority,
    pharmacies: initial.pharmacies,
    createdBy: initial.createdBy,
    assignedTo: initial.assignedTo,
    displayDate: initial.displayDate,
    dueTime: initial.dueTime,
    recipient: initial.recipient,
    phone: initial.phone,
    address: initial.address,
  } : {
    kind: chosenKind,
    title: "",
    details: "",
    priority: "normal",
    pharmacies: [initialPharmacy],
    createdBy: initialOperator,
    assignedTo: "",
    displayDate: localDateIso(),
    dueTime: "",
    recipient: "",
    phone: "",
    address: "",
  });
  const allNames = operators.map((operator) => operator.name);
  const update = <K extends keyof DailyTaskForm>(key: K, value: DailyTaskForm[K]) => setForm((current) => ({ ...current, [key]: value }));
  const togglePharmacy = (name: string) => update("pharmacies", form.pharmacies.includes(name) ? form.pharmacies.filter((item) => item !== name) : [...form.pharmacies, name]);
  const valid = form.title.trim() && form.createdBy && form.displayDate && form.pharmacies.length && (form.kind === "task" || form.recipient.trim());

  return (
    <div className="daily-modal-layer" role="presentation" onMouseDown={onClose}>
      <section className={`daily-modal ${form.kind}`} role="dialog" aria-modal="true" aria-labelledby="daily-modal-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div><span>{form.kind === "delivery" ? "NOUVELLE LIVRAISON" : "NOUVELLE ACTION"}</span><h2 id="daily-modal-title">{initial ? "Modifier l’action" : form.kind === "delivery" ? "Préparer une livraison" : "Ajouter à la to-do"}</h2></div>
          <button type="button" aria-label="Fermer" onClick={onClose}>×</button>
        </header>
        <div className="daily-form">
          <label className="wide"><span>Ce qu’il faut faire *</span><input autoFocus value={form.title} onChange={(event) => update("title", event.target.value)} placeholder={form.kind === "delivery" ? "Ex. Livrer le renouvellement de Mme Martin" : "Ex. Vérifier le rayon solaire avant 11 h"} /></label>
          <label><span>Créée par *</span><select value={form.createdBy} onChange={(event) => update("createdBy", event.target.value)}>{allNames.map((name) => <option key={name}>{name}</option>)}</select></label>
          <label><span>Destinée à</span><select value={form.assignedTo} onChange={(event) => update("assignedTo", event.target.value)}><option value="">Toute l’équipe</option>{allNames.map((name) => <option key={name}>{name}</option>)}</select></label>
          <label><span>Afficher le *</span><input type="date" value={form.displayDate} onChange={(event) => update("displayDate", event.target.value)} /></label>
          <label><span>Avant / créneau</span><input type="time" value={form.dueTime} onChange={(event) => update("dueTime", event.target.value)} /></label>
          <fieldset className="wide"><legend>Priorité</legend><div className="daily-priority-choice">{(["normal", "important", "urgent"] as MissionPriority[]).map((priority) => <button type="button" key={priority} className={`${priority} ${form.priority === priority ? "active" : ""}`} onClick={() => update("priority", priority)}><i />{priority === "normal" ? "Normale" : priority === "important" ? "Importante" : "Urgente"}</button>)}</div></fieldset>
          <fieldset className="wide"><legend>Pharmacies concernées *</legend><div className="daily-pharmacy-choice">{["Square", "Ragadan", "Laurentine"].map((name) => <button type="button" key={name} className={form.pharmacies.includes(name) ? "selected" : ""} onClick={() => togglePharmacy(name)}><i>{form.pharmacies.includes(name) ? "✓" : ""}</i>{name}</button>)}</div></fieldset>
          {form.kind === "delivery" && <>
            <label><span>Destinataire / patient *</span><input value={form.recipient} onChange={(event) => update("recipient", event.target.value)} placeholder="Nom complet" /></label>
            <label><span>Téléphone</span><input value={form.phone} onChange={(event) => update("phone", event.target.value)} inputMode="tel" placeholder="06…" /></label>
            <label className="wide"><span>Adresse de livraison</span><input value={form.address} onChange={(event) => update("address", event.target.value)} placeholder="Adresse, étage, digicode…" /></label>
          </>}
          <label className="wide"><span>Note utile</span><textarea value={form.details} onChange={(event) => update("details", event.target.value.slice(0, 1000))} placeholder="Consigne, contexte, personne à prévenir…" /></label>
        </div>
        <footer><p><span>TV</span> L’action apparaîtra automatiquement le jour prévu et disparaîtra dès sa validation.</p><div><button type="button" className="quiet" onClick={onClose}>Annuler</button><button type="button" className="save" disabled={!valid || saving} onClick={() => onSave(form)}>{saving ? "Enregistrement…" : initial ? "Enregistrer" : form.kind === "delivery" ? "Ajouter la livraison" : "Ajouter la tâche"}</button></div></footer>
      </section>
    </div>
  );
}

function DailyTasksModule({ onBack, initialPharmacy, initialOperator }: { onBack: () => void; initialPharmacy: MissionOperator["pharmacy"]; initialOperator: string }) {
  const pharmacyOperators = missionOperators.filter((item) => item.pharmacy === initialPharmacy);
  const pharmacyOperatorNames = pharmacyOperators.map((item) => item.name);
  const defaultOperator = pharmacyOperatorNames.includes(initialOperator) ? initialOperator : pharmacyOperatorNames[0] || initialOperator;
  const [data, setData] = useState<DailyTaskData>({ tasks: [], notes: [], syncedAt: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState<"today" | "tasks" | "deliveries" | "completed">("today");
  const [modal, setModal] = useState<{ kind: DailyTaskKind; task: DailyTask | null } | null>(null);
  const [noteTask, setNoteTask] = useState<DailyTask | null>(null);
  const [noteBody, setNoteBody] = useState("");
  const [operator, setOperator] = useState(defaultOperator);
  const [saving, setSaving] = useState(false);
  const today = localDateIso();

  const refresh = async (quiet = false) => {
    try {
      const response = await fetch("/api/daily-tasks", { cache: "no-store" });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);
      setData(normalizeDailyTaskData(result));
      setError("");
    } catch (requestError) {
      if (!quiet) setError(requestError instanceof Error ? requestError.message : "La synchronisation est indisponible.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const visible = data.tasks.filter((task) => {
    if (filter === "completed") return task.status === "completed";
    if (task.status !== "open") return false;
    if (filter === "tasks") return task.kind === "task";
    if (filter === "deliveries") return task.kind === "delivery";
    return task.displayDate <= today;
  });
  const open = data.tasks.filter((task) => task.status === "open");
  const deliveries = open.filter((task) => task.kind === "delivery" && task.displayDate <= today);
  const urgent = open.filter((task) => task.priority === "urgent" && task.displayDate <= today);

  const save = async (form: DailyTaskForm) => {
    setSaving(true);
    try {
      const response = await fetch("/api/daily-tasks", { method: modal?.task ? "PATCH" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...form, id: modal?.task?.id }) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);
      setModal(null);
      await refresh(true);
      publishDailyTasksUpdate();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Enregistrement impossible.");
    } finally { setSaving(false); }
  };

  const patchAction = async (task: DailyTask, action: "complete" | "reopen") => {
    setSaving(true);
    try {
      if (action === "complete") await markNiftyCampaignTaskDone(task);
      const response = await fetch("/api/daily-tasks", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: task.id, action, operator }) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Enregistrement impossible.");
      const savedTask = normalizeDailyTask(result);
      setData((current) => ({
        ...current,
        tasks: current.tasks.map((item) => item.id === task.id ? savedTask : item),
        syncedAt: Date.now(),
      }));
      await refresh(true);
      publishDailyTasksUpdate();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Enregistrement impossible.");
    } finally { setSaving(false); }
  };
  const addNote = async () => {
    if (!noteTask || !noteBody.trim()) return;
    setSaving(true);
    try {
      const response = await fetch("/api/daily-tasks", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: noteTask.id, action: "note", operator, note: noteBody }) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Publication de la note impossible.");
      setNoteTask(null);
      setNoteBody("");
      await refresh(true);
      publishDailyTasksUpdate();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Publication de la note impossible.");
    } finally { setSaving(false); }
  };
  const remove = async (task: DailyTask) => {
    if (!window.confirm(`Supprimer « ${task.title} » ?`)) return;
    setSaving(true);
    try {
      const response = await fetch(`/api/daily-tasks?id=${task.id}`, { method: "DELETE" });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Suppression impossible.");
      await refresh(true);
      publishDailyTasksUpdate();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Suppression impossible.");
    } finally { setSaving(false); }
  };

  return (
    <main className="daily-app">
      <header className="module-header">
        <button type="button" className="module-back" onClick={onBack}>← <span>Retour à l’accueil</span></button>
        <div className="wordmark module-wordmark"><span className="wordmark-symbol"><i /><i /><i /></span><span><strong>saint-laurent</strong><small>pharmacies</small></span></div>
        <div className="module-header-actions"><ModuleToolsLink /><span className="module-status"><i /> Liste synchronisée</span></div>
      </header>
      <section className="daily-page">
        <div className="daily-hero">
          <div><span>COCKPIT QUOTIDIEN</span><h1>À faire, à livrer,<br /><em>puis terminé.</em></h1><p>La même information au comptoir, dans le back-office et sur l’écran équipe.</p></div>
          <div className="daily-hero-actions"><span className="daily-sync"><i />Sync {data.syncedAt ? new Date(data.syncedAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "…"}</span><button type="button" className="task" onClick={() => setModal({ kind: "task", task: null })}>+ Tâche</button><button type="button" className="delivery" onClick={() => setModal({ kind: "delivery", task: null })}>+ Livraison</button></div>
        </div>
        <section className="daily-stats"><article><strong>{open.filter((task) => task.displayDate <= today).length}</strong><span>À traiter aujourd’hui</span></article><article className="delivery"><strong>{deliveries.length}</strong><span>Livraisons en attente</span></article><article className="urgent"><strong>{urgent.length}</strong><span>Actions urgentes</span></article></section>
        <div className="daily-toolbar">
          <div>{([["today", "Aujourd’hui"], ["tasks", "Tâches"], ["deliveries", "Livraisons"], ["completed", "Historique"]] as const).map(([value, label]) => <button type="button" key={value} className={filter === value ? "active" : ""} onClick={() => setFilter(value)}>{label}</button>)}</div>
          <label><span>Validé par</span><select value={operator} onChange={(event) => setOperator(event.target.value)}>{pharmacyOperators.map((item) => <option key={item.name}>{item.name}</option>)}</select></label>
        </div>
        {error && <div className="daily-error">{error}<button type="button" onClick={() => void refresh()}>Réessayer</button></div>}
        <section className="daily-list">
          {loading ? <div className="daily-empty">Synchronisation de la liste…</div> : visible.length ? visible.map((task) => {
            const isLate = task.status === "open" && task.displayDate < today;
            const notes = data.notes.filter((note) => note.taskId === task.id);
            return <article className={`daily-card ${task.kind} ${task.priority} ${task.status}`} key={task.id}>
              <div className="daily-kind-icon">{task.kind === "delivery" ? "▣" : "✓"}</div>
              <div className="daily-card-main">
                <header><div><span className="daily-kind">{task.kind === "delivery" ? "LIVRAISON" : "TÂCHE"} · {task.pharmacies.join(" · ")}</span><h2>{task.title}</h2></div><span className={`daily-state ${isLate ? "late" : task.priority}`}>{task.status === "completed" ? "Terminée" : isLate ? "En retard" : task.priority === "urgent" ? "Urgente" : task.priority === "important" ? "Importante" : "À faire"}</span></header>
                {task.kind === "delivery" && <div className="daily-delivery-line"><strong>{task.recipient}</strong>{task.address && <span>{task.address}</span>}{task.phone && <a href={`tel:${task.phone}`}>{task.phone}</a>}</div>}
                {task.details && <p className="daily-details">{task.details}</p>}
                <div className="daily-meta"><span>Créée par <strong>{task.createdBy}</strong></span><span>{task.assignedTo ? `Pour ${task.assignedTo}` : "Pour toute l’équipe"}</span><span>{followupDate(task.displayDate)}{task.dueTime ? ` · ${task.dueTime}` : ""}</span>{task.status === "completed" && <span>✓ {task.completedBy} · {task.completedAt ? new Date(task.completedAt).toLocaleString("fr-FR") : ""}</span>}</div>
                {notes.length > 0 && <div className="daily-notes">{notes.slice(0, 2).map((note) => <p key={note.id}><strong>{note.author}</strong>{note.body}</p>)}</div>}
                <footer>{task.status === "open" ? <button type="button" className="complete" disabled={saving} onClick={() => void patchAction(task, "complete")}>{saving ? "Enregistrement…" : "Effectué ✓"}</button> : <button type="button" className="reopen" disabled={saving} onClick={() => void patchAction(task, "reopen")}>{saving ? "Enregistrement…" : "Rouvrir"}</button>}<button type="button" disabled={saving} onClick={() => setModal({ kind: task.kind, task })}>Modifier</button><button type="button" disabled={saving} onClick={() => setNoteTask(task)}>+ Note</button><button type="button" className="delete" disabled={saving} onClick={() => void remove(task)}>Supprimer</button></footer>
              </div>
            </article>;
          }) : <div className="daily-empty"><span>✓</span><h2>{filter === "completed" ? "Aucune action terminée" : "Tout est traité"}</h2><p>La prochaine tâche ou livraison apparaîtra automatiquement ici.</p><button type="button" onClick={() => setModal({ kind: "task", task: null })}>Ajouter une tâche</button></div>}
        </section>
      </section>
      {modal && <DailyTaskModal kind={modal.kind} initial={modal.task} operators={pharmacyOperators} initialPharmacy={initialPharmacy} initialOperator={defaultOperator} saving={saving} onClose={() => setModal(null)} onSave={save} />}
      {noteTask && <div className="daily-note-layer" role="presentation" onMouseDown={() => !saving && setNoteTask(null)}><section role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}><span>AJOUTER UNE NOTE</span><h2>{noteTask.title}</h2><textarea autoFocus value={noteBody} onChange={(event) => setNoteBody(event.target.value.slice(0, 700))} placeholder="Information utile pour l’équipe…" /><div><button type="button" disabled={saving} onClick={() => setNoteTask(null)}>Annuler</button><button type="button" disabled={saving || !noteBody.trim()} onClick={() => void addNote()}>{saving ? "Publication…" : "Publier la note"}</button></div></section></div>}
    </main>
  );
}

type OperatorNotePriority = "normal" | "important" | "urgent";
type OperatorNoteStatus = "open" | "completed";

type OperatorNote = {
  id: number;
  owner: string;
  title: string;
  details: string;
  priority: OperatorNotePriority;
  dueDate: string;
  copiedFrom: string;
  status: OperatorNoteStatus;
  completedAt: number | null;
  createdAt: number;
  updatedAt: number;
};

function normalizeOperatorNote(row: Record<string, unknown>): OperatorNote {
  return {
    id: Number(row.id),
    owner: String(row.owner ?? ""),
    title: String(row.title ?? ""),
    details: String(row.details ?? ""),
    priority: String(row.priority ?? "normal") as OperatorNotePriority,
    dueDate: String(row.dueDate ?? row.due_date ?? ""),
    copiedFrom: String(row.copiedFrom ?? row.copied_from ?? ""),
    status: String(row.status ?? "open") as OperatorNoteStatus,
    completedAt: row.completedAt === null || row.completed_at === null ? null : Number(row.completedAt ?? row.completed_at ?? 0),
    createdAt: Number(row.createdAt ?? row.created_at ?? 0),
    updatedAt: Number(row.updatedAt ?? row.updated_at ?? 0),
  };
}

function OperatorNotesModule({ onBack, initialOperator }: { onBack: () => void; initialOperator: string }) {
  const [operator, setOperator] = useState(initialOperator);
  const [notes, setNotes] = useState<OperatorNote[]>([]);
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [priority, setPriority] = useState<OperatorNotePriority>("normal");
  const [dueDate, setDueDate] = useState(localDateIso());
  const [copyTo, setCopyTo] = useState("");
  const [filter, setFilter] = useState<"all" | OperatorNoteStatus | "urgent">("all");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const loadNotes = async () => {
    try {
      const response = await fetch("/api/operator-notes", { cache: "no-store" });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      setNotes((payload.notes ?? []).map(normalizeOperatorNote));
      setError("");
    } catch {
      setError("Le bloc-notes est momentanément indisponible.");
    }
  };

  useEffect(() => {
    void loadNotes();
  }, []);

  const operatorNotes = notes.filter((note) => note.owner === operator);
  const visibleNotes = operatorNotes.filter((note) => {
    if (filter === "all") return true;
    if (filter === "urgent") return note.priority === "urgent" && note.status === "open";
    return note.status === filter;
  });
  const openCount = operatorNotes.filter((note) => note.status === "open").length;
  const urgentCount = operatorNotes.filter((note) => note.status === "open" && note.priority === "urgent").length;
  const doneCount = operatorNotes.filter((note) => note.status === "completed").length;

  const createNote = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const response = await fetch("/api/operator-notes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ owner: operator, title, details, priority, dueDate, copyTo }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      setTitle("");
      setDetails("");
      setPriority("normal");
      setCopyTo("");
      await loadNotes();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "La note n’a pas pu être ajoutée.");
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (note: OperatorNote) => {
    try {
      const response = await fetch("/api/operator-notes", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: note.id, action: note.status === "open" ? "complete" : "reopen" }),
      });
      if (!response.ok) throw new Error();
      await loadNotes();
    } catch {
      setError("La note n’a pas pu être mise à jour.");
    }
  };

  const deleteNote = async (note: OperatorNote) => {
    if (!window.confirm(`Supprimer « ${note.title} » ?`)) return;
    try {
      const response = await fetch(`/api/operator-notes?id=${note.id}`, { method: "DELETE" });
      if (!response.ok) throw new Error();
      await loadNotes();
    } catch {
      setError("La note n’a pas pu être supprimée.");
    }
  };

  return (
    <main className="operator-notes-app">
      <header className="module-header">
        <button type="button" className="module-back" onClick={onBack}>← <span>Retour à l’accueil</span></button>
        <div className="wordmark module-wordmark">
          <span className="wordmark-symbol"><i /><i /><i /></span>
          <span><strong>saint-laurent</strong><small>pharmacies</small></span>
        </div>
        <div className="module-header-actions"><ModuleToolsLink /><span className="module-status"><i /> Notes synchronisées</span></div>
      </header>

      <section className="operator-notes-page">
        <div className="operator-notes-hero">
          <div><span>MON ESPACE · ACCÈS RAPIDE</span><h1>Bloc-notes <em>opérateur.</em></h1><p>Vos rappels personnels, datés et priorisés, sans encombrer la to-do générale.</p></div>
          <label><span>Bloc-notes de</span><select value={operator} onChange={(event) => setOperator(event.target.value)}>{missionOperators.map((item) => <option key={item.name} value={item.name}>{item.name} · {item.pharmacy}</option>)}</select></label>
        </div>

        <section className="operator-note-stats">
          <article><strong>{openCount}</strong><span>À faire</span></article>
          <article className="urgent"><strong>{urgentCount}</strong><span>Urgentes</span></article>
          <article className="done"><strong>{doneCount}</strong><span>Faites</span></article>
        </section>

        <section className="operator-note-composer">
          <div className="operator-note-section-title"><span>＋</span><div><h2>Ajouter une note personnelle</h2><p>Elle reste dans votre espace, sauf si vous choisissez d’en envoyer une copie.</p></div></div>
          <div className="operator-note-form">
            <label className="wide"><span>Tâche</span><input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Ex. rappeler Mme Dupont pour contention" /></label>
            <label><span>Date du rappel</span><input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} /><small className="operator-reminder-shortcuts"><button type="button" onClick={() => setDueDate(localDateIso())}>Aujourd’hui</button><button type="button" onClick={() => { const date = new Date(); date.setDate(date.getDate() + 1); setDueDate(localDateIso(date)); }}>Demain</button><button type="button" onClick={() => { const date = new Date(); date.setDate(date.getDate() + 7); setDueDate(localDateIso(date)); }}>Dans 7 jours</button></small></label>
            <label><span>Importance</span><select value={priority} onChange={(event) => setPriority(event.target.value as OperatorNotePriority)}><option value="normal">Normale</option><option value="important">Importante</option><option value="urgent">Urgente</option></select></label>
            <label className="wide"><span>Copie à un opérateur <small>facultatif</small></span><select value={copyTo} onChange={(event) => setCopyTo(event.target.value)}><option value="">Aucune copie</option>{missionOperators.filter((item) => item.name !== operator).map((item) => <option key={item.name} value={item.name}>{item.name} · {item.pharmacy}</option>)}</select></label>
            <label className="wide"><span>Note / détail</span><textarea value={details} onChange={(event) => setDetails(event.target.value.slice(0, 700))} placeholder="Détail utile, produit, patient, fournisseur, rappel…" /><small>{details.length}/700</small></label>
          </div>
          <div className="operator-note-composer-actions"><button type="button" className="primary" disabled={!title.trim() || saving} onClick={() => void createNote()}>{saving ? "Ajout…" : "Ajouter la note"}</button><button type="button" onClick={() => { setTitle(""); setDetails(""); setPriority("normal"); setCopyTo(""); setDueDate(localDateIso()); }}>Effacer</button></div>
        </section>

        <section className="operator-note-list-section">
          <div className="operator-note-list-heading"><div><span>MES NOTES</span><h2>Notes de {operator}</h2></div><div>{(["all", "open", "urgent", "completed"] as const).map((value) => <button type="button" key={value} className={filter === value ? "active" : ""} onClick={() => setFilter(value)}>{value === "all" ? "Toutes" : value === "open" ? "À faire" : value === "urgent" ? "Urgentes" : "Faites"}</button>)}</div></div>
          {error && <div className="operator-note-error">{error}<button type="button" onClick={() => void loadNotes()}>Réessayer</button></div>}
          <div className="operator-note-list">
            {visibleNotes.map((note) => <article key={note.id} className={`${note.priority} ${note.status}`}>
              <button type="button" className="operator-note-check" aria-label={note.status === "open" ? "Marquer comme faite" : "Rouvrir"} onClick={() => void updateStatus(note)}>{note.status === "completed" ? "✓" : ""}</button>
              <div><header><h3>{note.title}</h3><span>{note.priority === "urgent" ? "Urgente" : note.priority === "important" ? "Importante" : "Normale"}</span></header>{note.details && <p>{note.details}</p>}<footer><span>Échéance · {followupDate(note.dueDate)}</span>{note.copiedFrom && <span>Copie de {note.copiedFrom}</span>}</footer></div>
              <button type="button" className="operator-note-delete" onClick={() => void deleteNote(note)}>Supprimer</button>
            </article>)}
            {!visibleNotes.length && <div className="operator-note-empty"><span>✓</span><h3>Rien à signaler ici</h3><p>Ajoutez une note ou changez de filtre.</p></div>}
          </div>
        </section>
      </section>
    </main>
  );
}

type AdrenalineItem = {
  id: number;
  pharmacy: string;
  product: string;
  availabilityDate: string;
  expiryDate: string;
  batchNumber: string;
  operator: string;
  scanCode: string;
  alertAcknowledgedAt: number | null;
  createdAt: number;
  updatedAt: number;
};

function normalizeAdrenalineItem(row: Record<string, unknown>): AdrenalineItem {
  return {
    id: Number(row.id),
    pharmacy: String(row.pharmacy ?? ""),
    product: String(row.product ?? "Jext"),
    availabilityDate: String(row.availabilityDate ?? row.availability_date ?? ""),
    expiryDate: String(row.expiryDate ?? row.expiry_date ?? ""),
    batchNumber: String(row.batchNumber ?? row.batch_number ?? ""),
    operator: String(row.operator ?? ""),
    scanCode: String(row.scanCode ?? row.scan_code ?? ""),
    alertAcknowledgedAt: row.alertAcknowledgedAt === null || row.alert_acknowledged_at === null ? null : Number(row.alertAcknowledgedAt ?? row.alert_acknowledged_at ?? 0),
    createdAt: Number(row.createdAt ?? row.created_at ?? 0),
    updatedAt: Number(row.updatedAt ?? row.updated_at ?? 0),
  };
}

function daysUntilDate(value: string, now = new Date()) {
  if (!value) return Number.POSITIVE_INFINITY;
  const target = new Date(`${value}T12:00:00`);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
  return Math.ceil((target.getTime() - today.getTime()) / 86_400_000);
}

function adrenalineAlerts(items: AdrenalineItem[], now = new Date()) {
  return items
    .filter((item) => item.expiryDate && daysUntilDate(item.expiryDate, now) <= 15 && !item.alertAcknowledgedAt)
    .sort((a, b) => a.expiryDate.localeCompare(b.expiryDate));
}

function AdrenalineModule({ onBack }: { onBack: () => void }) {
  const [items, setItems] = useState<AdrenalineItem[]>([]);
  const [pharmacy, setPharmacy] = useState("Square");
  const [product, setProduct] = useState("Jext");
  const [availabilityDate, setAvailabilityDate] = useState(localDateIso());
  const [expiryDate, setExpiryDate] = useState("");
  const [batchNumber, setBatchNumber] = useState("");
  const [operator, setOperator] = useState("");
  const [scanCode, setScanCode] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadItems = async () => {
    try {
      const response = await fetch("/api/adrenaline", { cache: "no-store" });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      setItems((payload.items ?? []).map(normalizeAdrenalineItem));
      setError("");
    } catch {
      setError("Le suivi de l’adrénaline est momentanément indisponible.");
    }
  };

  useEffect(() => {
    void loadItems();
  }, []);

  useEffect(() => {
    const current = items.find((item) => item.pharmacy === pharmacy);
    if (!current) {
      setProduct("Jext");
      setAvailabilityDate(localDateIso());
      setExpiryDate("");
      setBatchNumber("");
      setOperator("");
      setScanCode("");
      return;
    }
    setProduct(current.product);
    setAvailabilityDate(current.availabilityDate);
    setExpiryDate(current.expiryDate);
    setBatchNumber(current.batchNumber);
    setOperator(current.operator);
    setScanCode(current.scanCode);
  }, [items, pharmacy]);

  const pharmacyOperators = missionOperators.filter((item) => item.pharmacy === pharmacy);
  const alerts = adrenalineAlerts(items);

  const saveItem = async () => {
    if (!expiryDate || !batchNumber.trim() || !operator) {
      setError("Renseignez la péremption, le numéro de lot et le responsable.");
      return;
    }
    setSaving(true);
    try {
      const response = await fetch("/api/adrenaline", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pharmacy, product, availabilityDate, expiryDate, batchNumber, operator, scanCode }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      await loadItems();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "L’enregistrement a échoué.");
    } finally {
      setSaving(false);
    }
  };

  const acknowledge = async (item: AdrenalineItem) => {
    try {
      const response = await fetch("/api/adrenaline", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: item.id, action: "acknowledge" }),
      });
      if (!response.ok) throw new Error();
      await loadItems();
    } catch {
      setError("L’alerte n’a pas pu être prise en compte.");
    }
  };

  return (
    <main className="adrenaline-app">
      <header className="module-header">
        <button type="button" className="module-back" onClick={onBack}>← <span>Retour à l’accueil</span></button>
        <div className="wordmark module-wordmark"><span className="wordmark-symbol"><i /><i /><i /></span><span><strong>saint-laurent</strong><small>pharmacies</small></span></div>
        <div className="module-header-actions"><ModuleToolsLink /><span className="module-status"><i /> Alerte automatique J−15</span></div>
      </header>

      <section className="adrenaline-page">
        <div className="adrenaline-hero">
          <div><span>VACCINATION · SÉCURITÉ</span><h1>Suivi de l’adrénaline</h1><p>Une traçabilité commune aux trois pharmacies, avec alerte automatique quinze jours avant péremption.</p></div>
          <div className={alerts.length ? "alert" : ""}><strong>{alerts.length ? `${alerts.length} alerte${alerts.length > 1 ? "s" : ""}` : "Tout est conforme"}</strong><small>{alerts.length ? "Visible sur l’accueil et la TV" : "Prochain contrôle automatique actif"}</small></div>
        </div>

        <div className="adrenaline-layout">
          <section className="adrenaline-form-card">
            <div className="adrenaline-section-title"><span>01</span><div><h2>Adrénaline mise à disposition</h2><p>Une fiche active par pharmacie.</p></div></div>
            <div className="adrenaline-form">
              <label><span>Pharmacie</span><select value={pharmacy} onChange={(event) => setPharmacy(event.target.value)}>{followupPharmacies.map((name) => <option key={name}>{name}</option>)}</select></label>
              <label><span>Produit</span><select value={product} onChange={(event) => setProduct(event.target.value)}><option>Jext</option><option>Anapen</option><option>Emerade</option><option>Autre</option></select><small>Jext est proposé par défaut, mais reste modifiable.</small></label>
              <label><span>Mise à disposition dans le local vaccin</span><input type="date" value={availabilityDate} onChange={(event) => setAvailabilityDate(event.target.value)} /></label>
              <label><span>Date de péremption *</span><input type="date" value={expiryDate} onChange={(event) => setExpiryDate(event.target.value)} /></label>
              <label><span>Numéro de lot *</span><input value={batchNumber} onChange={(event) => setBatchNumber(event.target.value)} placeholder="Ex. LOT-JX-2026-084" /></label>
              <label><span>Responsable / opérateur *</span><select value={operator} onChange={(event) => setOperator(event.target.value)}><option value="">Choisir un opérateur</option>{pharmacyOperators.map((item) => <option key={item.name} value={item.name}>{item.name} · {item.role}</option>)}</select></label>
              <label className="wide"><span>QR code / Datamatrix / texte scanné</span><textarea value={scanCode} onChange={(event) => setScanCode(event.target.value.slice(0, 600))} placeholder="Scanner ou coller ici le code : le lot et la péremption restent vérifiables avant enregistrement." /></label>
            </div>
            {error && <div className="adrenaline-error">{error}<button type="button" onClick={() => setError("")}>×</button></div>}
            <div className="adrenaline-form-actions"><button type="button" onClick={() => setScanCode("")}>Effacer le scan</button><button type="button" className="save" disabled={saving} onClick={() => void saveItem()}>{saving ? "Enregistrement…" : "Enregistrer la mise à disposition"}</button></div>
          </section>

          <section className="adrenaline-status-card">
            <div className="adrenaline-section-title"><span>02</span><div><h2>État actuel</h2><p>Vue immédiate des trois pharmacies.</p></div></div>
            <div className="adrenaline-status-list">
              {followupPharmacies.map((name) => {
                const item = items.find((entry) => entry.pharmacy === name);
                const days = item ? daysUntilDate(item.expiryDate) : Number.POSITIVE_INFINITY;
                const isAlert = Boolean(item && days <= 15 && !item.alertAcknowledgedAt);
                return <article key={name} className={isAlert ? "alert" : item ? "ok" : "empty"}>
                  <header><strong>{name}</strong>{item && <span>{isAlert ? days < 0 ? "Périmée" : `J−${days}` : "Conforme"}</span>}</header>
                  {item ? <><h3>{item.product} · lot {item.batchNumber}</h3><p>Péremption {followupDate(item.expiryDate)} · responsable {item.operator}</p><div><button type="button" onClick={() => setPharmacy(name)}>Modifier</button>{isAlert && <button type="button" className="acknowledge" onClick={() => void acknowledge(item)}>Alerte prise en compte</button>}</div></> : <><h3>Aucune adrénaline enregistrée</h3><p>Complétez la fiche pour activer le contrôle automatique.</p><button type="button" onClick={() => setPharmacy(name)}>Renseigner</button></>}
                </article>;
              })}
            </div>
          </section>
        </div>
      </section>
    </main>
  );
}

type CpamDecision = "to_process" | "contested" | "reimbursed" | "cancelled";

type CpamOverpayment = {
  id: number;
  pharmacy: string;
  notificationDate: string;
  reference: string;
  amountCents: number;
  invoiceNumber: string;
  patient: string;
  reason: string;
  decision: CpamDecision;
  followupDate: string;
  operator: string;
  note: string;
  createdAt: number;
  updatedAt: number;
};

function normalizeCpamOverpayment(row: Record<string, unknown>): CpamOverpayment {
  return {
    id: Number(row.id),
    pharmacy: String(row.pharmacy ?? ""),
    notificationDate: String(row.notificationDate ?? row.notification_date ?? ""),
    reference: String(row.reference ?? ""),
    amountCents: Number(row.amountCents ?? row.amount_cents ?? 0),
    invoiceNumber: String(row.invoiceNumber ?? row.invoice_number ?? ""),
    patient: String(row.patient ?? ""),
    reason: String(row.reason ?? ""),
    decision: String(row.decision ?? "to_process") as CpamDecision,
    followupDate: String(row.followupDate ?? row.followup_date ?? ""),
    operator: String(row.operator ?? ""),
    note: String(row.note ?? ""),
    createdAt: Number(row.createdAt ?? row.created_at ?? 0),
    updatedAt: Number(row.updatedAt ?? row.updated_at ?? 0),
  };
}

const cpamDecisionLabels: Record<CpamDecision, string> = {
  to_process: "À traiter",
  contested: "Contesté",
  reimbursed: "Remboursé",
  cancelled: "Annulé par la CPAM",
};

function formatEuro(cents: number) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(cents / 100);
}

function CpamOverpaymentsModule({ onBack }: { onBack: () => void }) {
  const [items, setItems] = useState<CpamOverpayment[]>([]);
  const [pharmacy, setPharmacy] = useState("Square");
  const [notificationDate, setNotificationDate] = useState(localDateIso());
  const [reference, setReference] = useState("");
  const [amount, setAmount] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [patient, setPatient] = useState("");
  const [reason, setReason] = useState("");
  const [decision, setDecision] = useState<CpamDecision>("to_process");
  const [followupDate, setFollowupDate] = useState("");
  const [operator, setOperator] = useState("");
  const [note, setNote] = useState("");
  const [decisionFilter, setDecisionFilter] = useState<"all" | CpamDecision>("all");
  const [pharmacyFilter, setPharmacyFilter] = useState("Toutes");
  const [yearFilter, setYearFilter] = useState("Toutes");
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadItems = async () => {
    try {
      const response = await fetch("/api/cpam-overpayments", { cache: "no-store" });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      setItems((payload.items ?? []).map(normalizeCpamOverpayment));
      setError("");
    } catch {
      setError("Le suivi des indus CPAM est momentanément indisponible.");
    }
  };

  useEffect(() => {
    void loadItems();
  }, []);

  const pharmacyOperators = missionOperators.filter((item) => item.pharmacy === pharmacy);
  const years = [...new Set(items.map((item) => item.notificationDate.slice(0, 4)).filter(Boolean))].sort().reverse();
  const normalizedQuery = query.trim().toLocaleLowerCase("fr");
  const filtered = items.filter((item) => {
    const matchesDecision = decisionFilter === "all" || item.decision === decisionFilter;
    const matchesPharmacy = pharmacyFilter === "Toutes" || item.pharmacy === pharmacyFilter;
    const matchesYear = yearFilter === "Toutes" || item.notificationDate.startsWith(yearFilter);
    const matchesQuery = !normalizedQuery || `${item.patient} ${item.invoiceNumber} ${item.reference} ${item.reason}`.toLocaleLowerCase("fr").includes(normalizedQuery);
    return matchesDecision && matchesPharmacy && matchesYear && matchesQuery;
  });
  const totalNotified = filtered.reduce((sum, item) => sum + item.amountCents, 0);
  const totalPending = filtered.filter((item) => item.decision === "to_process" || item.decision === "contested").reduce((sum, item) => sum + item.amountCents, 0);
  const totalReimbursed = filtered.filter((item) => item.decision === "reimbursed").reduce((sum, item) => sum + item.amountCents, 0);

  const resetForm = () => {
    setNotificationDate(localDateIso());
    setReference("");
    setAmount("");
    setInvoiceNumber("");
    setPatient("");
    setReason("");
    setDecision("to_process");
    setFollowupDate("");
    setOperator("");
    setNote("");
  };

  const saveItem = async () => {
    const amountCents = Math.round(Number(amount.replace(",", ".")) * 100);
    if (!notificationDate || !reference.trim() || !amountCents || !invoiceNumber.trim() || !patient.trim() || !reason.trim() || !operator) {
      setError("Complétez la date, la référence, le montant, la facture, le patient, la raison et le responsable.");
      return;
    }
    setSaving(true);
    try {
      const response = await fetch("/api/cpam-overpayments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pharmacy, notificationDate, reference, amountCents, invoiceNumber, patient, reason, decision, followupDate, operator, note }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      resetForm();
      await loadItems();
    } catch (reasonValue) {
      setError(reasonValue instanceof Error ? reasonValue.message : "L’indu n’a pas pu être enregistré.");
    } finally {
      setSaving(false);
    }
  };

  const updateDecision = async (item: CpamOverpayment, nextDecision: CpamDecision) => {
    try {
      const response = await fetch("/api/cpam-overpayments", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: item.id, decision: nextDecision }),
      });
      if (!response.ok) throw new Error();
      await loadItems();
    } catch {
      setError("La décision n’a pas pu être mise à jour.");
    }
  };

  const deleteItem = async (item: CpamOverpayment) => {
    if (!window.confirm(`Supprimer l’indu ${item.reference} ?`)) return;
    try {
      const response = await fetch(`/api/cpam-overpayments?id=${item.id}`, { method: "DELETE" });
      if (!response.ok) throw new Error();
      await loadItems();
    } catch {
      setError("L’indu n’a pas pu être supprimé.");
    }
  };

  return (
    <main className="cpam-app">
      <header className="module-header">
        <button type="button" className="module-back" onClick={onBack}>← <span>Retour à l’accueil</span></button>
        <div className="wordmark module-wordmark"><span className="wordmark-symbol"><i /><i /><i /></span><span><strong>saint-laurent</strong><small>pharmacies</small></span></div>
        <div className="module-header-actions"><ModuleToolsLink /><span className="module-status"><i /> Suivi centralisé</span></div>
      </header>

      <section className="cpam-page">
        <div className="cpam-hero">
          <div><span>FACTURATION · SUIVI ADMINISTRATIF</span><h1>Suivi des indus CPAM</h1><p>Notifications, montants, décisions et échéances regroupés pour les trois pharmacies.</p></div>
          <div><strong>{items.filter((item) => item.decision === "to_process" || item.decision === "contested").length}</strong><span>dossier(s) à suivre</span></div>
        </div>

        <div className="cpam-layout">
          <section className="cpam-form-card">
            <div className="cpam-section-heading"><span>＋</span><div><h2>Nouvel indu</h2><p>Les champs essentiels restent visibles en une seule saisie.</p></div></div>
            <div className="cpam-form">
              <label><span>Pharmacie</span><select value={pharmacy} onChange={(event) => { setPharmacy(event.target.value); setOperator(""); }}>{followupPharmacies.map((name) => <option key={name}>{name}</option>)}</select></label>
              <label><span>Date de notification *</span><input type="date" value={notificationDate} onChange={(event) => setNotificationDate(event.target.value)} /></label>
              <label><span>Indu n° / référence *</span><input value={reference} onChange={(event) => setReference(event.target.value)} placeholder="Ex. IND-2026-00482" /></label>
              <label><span>Montant (€) *</span><input inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="0,00" /></label>
              <label><span>N° de facture *</span><input value={invoiceNumber} onChange={(event) => setInvoiceNumber(event.target.value)} placeholder="Facture concernée" /></label>
              <label><span>Patient *</span><input value={patient} onChange={(event) => setPatient(event.target.value)} placeholder="Nom et prénom" /></label>
              <label className="wide"><span>Raison *</span><textarea value={reason} onChange={(event) => setReason(event.target.value.slice(0, 700))} placeholder="Ex. double facturation, hors nomenclature, posologie…" /></label>
              <label><span>Décision</span><select value={decision} onChange={(event) => setDecision(event.target.value as CpamDecision)}>{Object.entries(cpamDecisionLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
              <label><span>Prochaine échéance</span><input type="date" value={followupDate} onChange={(event) => setFollowupDate(event.target.value)} /></label>
              <label className="wide"><span>Responsable *</span><select value={operator} onChange={(event) => setOperator(event.target.value)}><option value="">Choisir un opérateur</option>{pharmacyOperators.map((item) => <option key={item.name} value={item.name}>{item.name} · {item.role}</option>)}</select></label>
              <label className="wide"><span>Note complémentaire</span><textarea value={note} onChange={(event) => setNote(event.target.value.slice(0, 700))} placeholder="Suite donnée, contact, justificatif transmis…" /></label>
            </div>
            {error && <div className="cpam-error">{error}<button type="button" onClick={() => setError("")}>×</button></div>}
            <div className="cpam-form-actions"><button type="button" className="save" disabled={saving} onClick={() => void saveItem()}>{saving ? "Enregistrement…" : "Enregistrer l’indu"}</button><button type="button" onClick={resetForm}>Vider</button></div>
          </section>

          <section className="cpam-list-card">
            <div className="cpam-list-heading"><div><span>VUE CENTRALISÉE</span><h2>Indus enregistrés</h2></div><button type="button" onClick={() => void loadItems()}>Actualiser</button></div>
            <div className="cpam-stats"><article><strong>{formatEuro(totalNotified)}</strong><span>Total notifié</span></article><article><strong>{formatEuro(totalPending)}</strong><span>À traiter / contesté</span></article><article><strong>{formatEuro(totalReimbursed)}</strong><span>Remboursé</span></article></div>
            <div className="cpam-filters">
              <label><span>Décision</span><select value={decisionFilter} onChange={(event) => setDecisionFilter(event.target.value as "all" | CpamDecision)}><option value="all">Toutes</option>{Object.entries(cpamDecisionLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
              <label><span>Pharmacie</span><select value={pharmacyFilter} onChange={(event) => setPharmacyFilter(event.target.value)}><option>Toutes</option>{followupPharmacies.map((name) => <option key={name}>{name}</option>)}</select></label>
              <label><span>Année</span><select value={yearFilter} onChange={(event) => setYearFilter(event.target.value)}><option>Toutes</option>{years.map((year) => <option key={year}>{year}</option>)}</select></label>
              <label className="search"><span>Recherche</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Patient, facture, référence…" /></label>
            </div>
            <div className="cpam-list">
              {filtered.map((item) => <article key={item.id} className={item.decision}>
                <div className="cpam-item-main"><header><div><span>{item.pharmacy} · {followupDate(item.notificationDate)}</span><h3>{item.reference}</h3></div><strong>{formatEuro(item.amountCents)}</strong></header><p><b>{item.patient}</b> · facture {item.invoiceNumber}</p><p>{item.reason}</p>{item.note && <small>{item.note}</small>}<footer><span>Responsable · {item.operator}</span>{item.followupDate && <span>Échéance · {followupDate(item.followupDate)}</span>}</footer></div>
                <div className="cpam-item-actions"><label><span>Décision</span><select value={item.decision} onChange={(event) => void updateDecision(item, event.target.value as CpamDecision)}>{Object.entries(cpamDecisionLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label><button type="button" onClick={() => void deleteItem(item)}>Supprimer</button></div>
              </article>)}
              {!filtered.length && <div className="cpam-empty"><span>€</span><h3>Aucun indu dans cette vue</h3><p>Ajoutez un dossier ou modifiez les filtres.</p></div>}
            </div>
          </section>
        </div>
      </section>
    </main>
  );
}

type ShoppingItem = {
  id: number;
  pharmacy: string;
  category: string;
  requestedBy: string;
  product: string;
  quantity: string;
  comment: string;
  status: "open" | "purchased";
  purchasedBy: string;
  purchasedAt: number | null;
  createdAt: number;
  updatedAt: number;
};

const shoppingCategories = ["Entretien", "Papeterie", "Confort équipe", "Emballages", "Petit matériel", "Autre"];

function normalizeShoppingItem(row: Record<string, unknown>): ShoppingItem {
  return {
    id: Number(row.id),
    pharmacy: String(row.pharmacy ?? ""),
    category: String(row.category ?? "Autre"),
    requestedBy: String(row.requestedBy ?? row.requested_by ?? ""),
    product: String(row.product ?? ""),
    quantity: String(row.quantity ?? ""),
    comment: String(row.comment ?? ""),
    status: String(row.status ?? "open") as "open" | "purchased",
    purchasedBy: String(row.purchasedBy ?? row.purchased_by ?? ""),
    purchasedAt: row.purchasedAt === null || row.purchased_at === null ? null : Number(row.purchasedAt ?? row.purchased_at ?? 0),
    createdAt: Number(row.createdAt ?? row.created_at ?? 0),
    updatedAt: Number(row.updatedAt ?? row.updated_at ?? 0),
  };
}

function ShoppingListModule({ onBack, initialPharmacy, initialOperator }: { onBack: () => void; initialPharmacy: string; initialOperator: string }) {
  const initialRequestedBy = missionOperators.some((item) => item.name === initialOperator && item.pharmacy === (initialPharmacy || "Square")) ? initialOperator : "";
  const [items, setItems] = useState<ShoppingItem[]>([]);
  const [pharmacy, setPharmacy] = useState(initialPharmacy || "Square");
  const [category, setCategory] = useState("Entretien");
  const [requestedBy, setRequestedBy] = useState(initialRequestedBy);
  const [product, setProduct] = useState("");
  const [quantity, setQuantity] = useState("");
  const [comment, setComment] = useState("");
  const [pharmacyFilters, setPharmacyFilters] = useState(followupPharmacies);
  const [view, setView] = useState<"active" | "purchased" | "all">("active");
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const loadItems = async () => {
    try {
      const response = await fetch("/api/shopping-list", { cache: "no-store" });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      setItems((payload.items ?? []).map(normalizeShoppingItem));
    } catch {
      setMessage("La liste de courses est momentanément indisponible.");
    }
  };

  useEffect(() => {
    void loadItems();
  }, []);

  const pharmacyOperators = missionOperators.filter((item) => item.pharmacy === pharmacy);
  const normalizedQuery = query.trim().toLocaleLowerCase("fr");
  const visibleItems = items.filter((item) => {
    const pharmacyMatch = pharmacyFilters.includes(item.pharmacy);
    const statusMatch = view === "all" || (view === "active" ? item.status === "open" : item.status === "purchased");
    const queryMatch = !normalizedQuery || `${item.product} ${item.quantity} ${item.comment} ${item.category} ${item.requestedBy}`.toLocaleLowerCase("fr").includes(normalizedQuery);
    return pharmacyMatch && statusMatch && queryMatch;
  });
  const activeCount = items.filter((item) => item.status === "open").length;
  const purchasedCount = items.filter((item) => item.status === "purchased").length;

  const addItem = async () => {
    if (!product.trim() || !requestedBy) {
      setMessage("Indiquez le besoin et la personne qui le demande.");
      return;
    }
    setSaving(true);
    try {
      const response = await fetch("/api/shopping-list", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pharmacy, category, requestedBy, product, quantity, comment }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      setProduct("");
      setQuantity("");
      setComment("");
      setMessage("Besoin ajouté à la liste commune.");
      await loadItems();
    } catch (reasonValue) {
      setMessage(reasonValue instanceof Error ? reasonValue.message : "Le besoin n’a pas pu être ajouté.");
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (item: ShoppingItem, action: "purchase" | "reopen") => {
    try {
      const response = await fetch("/api/shopping-list", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: item.id, action, purchasedBy: initialOperator }),
      });
      if (!response.ok) throw new Error();
      setMessage(action === "purchase" ? `Achat validé par ${initialOperator}.` : "Besoin remis dans la liste active.");
      await loadItems();
    } catch {
      setMessage("Le statut n’a pas pu être modifié.");
    }
  };

  const deleteItem = async (item: ShoppingItem) => {
    if (!window.confirm(`Supprimer « ${item.product} » de la liste ?`)) return;
    try {
      const response = await fetch(`/api/shopping-list?id=${item.id}`, { method: "DELETE" });
      if (!response.ok) throw new Error();
      await loadItems();
    } catch {
      setMessage("Le besoin n’a pas pu être supprimé.");
    }
  };

  const copyList = async () => {
    const activeItems = items.filter((item) => item.status === "open" && pharmacyFilters.includes(item.pharmacy));
    const content = activeItems.length
      ? activeItems.map((item) => `• ${item.product}${item.quantity ? ` — ${item.quantity}` : ""} [${item.pharmacy}]${item.comment ? ` · ${item.comment}` : ""}`).join("\n")
      : "Liste de courses à jour.";
    try {
      await navigator.clipboard.writeText(`Courses pharmacies\n\n${content}`);
      setMessage("Liste copiée, prête à être envoyée.");
    } catch {
      setMessage("La copie automatique n’est pas disponible sur cet appareil.");
    }
  };

  const togglePharmacy = (name: string) => {
    setPharmacyFilters((current) => current.includes(name) ? current.filter((item) => item !== name) : [...current, name]);
  };

  return (
    <main className="shopping-app">
      <header className="module-header">
        <button type="button" className="module-back" onClick={onBack}>← <span>Retour à l’accueil</span></button>
        <div className="wordmark module-wordmark"><span className="wordmark-symbol"><i /><i /><i /></span><span><strong>saint-laurent</strong><small>pharmacies</small></span></div>
        <div className="module-header-actions"><ModuleToolsLink /><span className="module-status"><i /> Liste synchronisée</span></div>
      </header>

      <section className="shopping-page">
        <div className="shopping-title">
          <div><span>LOGISTIQUE DU QUOTIDIEN</span><h1>Courses</h1><p>Une liste commune, claire et immédiatement utile aux trois pharmacies.</p></div>
          <div className="shopping-title-count"><strong>{activeCount}</strong><span>besoin{activeCount > 1 ? "s" : ""} actif{activeCount > 1 ? "s" : ""}</span></div>
        </div>

        <section className="shopping-composer">
          <div className="shopping-section-head"><span>＋</span><div><h2>Ajouter un besoin</h2><p>Notez-le maintenant, cochez-le une fois acheté.</p></div></div>
          <div className="shopping-form">
            <label><span>Pharmacie</span><select value={pharmacy} onChange={(event) => { setPharmacy(event.target.value); setRequestedBy(""); }}>{followupPharmacies.map((name) => <option key={name}>{name}</option>)}</select></label>
            <label><span>Catégorie</span><select value={category} onChange={(event) => setCategory(event.target.value)}>{shoppingCategories.map((name) => <option key={name}>{name}</option>)}</select></label>
            <label><span>Demandé par</span><select value={requestedBy} onChange={(event) => setRequestedBy(event.target.value)}><option value="">Choisir</option>{pharmacyOperators.map((item) => <option key={item.name} value={item.name}>{item.name}</option>)}</select></label>
            <label className="product"><span>Produit / besoin *</span><input value={product} onChange={(event) => setProduct(event.target.value.slice(0, 180))} placeholder="Ex. Sacs-poubelle, café, rouleaux de papier…" onKeyDown={(event) => { if (event.key === "Enter") void addItem(); }} /></label>
            <label><span>Quantité</span><input value={quantity} onChange={(event) => setQuantity(event.target.value.slice(0, 100))} placeholder="Ex. 2 packs" /></label>
            <label className="comment"><span>Commentaire facultatif</span><textarea value={comment} onChange={(event) => setComment(event.target.value.slice(0, 600))} placeholder="Urgence, marque souhaitée, magasin conseillé…" /></label>
          </div>
          <div className="shopping-composer-actions"><button type="button" className="primary" disabled={saving} onClick={() => void addItem()}>{saving ? "Ajout…" : "Ajouter à la liste"}</button><span>{message}</span></div>
        </section>

        <section className="shopping-list-card">
          <div className="shopping-toolbar">
            <div><span>LISTE PARTAGÉE</span><h2>À acheter</h2><p>Les achats terminés restent visibles pendant 48 h.</p></div>
            <div className="shopping-toolbar-actions"><button type="button" onClick={() => void copyList()}>Copier la liste</button><button type="button" onClick={() => void loadItems()}>Actualiser</button></div>
          </div>
          <div className="shopping-filters">
            <div className="shopping-pharmacy-filters"><span>Pharmacies affichées</span>{followupPharmacies.map((name) => <button type="button" key={name} className={pharmacyFilters.includes(name) ? "active" : ""} onClick={() => togglePharmacy(name)}><i>{pharmacyFilters.includes(name) ? "✓" : ""}</i>{name}</button>)}</div>
            <div className="shopping-view-tabs"><button type="button" className={view === "active" ? "active" : ""} onClick={() => setView("active")}>À acheter <b>{activeCount}</b></button><button type="button" className={view === "purchased" ? "active" : ""} onClick={() => setView("purchased")}>Achetés <b>{purchasedCount}</b></button><button type="button" className={view === "all" ? "active" : ""} onClick={() => setView("all")}>Tout</button></div>
            <label className="shopping-search"><span>⌕</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Rechercher dans la liste…" /></label>
          </div>
          <div className="shopping-list">
            {visibleItems.map((item) => <article key={item.id} className={item.status}>
              <button type="button" className="shopping-check" onClick={() => void updateStatus(item, item.status === "open" ? "purchase" : "reopen")} aria-label={item.status === "open" ? `Marquer ${item.product} comme acheté` : `Remettre ${item.product} à acheter`}>{item.status === "purchased" ? "✓" : ""}</button>
              <div className="shopping-item-copy"><header><h3>{item.product}</h3>{item.quantity && <strong>{item.quantity}</strong>}</header>{item.comment && <p>{item.comment}</p>}<footer><span className="pharmacy">{item.pharmacy}</span><span className="category">{item.category}</span><span>Demandé par {item.requestedBy}</span>{item.status === "purchased" && <span className="purchased-by">Acheté par {item.purchasedBy}</span>}</footer></div>
              <div className="shopping-item-actions"><button type="button" className={item.status === "open" ? "purchase" : "reopen"} onClick={() => void updateStatus(item, item.status === "open" ? "purchase" : "reopen")}>{item.status === "open" ? "Acheté ✓" : "Remettre à acheter"}</button><button type="button" className="delete" onClick={() => void deleteItem(item)}>Supprimer</button></div>
            </article>)}
            {!visibleItems.length && <div className="shopping-empty"><span>✓</span><div><h3>{view === "active" ? "La liste est à jour" : "Aucun achat dans cette vue"}</h3><p>{view === "active" ? "Le prochain besoin ajouté apparaîtra ici immédiatement." : "Modifiez les pharmacies ou la vue sélectionnée."}</p></div></div>}
          </div>
        </section>
      </section>
    </main>
  );
}

type StockLabelRequest = {
  id: number;
  ean: string;
  product: string;
  requestType: "stock" | "label";
  pharmacies: string[];
  comment: string;
  createdBy: string;
  status: "open" | "completed";
  result: "" | "ok" | "discrepancy" | "removed";
  completedBy: string;
  completedAt: number | null;
  createdAt: number;
  updatedAt: number;
};

function normalizeStockLabelRequest(row: Record<string, unknown>): StockLabelRequest {
  let pharmacies: string[] = [];
  try {
    pharmacies = Array.isArray(row.pharmacies) ? row.pharmacies.map(String) : JSON.parse(String(row.pharmacies ?? "[]"));
  } catch {
    pharmacies = [];
  }
  return {
    id: Number(row.id),
    ean: String(row.ean ?? ""),
    product: String(row.product ?? ""),
    requestType: String(row.requestType ?? row.request_type ?? "stock") as "stock" | "label",
    pharmacies,
    comment: String(row.comment ?? ""),
    createdBy: String(row.createdBy ?? row.created_by ?? ""),
    status: String(row.status ?? "open") as "open" | "completed",
    result: String(row.result ?? "") as "" | "ok" | "discrepancy" | "removed",
    completedBy: String(row.completedBy ?? row.completed_by ?? ""),
    completedAt: row.completedAt === null || row.completed_at === null ? null : Number(row.completedAt ?? row.completed_at ?? 0),
    createdAt: Number(row.createdAt ?? row.created_at ?? 0),
    updatedAt: Number(row.updatedAt ?? row.updated_at ?? 0),
  };
}

function StockLabelsModule({ onBack, initialOperator, initialPharmacy }: { onBack: () => void; initialOperator: string; initialPharmacy: string }) {
  const [items, setItems] = useState<StockLabelRequest[]>([]);
  const [paste, setPaste] = useState("");
  const [pendingRows, setPendingRows] = useState<{ ean: string; product: string }[]>([]);
  const [requestType, setRequestType] = useState<"stock" | "label">("stock");
  const [pharmacies, setPharmacies] = useState<string[]>([initialPharmacy]);
  const [comment, setComment] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"recent" | "open" | "completed" | "all">("recent");
  const [pharmacyFilters, setPharmacyFilters] = useState<string[]>([initialPharmacy]);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const loadItems = async () => {
    try {
      const response = await fetch("/api/stock-labels", { cache: "no-store" });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      setItems((payload.items ?? []).map(normalizeStockLabelRequest));
    } catch {
      setMessage("Le suivi stock / étiquettes est momentanément indisponible.");
    }
  };
  useEffect(() => {
    void loadItems();
  }, []);

  const parseRows = () => paste.split(/\r?\n/).map((line) => line.trim()).filter(Boolean).map((line) => {
    const match = line.match(/^(\d{7,14})[\s;,\t-]*(.*)$/);
    return match ? { ean: match[1], product: match[2].trim() } : { ean: "", product: line };
  }).filter((row) => row.product || row.ean);
  const prepareRows = async () => {
    const parsedRows = parseRows();
    const rows = await Promise.all(parsedRows.map(async (row) => {
      if (row.product || !row.ean) return row;
      try {
        const suggestion = await resolveCatalogEan(row.ean);
        return { ...row, product: suggestion?.product || `Produit EAN ${row.ean}` };
      } catch {
        return { ...row, product: `Produit EAN ${row.ean}` };
      }
    }));
    if (!rows.length) {
      setMessage("Collez au moins une ligne contenant un produit.");
      return;
    }
    setPendingRows(rows);
  };
  const saveRows = async () => {
    if (!pharmacies.length) {
      setMessage("Choisissez au moins une pharmacie.");
      return;
    }
    setSaving(true);
    try {
      const response = await fetch("/api/stock-labels", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: pendingRows.map((row) => ({ ...row, requestType, pharmacies, comment, createdBy: initialOperator })) }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.detail ? `${payload.error} ${payload.detail}` : payload.error);
      setPaste("");
      setPendingRows([]);
      setComment("");
      setMessage(`${payload.created} demande${payload.created > 1 ? "s" : ""} ajoutée${payload.created > 1 ? "s" : ""}.`);
      await loadItems();
    } catch (reasonValue) {
      setMessage(reasonValue instanceof Error ? reasonValue.message : "L’ajout a échoué.");
    } finally {
      setSaving(false);
    }
  };
  const updateItem = async (item: StockLabelRequest, action: "complete" | "reopen", result?: "ok" | "discrepancy" | "removed") => {
    if (action === "complete" && result === "discrepancy") {
      const corrected = window.confirm("As-tu corrigé le stock LGPI avec un pharmacien ?");
      if (!corrected) {
        setMessage("Corrige d’abord le stock LGPI avec un pharmacien, puis reviens valider l’écart.");
        return;
      }
    }
    try {
      const response = await fetch("/api/stock-labels", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: item.id, action, result, completedBy: initialOperator }),
      });
      if (!response.ok) throw new Error();
      setMessage(action === "reopen" ? "Demande rouverte." : result === "discrepancy" ? "Écart de stock enregistré." : "Demande traitée.");
      await loadItems();
    } catch {
      setMessage("La validation n’a pas pu être enregistrée.");
    }
  };
  const deleteItem = async (item: StockLabelRequest) => {
    if (!window.confirm(`Supprimer « ${item.product} » ?`)) return;
    const response = await fetch(`/api/stock-labels?id=${item.id}`, { method: "DELETE" });
    if (response.ok) await loadItems();
  };
  const recentThreshold = Date.now() - 6 * 24 * 60 * 60 * 1000;
  const normalizedQuery = query.trim().toLocaleLowerCase("fr");
  const filtered = items.filter((item) => {
    const matchesSearch = !normalizedQuery || `${item.product} ${item.ean} ${item.comment}`.toLocaleLowerCase("fr").includes(normalizedQuery);
    const matchesPharmacy = !pharmacyFilters.length || item.pharmacies.some((name) => pharmacyFilters.includes(name));
    const matchesStatus = statusFilter === "all" || statusFilter === "open" && item.status === "open" || statusFilter === "completed" && item.status === "completed" || statusFilter === "recent" && (item.status === "open" || item.updatedAt >= recentThreshold);
    return matchesSearch && matchesPharmacy && matchesStatus;
  });
  const stockItems = filtered.filter((item) => item.requestType === "stock");
  const labelItems = filtered.filter((item) => item.requestType === "label");
  const openCount = items.filter((item) => item.status === "open").length;
  const completedCount = items.filter((item) => item.status === "completed").length;
  const discrepancies = items.filter((item) => item.result === "discrepancy").length;

  const card = (item: StockLabelRequest) => <article key={item.id} className={`${item.status} ${item.result}`}>
    <div><h3>{item.ean ? `${item.ean} · ` : ""}{item.product}</h3><p>{item.requestType === "stock" ? "Stock physique" : "Étiquette à retirer"} · {item.pharmacies.join(" · ")} · par {item.createdBy}</p>{item.comment && <small>{item.comment}</small>}</div>
    <span className="stock-state">{item.status === "open" ? item.requestType === "stock" ? "À contrôler" : "À retirer" : item.result === "discrepancy" ? "Écart stock" : "Traité"}</span>
    <footer>{item.status === "open" ? item.requestType === "stock" ? <><button type="button" className="done" onClick={() => void updateItem(item, "complete", "ok")}>✓ Stock juste</button><button type="button" className="gap" onClick={() => void updateItem(item, "complete", "discrepancy")}>⚠ Écart</button></> : <button type="button" className="done" onClick={() => void updateItem(item, "complete", "removed")}>✓ Étiquette retirée</button> : <button type="button" onClick={() => void updateItem(item, "reopen")}>↻ Réouvrir</button>}<button type="button" className="delete" onClick={() => void deleteItem(item)}>Supprimer</button></footer>
  </article>;

  return (
    <main className="stock-label-app">
      <header className="module-header"><button type="button" className="module-back" onClick={onBack}>← <span>Retour à l’accueil</span></button><div className="wordmark module-wordmark"><span className="wordmark-symbol"><i /><i /><i /></span><span><strong>saint-laurent</strong><small>pharmacies</small></span></div><div className="module-header-actions"><ModuleToolsLink /><span className="module-status"><i /> Stock synchronisé</span></div></header>
      <section className="stock-label-page">
        <div className="stock-label-hero"><div><span>RAYON · CONTRÔLE TERRAIN</span><h1>Stock / étiquettes</h1><p>Une demande simple, attribuée uniquement aux pharmacies concernées.</p></div><div><article><strong>{openCount}</strong><span>à faire</span></article><article><strong>{completedCount}</strong><span>traitées</span></article><article className={discrepancies ? "gap" : ""}><strong>{discrepancies}</strong><span>écarts stock</span></article></div></div>
        <section className="stock-label-paste"><div className="stock-label-title"><span>⌁</span><div><h2>Collage rapide EAN + dénomination</h2><p>Une ligne par produit. Le code et le nom sont séparés automatiquement.</p></div></div><textarea value={paste} onChange={(event) => setPaste(event.target.value)} placeholder={"3400930000000 DOLIPRANE 1000MG CPR\n3660000000000 SVR SENSIFINE AR CREME 40ML"} /><div><button type="button" onClick={() => setPaste(parseRows().map((row) => `${row.ean} ${row.product}`.trim()).join("\n"))}>✨ Mettre en forme</button><button type="button" className="primary" onClick={() => void prepareRows()}>＋ Ajouter</button><span>Un EAN seul récupère aussi sa dénomination depuis le catalogue.</span></div></section>
        <section className="stock-label-filters"><label><span>🔎</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Rechercher produit, EAN ou note…" /></label><select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}><option value="recent">Actives + traitées 6 jours</option><option value="open">À faire</option><option value="completed">Traitées</option><option value="all">Toutes</option></select><div><span>Pharmacies affichées</span>{followupPharmacies.map((name) => <button type="button" className={pharmacyFilters.includes(name) ? "active" : ""} key={name} onClick={() => setPharmacyFilters((current) => current.includes(name) ? current.filter((item) => item !== name) : [...current, name])}>{pharmacyFilters.includes(name) ? "✓ " : ""}{name}</button>)}</div></section>
        <div className="stock-label-columns"><section className="stock"><header><span>📦 Stock à contrôler</span><b>{stockItems.length}</b></header><div>{stockItems.map(card)}{!stockItems.length && <p className="stock-label-empty">Aucun contrôle de stock dans cette vue.</p>}</div></section><section className="labels"><header><span>🏷️ Retirer une étiquette</span><b>{labelItems.length}</b></header><div>{labelItems.map(card)}{!labelItems.length && <p className="stock-label-empty">Aucune étiquette à retirer dans cette vue.</p>}</div></section></div>
      </section>
      {pendingRows.length > 0 && <div className="stock-label-modal-layer" role="presentation" onMouseDown={() => setPendingRows([])}><section role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}><header><div><span>CONFIRMER {pendingRows.length} PRODUIT{pendingRows.length > 1 ? "S" : ""}</span><h2>À qui confier la demande ?</h2></div><button type="button" onClick={() => setPendingRows([])}>×</button></header><div className="stock-label-modal-body"><label><span>Type de demande</span><div className="stock-label-type"><button type="button" className={requestType === "stock" ? "active" : ""} onClick={() => setRequestType("stock")}>📦 Contrôler le stock</button><button type="button" className={requestType === "label" ? "active" : ""} onClick={() => setRequestType("label")}>🏷️ Retirer l’étiquette</button></div></label><fieldset><legend>Pharmacies concernées</legend>{followupPharmacies.map((name) => <button type="button" className={pharmacies.includes(name) ? "active" : ""} key={name} onClick={() => setPharmacies((current) => current.includes(name) ? current.filter((item) => item !== name) : [...current, name])}>{pharmacies.includes(name) ? "✓ " : ""}{name}</button>)}</fieldset><label><span>Commentaire facultatif</span><textarea value={comment} onChange={(event) => setComment(event.target.value.slice(0, 700))} placeholder="Rayon, quantité attendue, détail utile…" /></label><div className="stock-label-preview">{pendingRows.slice(0, 5).map((row) => <p key={`${row.ean}-${row.product}`}><b>{row.ean}</b> {row.product}</p>)}{pendingRows.length > 5 && <p>+ {pendingRows.length - 5} autres lignes</p>}</div></div><footer><button type="button" onClick={() => setPendingRows([])}>Annuler</button><button type="button" className="primary" disabled={saving} onClick={() => void saveRows()}>{saving ? "Ajout…" : `Ajouter ${pendingRows.length} demande${pendingRows.length > 1 ? "s" : ""}`}</button></footer></section></div>}
      {message && <div className="module-toast">{message}</div>}
    </main>
  );
}

type GenericShortage = {
  id: number;
  molecule: string;
  ean: string;
  laboratory: string;
  entryDate: string;
  season: string;
  pharmacies: string[];
  comment: string;
  status: "active" | "returned";
  returnedAt: number | null;
  returnedBy: string;
  createdAt: number;
  updatedAt: number;
};

function normalizeGenericShortage(row: Record<string, unknown>): GenericShortage {
  let pharmacies: string[] = [];
  try {
    pharmacies = Array.isArray(row.pharmacies) ? row.pharmacies.map(String) : JSON.parse(String(row.pharmacies ?? "[]"));
  } catch {
    pharmacies = [];
  }
  return {
    id: Number(row.id),
    molecule: String(row.molecule ?? ""),
    ean: String(row.ean ?? ""),
    laboratory: String(row.laboratory ?? "Biogaran"),
    entryDate: String(row.entryDate ?? row.entry_date ?? ""),
    season: String(row.season ?? ""),
    pharmacies,
    comment: String(row.comment ?? ""),
    status: String(row.status || "active") as "active" | "returned",
    returnedAt: row.returnedAt === null || row.returned_at === null ? null : Number(row.returnedAt ?? row.returned_at ?? 0),
    returnedBy: String(row.returnedBy ?? row.returned_by ?? ""),
    createdAt: Number(row.createdAt ?? row.created_at ?? 0),
    updatedAt: Number(row.updatedAt ?? row.updated_at ?? 0),
  };
}

function GenericShortagesModule({ onBack, initialOperator }: { onBack: () => void; initialOperator: string }) {
  const emptyForm = () => ({ molecule: "", ean: "", laboratory: "Biogaran", entryDate: localDateIso(), season: "", pharmacies: [...followupPharmacies], comment: "" });
  const [items, setItems] = useState<GenericShortage[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const [pharmacyFilter, setPharmacyFilter] = useState("Toutes");
  const [statusFilter, setStatusFilter] = useState<"active" | "returned" | "all">("active");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const loadItems = async () => {
    try {
      const response = await fetch("/api/generic-shortages", { cache: "no-store" });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      const normalized = (payload.items ?? []).map(normalizeGenericShortage) as GenericShortage[];
      setItems(normalized);
      return normalized;
    } catch {
      setMessage("Le suivi des génériques est momentanément indisponible.");
      return [] as GenericShortage[];
    }
  };

  useEffect(() => {
    const initialize = async () => {
      const loaded = await loadItems();
      const params = new URLSearchParams(window.location.search);
      if (params.get("source") !== "pharmascan" || params.get("autoAdd") !== "generic-shortage") return;
      const ean = String(params.get("ean") || "").replace(/\D/g, "").slice(0, 18);
      const molecule = String(params.get("product") || "").trim().slice(0, 180);
      const laboratory = String(params.get("laboratory") || "Biogaran").trim().slice(0, 100) || "Biogaran";
      const requestedPharmacy = String(params.get("pharmacy") || "").trim();
      const pharmacies = followupPharmacies.includes(requestedPharmacy) ? [requestedPharmacy] : [...followupPharmacies];
      const imported = { molecule, ean, laboratory, entryDate: localDateIso(), season: "", pharmacies, comment: "Ajouté depuis PharmaScan Achat" };
      setForm(imported);
      if (!molecule) {
        setMessage("Produit prérempli depuis PharmaScan : complétez la molécule avant l’enregistrement.");
        return;
      }
      const alreadyExists = loaded.some((item) => item.status === "active"
        && ((ean && item.ean.replace(/\D/g, "") === ean) || item.molecule.trim().toLocaleLowerCase("fr") === molecule.toLocaleLowerCase("fr"))
        && pharmacies.every((pharmacy) => item.pharmacies.includes(pharmacy)));
      if (alreadyExists) {
        setMessage("Ce générique figure déjà dans la liste active.");
        window.history.replaceState({}, "", `${window.location.pathname}?tool=${encodeURIComponent("Génériques manquants")}`);
        return;
      }
      const response = await fetch("/api/generic-shortages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(imported),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        setMessage(response.status === 401 || response.status === 403
          ? "Produit prérempli : connectez ce poste puis cliquez sur « Ajouter la ligne »."
          : String(payload.error || "L’ajout depuis PharmaScan n’a pas été enregistré."));
        return;
      }
      setMessage("✓ Générique ajouté depuis PharmaScan.");
      window.history.replaceState({}, "", `${window.location.pathname}?tool=${encodeURIComponent("Génériques manquants")}`);
      await loadItems();
    };
    void initialize();
  }, []);

  const activeItems = items.filter((item) => item.status === "active");
  const biogaranCount = activeItems.filter((item) => item.laboratory.toLocaleLowerCase("fr").includes("biogaran")).length;
  const normalizedQuery = query.trim().toLocaleLowerCase("fr");
  const filteredItems = items.filter((item) => {
    const search = `${item.molecule} ${item.ean} ${item.laboratory} ${item.comment}`.toLocaleLowerCase("fr");
    return (!normalizedQuery || search.includes(normalizedQuery))
      && (pharmacyFilter === "Toutes" || item.pharmacies.includes(pharmacyFilter))
      && (statusFilter === "all" || item.status === statusFilter);
  });

  const resetForm = () => {
    setForm(emptyForm());
    setEditingId(null);
  };
  const togglePharmacy = (name: string) => setForm((current) => ({ ...current, pharmacies: current.pharmacies.includes(name) ? current.pharmacies.filter((item) => item !== name) : [...current.pharmacies, name] }));
  const saveItem = async () => {
    if (!form.molecule.trim() || !form.entryDate || !form.pharmacies.length) {
      setMessage("Indiquez la molécule, la date et au moins une pharmacie.");
      return;
    }
    setSaving(true);
    try {
      const response = await fetch("/api/generic-shortages", {
        method: editingId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, id: editingId }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      setMessage(editingId ? "La ligne a été mise à jour." : "La molécule manquante a été ajoutée.");
      resetForm();
      await loadItems();
    } catch (reasonValue) {
      setMessage(reasonValue instanceof Error ? reasonValue.message : "L’enregistrement a échoué.");
    } finally {
      setSaving(false);
    }
  };
  const editItem = (item: GenericShortage) => {
    setEditingId(item.id);
    setForm({ molecule: item.molecule, ean: item.ean, laboratory: item.laboratory, entryDate: item.entryDate, season: item.season, pharmacies: [...item.pharmacies], comment: item.comment });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const updateStatus = async (item: GenericShortage, action: "return" | "reactivate") => {
    try {
      const response = await fetch("/api/generic-shortages", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: item.id, action, operator: initialOperator }) });
      if (!response.ok) throw new Error();
      setMessage(action === "return" ? "Retour produit enregistré." : "Molécule réactivée.");
      await loadItems();
    } catch {
      setMessage("Le changement de statut n’a pas pu être enregistré.");
    }
  };
  const deleteItem = async (item: GenericShortage) => {
    if (!window.confirm(`Supprimer « ${item.molecule} » ?`)) return;
    try {
      const response = await fetch(`/api/generic-shortages?id=${item.id}`, { method: "DELETE" });
      if (!response.ok) throw new Error();
      await loadItems();
    } catch {
      setMessage("La suppression a échoué.");
    }
  };
  const emailSummary = () => {
    const lines = activeItems.map((item) => `- ${item.molecule}${item.ean ? ` (EAN ${item.ean})` : ""} — ${item.pharmacies.join(", ")}${item.comment ? ` — ${item.comment}` : ""}`);
    window.location.href = `mailto:?subject=${encodeURIComponent("Génériques manquants — Pharmacies Saint-Laurent")}&body=${encodeURIComponent(`Bonjour,\n\nVoici les génériques actuellement manquants :\n\n${lines.join("\n")}\n\nMerci.`)}`;
  };

  return (
    <main className="generic-app">
      <header className="module-header">
        <button type="button" className="module-back" onClick={onBack}>← <span>Retour à l’accueil</span></button>
        <div className="wordmark module-wordmark"><span className="wordmark-symbol"><i /><i /><i /></span><span><strong>saint-laurent</strong><small>pharmacies</small></span></div>
        <div className="module-header-actions"><ModuleToolsLink /><span className="module-status"><i /> Liste synchronisée</span></div>
      </header>
      <section className="generic-page">
        <div className="generic-hero">
          <div><span>STOCK · MÉDICAMENTS GÉNÉRIQUES</span><h1>Génériques manquants</h1><p>Molécule, laboratoire, saison et impact par pharmacie réunis dans une seule liste.</p></div>
          <div><article><strong>{activeItems.length}</strong><span>actif{activeItems.length > 1 ? "s" : ""}</span></article><article><strong>{biogaranCount}</strong><span>Biogaran</span></article></div>
        </div>

        <section className="generic-composer">
          <div className="generic-title"><span>{editingId ? "✎" : "＋"}</span><div><h2>{editingId ? "Modifier la molécule" : "Ajouter une molécule manquante"}</h2><p>Une saisie unique, visible immédiatement par les pharmacies concernées.</p></div></div>
          <div className="generic-form">
            <label><span>Molécule manquante *</span><input value={form.molecule} onChange={(event) => setForm({ ...form, molecule: event.target.value.slice(0, 180) })} placeholder="Ex. amoxicilline, ramipril, lévothyroxine…" /></label>
            <label><span>EAN / DataMatrix</span><CatalogAutocomplete value={form.ean} onChange={(ean) => setForm((current) => ({ ...current, ean: ean.slice(0, 160) }))} onPick={(suggestion) => setForm((current) => ({ ...current, ean: suggestion.ean, molecule: suggestion.product || current.molecule }))} placeholder="Scanner un EAN ou un DataMatrix" inputMode="text" /></label>
            <label><span>Laboratoire</span><input value={form.laboratory} onChange={(event) => setForm({ ...form, laboratory: event.target.value.slice(0, 100) })} placeholder="Biogaran" /></label>
            <label><span>Date de saisie *</span><input type="date" value={form.entryDate} onChange={(event) => setForm({ ...form, entryDate: event.target.value })} /></label>
            <label><span>Saison / période</span><input value={form.season} onChange={(event) => setForm({ ...form, season: event.target.value.slice(0, 100) })} placeholder="Été, hiver, rentrée…" /></label>
            <fieldset><legend>Pharmacies impactées</legend><button type="button" className={form.pharmacies.length === followupPharmacies.length ? "all active" : "all"} onClick={() => setForm({ ...form, pharmacies: form.pharmacies.length === followupPharmacies.length ? [] : [...followupPharmacies] })}>🏥 Toutes</button>{followupPharmacies.map((name) => <button type="button" className={form.pharmacies.includes(name) ? "active" : ""} key={name} onClick={() => togglePharmacy(name)}><i>{form.pharmacies.includes(name) ? "✓" : ""}</i>{name}</button>)}</fieldset>
            <label className="wide"><span>Commentaire / conduite à tenir</span><textarea value={form.comment} onChange={(event) => setForm({ ...form, comment: event.target.value.slice(0, 700) })} placeholder="Switch à proposer, vérifier retour grossiste, prévenir le comptoir…" /></label>
          </div>
          <div className="generic-actions"><button type="button" className="primary" disabled={saving} onClick={() => void saveItem()}>{saving ? "Enregistrement…" : editingId ? "✓ Enregistrer les modifications" : "＋ Ajouter la ligne"}</button><button type="button" onClick={resetForm}>{editingId ? "Annuler la modification" : "Réinitialiser"}</button><span>{message}</span></div>
        </section>

        <section className="generic-list-card">
          <div className="generic-toolbar">
            <label><span>🔎</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Rechercher molécule, EAN ou labo…" /></label>
            <select value={pharmacyFilter} onChange={(event) => setPharmacyFilter(event.target.value)}><option>Toutes</option>{followupPharmacies.map((name) => <option key={name}>{name}</option>)}</select>
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}><option value="active">Manquants actifs</option><option value="returned">Produits de retour</option><option value="all">Tous</option></select>
            <button type="button" className="export" onClick={() => window.print()}>📄 Export PDF</button>
            <button type="button" className="mail" onClick={emailSummary}>✉️ Préparer le mail labo</button>
          </div>
          <div className="generic-list">
            {filteredItems.map((item) => <article key={item.id} className={item.status}>
              <div><h2>{item.molecule}</h2>{item.comment && <p>{item.comment}</p>}<footer><span className="lab">Labo · {item.laboratory}</span>{item.ean && <span className="ean">EAN · {item.ean}</span>}<span className="date">Saisie · {followupDate(item.entryDate)}</span>{item.season && <span>{item.season}</span>}{item.pharmacies.map((name) => <span className="pharmacy" key={name}>{name}</span>)}</footer></div>
              <aside>{item.status === "active" ? <button type="button" className="return" onClick={() => void updateStatus(item, "return")}>✓ Produit de retour</button> : <button type="button" className="reactivate" onClick={() => void updateStatus(item, "reactivate")}>↻ Réactiver</button>}<button type="button" onClick={() => editItem(item)}>Modifier</button><button type="button" className="delete" onClick={() => void deleteItem(item)}>Supprimer</button></aside>
            </article>)}
            {!filteredItems.length && <div className="generic-empty"><span>💊</span><h2>Aucune molécule dans cette vue</h2><p>Ajoutez une première ligne ou modifiez les filtres.</p></div>}
          </div>
        </section>
      </section>
    </main>
  );
}

type MissingProductFrequency = "daily" | "every_two_days" | "weekly";

type MissingProduct = {
  id: number;
  product: string;
  ean: string;
  frequency: MissingProductFrequency;
  pharmacies: string[];
  comment: string;
  status: "active" | "obtained";
  lastCheckedAt: number | null;
  lastCheckedBy: string;
  obtainedAt: number | null;
  obtainedBy: string;
  createdAt: number;
  updatedAt: number;
};

const missingFrequencyLabels: Record<MissingProductFrequency, string> = {
  daily: "Tous les jours",
  every_two_days: "Tous les 2 jours",
  weekly: "Chaque semaine",
};

function normalizeMissingProduct(row: Record<string, unknown>): MissingProduct {
  let pharmacies: string[] = [];
  try {
    const value = row.pharmacies;
    pharmacies = Array.isArray(value) ? value.map(String) : JSON.parse(String(value ?? "[]"));
  } catch {
    pharmacies = [];
  }
  return {
    id: Number(row.id),
    product: String(row.product ?? ""),
    ean: String(row.ean ?? ""),
    frequency: String(row.frequency ?? "daily") as MissingProductFrequency,
    pharmacies,
    comment: String(row.comment ?? ""),
    status: String(row.status || "active") as "active" | "obtained",
    lastCheckedAt: row.lastCheckedAt === null || row.last_checked_at === null ? null : Number(row.lastCheckedAt ?? row.last_checked_at ?? 0),
    lastCheckedBy: String(row.lastCheckedBy ?? row.last_checked_by ?? ""),
    obtainedAt: row.obtainedAt === null || row.obtained_at === null ? null : Number(row.obtainedAt ?? row.obtained_at ?? 0),
    obtainedBy: String(row.obtainedBy ?? row.obtained_by ?? ""),
    createdAt: Number(row.createdAt ?? row.created_at ?? 0),
    updatedAt: Number(row.updatedAt ?? row.updated_at ?? 0),
  };
}

function isMissingProductDue(item: MissingProduct, now = new Date()) {
  if (item.status !== "active") return false;
  const checked = new Date(item.lastCheckedAt);
  if (item.frequency === "daily") {
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const checkpointMinutes = currentMinutes >= 15 * 60 ? 15 * 60 : currentMinutes >= 11 * 60 ? 11 * 60 : null;
    if (checkpointMinutes === null) return false;
    if (!item.lastCheckedAt || localDateIso(checked) < localDateIso(now)) return true;
    return checked.getHours() * 60 + checked.getMinutes() < checkpointMinutes;
  }
  if (!item.lastCheckedAt) return true;
  const interval = item.frequency === "every_two_days" ? 2 : 7;
  return now.getTime() - checked.getTime() >= interval * 24 * 60 * 60 * 1000;
}

function MissingProductsModule({ onBack, initialOperator }: { onBack: () => void; initialOperator: string }) {
  const [items, setItems] = useState<MissingProduct[]>([]);
  const [product, setProduct] = useState("");
  const [ean, setEan] = useState("");
  const [frequency, setFrequency] = useState<MissingProductFrequency>("daily");
  const [pharmacies, setPharmacies] = useState<string[]>(followupPharmacies);
  const [comment, setComment] = useState("");
  const [query, setQuery] = useState("");
  const [pharmacyFilter, setPharmacyFilter] = useState("Toutes");
  const [statusFilter, setStatusFilter] = useState<"active" | "due" | "obtained" | "all">("active");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const loadItems = async (): Promise<MissingProduct[] | null> => {
    try {
      const response = await fetch("/api/missing-products", { cache: "no-store" });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      const loaded = (payload.items ?? []).map(normalizeMissingProduct);
      setItems(loaded);
      return loaded;
    } catch {
      setMessage("Le contrôle des produits manquants est momentanément indisponible.");
      return null;
    }
  };

  useEffect(() => {
    void loadItems();
  }, []);

  const activeCount = items.filter((item) => item.status === "active").length;
  const dueCount = items.filter((item) => isMissingProductDue(item)).length;
  const normalizedQuery = query.trim().toLocaleLowerCase("fr");
  const filteredItems = items.filter((item) => {
    const searchMatch = !normalizedQuery || `${item.product} ${item.ean} ${item.comment}`.toLocaleLowerCase("fr").includes(normalizedQuery);
    const pharmacyMatch = pharmacyFilter === "Toutes" || item.pharmacies.includes(pharmacyFilter);
    const statusMatch = statusFilter === "all"
      || (statusFilter === "due" ? isMissingProductDue(item) : item.status === statusFilter);
    return searchMatch && pharmacyMatch && statusMatch;
  });

  const togglePharmacy = (name: string) => {
    setPharmacies((current) => current.includes(name) ? current.filter((item) => item !== name) : [...current, name]);
  };

  const addItem = async () => {
    if (!product.trim() || !pharmacies.length) {
      setMessage("Indiquez le produit et au moins une pharmacie.");
      return;
    }
    setSaving(true);
    try {
      const response = await fetch("/api/missing-products", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product, ean, frequency, pharmacies, comment }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      const reloaded = await loadItems();
      if (!reloaded?.some((item) => item.id === Number(payload.id))) {
        throw new Error("Le portail n’a pas confirmé la sauvegarde du produit.");
      }
      setProduct("");
      setEan("");
      setComment("");
      setFrequency("daily");
      setMessage("Produit sauvegardé et relu : le contrôle apparaît sur l’accueil et la TV.");
    } catch (reasonValue) {
      setMessage(reasonValue instanceof Error ? reasonValue.message : "Le produit n’a pas pu être ajouté.");
    } finally {
      setSaving(false);
    }
  };

  const updateItem = async (item: MissingProduct, action: "check" | "obtain" | "reactivate") => {
    try {
      const response = await fetch("/api/missing-products", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: item.id, action, operator: initialOperator }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      const reloaded = await loadItems();
      const saved = reloaded?.find((entry) => entry.id === item.id);
      if (!saved || (action === "obtain" ? saved.status !== "obtained" : saved.status !== "active")) throw new Error();
      setMessage(action === "check" ? `Contrôle sauvegardé par ${initialOperator}.` : action === "obtain" ? "Produit marqué comme obtenu et sauvegardé." : "Produit réactivé et sauvegardé.");
    } catch {
      setMessage("La mise à jour n’a pas pu être enregistrée.");
    }
  };

  const deleteItem = async (item: MissingProduct) => {
    if (!window.confirm(`Supprimer « ${item.product} » du contrôle ?`)) return;
    try {
      const response = await fetch(`/api/missing-products?id=${item.id}`, { method: "DELETE" });
      if (!response.ok) throw new Error();
      const reloaded = await loadItems();
      if (reloaded?.some((entry) => entry.id === item.id)) throw new Error();
      setMessage("Produit supprimé et liste resynchronisée.");
    } catch {
      setMessage("Le produit n’a pas pu être supprimé.");
    }
  };

  return (
    <main className="missing-app">
      <header className="module-header">
        <button type="button" className="module-back" onClick={onBack}>← <span>Retour à l’accueil</span></button>
        <div className="wordmark module-wordmark"><span className="wordmark-symbol"><i /><i /><i /></span><span><strong>saint-laurent</strong><small>pharmacies</small></span></div>
        <div className="module-header-actions"><ModuleToolsLink /><span className="module-status"><i /> Contrôles synchronisés</span></div>
      </header>

      <section className="missing-page">
        <div className="missing-hero">
          <div><span>STOCK · RUPTURES À SURVEILLER</span><h1>Produits manquants à contrôler au quotidien</h1><p>Les contrôles quotidiens déclenchent une alerte à 11 h puis à 15 h tant qu’ils ne sont pas validés.</p></div>
          <div className="missing-hero-stats"><article><strong>{activeCount}</strong><span>actif{activeCount > 1 ? "s" : ""}</span></article><article className={dueCount ? "due" : ""}><strong>{dueCount}</strong><span>à vérifier aujourd’hui</span></article></div>
        </div>

        <section className="missing-composer">
          <div className="missing-section-title"><span>＋</span><div><h2>Ajouter un produit à surveiller</h2><p>L’alerte reste visible sur l’accueil et la TV tant que le contrôle n’est pas validé.</p></div></div>
          <div className="missing-form">
            <label><span>Produit / référence *</span><input value={product} onChange={(event) => setProduct(event.target.value.slice(0, 180))} placeholder="Nom du produit ou référence" /></label>
            <label><span>EAN / DataMatrix</span><CatalogAutocomplete value={ean} onChange={(value) => setEan(value.slice(0, 160))} onPick={(suggestion) => { setEan(suggestion.ean); setProduct(suggestion.product || product); }} placeholder="Scanner un EAN ou un DataMatrix" inputMode="text" /></label>
            <label><span>Fréquence de vérification</span><select value={frequency} onChange={(event) => setFrequency(event.target.value as MissingProductFrequency)}>{Object.entries(missingFrequencyLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
            <fieldset><legend>Pharmacie(s) concernée(s)</legend><button type="button" className={pharmacies.length === followupPharmacies.length ? "all active" : "all"} onClick={() => setPharmacies(pharmacies.length === followupPharmacies.length ? [] : followupPharmacies)}>🏥 Toutes les pharmacies</button>{followupPharmacies.map((name) => <button type="button" className={pharmacies.includes(name) ? "active" : ""} key={name} onClick={() => togglePharmacy(name)}><i>{pharmacies.includes(name) ? "✓" : ""}</i>{name}</button>)}</fieldset>
            <label className="wide"><span>Commentaire</span><textarea value={comment} onChange={(event) => setComment(event.target.value.slice(0, 600))} placeholder="Grossiste en rupture, alternative possible, contact laboratoire…" /></label>
          </div>
          <div className="missing-form-actions"><button type="button" className="primary" disabled={saving} onClick={() => void addItem()}>{saving ? "Ajout…" : "＋ Ajouter le produit"}</button><button type="button" onClick={() => { setProduct(""); setEan(""); setComment(""); setFrequency("daily"); setPharmacies(followupPharmacies); }}>Réinitialiser</button><span>{message}</span></div>
        </section>

        <section className="missing-list-card">
          <div className="missing-toolbar">
            <label><span>🔎</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Rechercher produit ou EAN…" /></label>
            <select value={pharmacyFilter} onChange={(event) => setPharmacyFilter(event.target.value)}><option>Toutes</option>{followupPharmacies.map((name) => <option key={name}>{name}</option>)}</select>
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}><option value="active">Actifs</option><option value="due">À vérifier aujourd’hui</option><option value="obtained">Obtenus</option><option value="all">Tous</option></select>
            <button type="button" onClick={() => void loadItems()}>↻ Actualiser</button>
          </div>
          <div className="missing-list">
            {filteredItems.map((item) => {
              const due = isMissingProductDue(item);
              return <article key={item.id} className={`${item.status} ${due ? "due" : ""}`}>
                <div className="missing-main">
                  <header><h2>{item.product}</h2>{due && <b>⚠️ À vérifier aujourd’hui</b>}{item.status === "obtained" && <b className="obtained">✓ Obtenu</b>}</header>
                  {item.comment && <p>{item.comment}</p>}
                  <footer>{item.pharmacies.map((name) => <span className="pharmacy" key={name}>{name}</span>)}{item.ean && <span>EAN · {item.ean}</span>}<span>{missingFrequencyLabels[item.frequency]}</span>{item.lastCheckedAt && <span className="checked">Dernier contrôle · {new Date(item.lastCheckedAt).toLocaleDateString("fr-FR")} par {item.lastCheckedBy}</span>}</footer>
                </div>
                <div className="missing-actions">{item.status === "active" ? <><button type="button" className="check" onClick={() => void updateItem(item, "check")}>✓ Vérifié aujourd’hui</button><button type="button" className="obtain" onClick={() => void updateItem(item, "obtain")}>📦 Obtenu</button></> : <button type="button" className="reactivate" onClick={() => void updateItem(item, "reactivate")}>↻ Réactiver</button>}<button type="button" className="delete" onClick={() => void deleteItem(item)}>Supprimer</button></div>
              </article>;
            })}
            {!filteredItems.length && <div className="missing-empty"><span>✅</span><div><h2>Aucun produit dans cette vue</h2><p>Les prochains contrôles apparaîtront automatiquement à leur échéance.</p></div></div>}
          </div>
        </section>
      </section>
    </main>
  );
}

type ClearanceTriggerType = "ean" | "category" | "phrase" | "instruction";
type ClearanceTrigger = {
  id: string;
  type: ClearanceTriggerType;
  value: string;
  label?: string;
  instruction: string;
  active: boolean;
};
type ClearanceProduct = {
  id: number;
  ean: string;
  product: string;
  pharmacies: string[];
  stocks: Record<string, number>;
  expiryRisk: boolean;
  slowMoving: boolean;
  maximumPriority: boolean;
  note: string;
  triggers: ClearanceTrigger[];
  status: "active" | "inactive";
  soldCount: number;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
};

const clearanceTriggerLabels: Record<ClearanceTriggerType, string> = {
  ean: "Produit associé",
  category: "Catégorie LGPI",
  phrase: "Suggestion IA",
  instruction: "IA directive",
};

const clearanceTriggerMeta: Record<ClearanceTriggerType, {
  icon: string;
  title: string;
  description: string;
  label: string;
  placeholder: string;
  examples: string[];
}> = {
  ean: {
    icon: "📦",
    title: "EAN / produit",
    description: "Scanner ou rechercher précisément le produit qui doit déclencher l’alerte.",
    label: "EAN ou nom du produit déclencheur",
    placeholder: "Scanner un EAN ou taper le nom du produit",
    examples: ["Scanner maintenant", "Rechercher par nom", "* puis 4 chiffres"],
  },
  category: {
    icon: "🗂️",
    title: "Catégorie LGPI identique",
    description: "Le catalogue retrouve les produits de la même catégorie LGPI, à sélectionner.",
    label: "Produits de la même catégorie",
    placeholder: "La catégorie est détectée depuis le produit à écouler",
    examples: [],
  },
  phrase: {
    icon: "✨",
    title: "Suggestion IA",
    description: "L’IA propose automatiquement une liste cohérente de produits déclencheurs.",
    label: "Suggestions automatiques",
    placeholder: "L’IA analyse le produit à écouler",
    examples: [],
  },
  instruction: {
    icon: "🪄",
    title: "IA avec directive",
    description: "Donnez votre logique métier ; l’IA cherche les déclencheurs correspondants.",
    label: "Votre directive",
    placeholder: "Ex. produit post-piqûre équivalent",
    examples: ["Produit post-piqûre équivalent", "Soin complémentaire après injection", "Alternative de la même famille"],
  },
};

function parseClearanceValue<T>(value: unknown, fallback: T): T {
  try {
    return (typeof value === "string" ? JSON.parse(value) : value) as T ?? fallback;
  } catch {
    return fallback;
  }
}

function normalizeClearanceProduct(row: Record<string, unknown>): ClearanceProduct {
  return {
    id: Number(row.id),
    ean: String(row.ean ?? ""),
    product: String(row.product ?? ""),
    pharmacies: parseClearanceValue<string[]>(row.pharmacies, []),
    stocks: parseClearanceValue<Record<string, number>>(row.stocks, {}),
    expiryRisk: Number(row.expiryRisk ?? row.expiry_risk ?? 0) === 1,
    slowMoving: Number(row.slowMoving ?? row.slow_moving ?? 0) === 1,
    maximumPriority: Number(row.maximumPriority ?? row.maximum_priority ?? 0) === 1,
    note: String(row.note ?? ""),
    triggers: parseClearanceValue<ClearanceTrigger[]>(row.triggers, []),
    status: String(row.status ?? "active") as "active" | "inactive",
    soldCount: Number(row.soldCount ?? row.sold_count ?? 0),
    createdBy: String(row.createdBy ?? row.created_by ?? ""),
    createdAt: Number(row.createdAt ?? row.created_at ?? 0),
    updatedAt: Number(row.updatedAt ?? row.updated_at ?? 0),
  };
}

function blankClearanceProduct(initialPharmacy: string, operator: string): ClearanceProduct {
  return {
    id: 0,
    ean: "",
    product: "",
    pharmacies: [initialPharmacy],
    stocks: { Square: 0, Ragadan: 0, Laurentine: 0 },
    expiryRisk: false,
    slowMoving: false,
    maximumPriority: false,
    note: "",
    triggers: [],
    status: "active",
    soldCount: 0,
    createdBy: operator,
    createdAt: 0,
    updatedAt: 0,
  };
}

function ClearanceProductsModule({
  onBack,
  initialPharmacy,
  initialOperator,
}: {
  onBack: () => void;
  initialPharmacy: string;
  initialOperator: string;
}) {
  const [items, setItems] = useState<ClearanceProduct[]>([]);
  const [draft, setDraft] = useState<ClearanceProduct | null>(null);
  const [mode, setMode] = useState<"counter" | "manage">("counter");
  const [query, setQuery] = useState("");
  const [pharmacyFilter, setPharmacyFilter] = useState(initialPharmacy);
  const [activeOnly, setActiveOnly] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [triggerComposer, setTriggerComposer] = useState<ClearanceTrigger | null>(null);
  const [triggerEditingId, setTriggerEditingId] = useState<string | null>(null);
  const [triggerSuggestions, setTriggerSuggestions] = useState<CatalogSuggestion[]>([]);
  const [selectedTriggerEans, setSelectedTriggerEans] = useState<string[]>([]);
  const [triggerSuggestionLoading, setTriggerSuggestionLoading] = useState(false);
  const [triggerLabelsByEan, setTriggerLabelsByEan] = useState<Record<string, CatalogSuggestion>>({});
  const [lastSyncedAt, setLastSyncedAt] = useState(0);
  const [syncing, setSyncing] = useState(true);
  const [syncAvailable, setSyncAvailable] = useState(true);

  const flash = (message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice(""), 2600);
  };

  const loadItems = async () => {
    setSyncing(true);
    try {
      const response = await fetch("/api/clearance-products", { cache: "no-store" });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      setItems((payload.items ?? []).map(normalizeClearanceProduct));
      setLastSyncedAt(Number(payload.syncedAt) || Date.now());
      setSyncAvailable(true);
    } catch (error) {
      setSyncAvailable(false);
      flash(error instanceof Error && error.message ? error.message : "Le module est momentanément indisponible.");
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    void loadItems();
  }, []);

  useEffect(() => {
    const eans = Array.from(new Set(items
      .flatMap((item) => item.triggers)
      .filter((trigger) => trigger.type === "ean" && !trigger.label && /^\d{8,14}$/.test(trigger.value))
      .map((trigger) => trigger.value)))
      .filter((ean) => !triggerLabelsByEan[ean]);
    if (!eans.length) return;
    let cancelled = false;
    void Promise.all(eans.slice(0, 40).map(async (ean) => {
      try {
        return [ean, await resolveCatalogEan(ean)] as const;
      } catch {
        return [ean, null] as const;
      }
    })).then((entries) => {
      if (cancelled) return;
      setTriggerLabelsByEan((current) => {
        const next = { ...current };
        for (const [ean, suggestion] of entries) {
          if (suggestion) next[ean] = suggestion;
        }
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [items]);

  const setDraftField = <K extends keyof ClearanceProduct>(key: K, value: ClearanceProduct[K]) => {
    setDraft((current) => current ? { ...current, [key]: value } : current);
  };

  const toggleDraftPharmacy = (pharmacy: string) => {
    if (!draft) return;
    setDraftField("pharmacies", draft.pharmacies.includes(pharmacy)
      ? draft.pharmacies.filter((name) => name !== pharmacy)
      : [...draft.pharmacies, pharmacy]);
  };

  const applyCatalogProduct = async (suggestion: CatalogSuggestion) => {
    let stocks: Record<string, number> = { Square: 0, Ragadan: 0, Laurentine: 0 };
    try {
      const response = await fetch(`/api/product-catalog?ean=${encodeURIComponent(suggestion.ean)}&limit=12`, { cache: "no-store" });
      const payload = await response.json();
      if (response.ok) {
        for (const row of (payload.items ?? []) as CatalogSuggestion[]) {
          if (row.pharmacy in stocks) stocks = { ...stocks, [row.pharmacy]: Math.max(0, Number(row.stock) || 0) };
        }
      }
    } catch {
      stocks = { ...stocks, [suggestion.pharmacy]: Math.max(0, Number(suggestion.stock) || 0) };
    }
    setDraft((current) => current ? {
      ...current,
      ean: suggestion.ean,
      product: suggestion.product || current.product,
      stocks,
    } : current);
  };

  const openTriggerComposer = (trigger?: ClearanceTrigger) => {
    setTriggerEditingId(trigger?.id ?? null);
    setTriggerSuggestions([]);
    setSelectedTriggerEans([]);
    setTriggerComposer(trigger ? { ...trigger } : {
      id: `trigger-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type: "ean",
      value: "",
      instruction: "",
      active: true,
    });
  };

  const updateTrigger = (id: string, patch: Partial<ClearanceTrigger>) => {
    if (!draft) return;
    setDraftField("triggers", draft.triggers.map((trigger) => trigger.id === id ? { ...trigger, ...patch } : trigger));
  };

  const closeTriggerComposer = () => {
    setTriggerComposer(null);
    setTriggerEditingId(null);
    setTriggerSuggestions([]);
    setSelectedTriggerEans([]);
  };

  const toggleTriggerSuggestion = (suggestion: CatalogSuggestion) => {
    setTriggerSuggestions((current) => current.some((item) => item.ean === suggestion.ean)
      ? current
      : [...current, suggestion]);
    setSelectedTriggerEans((current) => current.includes(suggestion.ean)
      ? current.filter((ean) => ean !== suggestion.ean)
      : [...current, suggestion.ean]);
  };

  const saveTriggerComposer = () => {
    if (!draft || !triggerComposer) return;
    if (selectedTriggerEans.length) {
      const selected = triggerSuggestions.filter((suggestion) => selectedTriggerEans.includes(suggestion.ean));
      const additions: ClearanceTrigger[] = selected
        .filter((suggestion) => !draft.triggers.some((trigger) => trigger.type === "ean" && trigger.value === suggestion.ean))
        .map((suggestion) => ({
          id: `trigger-${Date.now()}-${suggestion.ean}`,
          type: "ean",
          value: suggestion.ean,
          label: suggestion.product,
          instruction: `Quand ${suggestion.product} est demandé ou scanné, penser à ${draft.product}.`,
          active: true,
        }));
      if (!additions.length) {
        flash("Ces produits sont déjà enregistrés comme déclencheurs.");
        return;
      }
      setDraftField("triggers", [...draft.triggers, ...additions]);
      closeTriggerComposer();
      flash(`${additions.length} déclencheur${additions.length > 1 ? "s" : ""} ajouté${additions.length > 1 ? "s" : ""}.`);
      return;
    }
    if (triggerComposer.type === "ean" && !triggerEditingId && !extractGtinFromScan(triggerComposer.value)) {
      flash("Recherchez puis cochez au moins un produit déclencheur.");
      return;
    }
    if (!triggerComposer.value.trim()) {
      flash("Indiquez ce qui doit déclencher la suggestion.");
      return;
    }
    const normalizedTrigger = {
      ...triggerComposer,
      value: triggerComposer.value.trim(),
      instruction: triggerComposer.instruction.trim(),
    };
    setDraftField("triggers", triggerEditingId
      ? draft.triggers.map((trigger) => trigger.id === triggerEditingId ? normalizedTrigger : trigger)
      : [...draft.triggers, normalizedTrigger]);
    closeTriggerComposer();
    flash(triggerEditingId ? "Déclencheur mis à jour." : "Déclencheur ajouté au produit.");
  };

  const suggestTriggerInstruction = () => {
    if (!triggerComposer) return;
    const productName = draft?.product.trim() || "ce produit";
    const situation = triggerComposer.value.trim() || clearanceTriggerMeta[triggerComposer.type].examples[0];
    setTriggerComposer({
      ...triggerComposer,
      value: situation,
      instruction: `Si le besoin correspond, penser à ${productName} et le proposer uniquement si c’est adapté au patient.`,
    });
  };

  const loadTriggerSuggestions = async () => {
    if (!draft || !triggerComposer) return;
    if (triggerComposer.type === "instruction" && !triggerComposer.value.trim()) {
      flash("Écrivez d’abord votre directive.");
      return;
    }
    setTriggerSuggestionLoading(true);
    setTriggerSuggestions([]);
    setSelectedTriggerEans([]);
    try {
      const mode = triggerComposer.type === "category" ? "category" : triggerComposer.type === "instruction" ? "directive" : "auto";
      const response = await fetch("/api/clearance-trigger-suggestions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode,
          product: draft.product,
          ean: draft.ean,
          pharmacy: draft.pharmacies[0] || initialPharmacy,
          directive: triggerComposer.value,
        }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      const suggestions = (payload.items ?? []) as CatalogSuggestion[];
      setTriggerSuggestions(suggestions);
      if (!suggestions.length) flash("Aucun produit catalogue pertinent trouvé. Essayez une directive plus précise.");
      else flash(`${suggestions.length} propositions catalogue trouvées.`);
    } catch (error) {
      flash(error instanceof Error ? error.message : "Les suggestions sont momentanément indisponibles.");
    } finally {
      setTriggerSuggestionLoading(false);
    }
  };

  useEffect(() => {
    if (!triggerComposer) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeTriggerComposer();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [triggerComposer]);

  const saveDraft = async () => {
    if (!draft?.product.trim() || !draft.pharmacies.length) {
      flash("Indiquez le produit et au moins une pharmacie.");
      return;
    }
    setSaving(true);
    try {
      const response = await fetch("/api/clearance-products", {
        method: draft.id ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft.id ? { ...draft, action: "save" } : draft),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      const saved = normalizeClearanceProduct(payload);
      setItems((current) => draft.id
        ? current.map((entry) => entry.id === saved.id ? saved : entry)
        : [saved, ...current.filter((entry) => entry.id !== saved.id)]);
      setLastSyncedAt(Date.now());
      setSyncAvailable(true);
      flash(draft.id ? "Produit enregistré." : "Produit ajouté au mode comptoir.");
      setDraft(null);
      await loadItems();
    } catch (error) {
      flash(error instanceof Error && error.message ? error.message : "Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  const recordSale = async (item: ClearanceProduct, pharmacy: string) => {
    try {
      const response = await fetch("/api/clearance-products", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: item.id, action: "sold", soldPharmacy: pharmacy }),
      });
      if (!response.ok) throw new Error();
      const updated = normalizeClearanceProduct(await response.json());
      setItems((current) => current.map((entry) => entry.id === updated.id ? updated : entry));
      flash(`Vente enregistrée pour ${pharmacy}.`);
    } catch {
      flash("La vente n’a pas pu être enregistrée.");
    }
  };

  const toggleStatus = async (item: ClearanceProduct) => {
    try {
      const response = await fetch("/api/clearance-products", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...item, action: "save", status: item.status === "active" ? "inactive" : "active" }),
      });
      if (!response.ok) throw new Error();
      const updated = normalizeClearanceProduct(await response.json());
      setItems((current) => current.map((entry) => entry.id === updated.id ? updated : entry));
      if (draft?.id === updated.id) setDraft(updated);
      flash(updated.status === "active" ? "Produit réactivé." : "Produit retiré du mode comptoir.");
    } catch {
      flash("La mise à jour n’a pas pu être enregistrée.");
    }
  };

  const deleteItem = async (item: ClearanceProduct) => {
    if (!window.confirm(`Supprimer définitivement « ${item.product} » ?`)) return;
    try {
      const response = await fetch(`/api/clearance-products?id=${item.id}`, { method: "DELETE" });
      if (!response.ok) throw new Error();
      setItems((current) => current.filter((entry) => entry.id !== item.id));
      setDraft(null);
      flash("Produit supprimé.");
    } catch {
      flash("La suppression n’a pas pu être enregistrée.");
    }
  };

  const normalizedQuery = query.trim().toLocaleLowerCase("fr");
  const visibleItems = items
    .filter((item) => !activeOnly || item.status === "active")
    .filter((item) => pharmacyFilter === "Toutes" || item.pharmacies.includes(pharmacyFilter))
    .filter((item) => {
      const reasonText = [
        item.expiryRisk ? "se périme bientôt péremption proche" : "",
        item.slowMoving ? "ne tourne pas rotation lente" : "",
        item.maximumPriority ? "priorité maximale urgent" : "",
      ].join(" ");
      return !normalizedQuery || `${item.product} ${item.ean} ${item.note} ${reasonText} ${item.triggers.map((trigger) => `${trigger.value} ${trigger.instruction}`).join(" ")}`.toLocaleLowerCase("fr").includes(normalizedQuery);
    });
  const activeCount = items.filter((item) => item.status === "active").length;
  const maximumCount = items.filter((item) => item.status === "active" && item.maximumPriority).length;
  const slowCount = items.filter((item) => item.status === "active" && item.slowMoving).length;
  const withoutTriggerCount = items.filter((item) => item.status === "active" && !item.triggers.some((trigger) => trigger.active && trigger.value.trim())).length;
  const triggerComposerMeta = triggerComposer ? clearanceTriggerMeta[triggerComposer.type] : null;

  return (
    <main className="clearance-app">
      <header className="module-header">
        <button type="button" className="module-back" onClick={onBack}>← <span>Retour à l’accueil</span></button>
        <div className="wordmark module-wordmark"><span className="wordmark-symbol"><i /><i /><i /></span><span><strong>saint-laurent</strong><small>pharmacies</small></span></div>
        <div className="module-header-actions"><ModuleToolsLink /><span className={`module-status ${syncAvailable ? "" : "offline"}`}><i /> {syncing ? "Synchronisation…" : syncAvailable ? `Synchronisé${lastSyncedAt ? ` à ${new Date(lastSyncedAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}` : ""}` : "Synchronisation indisponible"}</span></div>
      </header>

      <section className="clearance-page">
        <section className="clearance-hero">
          <div><span>OPTIMISATION COMPTOIR</span><h1>Produits à écouler</h1><p>La bonne suggestion, au bon moment, sans surcharger le conseil patient.</p></div>
          <div className="clearance-hero-actions"><button type="button" className="secondary" onClick={() => setMode(mode === "counter" ? "manage" : "counter")}>{mode === "counter" ? "⚙️ Gérer les produits" : "👀 Voir le mode comptoir"}</button><button type="button" onClick={() => { setDraft(blankClearanceProduct(initialPharmacy, initialOperator)); setMode("manage"); }}>＋ Nouveau produit</button></div>
        </section>

        <section className="clearance-stats">
          <button type="button" onClick={() => { setActiveOnly(true); setQuery(""); }}><span>📣</span><strong>{activeCount}</strong><small>produits actifs</small></button>
          <button type="button" className={maximumCount ? "urgent" : ""} onClick={() => setQuery("priorité maximale")}><span>🔥</span><strong>{maximumCount}</strong><small>priorité maximale</small></button>
          <button type="button" onClick={() => setQuery("ne tourne pas")}><span>🐢</span><strong>{slowCount}</strong><small>ne tournent pas</small></button>
          <button type="button" className={withoutTriggerCount ? "warning" : ""} onClick={() => setMode("manage")}><span>⚠️</span><strong>{withoutTriggerCount}</strong><small>sans déclencheur</small></button>
        </section>

        <section className="clearance-toolbar">
          <div className="clearance-tabs"><button type="button" className={mode === "counter" ? "active" : ""} onClick={() => setMode("counter")}>👀 Comptoir</button><button type="button" className={mode === "manage" ? "active" : ""} onClick={() => setMode("manage")}>⚙️ Gestion</button></div>
          <label><span>⌕</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Produit, EAN, déclencheur, note…" /></label>
          <select value={pharmacyFilter} onChange={(event) => setPharmacyFilter(event.target.value)}><option>Toutes</option>{followupPharmacies.map((name) => <option key={name}>{name}</option>)}</select>
          <button type="button" className={activeOnly ? "active-filter" : ""} onClick={() => setActiveOnly((current) => !current)}>{activeOnly ? "✓ Actifs uniquement" : "Tous les statuts"}</button>
        </section>

        {mode === "manage" && draft && (
          <section className="clearance-editor">
            <header><div><span>{draft.id ? "MODIFIER LE PRODUIT" : "NOUVEAU PRODUIT"}</span><h2>{draft.product || "Produit à écouler"}</h2></div><div><button type="button" onClick={() => setDraft(null)}>Plier</button>{draft.id && <button type="button" onClick={() => void toggleStatus(draft)}>{draft.status === "active" ? "Désactiver" : "Réactiver"}</button>}<button type="button" className="save" disabled={saving} onClick={() => void saveDraft()}>{saving ? "Enregistrement…" : "💾 Enregistrer"}</button>{draft.id && <button type="button" className="delete" onClick={() => void deleteItem(draft)}>Supprimer</button>}</div></header>
            <div className="clearance-editor-grid">
              <section className="clearance-product-picker"><h3>Produit du catalogue</h3><div className="clearance-fields"><label><span>EAN / DataMatrix</span><CatalogAutocomplete value={draft.ean} onChange={(ean) => setDraftField("ean", ean.slice(0, 160))} onPick={(suggestion) => void applyCatalogProduct(suggestion)} placeholder="Scanner un EAN ou un DataMatrix" inputMode="text" /></label><label className="wide"><span>Nom produit *</span><CatalogAutocomplete value={draft.product} confirmed={Boolean(draft.ean)} onChange={(product) => { setDraftField("product", product.slice(0, 180)); setDraftField("ean", ""); }} onPick={(suggestion) => void applyCatalogProduct(suggestion)} placeholder="Tapez le nom : les produits sont proposés" inputMode="search" /></label></div><div className="clearance-catalog-stocks"><b>Stocks catalogue</b>{draft.ean ? followupPharmacies.map((pharmacy) => <span key={pharmacy}>{pharmacy}<strong>{draft.stocks[pharmacy] ?? 0}</strong></span>) : <small>Choisissez un produit pour récupérer les stocks automatiquement.</small>}</div></section>
              <section><h3>Pharmacies concernées</h3><div className="clearance-pharmacy-pills">{followupPharmacies.map((pharmacy) => <button type="button" key={pharmacy} className={draft.pharmacies.includes(pharmacy) ? "active" : ""} onClick={() => toggleDraftPharmacy(pharmacy)}><i>{draft.pharmacies.includes(pharmacy) ? "✓" : ""}</i>{pharmacy}</button>)}</div></section>
            </div>
            <section className="clearance-reasons"><h3>Pourquoi l’écouler ?</h3><div><button type="button" className={draft.expiryRisk ? "active expiry" : "expiry"} onClick={() => setDraftField("expiryRisk", !draft.expiryRisk)}>⏳ Se périme bientôt</button><button type="button" className={draft.slowMoving ? "active slow" : "slow"} onClick={() => setDraftField("slowMoving", !draft.slowMoving)}>🐢 Ne tourne pas</button><button type="button" className={draft.maximumPriority ? "active maximum" : "maximum"} onClick={() => setDraftField("maximumPriority", !draft.maximumPriority)}>🔥 Priorité maximale</button></div></section>
            <section className="clearance-triggers">
              <header><div><span className="clearance-section-kicker">ASSOCIATIONS COMPTOIR</span><h3>Quand penser à ce produit ?</h3><p>Choisissez un EAN, la même catégorie LGPI ou laissez l’IA proposer des déclencheurs réels du catalogue.</p></div><button type="button" onClick={() => openTriggerComposer()}>＋ Nouveau déclencheur</button></header>
              <div className="clearance-trigger-list">
                {draft.triggers.map((trigger) => {
                  const meta = clearanceTriggerMeta[trigger.type];
                  return <article key={trigger.id} className={trigger.active ? "" : "inactive"}>
                    <div className="clearance-trigger-card-icon">{meta.icon}</div>
                    <div className="clearance-trigger-card-main">
                      <span>{meta.title}</span>
                      <strong>{trigger.label || triggerLabelsByEan[trigger.value]?.product || trigger.value || "Déclencheur à compléter"}</strong>
                      {(trigger.label || triggerLabelsByEan[trigger.value]) && <small>EAN {trigger.value}</small>}
                      <p>{trigger.instruction || `Penser à ${draft.product || "ce produit"} si la situation correspond.`}</p>
                    </div>
                    <div className="clearance-trigger-card-actions">
                      <button type="button" className={trigger.active ? "status on" : "status"} onClick={() => updateTrigger(trigger.id, { active: !trigger.active })}>{trigger.active ? "✓ Actif" : "En pause"}</button>
                      <button type="button" className="edit" onClick={() => openTriggerComposer(trigger)}>✎ Modifier</button>
                      <button type="button" className="remove" aria-label={`Supprimer ${trigger.value}`} onClick={() => setDraftField("triggers", draft.triggers.filter((entry) => entry.id !== trigger.id))}>×</button>
                    </div>
                  </article>;
                })}
                {!draft.triggers.length && <button type="button" className="clearance-trigger-empty" onClick={() => openTriggerComposer()}><span>🎯</span><div><strong>Aucun déclencheur</strong><p>Ajoutez le premier signal pour faire remonter ce produit au bon moment.</p></div><b>Commencer →</b></button>}
              </div>
            </section>
          </section>
        )}

        <section className="clearance-list-card">
          <header><div><span>{mode === "counter" ? "MODE COMPTOIR" : "CATALOGUE"}</span><h2>{mode === "counter" ? "Suggestions immédiatement exploitables" : "Produits actifs et archivés"}</h2></div><b>{visibleItems.length} produit{visibleItems.length > 1 ? "s" : ""}</b></header>
          <div className="clearance-list">
            {visibleItems.map((item) => {
              const activeTriggers = item.triggers.filter((trigger) => trigger.active && trigger.value.trim());
              return <article key={item.id} className={`${item.status} ${item.maximumPriority ? "maximum" : ""}`}>
                <div className="clearance-product-info"><div><h3>{item.product}</h3>{item.status === "inactive" && <span className="inactive">Inactif</span>}{item.expiryRisk && <span className="expiry">⏳ Péremption proche</span>}{item.slowMoving && <span className="slow">🐢 Ne tourne pas</span>}{item.maximumPriority && <span className="maximum">🔥 Priorité max</span>}</div><footer>{item.pharmacies.map((pharmacy) => <span key={pharmacy}>{pharmacy} · stock {item.stocks[pharmacy] ?? 0}</span>)}{item.ean && <span>EAN {item.ean}</span>}<span>{item.soldCount} vendu{item.soldCount > 1 ? "s" : ""}</span></footer></div>
                <div className="clearance-trigger-summary">{activeTriggers.length ? activeTriggers.slice(0, 3).map((trigger) => {
                  const resolvedLabel = trigger.label || triggerLabelsByEan[trigger.value]?.product;
                  return <button type="button" className="clearance-trigger-bubble" key={trigger.id}><b>{clearanceTriggerLabels[trigger.type]}</b><span>{resolvedLabel || "Survoler pour voir"}</span><aside><strong>{resolvedLabel || trigger.value}</strong>{resolvedLabel && <small>EAN {trigger.value}</small>}{trigger.instruction && <p>{trigger.instruction}</p>}</aside></button>;
                }) : <span className="missing"><b>⚠️ Aucun déclencheur</b>À compléter dans la gestion</span>}</div>
                <div className="clearance-item-actions">{mode === "counter" ? item.pharmacies.map((pharmacy) => <button type="button" className="sale" key={pharmacy} disabled={(item.stocks[pharmacy] ?? 0) <= 0} onClick={() => void recordSale(item, pharmacy)}>✓ Vendu {pharmacy} −1</button>) : <><button type="button" onClick={() => { setDraft({ ...item, stocks: { ...item.stocks }, triggers: item.triggers.map((trigger) => ({ ...trigger })) }); window.scrollTo({ top: 0, behavior: "smooth" }); }}>✎ Modifier</button><button type="button" onClick={() => void toggleStatus(item)}>{item.status === "active" ? "Désactiver" : "Réactiver"}</button></>}</div>
              </article>;
            })}
            {!visibleItems.length && <div className="clearance-empty"><span>📣</span><div><h3>Aucun produit dans cette vue</h3><p>Créez le premier produit à écouler ou modifiez les filtres.</p></div><button type="button" onClick={() => { setDraft(blankClearanceProduct(initialPharmacy, initialOperator)); setMode("manage"); }}>＋ Nouveau produit</button></div>}
          </div>
        </section>
      </section>
      {triggerComposer && triggerComposerMeta && draft && (
        <div className="clearance-trigger-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeTriggerComposer(); }}>
          <section className="clearance-trigger-composer" role="dialog" aria-modal="true" aria-labelledby="clearance-trigger-title">
            <header>
              <div>
                <span>🎯 DÉCLENCHEUR COMPTOIR</span>
                <h2 id="clearance-trigger-title">{triggerEditingId ? "Modifier le déclencheur" : "Quand faut-il penser à ce produit ?"}</h2>
                <p>Choisissez un signal simple. L’équipe verra immédiatement quoi faire au comptoir.</p>
              </div>
              <button type="button" onClick={closeTriggerComposer} aria-label="Fermer le volet">×</button>
            </header>

            <div className="clearance-trigger-types" role="tablist" aria-label="Type de déclencheur">
              {(Object.entries(clearanceTriggerMeta) as Array<[ClearanceTriggerType, (typeof clearanceTriggerMeta)[ClearanceTriggerType]]>).map(([type, meta]) => <button type="button" role="tab" aria-selected={triggerComposer.type === type} className={triggerComposer.type === type ? "active" : ""} key={type} onClick={() => {
                setTriggerComposer({ ...triggerComposer, type, value: "", label: "", instruction: "" });
                setTriggerSuggestions([]);
                setSelectedTriggerEans([]);
              }}>
                <span>{meta.icon}</span><strong>{meta.title}</strong><small>{meta.description}</small>
              </button>)}
            </div>

            <div className="clearance-trigger-composer-body">
              <section className="clearance-trigger-form">
                <div className="clearance-trigger-step"><i>1</i><div><strong>{triggerComposerMeta.title}</strong><span>{triggerComposerMeta.description}</span></div></div>
                {triggerComposer.type === "ean" ? (
                  <label>
                    <span>{triggerComposerMeta.label} *</span>
                    <CatalogAutocomplete
                      value={triggerComposer.value}
                      onChange={(value) => setTriggerComposer({ ...triggerComposer, value: value.slice(0, 220) })}
                      onPick={triggerEditingId ? (suggestion) => setTriggerComposer({ ...triggerComposer, value: suggestion.ean, label: suggestion.product }) : undefined}
                      multiple={!triggerEditingId}
                      selectedEans={selectedTriggerEans}
                      onToggle={toggleTriggerSuggestion}
                      placeholder={triggerComposerMeta.placeholder}
                      inputMode="text"
                    />
                    {!triggerEditingId && <small className="clearance-trigger-multi-help">☑ Cochez un ou plusieurs produits dans les résultats, puis validez-les ensemble.</small>}
                  </label>
                ) : (
                  <>
                    {triggerComposer.type === "instruction" && (
                      <label>
                        <span>{triggerComposerMeta.label} *</span>
                        <textarea autoFocus value={triggerComposer.value} onChange={(event) => setTriggerComposer({ ...triggerComposer, value: event.target.value.slice(0, 500) })} placeholder={triggerComposerMeta.placeholder} />
                      </label>
                    )}
                    <button type="button" className="clearance-trigger-suggest clearance-trigger-load" disabled={triggerSuggestionLoading} onClick={() => void loadTriggerSuggestions()}>
                      {triggerSuggestionLoading ? "Analyse du catalogue…" : triggerComposer.type === "category" ? "🗂️ Afficher la catégorie LGPI" : triggerComposer.type === "phrase" ? "✨ Générer les suggestions IA" : "🪄 Appliquer ma directive"}
                    </button>
                    {!!triggerSuggestions.length && (
                      <div className="clearance-ai-suggestions">
                        <header><strong>Sélectionnez les produits déclencheurs</strong><span>{selectedTriggerEans.length} choisi{selectedTriggerEans.length > 1 ? "s" : ""}</span></header>
                        <div>{triggerSuggestions.map((suggestion) => <button type="button" className={selectedTriggerEans.includes(suggestion.ean) ? "selected" : ""} key={suggestion.ean} onClick={() => setSelectedTriggerEans((current) => current.includes(suggestion.ean) ? current.filter((ean) => ean !== suggestion.ean) : [...current, suggestion.ean])}><i>{selectedTriggerEans.includes(suggestion.ean) ? "✓" : "+"}</i><strong>{suggestion.product}</strong><small>EAN {suggestion.ean} · {suggestion.pharmacy} · stock {suggestion.stock}</small></button>)}</div>
                      </div>
                    )}
                  </>
                )}

                {triggerComposer.type === "ean" && (
                  <>
                    <div className="clearance-trigger-step"><i>2</i><div><strong>Consigne courte</strong><span>Facultative : la suggestion reste visible même sans texte.</span></div></div>
                    <label>
                      <span>Action attendue au comptoir</span>
                      <textarea value={triggerComposer.instruction} onChange={(event) => setTriggerComposer({ ...triggerComposer, instruction: event.target.value.slice(0, 500) })} placeholder={`Ex. penser à ${draft.product || "ce produit"} si le besoin correspond…`} />
                    </label>
                    <button type="button" className="clearance-trigger-suggest" onClick={suggestTriggerInstruction}>✨ Formuler automatiquement</button>
                  </>
                )}
              </section>

              <aside className="clearance-trigger-preview">
                <span>APERÇU ÉQUIPE</span>
                <h3>{draft.product || "Produit à écouler"}</h3>
                <div className="clearance-trigger-preview-when">
                  <i>{triggerComposerMeta.icon}</i>
                  <div><small>QUAND</small><strong>{triggerComposer.label || triggerLabelsByEan[triggerComposer.value]?.product || triggerComposer.value || (selectedTriggerEans.length ? `${selectedTriggerEans.length} produit(s) sélectionné(s)` : "Renseignez le signal déclencheur")}</strong>{(triggerComposer.label || triggerLabelsByEan[triggerComposer.value]) && <span>EAN {triggerComposer.value}</span>}</div>
                </div>
                <div className="clearance-trigger-preview-then">
                  <small>ALORS</small>
                  <strong>Penser à ce produit</strong>
                  <p>{triggerComposer.instruction || "La consigne équipe apparaîtra ici."}</p>
                </div>
                <footer>{draft.pharmacies.map((pharmacy) => <span key={pharmacy}>🏥 {pharmacy}</span>)}</footer>
              </aside>
            </div>

            <footer>
              <div><span className={triggerComposer.value.trim() || selectedTriggerEans.length ? "ready" : ""}>{triggerComposer.value.trim() || selectedTriggerEans.length ? "✓ Prêt à ajouter" : "Choisissez au moins un produit"}</span><small>Échap pour fermer</small></div>
              <button type="button" className="secondary" onClick={closeTriggerComposer}>Annuler</button>
              <button type="button" className="primary" disabled={!triggerComposer.value.trim() && !selectedTriggerEans.length} onClick={saveTriggerComposer}>{triggerEditingId ? "Enregistrer les modifications" : selectedTriggerEans.length ? `＋ Ajouter ${selectedTriggerEans.length} déclencheur${selectedTriggerEans.length > 1 ? "s" : ""}` : "＋ Ajouter le déclencheur"}</button>
            </footer>
          </section>
        </div>
      )}
      {notice && <div className="module-toast">{notice}</div>}
    </main>
  );
}

type AssociatedSaleProduct = {
  id: string;
  name: string;
  ean: string;
  quantity: number;
  note: string;
};

type AssociatedSale = {
  id: number;
  triggerName: string;
  triggerEans: string[];
  pharmacies: string[];
  counterMessage: string;
  associatedProducts: AssociatedSaleProduct[];
  priority: "normal" | "important";
  status: "active" | "inactive";
  createdBy: string;
  createdAt: number;
  updatedAt: number;
};

type CounterAlert = {
  id: string;
  source: "associated_sale" | "clearance";
  priority: "normal" | "important";
  title: string;
  message: string;
  products: AssociatedSaleProduct[];
  reasons?: { expiryRisk?: boolean; slowMoving?: boolean };
};

type ScanMatch = {
  scan: { kind: "ean" | "datamatrix"; gtin: string; batch: string; expiry: string; serial: string };
  alerts: CounterAlert[];
};

function normalizeAssociatedSale(row: Record<string, unknown>): AssociatedSale {
  return {
    id: Number(row.id),
    triggerName: String(row.triggerName ?? row.trigger_name ?? ""),
    triggerEans: parseClearanceValue<string[]>(row.triggerEans ?? row.trigger_eans, []),
    pharmacies: parseClearanceValue<string[]>(row.pharmacies, []),
    counterMessage: String(row.counterMessage ?? row.counter_message ?? ""),
    associatedProducts: parseClearanceValue<AssociatedSaleProduct[]>(row.associatedProducts ?? row.associated_products, []),
    priority: String(row.priority ?? "normal") === "important" ? "important" : "normal",
    status: String(row.status ?? "active") === "inactive" ? "inactive" : "active",
    createdBy: String(row.createdBy ?? row.created_by ?? ""),
    createdAt: Number(row.createdAt ?? row.created_at ?? 0),
    updatedAt: Number(row.updatedAt ?? row.updated_at ?? 0),
  };
}

function blankAssociatedSale(initialPharmacy: string, operator: string): AssociatedSale {
  return {
    id: 0,
    triggerName: "",
    triggerEans: [],
    pharmacies: [initialPharmacy],
    counterMessage: "",
    associatedProducts: [],
    priority: "normal",
    status: "active",
    createdBy: operator,
    createdAt: 0,
    updatedAt: 0,
  };
}

function AssociatedSalesModule({
  onBack,
  initialPharmacy,
  initialOperator,
}: {
  onBack: () => void;
  initialPharmacy: string;
  initialOperator: string;
}) {
  const [items, setItems] = useState<AssociatedSale[]>([]);
  const [mode, setMode] = useState<"counter" | "manage">("manage");
  const [draft, setDraft] = useState<AssociatedSale | null>(null);
  const [eanInput, setEanInput] = useState("");
  const [triggerCatalogLabels, setTriggerCatalogLabels] = useState<Record<string, CatalogSuggestion>>({});
  const [productDraft, setProductDraft] = useState({ name: "", ean: "", quantity: 1, note: "" });
  const [scanCode, setScanCode] = useState("");
  const [scanPharmacy, setScanPharmacy] = useState(initialPharmacy);
  const [scanMatch, setScanMatch] = useState<ScanMatch | null>(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("");

  const flash = (message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice(""), 2800);
  };

  const loadItems = async () => {
    try {
      const response = await fetch("/api/associated-sales", { cache: "no-store" });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      setItems((payload.items ?? []).map(normalizeAssociatedSale));
    } catch (error) {
      flash(error instanceof Error ? error.message : "Le module est momentanément indisponible.");
    }
  };

  useEffect(() => {
    void loadItems();
    const timer = startVisiblePolling(loadItems, PORTAL_LIVE_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, []);

  const setDraftField = <K extends keyof AssociatedSale>(key: K, value: AssociatedSale[K]) =>
    setDraft((current) => current ? { ...current, [key]: value } : current);

  useEffect(() => {
    const triggerEans = draft?.triggerEans ?? [];
    const missingEans = triggerEans.filter((ean) => !triggerCatalogLabels[ean]);
    if (!missingEans.length) return;
    const controller = new AbortController();
    void Promise.all(missingEans.map((ean) => resolveCatalogEan(ean, controller.signal)))
      .then((suggestions) => {
        if (controller.signal.aborted) return;
        setTriggerCatalogLabels((current) => {
          const next = { ...current };
          suggestions.forEach((suggestion) => {
            if (suggestion) next[suggestion.ean] = suggestion;
          });
          return next;
        });
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [draft?.triggerEans, triggerCatalogLabels]);

  const addTriggerEan = async () => {
    if (!draft) return;
    const value = eanInput.replace(/\D/g, "");
    if (value.length < 8) return flash("Scannez ou saisissez un EAN valide.");
    try {
      const suggestion = triggerCatalogLabels[value] ?? await resolveCatalogEan(value);
      if (suggestion) {
        setTriggerCatalogLabels((current) => ({ ...current, [value]: suggestion }));
        if (!draft.triggerName.trim()) setDraftField("triggerName", suggestion.product || suggestion.ean);
      }
    } catch {
      // L'EAN reste utilisable même si le catalogue est momentanément indisponible.
    }
    if (!draft.triggerEans.includes(value)) setDraftField("triggerEans", [...draft.triggerEans, value]);
    setEanInput("");
  };

  const addAssociatedProduct = () => {
    if (!draft || !productDraft.name.trim()) return flash("Indiquez le produit à proposer.");
    setDraftField("associatedProducts", [...draft.associatedProducts, {
      id: `product-${Date.now()}`,
      name: productDraft.name.trim(),
      ean: productDraft.ean.replace(/\D/g, ""),
      quantity: Math.max(1, productDraft.quantity),
      note: productDraft.note.trim(),
    }]);
    setProductDraft({ name: "", ean: "", quantity: 1, note: "" });
  };

  const saveDraft = async () => {
    if (!draft) return;
    const pendingEan = eanInput.replace(/\D/g, "");
    const pendingProduct = productDraft.name.trim();
    let preparedDraft: AssociatedSale = {
      ...draft,
      triggerEans: pendingEan.length >= 8 && !draft.triggerEans.includes(pendingEan)
        ? [...draft.triggerEans, pendingEan]
        : draft.triggerEans,
      associatedProducts: pendingProduct
        ? [...draft.associatedProducts, {
          id: `product-${Date.now()}`,
          name: pendingProduct,
          ean: productDraft.ean.replace(/\D/g, ""),
          quantity: Math.max(1, productDraft.quantity),
          note: productDraft.note.trim(),
        }]
        : draft.associatedProducts,
    };
    if (!preparedDraft.triggerName.trim() && pendingEan) {
      try {
        const suggestion = triggerCatalogLabels[pendingEan] ?? await resolveCatalogEan(pendingEan);
        if (suggestion) preparedDraft = { ...preparedDraft, triggerName: suggestion.product || suggestion.ean };
      } catch {
        // La validation ci-dessous donnera une consigne claire si le nom manque encore.
      }
    }
    if (!preparedDraft.triggerName.trim() || !preparedDraft.triggerEans.length || !preparedDraft.pharmacies.length) {
      return flash("Produit déclencheur, EAN et pharmacie sont obligatoires.");
    }
    setLoading(true);
    try {
      const response = await fetch("/api/associated-sales", {
        method: preparedDraft.id ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(preparedDraft),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      setEanInput("");
      setProductDraft({ name: "", ean: "", quantity: 1, note: "" });
      setDraft(null);
      await loadItems();
      flash("Association enregistrée et disponible pour les postes de vente.");
    } catch (error) {
      flash(error instanceof Error ? error.message : "Enregistrement impossible.");
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (item: AssociatedSale) => {
    try {
      const response = await fetch("/api/associated-sales", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...item, status: item.status === "active" ? "inactive" : "active" }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      setItems((current) => current.map((entry) => entry.id === item.id ? normalizeAssociatedSale(payload) : entry));
    } catch (error) {
      flash(error instanceof Error ? error.message : "Modification impossible.");
    }
  };

  const removeItem = async (item: AssociatedSale) => {
    if (!window.confirm(`Supprimer l’association « ${item.triggerName} » ?`)) return;
    try {
      const response = await fetch(`/api/associated-sales?id=${item.id}`, { method: "DELETE" });
      if (!response.ok) throw new Error((await response.json()).error);
      setItems((current) => current.filter((entry) => entry.id !== item.id));
      flash("Association supprimée.");
    } catch (error) {
      flash(error instanceof Error ? error.message : "Suppression impossible.");
    }
  };

  const testScan = async () => {
    if (!scanCode.trim()) return flash("Scannez un produit pour lancer le test.");
    setLoading(true);
    try {
      const response = await fetch("/api/associated-sales", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "match", rawCode: scanCode, pharmacy: scanPharmacy }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      setScanMatch(payload);
      if (!payload.alerts?.length) flash("Scan reconnu : aucune consigne active pour ce produit.");
    } catch (error) {
      flash(error instanceof Error ? error.message : "Le scan n’a pas pu être analysé.");
    } finally {
      setLoading(false);
    }
  };

  const filtered = items.filter((item) => {
    const text = `${item.triggerName} ${item.triggerEans.join(" ")} ${item.associatedProducts.map((product) => product.name).join(" ")}`.toLocaleLowerCase("fr");
    return !query.trim() || text.includes(query.trim().toLocaleLowerCase("fr"));
  });

  return (
    <main className="module-page associated-sales-page">
      <header className="module-header">
        <button type="button" className="module-back" onClick={onBack}>← <span>Retour à l’accueil</span></button>
        <div className="wordmark module-wordmark"><span className="wordmark-symbol"><i /><i /><i /></span><span><strong>saint-laurent</strong><small>pharmacies</small></span></div>
        <div className="module-header-actions"><ModuleToolsLink /><span className="module-status"><i /> Conseils synchronisés</span></div>
      </header>

      <section className="associated-sales-hero">
        <div>
          <p>💡 CONSEIL AU COMPTOIR</p>
          <h1>Ventes associées</h1>
          <span>Le bon conseil apparaît au bon moment, sans quitter LGPI.</span>
        </div>
        <div className="associated-sales-kpis">
          <b>{items.filter((item) => item.status === "active").length}<small>conseils actifs</small></b>
          <b>{items.reduce((sum, item) => sum + item.triggerEans.length, 0)}<small>EAN surveillés</small></b>
        </div>
      </section>

      <nav className="associated-sales-tabs">
        <button type="button" className={mode === "manage" ? "active" : ""} onClick={() => setMode("manage")}>🧩 Gérer les associations</button>
        <button type="button" className={mode === "counter" ? "active" : ""} onClick={() => setMode("counter")}>⚡ Test comptoir</button>
        <button type="button" className="new" onClick={() => { setMode("manage"); setDraft(blankAssociatedSale(initialPharmacy, initialOperator)); }}>＋ Nouvelle association</button>
      </nav>

      {mode === "counter" && (
        <section className="associated-counter">
          <div className="associated-scan-panel">
            <div className="associated-scan-heading">
              <span>▥</span>
              <div><p>SIMULATION DU POSTE DE VENTE</p><h2>Scannez comme avec la douchette</h2><small>EAN-8, EAN-13, GTIN-14 et DataMatrix GS1</small></div>
            </div>
            <label>Pharmacie
              <select value={scanPharmacy} onChange={(event) => setScanPharmacy(event.target.value)}>
                {["Square", "Ragadan", "Laurentine"].map((name) => <option key={name}>{name}</option>)}
              </select>
            </label>
            <label>Code scanné
              <div className="associated-scan-input">
                <input autoFocus value={scanCode} onChange={(event) => setScanCode(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void testScan(); }} placeholder="Scannez l’EAN ou le DataMatrix ici…" />
                <button type="button" onClick={() => void testScan()} disabled={loading}>{loading ? "Analyse…" : "Tester le popup"}</button>
              </div>
            </label>
            <p className="associated-scan-help">Le DataMatrix permet aussi d’identifier le lot, la péremption et le numéro de série. Ces données restent informatives et ne sont pas enregistrées.</p>
          </div>

          <div className={`associated-popup-demo ${scanMatch?.alerts.length ? "visible" : ""}`}>
            <div className="associated-popup-title"><span>✦</span><div><small>SAINT-LAURENT · CONSEIL COMPTOIR</small><strong>{scanMatch?.alerts.length ? `${scanMatch.alerts.length} conseil${scanMatch.alerts.length > 1 ? "s" : ""} disponible${scanMatch.alerts.length > 1 ? "s" : ""}` : "Aperçu de l’alerte LGPI"}</strong></div><i>×</i></div>
            {scanMatch?.scan && <div className="associated-scan-meta"><span>{scanMatch.scan.kind === "datamatrix" ? "▦ DataMatrix" : "▥ EAN"} · {scanMatch.scan.gtin || "GTIN détecté"}</span>{scanMatch.scan.batch && <span>Lot {scanMatch.scan.batch}</span>}{scanMatch.scan.expiry && <span>Exp. {followupDate(scanMatch.scan.expiry)}</span>}</div>}
            {scanMatch?.alerts.length ? scanMatch.alerts.map((alert) => (
              <article key={alert.id} className={alert.priority === "important" ? "important" : ""}>
                <header><span>{alert.source === "clearance" ? "📣 À ÉCOULER" : "💡 VENTE ASSOCIÉE"}</span>{alert.priority === "important" && <b>IMPORTANT</b>}</header>
                <h3>{alert.title}</h3>
                <p>{alert.message}</p>
                {!!alert.products?.length && <div>{alert.products.map((product) => <strong key={product.id}>＋ {product.name}{product.quantity > 1 ? ` ×${product.quantity}` : ""}{product.note ? <small>{product.note}</small> : null}</strong>)}</div>}
              </article>
            )) : <div className="associated-popup-empty"><span>🖥️</span><h3>L’alerte se posera au-dessus de LGPI</h3><p>Grande, lisible, courte et fermable en un clic. Elle ne bloque jamais la vente.</p></div>}
            <footer><button type="button">Plus tard</button><button type="button" className="ack">✓ J’ai vu</button></footer>
          </div>
        </section>
      )}

      {mode === "manage" && (
        <section className="associated-manage">
          <header><div><p>ASSOCIATIONS ACTIVES</p><h2>Construire un conseil simple et utile</h2></div><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Rechercher produit, EAN…" /></header>
          <div className="associated-rule-list">
            {filtered.map((item) => (
              <article key={item.id} className={item.status === "inactive" ? "inactive" : ""}>
                <div className="associated-rule-icon">↗</div>
                <div className="associated-rule-main">
                  <header><h3>{item.triggerName}</h3><span className={item.priority}>{item.priority === "important" ? "🔥 Important" : "Actif"}</span></header>
                  <p>{item.counterMessage || "Aucune phrase comptoir renseignée."}</p>
                  <footer>{item.pharmacies.map((name) => <span key={name}>🏥 {name}</span>)}<span>▥ {item.triggerEans.length} EAN</span><span>＋ {item.associatedProducts.length} produit(s)</span></footer>
                </div>
                <div className="associated-rule-products">{item.associatedProducts.slice(0, 3).map((product) => <span key={product.id}>{product.name}</span>)}</div>
                <div className="associated-rule-actions"><button type="button" onClick={() => setDraft({ ...item })}>Modifier</button><button type="button" onClick={() => void toggleStatus(item)}>{item.status === "active" ? "Désactiver" : "Réactiver"}</button><button type="button" className="delete" onClick={() => void removeItem(item)}>Supprimer</button></div>
              </article>
            ))}
            {!filtered.length && <div className="associated-empty">💡 Aucune association dans cette vue.</div>}
          </div>
        </section>
      )}

      {draft && (
        <div className="associated-drawer-backdrop" onMouseDown={(event) => { if (event.currentTarget === event.target) setDraft(null); }}>
          <section className="associated-drawer">
            <header><div><p>NOUVELLE RÈGLE COMPTOIR</p><h2>{draft.id ? "Modifier l’association" : "Créer une vente associée"}</h2></div><button type="button" onClick={() => setDraft(null)}>×</button></header>
            <div className="associated-drawer-grid">
              <section className="associated-step-card">
                <h3><span>1</span> Quel scan déclenche le conseil ?</h3>
                <label>Nom du produit ou de la famille<input value={draft.triggerName} onChange={(event) => setDraftField("triggerName", event.target.value)} placeholder="Ex. Préparation colique" /></label>
                <label>EAN / GTIN déclencheur
                  <div className="associated-inline">
                    <CatalogAutocomplete
                      value={eanInput}
                      onChange={setEanInput}
                      onPick={(suggestion) => {
                        setEanInput(suggestion.ean);
                        setTriggerCatalogLabels((current) => ({ ...current, [suggestion.ean]: suggestion }));
                        if (!draft.triggerName.trim()) setDraftField("triggerName", suggestion.product || suggestion.ean);
                      }}
                      placeholder="Début d’EAN, ou * puis les 4 derniers chiffres"
                      inputMode="text"
                    />
                    <button type="button" onClick={() => void addTriggerEan()}>＋ Ajouter</button>
                  </div>
                  <small className="associated-catalog-help">Ex. <b>34009</b> filtre depuis le début · <b>*1825</b> retrouve l’EAN par ses 4 derniers chiffres.</small>
                </label>
                <div className="associated-chips">{draft.triggerEans.map((ean) => <button type="button" key={ean} onClick={() => setDraftField("triggerEans", draft.triggerEans.filter((value) => value !== ean))}>▥ <b>{triggerCatalogLabels[ean]?.product || "Produit non retrouvé"}</b><small>EAN {ean}</small> ×</button>)}</div>
              </section>
              <section className="associated-step-card">
                <h3><span>2</span> Où doit-il apparaître ?</h3>
                <p className="associated-step-copy">La règle est diffusée uniquement dans les pharmacies cochées.</p>
                <div className="associated-pharmacies">{["Square", "Ragadan", "Laurentine"].map((name) => <label key={name}><input type="checkbox" checked={draft.pharmacies.includes(name)} onChange={() => setDraftField("pharmacies", draft.pharmacies.includes(name) ? draft.pharmacies.filter((value) => value !== name) : [...draft.pharmacies, name])} />🏥 {name}</label>)}</div>
              </section>
              <section className="associated-step-card">
                <h3><span>3</span> Que doit voir l’équipe ?</h3>
                <label>Phrase courte à dire au patient<textarea value={draft.counterMessage} onChange={(event) => setDraftField("counterMessage", event.target.value.slice(0, 600))} placeholder="Ex. Pensez à proposer une cure de probiotiques si cela convient au patient." /></label>
                <div className="associated-priority"><button type="button" className={draft.priority === "normal" ? "active" : ""} onClick={() => setDraftField("priority", "normal")}>🟢 Conseil normal</button><button type="button" className={draft.priority === "important" ? "active important" : ""} onClick={() => setDraftField("priority", "important")}>🔥 Important</button></div>
              </section>
              <section className="associated-step-card">
                <h3><span>4</span> Quel produit proposer ?</h3>
                <div className="associated-product-form">
                  <CatalogAutocomplete
                    value={productDraft.name}
                    confirmed={Boolean(productDraft.ean)}
                    onChange={(name) => setProductDraft({ ...productDraft, name, ean: "" })}
                    onPick={(suggestion) => setProductDraft({ ...productDraft, name: suggestion.product || suggestion.ean, ean: suggestion.ean })}
                    placeholder="Nom ou EAN du produit associé"
                  />
                  <CatalogAutocomplete
                    value={productDraft.ean}
                    onChange={(ean) => setProductDraft({ ...productDraft, ean })}
                    onPick={(suggestion) => setProductDraft({ ...productDraft, name: suggestion.product || suggestion.ean, ean: suggestion.ean })}
                    placeholder="EAN facultatif"
                  />
                  <input type="number" min="1" max="99" value={productDraft.quantity} onChange={(event) => setProductDraft({ ...productDraft, quantity: Number(event.target.value) || 1 })} />
                  <input value={productDraft.note} onChange={(event) => setProductDraft({ ...productDraft, note: event.target.value })} placeholder="Note facultative" />
                  <button type="button" onClick={addAssociatedProduct}>＋</button>
                </div>
                <div className="associated-products-added">{draft.associatedProducts.map((product) => <div key={product.id}><span>＋</span><strong>{product.name}<small>{product.ean || "Sans EAN"}{product.note ? ` · ${product.note}` : ""}</small></strong><button type="button" onClick={() => setDraftField("associatedProducts", draft.associatedProducts.filter((value) => value.id !== product.id))}>×</button></div>)}</div>
              </section>
            </div>
            <footer><span>{draft.triggerEans.length} déclencheur(s) · {draft.associatedProducts.length} produit(s) associé(s)</span><button type="button" onClick={() => setDraft(null)}>Annuler</button><button type="button" className="primary" disabled={loading} onClick={() => void saveDraft()}>{loading ? "Enregistrement…" : "✓ Enregistrer et diffuser"}</button></footer>
          </section>
        </div>
      )}
      {notice && <div className="module-toast">{notice}</div>}
    </main>
  );
}

type PatientFollowup = {
  id: number;
  patient: string;
  phone: string;
  pharmacy: string;
  advisor: string;
  seenDate: string;
  recontactDate: string;
  advice: string;
  note: string;
  status: "active" | "closed";
  createdAt: string;
  updatedAt: string;
};

type FollowupForm = Omit<PatientFollowup, "id" | "status" | "createdAt" | "updatedAt">;

const followupPharmacies = ["Ragadan", "Square", "Laurentine"];

function localDateIso(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function offsetDate(days: number) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return localDateIso(date);
}

function followupDate(value: string) {
  if (!value) return "";
  return new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })
    .format(new Date(`${value}T12:00:00`));
}

function patientInitials(name: string) {
  const ignored = new Set(["mme", "mlle", "m", "monsieur", "madame"]);
  const letters = name
    .replace(/[.,]/g, " ")
    .split(/\s+/)
    .filter((part) => part && !ignored.has(part.toLocaleLowerCase("fr")))
    .slice(0, 2)
    .map((part) => part[0].toLocaleUpperCase("fr"));
  return letters.join(".") + (letters.length ? "." : "");
}

type DailyBrief = {
  updatedAt: number;
  nameDay: string;
  today: null | { weatherCode: number; current: number; max: number; min: number; rain: number };
  tomorrow: null | { weatherCode: number; max: number; min: number; rain: number };
  sea: null | { wave: number; maxWave: number; temperature: number; direction: number };
};

const emptyDailyBrief: DailyBrief = { updatedAt: 0, nameDay: "", today: null, tomorrow: null, sea: null };

function weatherIcon(code = 0) {
  if (code === 0) return "☀️";
  if (code <= 3) return "🌤️";
  if (code === 45 || code === 48) return "🌫️";
  if (code >= 95) return "⛈️";
  if (code >= 71) return "🌨️";
  if (code >= 51) return "🌦️";
  return "☁️";
}

function useDailyBrief() {
  const [brief, setBrief] = useState<DailyBrief>(emptyDailyBrief);
  useEffect(() => {
    const load = async () => {
      try {
        const response = await fetch("/api/daily-brief", { cache: "no-store" });
        if (response.ok) setBrief(await response.json());
      } catch {
        // The portal stays usable if the external forecast is temporarily unavailable.
      }
    };
    void load();
    const timer = window.setInterval(load, 30 * 60 * 1000);
    return () => window.clearInterval(timer);
  }, []);
  return brief;
}

function nextTeamBirthday(pharmacy: string, now = new Date()) {
  const candidates = missionOperators
    .filter((operator) => operator.pharmacy === pharmacy && operator.birthDate)
    .map((operator) => {
      const [, month, day] = operator.birthDate!.split("-").map(Number);
      let next = new Date(now.getFullYear(), month - 1, day, 12);
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
      if (next < today) next = new Date(now.getFullYear() + 1, month - 1, day, 12);
      return { name: operator.name, date: next, days: Math.round((next.getTime() - today.getTime()) / 86_400_000) };
    })
    .sort((a, b) => a.date.getTime() - b.date.getTime());
  return candidates[0] ?? null;
}

type LaboratoryPriority = "normal" | "important" | "urgent";
type LaboratoryStatus = "planned" | "completed";
type LaboratoryExpiryManagement = {
  management: "" | "credit" | "free_units";
  laboratoryPaid: "" | "yes" | "no";
  dispatchStatus: "" | "to_process" | "processed_on_order";
  officentralDispatch: "" | "yes" | "no";
  comment: string;
  allocations: { Square: string; Ragadan: string; Laurentine: string; otherPharmacy: string; otherAmount: string };
  hasRemainder: boolean;
  remainderAmount: string;
  remainderStatus: "to_process" | "processed";
  remainderNote: string;
};

const emptyLaboratoryExpiryManagement = (): LaboratoryExpiryManagement => ({
  management: "",
  laboratoryPaid: "",
  dispatchStatus: "",
  officentralDispatch: "",
  comment: "",
  allocations: { Square: "", Ragadan: "", Laurentine: "", otherPharmacy: "", otherAmount: "" },
  hasRemainder: false,
  remainderAmount: "",
  remainderStatus: "to_process",
  remainderNote: "",
});

function normalizeLaboratoryExpiryManagement(value: unknown): LaboratoryExpiryManagement {
  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return emptyLaboratoryExpiryManagement();
    const source = parsed as Record<string, unknown>;
    const allocations = source.allocations && typeof source.allocations === "object" && !Array.isArray(source.allocations)
      ? source.allocations as Record<string, unknown>
      : {};
    return {
      management: source.management === "credit" || source.management === "free_units" ? source.management : "",
      laboratoryPaid: source.laboratoryPaid === "yes" || source.laboratoryPaid === "no" ? source.laboratoryPaid : "",
      dispatchStatus: source.dispatchStatus === "to_process" || source.dispatchStatus === "processed_on_order" ? source.dispatchStatus : "",
      officentralDispatch: source.officentralDispatch === "yes" || source.officentralDispatch === "no" ? source.officentralDispatch : "",
      comment: String(source.comment ?? ""),
      allocations: {
        Square: String(allocations.Square ?? ""),
        Ragadan: String(allocations.Ragadan ?? ""),
        Laurentine: String(allocations.Laurentine ?? ""),
        otherPharmacy: String(allocations.otherPharmacy ?? ""),
        otherAmount: String(allocations.otherAmount ?? ""),
      },
      hasRemainder: source.hasRemainder === true,
      remainderAmount: String(source.remainderAmount ?? ""),
      remainderStatus: source.remainderStatus === "processed" ? "processed" : "to_process",
      remainderNote: String(source.remainderNote ?? ""),
    };
  } catch {
    return emptyLaboratoryExpiryManagement();
  }
}

function laboratoryExpiryPending(value: LaboratoryExpiryManagement) {
  return value.laboratoryPaid === "no"
    || value.dispatchStatus === "to_process"
    || (value.hasRemainder && value.remainderStatus === "to_process");
}

function laboratoryExpiryHasData(value: LaboratoryExpiryManagement) {
  return Boolean(value.management || value.laboratoryPaid || value.dispatchStatus || value.officentralDispatch || value.comment
    || Object.values(value.allocations).some(Boolean) || value.hasRemainder || value.remainderAmount || value.remainderNote);
}

type LaboratoryFollowup = {
  id: number;
  laboratory: string;
  contactName: string;
  contactRole: string;
  phone: string;
  email: string;
  subject: string;
  scheduledDate: string;
  scheduledTime: string;
  durationMinutes: number;
  isLaboratory: boolean;
  preparationStatus: Record<string, boolean>;
  pharmacy: string;
  assignedTo: string;
  priority: LaboratoryPriority;
  notes: string;
  nextAction: string;
  outcome: string;
  attachments: LaboratoryDocument[];
  expiryManagement: LaboratoryExpiryManagement;
  status: LaboratoryStatus;
  completedAt: number | null;
  createdAt: number;
  updatedAt: number;
};

type LaboratoryAnnualTerm = { id: string; year: string; text: string };
type LaboratoryProduct = { id: string; name: string; quantity: string; note: string; status: "open" | "done"; ean?: string; pharmacy?: string; addedBy?: string };
type LaboratoryDocument = { id: string; title: string; url: string; kind: string; addedAt: string };
type LaboratoryWorkstream = {
  id: string;
  type: "bri" | "coop";
  operationType?: "BRI" | "COOP" | "TG";
  briId?: string;
  title: string;
  amount: string;
  dueDate: string;
  status: "open" | "done";
  paymentStatus?: "pending" | "paid";
  paidAt?: string;
  note: string;
};
type LaboratoryDispute = {
  id: string;
  title: string;
  openedDate: string;
  dueDate?: string;
  status: "open" | "resolved";
  note: string;
  assignedTo?: string;
  reminderEnabled?: boolean;
  followedInNotesAt?: number;
  attachments?: LaboratoryDocument[];
};

type LaboratoryProfile = {
  id: number;
  name: string;
  pharmacies: string[];
  delegateName: string;
  delegateRole: string;
  delegateEmail: string;
  delegatePhone: string;
  delegateAltPhone: string;
  regionalName: string;
  regionalEmail: string;
  regionalPhone: string;
  headquartersContact: string;
  headquartersEmail: string;
  headquartersPhone: string;
  assignedTo: string;
  commercialTerms: string;
  francoValue: string;
  francoMode: "amount" | "units";
  permanentNotes: string;
  annualTerms: LaboratoryAnnualTerm[];
  products: LaboratoryProduct[];
  documents: LaboratoryDocument[];
  workstreams: LaboratoryWorkstream[];
  disputes: LaboratoryDispute[];
  active: boolean;
  createdAt: number;
  updatedAt: number;
};

function parseLaboratoryArray<T>(value: unknown): T[] {
  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    return Array.isArray(parsed) ? parsed as T[] : [];
  } catch {
    return [];
  }
}

function normalizeLaboratoryProfile(row: Record<string, unknown>): LaboratoryProfile {
  return {
    id: Number(row.id),
    name: String(row.name ?? ""),
    pharmacies: parseLaboratoryArray<string>(row.pharmacies),
    delegateName: String(row.delegateName ?? row.delegate_name ?? ""),
    delegateRole: String(row.delegateRole ?? row.delegate_role ?? ""),
    delegateEmail: String(row.delegateEmail ?? row.delegate_email ?? ""),
    delegatePhone: String(row.delegatePhone ?? row.delegate_phone ?? ""),
    delegateAltPhone: String(row.delegateAltPhone ?? row.delegate_alt_phone ?? ""),
    regionalName: String(row.regionalName ?? row.regional_name ?? ""),
    regionalEmail: String(row.regionalEmail ?? row.regional_email ?? ""),
    regionalPhone: String(row.regionalPhone ?? row.regional_phone ?? ""),
    headquartersContact: String(row.headquartersContact ?? row.headquarters_contact ?? ""),
    headquartersEmail: String(row.headquartersEmail ?? row.headquarters_email ?? ""),
    headquartersPhone: String(row.headquartersPhone ?? row.headquarters_phone ?? ""),
    assignedTo: String(row.assignedTo ?? row.assigned_to ?? ""),
    commercialTerms: String(row.commercialTerms ?? row.commercial_terms ?? ""),
    francoValue: String(row.francoValue ?? row.franco_value ?? ""),
    francoMode: String(row.francoMode ?? row.franco_mode ?? "amount") === "units" ? "units" : "amount",
    permanentNotes: String(row.permanentNotes ?? row.permanent_notes ?? ""),
    annualTerms: parseLaboratoryArray<LaboratoryAnnualTerm>(row.annualTerms ?? row.annual_terms),
    products: parseLaboratoryArray<LaboratoryProduct>(row.products),
    documents: parseLaboratoryArray<LaboratoryDocument>(row.documents),
    workstreams: parseLaboratoryArray<LaboratoryWorkstream>(row.workstreams),
    disputes: parseLaboratoryArray<LaboratoryDispute>(row.disputes),
    active: Number(row.active ?? 1) !== 0,
    createdAt: Number(row.createdAt ?? row.created_at ?? 0),
    updatedAt: Number(row.updatedAt ?? row.updated_at ?? 0),
  };
}

function normalizeLaboratoryFollowup(row: Record<string, unknown>): LaboratoryFollowup {
  const preparationStatus = (() => {
    try {
      const parsed = typeof (row.preparationStatus ?? row.preparation_status) === "string"
        ? JSON.parse(String(row.preparationStatus ?? row.preparation_status))
        : row.preparationStatus ?? row.preparation_status;
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? Object.fromEntries(Object.entries(parsed as Record<string, unknown>).map(([key, value]) => [key, Boolean(value)]))
        : {};
    } catch {
      return {};
    }
  })();
  return {
    id: Number(row.id),
    laboratory: String(row.laboratory ?? ""),
    contactName: String(row.contactName ?? row.contact_name ?? ""),
    contactRole: String(row.contactRole ?? row.contact_role ?? ""),
    phone: String(row.phone ?? ""),
    email: String(row.email ?? ""),
    subject: String(row.subject ?? ""),
    scheduledDate: String(row.scheduledDate ?? row.scheduled_date ?? ""),
    scheduledTime: String(row.scheduledTime ?? row.scheduled_time ?? ""),
    durationMinutes: Math.max(15, Number(row.durationMinutes ?? row.duration_minutes ?? 60) || 60),
    isLaboratory: Number(row.isLaboratory ?? row.is_laboratory ?? 1) !== 0,
    preparationStatus,
    pharmacy: String(row.pharmacy ?? ""),
    assignedTo: String(row.assignedTo ?? row.assigned_to ?? ""),
    priority: String(row.priority ?? "normal") as LaboratoryPriority,
    notes: String(row.notes ?? ""),
    nextAction: String(row.nextAction ?? row.next_action ?? ""),
    outcome: String(row.outcome ?? ""),
    attachments: parseLaboratoryArray<LaboratoryDocument>(row.attachments),
    expiryManagement: normalizeLaboratoryExpiryManagement(row.expiryManagement ?? row.expiry_management),
    status: String(row.status ?? "planned") as LaboratoryStatus,
    completedAt: row.completedAt === null || row.completed_at === null ? null : Number(row.completedAt ?? row.completed_at ?? 0),
    createdAt: Number(row.createdAt ?? row.created_at ?? 0),
    updatedAt: Number(row.updatedAt ?? row.updated_at ?? 0),
  };
}

function laboratoryDue(item: LaboratoryFollowup, now = new Date()) {
  return item.status === "planned" && item.scheduledDate <= localDateIso(now);
}

function laboratoryPreparationDue(item: LaboratoryFollowup, pharmacy: string, now = new Date()) {
  if (item.status !== "planned" || !item.isLaboratory || item.preparationStatus[pharmacy]) return false;
  const today = new Date(`${localDateIso(now)}T12:00:00`);
  const appointment = new Date(`${item.scheduledDate}T12:00:00`);
  const days = Math.round((appointment.getTime() - today.getTime()) / 86_400_000);
  return days <= 4;
}

function laboratoryEndTime(start: string, durationMinutes: number) {
  if (!start) return "";
  const [hours, minutes] = start.split(":").map(Number);
  const total = hours * 60 + minutes + durationMinutes;
  return `${String(Math.floor(total / 60) % 24).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

function laboratoryItemId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function LaboratoryExpiryManagementFields({
  value,
  onChange,
}: {
  value: LaboratoryExpiryManagement;
  onChange: (value: LaboratoryExpiryManagement) => void;
}) {
  const set = <K extends keyof LaboratoryExpiryManagement>(key: K, next: LaboratoryExpiryManagement[K]) => onChange({ ...value, [key]: next });
  const setAllocation = (key: keyof LaboratoryExpiryManagement["allocations"], next: string) => onChange({ ...value, allocations: { ...value.allocations, [key]: next } });
  const amount = (entry: string) => Number(entry.replace(/\s/g, "").replace(",", ".")) || 0;
  const total = amount(value.allocations.Square) + amount(value.allocations.Ragadan) + amount(value.allocations.Laurentine) + amount(value.allocations.otherAmount);
  return (
    <section className="laboratory-expiry-management">
      <header><div><span>♻️</span><div><strong>Gestion périmés</strong><small>Avoirs, unités gratuites, paiement et répartition réseau</small></div></div>{laboratoryExpiryPending(value) && <b>À reprendre au prochain RDV</b>}</header>
      <div className="laboratory-expiry-status-grid">
        <label><span>Gestion</span><select value={value.management} onChange={(event) => set("management", event.target.value as LaboratoryExpiryManagement["management"])}><option value="">Non renseigné</option><option value="credit">Avoir</option><option value="free_units">UG</option></select></label>
        <label><span>Labo a payé</span><select value={value.laboratoryPaid} onChange={(event) => set("laboratoryPaid", event.target.value as LaboratoryExpiryManagement["laboratoryPaid"])}><option value="">Non renseigné</option><option value="yes">Oui</option><option value="no">Non</option></select></label>
        <label><span>Dispatch pharma</span><select value={value.dispatchStatus} onChange={(event) => set("dispatchStatus", event.target.value as LaboratoryExpiryManagement["dispatchStatus"])}><option value="">Non renseigné</option><option value="to_process">À traiter</option><option value="processed_on_order">Traité au moment de la commande</option></select></label>
        <label><span>Si UG : dispatch via Officentral ?</span><select disabled={value.management !== "free_units"} value={value.officentralDispatch} onChange={(event) => set("officentralDispatch", event.target.value as LaboratoryExpiryManagement["officentralDispatch"])}><option value="">Non renseigné</option><option value="yes">Oui</option><option value="no">Non</option></select></label>
      </div>
      <label className="laboratory-expiry-comment"><span>Commentaire périmés</span><textarea value={value.comment} onChange={(event) => set("comment", event.target.value)} placeholder="UG attendues, avoir partiel, dispatch à faire via Officentral, laboratoire à relancer…" /></label>
      <div className="laboratory-expiry-allocation">
        <h4>Répartition par pharmacie</h4>
        <div><label><span>Square</span><input inputMode="decimal" value={value.allocations.Square} onChange={(event) => setAllocation("Square", event.target.value)} placeholder="€" /></label><label><span>Ragadan</span><input inputMode="decimal" value={value.allocations.Ragadan} onChange={(event) => setAllocation("Ragadan", event.target.value)} placeholder="€" /></label><label><span>Laurentine</span><input inputMode="decimal" value={value.allocations.Laurentine} onChange={(event) => setAllocation("Laurentine", event.target.value)} placeholder="€" /></label><label><span>Autre pharmacie</span><input value={value.allocations.otherPharmacy} onChange={(event) => setAllocation("otherPharmacy", event.target.value)} placeholder="Nom libre" /></label><label><span>Montant autre</span><input inputMode="decimal" value={value.allocations.otherAmount} onChange={(event) => setAllocation("otherAmount", event.target.value)} placeholder="€" /></label><label className="total"><span>Somme totale</span><output>{total.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</output></label></div>
      </div>
      <details className="laboratory-expiry-remainder" open={value.hasRemainder}>
        <summary>Reliquat non traité / reste à suivre</summary>
        <label className="toggle"><input type="checkbox" checked={value.hasRemainder} onChange={(event) => set("hasRemainder", event.target.checked)} /><span>Il reste un reliquat non traité</span></label>
        {value.hasRemainder && <div><label><span>Montant reliquat</span><input inputMode="decimal" value={value.remainderAmount} onChange={(event) => set("remainderAmount", event.target.value)} placeholder="€" /></label><label><span>Statut</span><select value={value.remainderStatus} onChange={(event) => set("remainderStatus", event.target.value as LaboratoryExpiryManagement["remainderStatus"])}><option value="to_process">À traiter</option><option value="processed">Traité</option></select></label><label className="wide"><span>Note reliquat</span><textarea value={value.remainderNote} onChange={(event) => set("remainderNote", event.target.value)} placeholder="Reste à reprendre, avoir partiel reçu, reliquat à relancer…" /></label></div>}
      </details>
    </section>
  );
}

function LaboratoryCalendar({
  items,
  mode,
  selectedDate,
  onSelectDate,
  onModeChange,
  onCreateSlot,
}: {
  items: LaboratoryFollowup[];
  mode: "list" | "day" | "week" | "month";
  selectedDate: string;
  onSelectDate: (date: string) => void;
  onModeChange: (mode: "list" | "day" | "week" | "month") => void;
  onCreateSlot: (date: string, time: string) => void;
}) {
  const [mobileView, setMobileView] = useState<"month" | "day">("month");
  const selected = new Date(`${selectedDate}T12:00:00`);
  const start = new Date(selected);
  if (mode === "month") {
    start.setDate(1);
    const mondayOffset = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - mondayOffset);
  } else if (mode === "week") {
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  }
  const days = Array.from({ length: mode === "month" ? 42 : mode === "week" ? 7 : 1 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return date;
  });
  const move = (amount: number) => {
    const date = new Date(selected);
    if (mode === "month") date.setMonth(date.getMonth() + amount);
    else if (mode === "week") date.setDate(date.getDate() + amount * 7);
    else date.setDate(date.getDate() + amount);
    onSelectDate(localDateIso(date));
  };

  const mobileMonthStart = new Date(selected);
  mobileMonthStart.setDate(1);
  mobileMonthStart.setDate(mobileMonthStart.getDate() - ((mobileMonthStart.getDay() + 6) % 7));
  const mobileDays = Array.from({ length: 42 }, (_, index) => {
    const date = new Date(mobileMonthStart);
    date.setDate(mobileMonthStart.getDate() + index);
    return date;
  });
  const moveMobileMonth = (amount: number) => {
    const date = new Date(selected);
    date.setDate(1);
    date.setMonth(date.getMonth() + amount);
    onSelectDate(localDateIso(date));
    setMobileView("month");
  };
  const moveMobileDay = (amount: number) => {
    const date = new Date(selected);
    date.setDate(date.getDate() + amount);
    onSelectDate(localDateIso(date));
  };
  const selectedDayItems = items
    .filter((item) => item.scheduledDate === selectedDate)
    .sort((a, b) => (a.scheduledTime || "00:00").localeCompare(b.scheduledTime || "00:00"));
  const daySlots = Array.from({ length: 25 }, (_, index) => {
    const totalMinutes = 8 * 60 + index * 30;
    return `${String(Math.floor(totalMinutes / 60)).padStart(2, "0")}:${String(totalMinutes % 60).padStart(2, "0")}`;
  });
  const timeMinutes = (value: string) => {
    const [hours = "0", minutes = "0"] = value.split(":");
    return Number(hours) * 60 + Number(minutes);
  };
  const mobileCalendar = <section className="laboratory-mobile-calendar" aria-label="Calendrier des rendez-vous">
    <header>
      <div><small>AGENDA LABO</small><strong>{mobileView === "day" ? selected.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }) : selected.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</strong></div>
      <nav><button type="button" onClick={() => mobileView === "day" ? moveMobileDay(-1) : moveMobileMonth(-1)} aria-label={mobileView === "day" ? "Jour précédent" : "Mois précédent"}>‹</button><button type="button" className="today-button" onClick={() => { onSelectDate(localDateIso()); setMobileView("day"); }}>Aujourd’hui</button><button type="button" onClick={() => mobileView === "day" ? moveMobileDay(1) : moveMobileMonth(1)} aria-label={mobileView === "day" ? "Jour suivant" : "Mois suivant"}>›</button></nav>
    </header>
    {mobileView === "month" ? <>
      <div className="laboratory-mobile-weekdays" aria-hidden="true">{["L", "M", "M", "J", "V", "S", "D"].map((day, index) => <span key={`${day}-${index}`}>{day}</span>)}</div>
      <div className="laboratory-mobile-month">
        {mobileDays.map((date) => {
          const iso = localDateIso(date);
          const count = items.filter((item) => item.scheduledDate === iso).length;
          return <button type="button" key={`mobile-${iso}`} className={`${iso === selectedDate ? "selected" : ""} ${iso === localDateIso() ? "today" : ""} ${date.getMonth() !== selected.getMonth() ? "outside" : ""}`} onClick={() => { onSelectDate(iso); setMobileView("day"); }} aria-label={`Voir la journée du ${followupDate(iso)}`}><strong>{date.getDate()}</strong>{count > 0 && <i>{count}</i>}</button>;
        })}
      </div>
      <p className="laboratory-mobile-hint">Touchez un jour pour afficher toute sa journée.</p>
    </> : <div className="laboratory-mobile-day">
      <div className="laboratory-mobile-day-toolbar">
        <button type="button" className="laboratory-mobile-back-month" onClick={() => setMobileView("month")}>‹ Mois</button>
        <button type="button" className="laboratory-mobile-day-add" onClick={() => onCreateSlot(selectedDate, "10:00")} aria-label={`Ajouter un rendez-vous le ${followupDate(selectedDate)}`}>＋</button>
      </div>
      <div className="laboratory-mobile-day-summary"><strong>{selectedDayItems.length}</strong><span>rendez-vous aujourd’hui</span></div>
      <div className="laboratory-mobile-day-timeline" aria-label={`Rendez-vous du ${followupDate(selectedDate)}`}>
        {daySlots.map((time) => {
          const slotStart = timeMinutes(time);
          const slotItems = selectedDayItems.filter((item) => {
            const appointmentTime = timeMinutes(item.scheduledTime || "08:00");
            return appointmentTime >= slotStart && appointmentTime < slotStart + 30;
          });
          const coveredByEarlierAppointment = selectedDayItems.some((item) => {
            const appointmentTime = timeMinutes(item.scheduledTime || "08:00");
            return appointmentTime < slotStart && appointmentTime + Math.max(item.durationMinutes || 60, 30) > slotStart;
          });
          if (!slotItems.length && coveredByEarlierAppointment) return null;
          return <div className={`laboratory-mobile-day-slot ${slotItems.length ? "busy" : ""}`} key={`mobile-day-${time}`}>
            <time>{time}</time>
            <div className="laboratory-mobile-day-track">
              {slotItems.length ? slotItems.map((item) => <article key={item.id} style={{ minHeight: `${Math.max(76, Math.round(Math.max(item.durationMinutes || 60, 30) / 30) * 42)}px` }}>
                <div className="laboratory-mobile-day-card-head"><strong>{item.laboratory}</strong><i>{item.status === "completed" ? "Terminé" : "RDV"}</i></div>
                <span>◷ {item.scheduledTime || time}–{laboratoryEndTime(item.scheduledTime || time, item.durationMinutes)}{item.contactName ? ` · ${item.contactName}` : ""}</span>
                <small>{[item.pharmacy, item.assignedTo ? `resp. ${item.assignedTo}` : ""].filter(Boolean).join(" · ")}</small>
                {item.notes && <p>{item.notes}</p>}
              </article>) : <button type="button" onClick={() => onCreateSlot(selectedDate, time)} aria-label={`Ajouter un rendez-vous à ${time}`}>＋ Ajouter à {time}</button>}
            </div>
          </div>;
        })}
      </div>
      <button type="button" className="laboratory-mobile-day-primary" onClick={() => onCreateSlot(selectedDate, "10:00")}><span>＋</span><b>Nouveau rendez-vous</b></button>
    </div>}
    {mobileView === "month" && <button type="button" className="laboratory-mobile-create" onClick={() => onCreateSlot(selectedDate, "10:00")}><span>＋</span><b>Rendez-vous le {followupDate(selectedDate)}</b></button>}
  </section>;

  if (mode === "list") return (
    <>{mobileCalendar}<div className="laboratory-calendar-switch">
      <button type="button" className="active" onClick={() => onModeChange("list")}>Liste</button>
      <button type="button" onClick={() => onModeChange("day")}>Journée</button>
      <button type="button" onClick={() => onModeChange("week")}>Semaine</button>
      <button type="button" onClick={() => onModeChange("month")}>Mois</button>
    </div></>
  );

  return (
    <>{mobileCalendar}<section className={`laboratory-calendar ${mode}`}>
      <header>
        <div className="laboratory-calendar-switch">
          <button type="button" onClick={() => onModeChange("list")}>Liste</button>
          <button type="button" className={mode === "day" ? "active" : ""} onClick={() => onModeChange("day")}>Journée</button>
          <button type="button" className={mode === "week" ? "active" : ""} onClick={() => onModeChange("week")}>Semaine</button>
          <button type="button" className={mode === "month" ? "active" : ""} onClick={() => onModeChange("month")}>Mois</button>
        </div>
        <button type="button" onClick={() => move(-1)}>←</button>
        <strong>{selected.toLocaleDateString("fr-FR", mode === "month" ? { month: "long", year: "numeric" } : { weekday: mode === "day" ? "long" : undefined, day: "numeric", month: "long", year: "numeric" })}</strong>
        <button type="button" onClick={() => { onSelectDate(localDateIso()); onModeChange("day"); }}>Aujourd’hui</button>
        <button type="button" onClick={() => move(1)}>→</button>
      </header>
      {mode === "day" ? (
        <div className="laboratory-day-schedule">
          <header><div><strong>RDV du jour — {selectedDayItems.length}</strong><span>Journée complète · 08:00 à 20:00</span></div><button type="button" onClick={() => onCreateSlot(selectedDate, "10:00")}>＋ Nouveau RDV</button></header>
          <div className="laboratory-day-timeline">
            {daySlots.map((time) => {
              const slotStart = timeMinutes(time);
              const slotItems = selectedDayItems.filter((item) => {
                const appointmentTime = timeMinutes(item.scheduledTime || "08:00");
                return appointmentTime >= slotStart && appointmentTime < slotStart + 30;
              });
              return <div className={`laboratory-day-slot ${slotItems.length ? "busy" : ""}`} key={`day-${time}`}><time>{time}</time><div>{slotItems.map((item) => <article key={item.id}><strong>{item.laboratory}</strong><span>{item.scheduledTime || time}–{laboratoryEndTime(item.scheduledTime || time, item.durationMinutes)}{item.contactName ? ` · ${item.contactName}` : ""}</span><small>{[item.pharmacy, item.assignedTo ? `resp. ${item.assignedTo}` : "", item.notes].filter(Boolean).join(" · ")}</small></article>)}</div></div>;
            })}
          </div>
        </div>
      ) : mode === "week" ? (
        <div className="laboratory-week-schedule">
          <div className="laboratory-week-corner">HEURE</div>
          {days.map((date) => {
            const iso = localDateIso(date);
            return <button type="button" key={`head-${iso}`} className={`laboratory-week-day ${iso === selectedDate ? "selected" : ""} ${iso === localDateIso() ? "today" : ""}`} onClick={() => { onSelectDate(iso); onModeChange("day"); }}>
              <span>{date.toLocaleDateString("fr-FR", { weekday: "short" })}</span><strong>{date.getDate()}</strong>
            </button>;
          })}
          {Array.from({ length: 11 }, (_, index) => `${String(index + 8).padStart(2, "0")}:00`).flatMap((time) => [
            <div className="laboratory-week-time" key={`time-${time}`}>{time}</div>,
            ...days.map((date) => {
              const iso = localDateIso(date);
              const slotItems = items.filter((item) => item.scheduledDate === iso && (item.scheduledTime || "08:00").slice(0, 2) === time.slice(0, 2));
              return (
                <button type="button" className={`laboratory-week-slot ${slotItems.length ? "busy" : ""}`} key={`${iso}-${time}`} onClick={() => { if (slotItems.length) { onSelectDate(iso); onModeChange("day"); } else onCreateSlot(iso, time); }} aria-label={slotItems.length ? `Voir la journée du ${followupDate(iso)}` : `Créer un rendez-vous le ${followupDate(iso)} à ${time}`}>
                  {slotItems.slice(0, 2).map((item) => <i key={item.id}><b>{item.scheduledTime || time}</b><span>{item.laboratory}</span></i>)}
                  {!slotItems.length && <span className="laboratory-slot-add">＋</span>}
                </button>
              );
            }),
          ])}
        </div>
      ) : <div className="laboratory-calendar-grid">
        {days.map((date) => {
          const iso = localDateIso(date);
          const dayItems = items.filter((item) => item.scheduledDate === iso);
          return (
            <button type="button" key={iso} className={`${iso === selectedDate ? "selected" : ""} ${iso === localDateIso() ? "today" : ""}`} onClick={() => { onSelectDate(iso); onModeChange("day"); }} aria-label={`Voir toute la journée du ${followupDate(iso)}`}>
              <span>{date.toLocaleDateString("fr-FR", { weekday: "short" })}</span>
              <strong>{date.getDate()}</strong>
              <div>{dayItems.slice(0, mode === "month" ? 2 : 4).map((item) => <i key={item.id}>{item.scheduledTime || "•"} {item.laboratory}</i>)}</div>
              {dayItems.length > (mode === "month" ? 2 : 4) && <small>+{dayItems.length - (mode === "month" ? 2 : 4)}</small>}
            </button>
          );
        })}
      </div>}
    </section></>
  );
}

function LaboratoryProfileEditor({
  profile,
  followups,
  onChange,
  onSave,
  onDelete,
  onClose,
  onPlan,
  onMoveFollowup,
  onDeleteFollowup,
  onPrepareFollowup,
  onCompleteFollowup,
  onFollowDispute,
  onCreateBriOperation,
  onSetBriPayment,
  onOpenBriOperation,
  operator,
  saving,
}: {
  profile: LaboratoryProfile;
  followups: LaboratoryFollowup[];
  onChange: (profile: LaboratoryProfile) => void;
  onSave: (profile?: LaboratoryProfile) => void;
  onDelete: () => void;
  onClose: () => void;
  onPlan: () => void;
  onMoveFollowup: (item: LaboratoryFollowup) => void;
  onDeleteFollowup: (item: LaboratoryFollowup) => void;
  onPrepareFollowup: (item: LaboratoryFollowup) => void;
  onCompleteFollowup: (item: LaboratoryFollowup) => void;
  onFollowDispute: (profile: LaboratoryProfile, dispute: LaboratoryDispute) => void;
  onCreateBriOperation: (profile: LaboratoryProfile, operationType: "BRI" | "COOP" | "TG") => void;
  onSetBriPayment: (profile: LaboratoryProfile, entry: LaboratoryWorkstream, paid: boolean) => void;
  onOpenBriOperation: (entry: LaboratoryWorkstream) => void;
  operator: string;
  saving: boolean;
}) {
  const [basketEan, setBasketEan] = useState("");
  const [basketAdding, setBasketAdding] = useState(false);
  const [basketMessage, setBasketMessage] = useState("");
  const [basketSuggestions, setBasketSuggestions] = useState<Array<Record<string, unknown>>>([]);
  const [documentUploading, setDocumentUploading] = useState(false);
  const [documentMessage, setDocumentMessage] = useState("");
  const [disputeUploading, setDisputeUploading] = useState("");
  const documentInputRef = useRef<HTMLInputElement>(null);
  const linked = followups.filter((item) => item.laboratory.toLocaleLowerCase("fr") === profile.name.toLocaleLowerCase("fr"));
  const planned = linked.filter((item) => item.status === "planned");
  const next = [...planned].sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate))[0];
  const openDisputes = profile.disputes.filter((item) => item.status === "open").length;
  const set = <K extends keyof LaboratoryProfile>(key: K, value: LaboratoryProfile[K]) => onChange({ ...profile, [key]: value });
  const addProduct = () => set("products", [...profile.products, { id: laboratoryItemId("product"), name: "", quantity: "1", note: "", status: "open" }]);
  const addCatalogProduct = (catalogItem: Record<string, unknown>) => {
    const ean = String(catalogItem.ean || "");
    const name = String(catalogItem.product || catalogItem.display_product || ean);
    const pharmacy = String(catalogItem.pharmacy || "");
    if (profile.products.some((item) => item.ean === ean && item.status === "open")) return setBasketMessage("Ce produit est déjà dans le panier.");
    set("products", [...profile.products, { id: laboratoryItemId("ean"), name, ean, quantity: "1", note: pharmacy ? `Catalogue : ${pharmacy}` : "", pharmacy, status: "open" }]);
    setBasketEan("");
    setBasketSuggestions([]);
    setBasketMessage(`${name} ajouté au panier. Enregistrez la fiche pour le conserver.`);
  };
  const searchCatalogProduct = async () => {
    const ean = extractGtinFromScan(basketEan);
    const query = basketEan.trim();
    if (!ean && query.length < 3) return setBasketMessage("Saisissez au moins 3 lettres ou scannez un EAN/DataMatrix.");
    setBasketAdding(true);
    setBasketMessage("");
    try {
      const response = await fetch(`/api/product-catalog?${ean ? `ean=${encodeURIComponent(ean)}` : `search=${encodeURIComponent(query)}`}&limit=12`, { cache: "no-store" });
      const payload = await response.json() as { items?: Array<Record<string, unknown>>; error?: string };
      if (!response.ok) throw new Error(payload.error || "Catalogue indisponible.");
      const suggestions = payload.items ?? [];
      if (!suggestions.length) throw new Error("Aucun produit correspondant dans le catalogue LGPI.");
      if (ean) addCatalogProduct(suggestions[0]);
      else { setBasketSuggestions(suggestions); setBasketMessage(`${suggestions.length} résultat(s) LGPI : choisissez le produit.`); }
    } catch (error) {
      setBasketMessage(error instanceof Error ? error.message : "Recherche EAN impossible.");
    } finally {
      setBasketAdding(false);
    }
  };
  const addAnnual = () => set("annualTerms", [...profile.annualTerms, { id: laboratoryItemId("annual"), year: String(new Date().getFullYear()), text: "" }]);
  const addDocumentLink = () => set("documents", [...profile.documents, { id: laboratoryItemId("link"), title: "", url: "", kind: "Lien", addedAt: localDateIso() }]);
  const uploadDocument = async (file?: File) => {
    if (!file) return;
    setDocumentUploading(true);
    setDocumentMessage("");
    try {
      const optimizedFile = await compressFileForUpload(file);
      if (optimizedFile.size > 8 * 1024 * 1024) throw new Error("Après compression, le document dépasse encore 8 Mo.");
      const form = new FormData();
      form.set("file", optimizedFile);
      form.set("title", `${profile.name} · ${optimizedFile.name.replace(/\.[^.]+$/, "")}`);
      form.set("newCategory", "Laboratoires");
      form.set("pharmacies", JSON.stringify(profile.pharmacies));
      form.set("uploadedBy", operator);
      const response = await fetch("/api/drive", { method: "POST", body: form });
      const payload = await response.json() as { id?: number; error?: string };
      if (!response.ok || !payload.id) throw new Error(payload.error || "Envoi impossible.");
      set("documents", [...profile.documents, {
        id: laboratoryItemId("document"),
        title: optimizedFile.name.replace(/\.[^.]+$/, ""),
        url: `/api/drive?download=${payload.id}`,
        kind: "Document Drive",
        addedAt: localDateIso(),
      }]);
      setDocumentMessage("Document ajouté au Drive. Enregistrez la fiche laboratoire.");
    } catch (error) {
      setDocumentMessage(error instanceof Error ? error.message : "Le document n’a pas pu être ajouté.");
    } finally {
      setDocumentUploading(false);
      if (documentInputRef.current) documentInputRef.current.value = "";
    }
  };
  const addDispute = () => set("disputes", [...profile.disputes, { id: laboratoryItemId("dispute"), title: "", openedDate: localDateIso(), dueDate: localDateIso(), assignedTo: operator, reminderEnabled: false, status: "open", note: "", attachments: [] }]);
  const removeDispute = (dispute: LaboratoryDispute) => {
    if (!window.confirm(`Supprimer définitivement le litige « ${dispute.title || "sans titre"} » ?`)) return;
    const updated = { ...profile, disputes: profile.disputes.filter((item) => item.id !== dispute.id) };
    onChange(updated);
    onSave(updated);
  };
  const uploadDisputeDocument = async (dispute: LaboratoryDispute, file?: File) => {
    if (!file) return;
    setDisputeUploading(dispute.id);
    try {
      const optimizedFile = await compressFileForUpload(file);
      if (optimizedFile.size > 8 * 1024 * 1024) throw new Error("Après compression, la pièce jointe dépasse encore 8 Mo.");
      const formData = new FormData();
      formData.set("file", optimizedFile);
      formData.set("title", `Litige ${profile.name} · ${optimizedFile.name.replace(/\.[^.]+$/, "")}`);
      formData.set("newCategory", "Litiges laboratoires");
      formData.set("pharmacies", JSON.stringify(profile.pharmacies));
      formData.set("uploadedBy", operator);
      const response = await fetch("/api/drive", { method: "POST", body: formData });
      const payload = await response.json() as { id?: number; error?: string };
      if (!response.ok || !payload.id) throw new Error(payload.error || "Envoi impossible.");
      const attachment: LaboratoryDocument = { id: laboratoryItemId("litige-document"), title: optimizedFile.name, url: `/api/drive?download=${payload.id}`, kind: "Pièce jointe", addedAt: localDateIso() };
      set("disputes", profile.disputes.map((item) => item.id === dispute.id ? { ...item, attachments: [...(item.attachments ?? []), attachment] } : item));
    } catch (error) {
      setBasketMessage(error instanceof Error ? error.message : "Pièce jointe impossible.");
    } finally { setDisputeUploading(""); }
  };

  return (
    <div className="laboratory-profile">
      <header className="laboratory-profile-head">
        <button type="button" onClick={onClose}>← Annuaire</button>
        <div><span>FICHE LABORATOIRE</span><input value={profile.name} onChange={(event) => set("name", event.target.value)} /></div>
        <div className="laboratory-profile-head-actions"><button type="button" onClick={onPlan}>＋ Prendre un RDV</button><button type="button" className="save" disabled={saving} onClick={onSave}>{saving ? "Enregistrement…" : "💾 Enregistrer"}</button><button type="button" className="delete" onClick={onDelete}>🗑</button></div>
      </header>

      <section className="laboratory-profile-kpis">
        <div><span>🧾</span><strong>{linked.filter((item) => item.status === "completed").length}</strong><small>visites tracées</small></div>
        <div className={openDisputes ? "alert" : ""}><span>⚠️</span><strong>{openDisputes}</strong><small>litiges ouverts</small></div>
        <div><span>🗓️</span><strong>{planned.length}</strong><small>rendez-vous prévus</small></div>
        <div><span>🧺</span><strong>{profile.products.filter((item) => item.status === "open").length}</strong><small>sujets panier</small></div>
        <div><span>⏭</span><strong>{next ? followupDate(next.scheduledDate) : "—"}</strong><small>prochain rendez-vous</small></div>
      </section>

      <div className="laboratory-profile-columns">
        <section className="laboratory-profile-card">
          <header><div><span>👤</span><h3>Contacts & responsabilité</h3></div></header>
          <div className="laboratory-profile-form two">
            <label><span>Délégué(e)</span><input value={profile.delegateName} onChange={(event) => set("delegateName", event.target.value)} placeholder="Nom du contact terrain" /></label>
            <label><span>Fonction</span><input value={profile.delegateRole} onChange={(event) => set("delegateRole", event.target.value)} placeholder="Délégué, commercial…" /></label>
            <label><span>Email</span><span className="laboratory-email-field"><input type="email" value={profile.delegateEmail} onChange={(event) => set("delegateEmail", event.target.value)} placeholder="delegue@labo.fr" />{profile.delegateEmail && <a href={`mailto:${profile.delegateEmail}?subject=${encodeURIComponent(`Pharmacies Saint-Laurent · ${profile.name}`)}`} aria-label={`Écrire à ${profile.delegateEmail}`}>✉</a>}</span></label>
            <label><span>Téléphone</span><input value={profile.delegatePhone} onChange={(event) => set("delegatePhone", event.target.value)} placeholder="06…" /></label>
            <label><span>Contact siège</span><input value={profile.headquartersContact} onChange={(event) => set("headquartersContact", event.target.value)} placeholder="Service / interlocuteur" /></label>
            <label><span>Email siège</span><span className="laboratory-email-field"><input type="email" value={profile.headquartersEmail} onChange={(event) => set("headquartersEmail", event.target.value)} placeholder="contact@labo.fr" />{profile.headquartersEmail && <a href={`mailto:${profile.headquartersEmail}?subject=${encodeURIComponent(`Pharmacies Saint-Laurent · ${profile.name}`)}`} aria-label={`Écrire à ${profile.headquartersEmail}`}>✉</a>}</span></label>
            <label><span>Responsable interne</span><select value={profile.assignedTo} onChange={(event) => set("assignedTo", event.target.value)}><option value="">Non attribué</option>{missionOperators.map((operator) => <option key={`${operator.pharmacy}-${operator.name}`} value={operator.name}>{operator.name} · {operator.pharmacy}</option>)}</select></label>
          </div>
          <details className="laboratory-regional-contact">
            <summary><span>🧭</span><div><strong>Directeur régional</strong><small>{profile.regionalName || "Contact à compléter"}</small></div><b>Plier / déplier</b></summary>
            <div className="laboratory-profile-form two">
              <label><span>Nom</span><input value={profile.regionalName} onChange={(event) => set("regionalName", event.target.value)} placeholder="Nom du directeur régional" /></label>
              <label><span>Téléphone</span><input value={profile.regionalPhone} onChange={(event) => set("regionalPhone", event.target.value)} placeholder="06…" /></label>
              <label className="wide"><span>Email</span><span className="laboratory-email-field"><input type="email" value={profile.regionalEmail} onChange={(event) => set("regionalEmail", event.target.value)} placeholder="region@labo.fr" />{profile.regionalEmail && <a href={`mailto:${profile.regionalEmail}?subject=${encodeURIComponent(`Pharmacies Saint-Laurent · ${profile.name}`)}`} aria-label={`Écrire à ${profile.regionalEmail}`}>✉</a>}</span></label>
            </div>
          </details>
          <fieldset><legend>Pharmacies concernées</legend>{followupPharmacies.map((pharmacy) => <label key={pharmacy}><input type="checkbox" checked={profile.pharmacies.includes(pharmacy)} onChange={() => set("pharmacies", profile.pharmacies.includes(pharmacy) ? profile.pharmacies.filter((name) => name !== pharmacy) : [...profile.pharmacies, pharmacy])} /> {pharmacy}</label>)}</fieldset>
        </section>

        <section className="laboratory-profile-card">
          <header><div><span>💶</span><h3>Conditions commerciales</h3></div><button type="button" onClick={addAnnual}>＋ Année</button></header>
          <div className="laboratory-profile-form">
            <label><span>Conditions générales</span><textarea value={profile.commercialTerms} onChange={(event) => set("commercialTerms", event.target.value)} placeholder="Remise, unités gratuites, reprises, fréquence de visite…" /></label>
            <div className="laboratory-inline-fields"><label><span>Franco</span><input value={profile.francoValue} onChange={(event) => set("francoValue", event.target.value)} placeholder="350 ou 24" /></label><label><span>Mode</span><select value={profile.francoMode} onChange={(event) => set("francoMode", event.target.value as "amount" | "units")}><option value="amount">Montant €</option><option value="units">Unités</option></select></label></div>
          </div>
          <div className="laboratory-mini-list">
            {profile.annualTerms.map((term) => <div key={term.id}><input value={term.year} onChange={(event) => set("annualTerms", profile.annualTerms.map((entry) => entry.id === term.id ? { ...entry, year: event.target.value } : entry))} /><input value={term.text} onChange={(event) => set("annualTerms", profile.annualTerms.map((entry) => entry.id === term.id ? { ...entry, text: event.target.value } : entry))} placeholder="Évolution des conditions, accord, remise…" /><button type="button" onClick={() => set("annualTerms", profile.annualTerms.filter((entry) => entry.id !== term.id))}>×</button></div>)}
          </div>
        </section>
      </div>

      <div className="laboratory-profile-columns">
        <section className="laboratory-profile-card">
          <header><div><span>🧺</span><h3>Panier du prochain échange</h3></div><button type="button" onClick={addProduct}>＋ Sujet libre</button></header>
          <div className="laboratory-ean-add">
            <div><span>EAN, DataMatrix ou dénomination LGPI</span><input inputMode="text" value={basketEan} onChange={(event) => setBasketEan(event.target.value.slice(0, 160))} onKeyDown={(event) => { if (event.key === "Enter") void searchCatalogProduct(); }} placeholder="Scanner ou rechercher par nom du produit" /></div>
            <button type="button" disabled={basketAdding || basketEan.trim().length < 3} onClick={() => void searchCatalogProduct()}>{basketAdding ? "Recherche LGPI…" : "⌕ Rechercher dans LGPI"}</button>
          </div>
          {basketMessage && <p className="laboratory-basket-message">{basketMessage}</p>}
          {basketSuggestions.length > 0 && <div className="laboratory-catalog-suggestions">{basketSuggestions.map((item, index) => <button type="button" key={`${String(item.ean)}-${String(item.pharmacy)}-${index}`} onClick={() => addCatalogProduct(item)}><strong>{String(item.product || item.display_product || "Produit")}</strong><small>EAN {String(item.ean || "—")} · {String(item.pharmacy || "catalogue réseau")} · stock {String(item.stock ?? "—")}</small></button>)}</div>}
          <div className="laboratory-table">
            {profile.products.map((product) => <div key={product.id} className={product.status === "done" ? "done" : ""}><button type="button" onClick={() => set("products", profile.products.map((entry) => entry.id === product.id ? { ...entry, status: entry.status === "done" ? "open" : "done" } : entry))}>{product.status === "done" ? "✓" : "○"}</button><span className="laboratory-product-name"><input value={product.name} onChange={(event) => set("products", profile.products.map((entry) => entry.id === product.id ? { ...entry, name: event.target.value } : entry))} placeholder="Produit / sujet" />{product.ean && <small>EAN {product.ean}{product.pharmacy ? ` · ${product.pharmacy}` : ""}</small>}</span><input value={product.quantity} onChange={(event) => set("products", profile.products.map((entry) => entry.id === product.id ? { ...entry, quantity: event.target.value } : entry))} placeholder="Qté" /><input value={product.note} onChange={(event) => set("products", profile.products.map((entry) => entry.id === product.id ? { ...entry, note: event.target.value } : entry))} placeholder="Note" /><button type="button" onClick={() => set("products", profile.products.filter((entry) => entry.id !== product.id))}>×</button></div>)}
            {!profile.products.length && <p>Ajoutez les produits, tarifs ou opérations à aborder au prochain rendez-vous.</p>}
          </div>
        </section>
        <section className="laboratory-profile-card laboratory-documents-card">
          <header><div><span>📎</span><h3>Documents & liens utiles</h3></div><div className="laboratory-card-actions"><input ref={documentInputRef} type="file" hidden onChange={(event) => void uploadDocument(event.target.files?.[0])} /><button type="button" disabled={documentUploading} onClick={() => documentInputRef.current?.click()}>{documentUploading ? "Envoi…" : "＋ Document"}</button><button type="button" onClick={addDocumentLink}>＋ Lien</button></div></header>
          {documentMessage && <p className="laboratory-document-message">{documentMessage}</p>}
          <div className="laboratory-document-list">
            {profile.documents.map((document) => <div key={document.id}><span>📄</span><input value={document.title} onChange={(event) => set("documents", profile.documents.map((entry) => entry.id === document.id ? { ...entry, title: event.target.value } : entry))} placeholder="Catalogue, tarif, contrat…" /><input value={document.url} onChange={(event) => set("documents", profile.documents.map((entry) => entry.id === document.id ? { ...entry, url: event.target.value } : entry))} placeholder="Lien Drive ou URL" />{document.url && <a href={document.url} target="_blank" rel="noreferrer">Ouvrir</a>}<button type="button" onClick={() => set("documents", profile.documents.filter((entry) => entry.id !== document.id))}>×</button></div>)}
            {!profile.documents.length && <p>Ajoutez ici les liens Drive vers catalogues, accords et présentations.</p>}
          </div>
        </section>
      </div>

      <section className="laboratory-profile-card">
        <header><div><span>📌</span><h3>Mémo relationnel permanent</h3></div></header>
        <textarea className="laboratory-permanent-notes" value={profile.permanentNotes} onChange={(event) => set("permanentNotes", event.target.value)} placeholder="Habitudes du commercial, points sensibles, engagements récurrents, informations à ne pas perdre…" />
      </section>

      <div className="laboratory-profile-columns three">
        <section className="laboratory-profile-card laboratory-operation-card">
          <header><div><span>💶</span><h3>BRI & règlements</h3></div><button type="button" onClick={() => onCreateBriOperation(profile, "BRI")}>＋ Créer un BRI</button></header>
          <div className="laboratory-linked-operations">{profile.workstreams.filter((entry) => entry.status === "open" && (entry.operationType === "BRI" || (!entry.operationType && entry.type === "bri"))).map((entry) => <article key={entry.id}><div><b>{entry.operationType || "BRI"}</b><strong>{entry.title || "Opération BRI"}</strong><small>{entry.amount ? `${entry.amount} € · ` : ""}{entry.dueDate ? `échéance ${followupDate(entry.dueDate)}` : "Paiement en attente"}</small></div><span>En attente</span><footer>{entry.briId && <button type="button" onClick={() => onOpenBriOperation(entry)}>Ouvrir dans BRI</button>}<button type="button" className="paid" onClick={() => onSetBriPayment(profile, entry, true)}>✓ Marquer payé</button></footer></article>)}</div>
          {!profile.workstreams.some((entry) => entry.status === "open" && (entry.operationType === "BRI" || (!entry.operationType && entry.type === "bri"))) && <p className="laboratory-muted">Aucun BRI en attente de règlement.</p>}
        </section>
        <section className="laboratory-profile-card laboratory-operation-card">
          <header><div><span>🤝</span><h3>COOP / TG</h3></div><div className="laboratory-card-actions"><button type="button" onClick={() => onCreateBriOperation(profile, "COOP")}>＋ COOP</button><button type="button" onClick={() => onCreateBriOperation(profile, "TG")}>＋ TG</button></div></header>
          <div className="laboratory-linked-operations">{profile.workstreams.filter((entry) => entry.status === "open" && (entry.operationType === "COOP" || entry.operationType === "TG" || (!entry.operationType && entry.type === "coop"))).map((entry) => <article key={entry.id}><div><b>{entry.operationType || "COOP / TG"}</b><strong>{entry.title || "Opération commerciale"}</strong><small>{entry.amount ? `${entry.amount} € · ` : ""}{entry.dueDate ? `échéance ${followupDate(entry.dueDate)}` : "Paiement en attente"}</small></div><span>En attente</span><footer>{entry.briId && <button type="button" onClick={() => onOpenBriOperation(entry)}>Ouvrir dans BRI</button>}<button type="button" className="paid" onClick={() => onSetBriPayment(profile, entry, true)}>✓ Marquer payé</button></footer></article>)}</div>
          {!profile.workstreams.some((entry) => entry.status === "open" && (entry.type === "coop" || entry.operationType === "COOP" || entry.operationType === "TG")) && <p className="laboratory-muted">Aucune COOP ni TG en attente de règlement.</p>}
        </section>
        <details className="laboratory-profile-card laboratory-disputes-card">
          <summary><div><span>⚠️</span><h3>Litiges</h3><b>{openDisputes} à traiter</b></div><strong>Consulter / replier</strong></summary>
          <div className="laboratory-disputes-content">
            <button type="button" className="laboratory-add-dispute" onClick={addDispute}>＋ Ajouter un litige</button>
            <div className="laboratory-dispute-mini-list">{profile.disputes.map((entry) => <details key={entry.id} className={entry.status}>
              <summary><div><span className="laboratory-dispute-status">{entry.status === "resolved" ? "✓ Traité" : "! À traiter"}</span><strong>{entry.title || "Nouveau litige à compléter"}</strong></div><span>Consulter</span></summary>
              <div className="laboratory-dispute-editor">
                <button type="button" className="laboratory-dispute-status" onClick={() => set("disputes", profile.disputes.map((item) => item.id === entry.id ? { ...item, status: item.status === "resolved" ? "open" : "resolved" } : item))}>{entry.status === "resolved" ? "↺ Rouvrir le litige" : "✓ Marquer comme traité"}</button>
                <input value={entry.title} onChange={(event) => set("disputes", profile.disputes.map((item) => item.id === entry.id ? { ...item, title: event.target.value } : item))} placeholder="Motif du litige" />
                <textarea value={entry.note} onChange={(event) => set("disputes", profile.disputes.map((item) => item.id === entry.id ? { ...item, note: event.target.value } : item))} placeholder="Détail, facture, interlocuteur, réponse attendue…" />
                <div className="laboratory-dispute-files"><label>{disputeUploading === entry.id ? "Envoi…" : "📎 Ajouter un fichier"}<input type="file" hidden disabled={Boolean(disputeUploading)} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv" onChange={(event) => void uploadDisputeDocument(entry, event.target.files?.[0])} /></label>{(entry.attachments ?? []).map((file) => <a key={file.id} href={file.url} target="_blank" rel="noreferrer">📄 {file.title}</a>)}</div>
                <label className="laboratory-dispute-reminder"><input type="checkbox" checked={Boolean(entry.reminderEnabled)} onChange={(event) => set("disputes", profile.disputes.map((item) => item.id === entry.id ? { ...item, reminderEnabled: event.target.checked } : item))} /><span>Voulez-vous un rappel à une date précise dans un bloc-notes ?</span></label>
                {entry.reminderEnabled && <div className="laboratory-dispute-reminder-fields"><label><span>Date du rappel</span><input type="date" value={entry.dueDate || entry.openedDate} onChange={(event) => set("disputes", profile.disputes.map((item) => item.id === entry.id ? { ...item, dueDate: event.target.value } : item))} /></label><label><span>Bloc-notes de</span><select value={entry.assignedTo || operator} onChange={(event) => set("disputes", profile.disputes.map((item) => item.id === entry.id ? { ...item, assignedTo: event.target.value } : item))}>{missionOperators.map((item) => <option key={`${item.pharmacy}-${item.name}`} value={item.name}>{item.name} · {item.pharmacy}</option>)}</select></label></div>}
                <footer><button type="button" className="laboratory-save-dispute" onClick={() => entry.reminderEnabled ? onFollowDispute(profile, entry) : onSave(profile)}>💾 Enregistrer</button><button type="button" className="laboratory-delete-dispute" onClick={() => removeDispute(entry)}>🗑 Supprimer</button></footer>
              </div>
            </details>)}</div>
            {!profile.disputes.length && <p className="laboratory-muted">Aucun litige sur cette fiche.</p>}
          </div>
        </details>
      </div>

      <section className="laboratory-profile-card laboratory-bri-archives">
        <header><div><span>🗄️</span><h3>Archives BRI</h3></div><b>{profile.workstreams.filter((entry) => entry.status === "done").length} opération(s) réglée(s)</b></header>
        <div>{profile.workstreams.filter((entry) => entry.status === "done").sort((a, b) => (b.paidAt || b.dueDate).localeCompare(a.paidAt || a.dueDate)).map((entry) => <article key={entry.id}><span>{entry.operationType || (entry.type === "bri" ? "BRI" : "COOP / TG")}</span><div><strong>{entry.title || "Opération réglée"}</strong><small>{entry.amount ? `${entry.amount} € · ` : ""}payé{entry.paidAt ? ` le ${followupDate(entry.paidAt)}` : ""}</small></div><button type="button" onClick={() => onSetBriPayment(profile, entry, false)}>Remettre en attente</button>{entry.briId && <button type="button" className="open" onClick={() => onOpenBriOperation(entry)}>Ouvrir</button>}</article>)}</div>
        {!profile.workstreams.some((entry) => entry.status === "done") && <p className="laboratory-muted">Les opérations payées basculeront automatiquement ici depuis le suivi des règlements.</p>}
      </section>

      <section className="laboratory-profile-card">
        <header><div><span>🧾</span><h3>Visites, comptes-rendus & rendez-vous liés</h3></div><button type="button" onClick={onPlan}>＋ Planifier</button></header>
        <div className="laboratory-linked-list">
          {linked.sort((a, b) => b.scheduledDate.localeCompare(a.scheduledDate)).map((item) => <article key={item.id}><time>{followupDate(item.scheduledDate)} {item.scheduledTime}</time><div><strong>{item.subject}</strong><span>{item.pharmacy} · {item.assignedTo || "Non attribué"}</span>{item.status === "planned" && item.notes && <p><b>Préparation :</b> {item.notes}</p>}{item.outcome && <p>{item.outcome}</p>}{item.attachments.length > 0 && <div className="laboratory-report-files">{item.attachments.map((file) => <a key={file.id} href={file.url} target="_blank" rel="noreferrer">📎 {file.title}</a>)}</div>}</div><b className={item.status}>{item.status === "completed" ? "Compte-rendu" : "À venir"}</b><div className="laboratory-linked-actions">{item.status === "planned" && item.scheduledDate > localDateIso() && <button type="button" className="prepare" onClick={() => onPrepareFollowup(item)}>✍ Préparer</button>}{item.status === "planned" && item.scheduledDate <= localDateIso() && <button type="button" className="complete" onClick={() => onCompleteFollowup(item)}>✓ Compte rendu</button>}<button type="button" onClick={() => onMoveFollowup(item)}>🗓 Déplacer</button><button type="button" className="delete" onClick={() => onDeleteFollowup(item)}>🗑 Supprimer</button></div></article>)}
          {!linked.length && <p className="laboratory-muted">Aucun rendez-vous ni compte-rendu lié à cette fiche.</p>}
        </div>
      </section>
    </div>
  );
}

function LaboratoryModule({
  onBack,
  initialPharmacy,
  initialOperator,
}: {
  onBack: () => void;
  initialPharmacy: string;
  initialOperator: string;
}) {
  const defaultPharmacy = followupPharmacies.includes(initialPharmacy) ? initialPharmacy : "Square";
  const [items, setItems] = useState<LaboratoryFollowup[]>([]);
  const [profiles, setProfiles] = useState<LaboratoryProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [view, setView] = useState<"agenda" | "relances" | "reports" | "data" | "dossiers" | "litiges">("agenda");
  const [calendarMode, setCalendarMode] = useState<"list" | "day" | "week" | "month">("month");
  const [calendarDate, setCalendarDate] = useState(localDateIso());
  const [selectedProfile, setSelectedProfile] = useState<LaboratoryProfile | null>(null);
  const [query, setQuery] = useState("");
  const [notice, setNotice] = useState("");
  const [closingItem, setClosingItem] = useState<LaboratoryFollowup | null>(null);
  const [outcome, setOutcome] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [createdConfirmation, setCreatedConfirmation] = useState<LaboratoryFollowup | null>(null);
  const [disputeReminderProfile, setDisputeReminderProfile] = useState<LaboratoryProfile | null>(null);
  const [reschedulingItem, setReschedulingItem] = useState<LaboratoryFollowup | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState(localDateIso());
  const [rescheduleTime, setRescheduleTime] = useState("10:00");
  const [preparingItem, setPreparingItem] = useState<LaboratoryFollowup | null>(null);
  const [preparationNotes, setPreparationNotes] = useState("");
  const [expiryManagement, setExpiryManagement] = useState<LaboratoryExpiryManagement>(emptyLaboratoryExpiryManagement);
  const [reportAttachments, setReportAttachments] = useState<LaboratoryDocument[]>([]);
  const [reportUploading, setReportUploading] = useState(false);
  const [reportReminderEnabled, setReportReminderEnabled] = useState(false);
  const [reportReminderDate, setReportReminderDate] = useState(localDateIso());
  const [reportReminderOwner, setReportReminderOwner] = useState(initialOperator);
  const [reportNextAppointment, setReportNextAppointment] = useState(false);
  const [reportNextDate, setReportNextDate] = useState(localDateIso());
  const [reportNextTime, setReportNextTime] = useState("10:00");
  const [form, setForm] = useState({
    laboratory: "",
    contactName: "",
    contactRole: "Délégué(e)",
    phone: "",
    email: "",
    subject: "Rendez-vous",
    scheduledDate: localDateIso(),
    scheduledTime: "10:00",
    durationMinutes: 60,
    isLaboratory: true,
    pharmacy: defaultPharmacy,
    assignedTo: initialOperator || missionOperators.find((operator) => operator.pharmacy === defaultPharmacy)?.name || "",
    priority: "normal" as LaboratoryPriority,
    notes: "",
    nextAction: "",
  });
  const today = localDateIso();
  const openComposerAt = (date = localDateIso(), time = "10:00") => {
    setForm((current) => ({ ...current, scheduledDate: date, scheduledTime: time, durationMinutes: current.durationMinutes || 60 }));
    setComposerOpen(true);
    window.setTimeout(() => window.scrollTo({ top: 120, behavior: "smooth" }), 0);
  };

  const flash = (text: string) => {
    setNotice(text);
    window.setTimeout(() => setNotice(""), 2500);
  };

  const load = async () => {
    try {
      const [followupsResponse, profilesResponse] = await Promise.all([
        fetch("/api/laboratory-followups", { cache: "no-store" }),
        fetch("/api/laboratories", { cache: "no-store" }),
      ]);
      if (!followupsResponse.ok || !profilesResponse.ok) throw new Error();
      const [followupsPayload, profilesPayload] = await Promise.all([followupsResponse.json(), profilesResponse.json()]);
      setItems((followupsPayload.items ?? []).map(normalizeLaboratoryFollowup));
      const nextProfiles = (profilesPayload.items ?? []).map(normalizeLaboratoryProfile);
      setProfiles(nextProfiles);
      const requestedView = new URLSearchParams(window.location.search).get("labView");
      if (["agenda", "relances", "reports", "data", "dossiers", "litiges"].includes(requestedView ?? "")) {
        setView(requestedView as "agenda" | "relances" | "reports" | "data" | "dossiers" | "litiges");
      }
      setSelectedProfile((current) => {
        if (current) return nextProfiles.find((profile: LaboratoryProfile) => profile.id === current.id) ?? null;
        const requested = new URLSearchParams(window.location.search).get("lab")?.trim().toLocaleLowerCase("fr");
        const match = requested ? nextProfiles.find((profile: LaboratoryProfile) => profile.name.toLocaleLowerCase("fr") === requested) : null;
        if (match) setView("dossiers");
        return match ?? null;
      });
    } catch {
      flash("Le suivi laboratoire est momentanément indisponible.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const createItem = async () => {
    if (!form.laboratory.trim() || !form.scheduledDate || !form.scheduledTime) {
      flash(`${form.isLaboratory ? "Laboratoire" : "Titre"}, date et heure sont obligatoires.`);
      return;
    }
    setSaving(true);
    try {
      const laboratoryProfile = profiles.find((profile) => profile.name.toLocaleLowerCase("fr") === form.laboratory.trim().toLocaleLowerCase("fr"));
      const response = await fetch("/api/laboratory-followups", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          subject: form.subject.trim() || (form.isLaboratory ? `Rendez-vous ${form.laboratory.trim()}` : form.laboratory.trim()),
          preparationPharmacies: form.isLaboratory
            ? laboratoryProfile?.pharmacies.length ? laboratoryProfile.pharmacies : [form.pharmacy]
            : [],
        }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error);
      }
      const created = normalizeLaboratoryFollowup(await response.json());
      setItems((current) => [created, ...current]);
      setForm((current) => ({ ...current, laboratory: "", contactName: "", phone: "", email: "", subject: "Rendez-vous", notes: "", nextAction: "", scheduledDate: localDateIso(), scheduledTime: "10:00", durationMinutes: 60, isLaboratory: true }));
      setComposerOpen(false);
      setCreatedConfirmation(created);
    } catch (error) {
      flash(error instanceof Error && error.message ? error.message : "Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  const updateItem = async (
    item: LaboratoryFollowup,
    action: "complete" | "reopen" | "reschedule" | "preparation" | "prepare_visit",
    extra: Record<string, unknown> = {},
  ) => {
    try {
      const response = await fetch("/api/laboratory-followups", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: item.id, action, ...extra }),
      });
      if (!response.ok) throw new Error();
      const updated = normalizeLaboratoryFollowup(await response.json());
      setItems((current) => current.map((entry) => entry.id === item.id ? updated : entry));
      flash(action === "complete" ? "Rendez-vous clôturé et compte-rendu conservé." : action === "reopen" ? "Suivi rouvert." : action === "prepare_visit" ? "Préparation de visite enregistrée." : action === "preparation" ? "État de préparation enregistré dans le rendez-vous." : "Relance reprogrammée.");
    } catch {
      flash("La modification n’a pas pu être enregistrée.");
    }
  };

  const expiryManagementFor = (item: LaboratoryFollowup) => {
    if (laboratoryExpiryHasData(item.expiryManagement)) return item.expiryManagement;
    return [...items]
      .filter((entry) => entry.id !== item.id && entry.status === "completed" && entry.laboratory.toLocaleLowerCase("fr") === item.laboratory.toLocaleLowerCase("fr") && laboratoryExpiryPending(entry.expiryManagement))
      .sort((a, b) => b.updatedAt - a.updatedAt)[0]?.expiryManagement ?? emptyLaboratoryExpiryManagement();
  };

  const completeItem = async () => {
    if (!closingItem) return;
    await updateItem(closingItem, "complete", { outcome, nextAction, attachments: reportAttachments, expiryManagement });
    if (reportReminderEnabled && reportReminderDate && reportReminderOwner) {
      await fetch("/api/operator-notes", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ owner: reportReminderOwner, title: `Suivi labo ${closingItem.laboratory}`, details: nextAction || outcome.slice(0, 500), priority: "important", dueDate: reportReminderDate }) });
    }
    if (reportNextAppointment && reportNextDate) {
      await fetch("/api/laboratory-followups", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ laboratory: closingItem.laboratory, contactName: closingItem.contactName, contactRole: closingItem.contactRole, phone: closingItem.phone, email: closingItem.email, subject: `Prochain rendez-vous ${closingItem.laboratory}`, scheduledDate: reportNextDate, scheduledTime: reportNextTime, durationMinutes: closingItem.durationMinutes, isLaboratory: true, preparationPharmacies: Object.keys(closingItem.preparationStatus).length ? Object.keys(closingItem.preparationStatus) : [closingItem.pharmacy], pharmacy: closingItem.pharmacy, assignedTo: closingItem.assignedTo, priority: "normal", notes: nextAction, expiryManagement: laboratoryExpiryPending(expiryManagement) ? expiryManagement : emptyLaboratoryExpiryManagement() }) });
      await load();
    }
    setClosingItem(null);
    setOutcome("");
    setNextAction("");
    setReportAttachments([]);
    setExpiryManagement(emptyLaboratoryExpiryManagement());
  };

  const openLaboratoryReport = (item: LaboratoryFollowup) => {
    setClosingItem(item);
    setOutcome(item.notes || item.outcome || "");
    setNextAction(item.nextAction);
    setReportAttachments(item.attachments || []);
    setExpiryManagement(expiryManagementFor(item));
    setReportReminderEnabled(false);
    setReportReminderDate(localDateIso());
    setReportReminderOwner(item.assignedTo || initialOperator);
    setReportNextAppointment(false);
    setReportNextDate(localDateIso(new Date(Date.now() + 28 * 86_400_000)));
    setReportNextTime(item.scheduledTime || "10:00");
  };

  const uploadReportFiles = async (files: FileList | null) => {
    if (!files?.length || !closingItem) return;
    setReportUploading(true);
    try {
      const uploaded: LaboratoryDocument[] = [];
      for (const sourceFile of Array.from(files).slice(0, 10)) {
        const file = await compressFileForUpload(sourceFile);
        if (file.size > 8 * 1024 * 1024) throw new Error(`${file.name} dépasse encore 8 Mo après compression.`);
        const formData = new FormData();
        formData.set("file", file);
        formData.set("title", `Compte rendu ${closingItem.laboratory} · ${file.name.replace(/\.[^.]+$/, "")}`);
        formData.set("newCategory", "Comptes rendus laboratoire");
        formData.set("pharmacies", JSON.stringify([closingItem.pharmacy]));
        formData.set("uploadedBy", closingItem.assignedTo || initialOperator);
        const response = await fetch("/api/drive", { method: "POST", body: formData });
        const payload = await response.json() as { id?: number; error?: string };
        if (!response.ok || !payload.id) throw new Error(payload.error || `Envoi impossible pour ${file.name}.`);
        uploaded.push({ id: laboratoryItemId("compte-rendu"), title: file.name, url: `/api/drive?download=${payload.id}`, kind: "Pièce jointe compte rendu", addedAt: localDateIso() });
      }
      setReportAttachments((current) => [...current, ...uploaded]);
    } catch (error) { flash(error instanceof Error ? error.message : "Pièce jointe impossible."); }
    finally { setReportUploading(false); }
  };

  const postponeItem = (item: LaboratoryFollowup, days = 7) => {
    const date = new Date(`${item.scheduledDate || today}T12:00:00`);
    date.setDate(date.getDate() + days);
    void updateItem(item, "reschedule", { scheduledDate: localDateIso(date), scheduledTime: item.scheduledTime });
  };

  const openReschedule = (item: LaboratoryFollowup) => {
    setReschedulingItem(item);
    setRescheduleDate(item.scheduledDate || today);
    setRescheduleTime(item.scheduledTime || "10:00");
  };

  const confirmReschedule = async () => {
    if (!reschedulingItem || !rescheduleDate) return;
    await updateItem(reschedulingItem, "reschedule", { scheduledDate: rescheduleDate, scheduledTime: rescheduleTime });
    setReschedulingItem(null);
  };

  const openVisitPreparation = (item: LaboratoryFollowup) => {
    setPreparingItem(item);
    setPreparationNotes(item.notes || "");
    setExpiryManagement(expiryManagementFor(item));
  };

  const confirmVisitPreparation = async () => {
    if (!preparingItem) return;
    await updateItem(preparingItem, "prepare_visit", { notes: preparationNotes, expiryManagement });
    setPreparingItem(null);
  };

  const togglePreparation = async (item: LaboratoryFollowup, pharmacy: string) => {
    await updateItem(item, "preparation", {
      pharmacy,
      prepared: !item.preparationStatus[pharmacy],
    });
  };

  const deleteItem = async (item: LaboratoryFollowup) => {
    if (!window.confirm(`Supprimer le suivi ${item.laboratory} ?`)) return;
    try {
      const response = await fetch(`/api/laboratory-followups?id=${item.id}`, { method: "DELETE" });
      if (!response.ok) throw new Error();
      setItems((current) => current.filter((entry) => entry.id !== item.id));
      flash("Suivi supprimé.");
    } catch {
      flash("Suppression impossible.");
    }
  };

  const createProfile = async () => {
    const name = window.prompt("Nom du laboratoire");
    if (!name?.trim()) return;
    setSaving(true);
    try {
      const response = await fetch("/api/laboratories", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, pharmacies: [initialPharmacy] }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error);
      const created = normalizeLaboratoryProfile(payload);
      setProfiles((current) => [...current, created].sort((a, b) => a.name.localeCompare(b.name, "fr")));
      setSelectedProfile(created);
      setView("dossiers");
      flash("Dossier laboratoire créé.");
    } catch (error) {
      flash(error instanceof Error && error.message ? error.message : "Création impossible.");
    } finally {
      setSaving(false);
    }
  };

  const saveProfile = async (profile: LaboratoryProfile, selectAfterSave = true) => {
    setSaving(true);
    try {
      const response = await fetch("/api/laboratories", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(profile),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error);
      const saved = normalizeLaboratoryProfile(payload);
      setProfiles((current) => current.map((entry) => entry.id === saved.id ? saved : entry));
      if (selectAfterSave) setSelectedProfile(saved);
      flash("Fiche laboratoire enregistrée.");
    } catch (error) {
      flash(error instanceof Error && error.message ? error.message : "Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  const openBriOperation = (entry: LaboratoryWorkstream) => {
    if (!entry.briId) return;
    window.location.href = `/?tool=BRI&briEdit=${encodeURIComponent(entry.briId)}`;
  };

  const createBriOperation = async (profile: LaboratoryProfile, operationType: "BRI" | "COOP" | "TG") => {
    setSaving(true);
    try {
      const response = await fetch("/api/bri-store", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "create_from_laboratory",
          laboratory: profile.name,
          profileId: profile.id,
          operationType,
          pharmacy: profile.pharmacies.join(", ") || initialPharmacy,
          operator: profile.assignedTo || initialOperator,
        }),
      });
      const payload = await response.json() as { id?: string; error?: string };
      if (!response.ok || !payload.id) throw new Error(payload.error || "Création impossible.");
      window.location.href = `/?tool=BRI&briEdit=${encodeURIComponent(payload.id)}`;
    } catch (error) {
      flash(error instanceof Error ? error.message : "La création de l’opération a échoué.");
      setSaving(false);
    }
  };

  const setBriPayment = async (profile: LaboratoryProfile, entry: LaboratoryWorkstream, paid: boolean) => {
    if (!entry.briId) return flash("Cette ancienne ligne n’est pas encore liée au suivi BRI.");
    try {
      const paidAt = paid ? localDateIso() : "";
      const response = await fetch("/api/bri-store", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ collection: "bri_reglements", id: entry.briId, merge: true, data: { laboPaye: paid ? "oui" : "non", laboPayeDate: paidAt } }),
      });
      if (!response.ok) throw new Error();
      const updatedProfile = {
        ...profile,
        workstreams: profile.workstreams.map((item) => item.id === entry.id ? { ...item, status: paid ? "done" as const : "open" as const, paymentStatus: paid ? "paid" as const : "pending" as const, paidAt } : item),
      };
      setProfiles((current) => current.map((item) => item.id === profile.id ? updatedProfile : item));
      setSelectedProfile(updatedProfile);
      flash(paid ? "Paiement synchronisé : opération archivée." : "Opération remise en attente dans les deux modules.");
    } catch {
      flash("Le paiement n’a pas pu être synchronisé.");
    }
  };

  const openProfile = (profile: LaboratoryProfile) => {
    setSelectedProfile(profile);
    if (profile.disputes.some((item) => item.status === "open")) setDisputeReminderProfile(profile);
  };

  const changeDispute = (profile: LaboratoryProfile, disputeId: string, patch: Partial<LaboratoryDispute>) => {
    const updated = { ...profile, disputes: profile.disputes.map((item) => item.id === disputeId ? { ...item, ...patch } : item) };
    setProfiles((current) => current.map((item) => item.id === profile.id ? updated : item));
    if (selectedProfile?.id === profile.id) setSelectedProfile(updated);
  };

  const followDisputeInNotes = async (profile: LaboratoryProfile, dispute: LaboratoryDispute) => {
    const owner = dispute.assignedTo || profile.assignedTo || initialOperator;
    if (!owner) return flash("Choisissez un responsable pour suivre ce litige.");
    try {
      const response = await fetch("/api/operator-notes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          owner,
          title: `Litige ${profile.name} · ${dispute.title || "à préciser"}`,
          details: [dispute.note, `Fiche laboratoire : ${profile.name}`].filter(Boolean).join("\n"),
          priority: "important",
          dueDate: dispute.dueDate || dispute.openedDate || localDateIso(),
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error);
      const updated = { ...profile, disputes: profile.disputes.map((item) => item.id === dispute.id ? { ...item, assignedTo: owner, followedInNotesAt: Date.now() } : item) };
      await saveProfile(updated, selectedProfile?.id === profile.id);
      flash(`Litige ajouté au bloc-notes de ${owner}.`);
    } catch (error) {
      flash(error instanceof Error && error.message ? error.message : "Le litige n’a pas pu être ajouté au bloc-notes.");
    }
  };

  const deleteProfile = async (profile: LaboratoryProfile) => {
    if (!window.confirm(`Supprimer la fiche ${profile.name} ? Les rendez-vous et comptes-rendus resteront dans l’historique.`)) return;
    try {
      const response = await fetch(`/api/laboratories?id=${profile.id}`, { method: "DELETE" });
      if (!response.ok) throw new Error();
      setProfiles((current) => current.filter((entry) => entry.id !== profile.id));
      setSelectedProfile(null);
      flash("Fiche laboratoire supprimée.");
    } catch {
      flash("Suppression impossible.");
    }
  };

  const activeItems = items.filter((item) => item.status === "planned");
  const dueItems = activeItems.filter((item) => item.scheduledDate <= today);
  const weekLimit = offsetDate(7);
  const weekItems = activeItems.filter((item) => item.scheduledDate >= today && item.scheduledDate <= weekLimit);
  const laboratoryCount = profiles.filter((profile) => profile.active).length || new Set(items.map((item) => item.laboratory.toLocaleLowerCase("fr"))).size;
  const normalizedQuery = query.trim().toLocaleLowerCase("fr");
  const filtered = items
    .filter((item) => !normalizedQuery || `${item.laboratory} ${item.contactName} ${item.subject} ${item.notes} ${item.assignedTo}`.toLocaleLowerCase("fr").includes(normalizedQuery))
    .filter((item) => view === "relances" ? laboratoryDue(item) : view === "reports" ? item.status === "completed" : view === "agenda" ? item.status === "planned" : true)
    .sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate) || a.scheduledTime.localeCompare(b.scheduledTime));
  const visibleProfiles = profiles
    .filter((profile) => profile.active)
    .filter((profile) => !normalizedQuery || `${profile.name} ${profile.delegateName} ${profile.assignedTo} ${profile.commercialTerms}`.toLocaleLowerCase("fr").includes(normalizedQuery));
  const visibleDisputes = profiles.flatMap((profile) => profile.disputes.map((dispute) => ({ profile, dispute })))
    .filter(({ profile, dispute }) => !normalizedQuery || `${profile.name} ${dispute.title} ${dispute.note} ${dispute.assignedTo || ""}`.toLocaleLowerCase("fr").includes(normalizedQuery))
    .sort((a, b) => (a.dispute.status === "open" ? 0 : 1) - (b.dispute.status === "open" ? 0 : 1) || (a.dispute.dueDate || a.dispute.openedDate).localeCompare(b.dispute.dueDate || b.dispute.openedDate));
  const openDisputeCount = visibleDisputes.filter(({ dispute }) => dispute.status === "open").length;

  const pharmacyOperators = missionOperators.filter((operator) => operator.pharmacy === form.pharmacy);

  return (
    <main className="laboratory-app">
      <header className="module-header">
        <button type="button" className="module-back" onClick={onBack}>← <span>Retour à l’accueil</span></button>
        <div className="wordmark module-wordmark"><span className="wordmark-symbol"><i /><i /><i /></span><span><strong>saint-laurent</strong><small>pharmacies</small></span></div>
        <div className="module-header-actions"><ModuleToolsLink /><span className="module-status"><i /> Laboratoires synchronisés</span></div>
      </header>

      <div className="laboratory-page">
        <section className="laboratory-hero">
          <div><span>RELATION LABORATOIRES</span><h1>Laboratoires</h1><p>Fiches, rendez-vous, opérations commerciales, règlements et relances réunis dans une seule vue.</p></div>
          <div className="laboratory-hero-actions">
            <button type="button" className="secondary" onClick={() => { setView("dossiers"); setSelectedProfile(null); }}>📂 Dossiers labo</button>
            <button type="button" onClick={() => composerOpen ? setComposerOpen(false) : openComposerAt()}>＋ Prendre un RDV</button>
          </div>
        </section>

        <section className="laboratory-quick-search">
          <div><span>🧪</span><div><strong>Accéder directement à une fiche laboratoire</strong><small>Recherchez par nom, délégué ou responsable interne.</small></div></div>
          <label><span>⌕</span><input value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" && visibleProfiles[0]) { setView("dossiers"); openProfile(visibleProfiles[0]); } }} placeholder="Ex. Bouchara, Pierre Fabre, Bioderma…" /></label>
          {query.trim() && <div className="laboratory-quick-results">{visibleProfiles.slice(0, 6).map((profile) => <button type="button" key={profile.id} onClick={() => { setView("dossiers"); openProfile(profile); }}><span>{profile.name.slice(0, 2).toLocaleUpperCase("fr")}</span><div><strong>{profile.name}</strong><small>{profile.delegateName || "Contact à compléter"}</small></div><b>Ouvrir →</b></button>)}</div>}
        </section>

        <section className="laboratory-stats" aria-label="Résumé du suivi laboratoire">
          <button type="button" className={dueItems.length ? "alert" : ""} onClick={() => setView("relances")}><span>⏰</span><strong>{dueItems.length}</strong><small>à traiter maintenant</small></button>
          <button type="button" onClick={() => setView("agenda")}><span>🗓️</span><strong>{weekItems.length}</strong><small>dans les 7 jours</small></button>
          <button type="button" onClick={() => { setView("dossiers"); setSelectedProfile(null); }}><span>🤝</span><strong>{laboratoryCount}</strong><small>laboratoires suivis</small></button>
          <button type="button" className={openDisputeCount ? "alert" : ""} onClick={() => { setView("litiges"); setSelectedProfile(null); }}><span>⚠️</span><strong>{openDisputeCount}</strong><small>litiges à traiter</small></button>
          <button type="button" onClick={() => setView("reports")}><span>✓</span><strong>{items.filter((item) => item.status === "completed").length}</strong><small>comptes rendus</small></button>
        </section>

        {composerOpen && (
          <section className="laboratory-composer">
            <header><div><span>AGENDA PARTAGÉ</span><h2>Prendre un RDV</h2><p>Les informations essentielles, sans surcharger le formulaire.</p></div><button type="button" onClick={() => setComposerOpen(false)}>×</button></header>
            <label className="laboratory-external-toggle"><input type="checkbox" checked={!form.isLaboratory} onChange={(event) => { const external = event.target.checked; setForm((current) => ({ ...current, isLaboratory: !external, laboratory: "", subject: external ? "" : "Rendez-vous" })); }} /><span><strong>Il ne s’agit pas d’un laboratoire</strong><small>Le rendez-vous restera visible dans l’agenda, sans alerte de préparation J−4.</small></span></label>
            <div className="laboratory-form-grid compact">
              <label className="laboratory-primary-field"><span>{form.isLaboratory ? "Choix du laboratoire *" : "Titre du rendez-vous *"}</span><input list={form.isLaboratory ? "laboratory-profile-options" : undefined} value={form.laboratory} onChange={(event) => { const laboratory = event.target.value; setForm((current) => ({ ...current, laboratory })); }} placeholder={form.isLaboratory ? "Rechercher un laboratoire…" : "Ex. Expert-comptable, entretien réseau…"} /></label>
              <datalist id="laboratory-profile-options">{profiles.map((profile) => <option key={profile.id} value={profile.name} />)}</datalist>
              <label><span>Date *</span><input type="date" value={form.scheduledDate} onInput={(event) => { const scheduledDate = event.currentTarget.value; setForm((current) => ({ ...current, scheduledDate })); }} onChange={(event) => { const scheduledDate = event.target.value; setForm((current) => ({ ...current, scheduledDate })); }} onBlur={(event) => { const scheduledDate = event.currentTarget.value; setForm((current) => ({ ...current, scheduledDate })); }} /></label>
              <label><span>Heure *</span><input type="time" value={form.scheduledTime} onInput={(event) => { const scheduledTime = event.currentTarget.value; setForm((current) => ({ ...current, scheduledTime })); }} onChange={(event) => { const scheduledTime = event.target.value; setForm((current) => ({ ...current, scheduledTime })); }} onBlur={(event) => { const scheduledTime = event.currentTarget.value; setForm((current) => ({ ...current, scheduledTime })); }} /></label>
              <label><span>Durée</span><select value={form.durationMinutes} onChange={(event) => { const durationMinutes = Number(event.target.value); setForm((current) => ({ ...current, durationMinutes })); }}><option value={30}>30 min</option><option value={60}>1 heure</option><option value={90}>1 h 30</option><option value={120}>2 heures</option></select></label>
              <label className="wide"><span>Préparation / points à négocier</span><textarea value={form.notes} onChange={(event) => { const notes = event.target.value; setForm((current) => ({ ...current, notes })); }} placeholder={form.isLaboratory ? "Inventaire, périmés, conditions, produits du panier, documents à préparer…" : "Points à préparer pour ce rendez-vous…"} /></label>
            </div>
            <footer><p>{form.isLaboratory ? "🔔 Quatre jours avant, chaque officine concernée pourra confirmer inventaire et périmés." : "🗓️ Ce rendez-vous apparaîtra uniquement dans l’agenda."}</p><div><button type="button" className="quiet" onClick={() => setComposerOpen(false)}>Annuler</button><button type="button" disabled={saving} onClick={() => void createItem()}>{saving ? "Enregistrement…" : "✓ Enregistrer le RDV"}</button></div></footer>
          </section>
        )}

        <section className="laboratory-workspace">
          <div className="laboratory-toolbar">
            <div className="laboratory-tabs">
              <button type="button" className={view === "agenda" ? "active" : ""} onClick={() => setView("agenda")}>🗓️ RDV labo</button>
              <button type="button" className={view === "reports" ? "active" : ""} onClick={() => setView("reports")}>🧾 Comptes rendus</button>
              <button type="button" className={view === "data" ? "active" : ""} onClick={() => { setView("data"); setSelectedProfile(null); }}>📊 Données labo</button>
              <button type="button" className={view === "dossiers" ? "active" : ""} onClick={() => { setView("dossiers"); setSelectedProfile(null); }}>📂 Fiches labo</button>
              <button type="button" className={view === "relances" ? "active" : ""} onClick={() => setView("relances")}>⏰ Relances <b>{dueItems.length}</b></button>
              <button type="button" className={view === "litiges" ? "active" : ""} onClick={() => { setView("litiges"); setSelectedProfile(null); }}>⚠️ Litiges <b>{openDisputeCount}</b></button>
            </div>
            <label className="laboratory-search"><span>⌕</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Laboratoire, contact, sujet…" /></label>
          </div>

          {view === "data" ? (
            <div className="laboratory-data-page">
              <header><div><span>DONNÉES LABORATOIRES</span><h2>Contacts, conditions et documents</h2><p>Les informations déjà enregistrées dans les fiches sont réunies ici, sans double saisie.</p></div><b>{visibleProfiles.length} laboratoire{visibleProfiles.length > 1 ? "s" : ""}</b></header>
              <div className="laboratory-data-summary">
                <article><span>👥</span><strong>{visibleProfiles.filter((profile) => profile.delegateName || profile.regionalName || profile.headquartersContact).length}</strong><small>contacts renseignés</small></article>
                <article><span>💶</span><strong>{visibleProfiles.filter((profile) => profile.commercialTerms || profile.francoValue || profile.annualTerms.length).length}</strong><small>conditions disponibles</small></article>
                <article><span>📎</span><strong>{visibleProfiles.reduce((total, profile) => total + profile.documents.length, 0)}</strong><small>documents labo</small></article>
                <article><span>⚑</span><strong>{visibleProfiles.reduce((total, profile) => total + profile.workstreams.filter((item) => item.status === "open").length, 0)}</strong><small>opérations ouvertes</small></article>
              </div>
              <div className="laboratory-data-grid">
                {visibleProfiles.map((profile) => {
                  const openWork = profile.workstreams.filter((item) => item.status === "open").length;
                  return <article key={profile.id}>
                    <header><span>{profile.name.slice(0, 2).toLocaleUpperCase("fr")}</span><div><h3>{profile.name}</h3><small>{profile.pharmacies.join(" · ") || "Pharmacie à préciser"}</small></div></header>
                    <dl><div><dt>Contact</dt><dd>{profile.delegateName || profile.regionalName || profile.headquartersContact || "À compléter"}</dd></div><div><dt>Responsable</dt><dd>{profile.assignedTo || "Non attribué"}</dd></div><div><dt>Conditions</dt><dd>{profile.commercialTerms || (profile.francoValue ? `Franco ${profile.francoValue}${profile.francoMode === "units" ? " unités" : " €"}` : "À compléter")}</dd></div><div><dt>Documents / opérations</dt><dd>{profile.documents.length} document{profile.documents.length > 1 ? "s" : ""} · {openWork} ouverte{openWork > 1 ? "s" : ""}</dd></div></dl>
                    <button type="button" onClick={() => { setView("dossiers"); openProfile(profile); }}>Ouvrir la fiche labo →</button>
                  </article>;
                })}
                {!visibleProfiles.length && <div className="laboratory-empty"><span>📊</span><strong>Aucune donnée labo dans cette vue</strong><p>Créez une fiche laboratoire pour centraliser contacts, conditions et documents.</p><button type="button" onClick={() => { setView("dossiers"); void createProfile(); }}>＋ Créer une fiche labo</button></div>}
              </div>
            </div>
          ) : view === "litiges" ? (
            <div className="laboratory-disputes-page">
              <header><div><span>SUIVI CENTRALISÉ</span><h2>Litiges laboratoires</h2><p>Modifiez, relancez ou classez chaque dossier comme traité.</p></div><b>{openDisputeCount} ouvert{openDisputeCount > 1 ? "s" : ""}</b></header>
              <div className="laboratory-dispute-list">
                {visibleDisputes.map(({ profile, dispute }) => <article key={`${profile.id}-${dispute.id}`} className={dispute.status}>
                  <header><div><span>{profile.name}</span><strong>{dispute.status === "open" ? "À traiter" : "Traité"}</strong></div><button type="button" onClick={() => changeDispute(profile, dispute.id, { status: dispute.status === "open" ? "resolved" : "open" })}>{dispute.status === "open" ? "✓ Marquer traité" : "↺ Rouvrir"}</button></header>
                  <label><span>Objet du litige</span><input value={dispute.title} onChange={(event) => changeDispute(profile, dispute.id, { title: event.target.value })} placeholder="Facture, reprise, remise, livraison…" /></label>
                  <label><span>Détail et réponse attendue</span><textarea value={dispute.note} onChange={(event) => changeDispute(profile, dispute.id, { note: event.target.value })} placeholder="Résumé précis du dossier" /></label>
                  <div className="laboratory-dispute-fields"><label><span>Date de rappel</span><input type="date" value={dispute.dueDate || dispute.openedDate} onChange={(event) => changeDispute(profile, dispute.id, { dueDate: event.target.value })} /></label><label><span>Responsable</span><select value={dispute.assignedTo || profile.assignedTo || initialOperator} onChange={(event) => changeDispute(profile, dispute.id, { assignedTo: event.target.value })}>{missionOperators.map((item) => <option key={`${item.pharmacy}-${item.name}`} value={item.name}>{item.name} · {item.pharmacy}</option>)}</select></label></div>
                  <footer><button type="button" onClick={() => void followDisputeInNotes(profile, dispute)}>{dispute.followedInNotesAt ? "✓ Déjà dans le bloc-notes" : "📌 Suivre dans mon bloc-notes"}</button><button type="button" className="save" onClick={() => void saveProfile(profile, false)}>💾 Enregistrer le litige</button><button type="button" onClick={() => { setView("dossiers"); openProfile(profile); }}>Ouvrir la fiche labo →</button></footer>
                </article>)}
                {!visibleDisputes.length && <div className="laboratory-empty"><span>✓</span><strong>Aucun litige dans cette vue</strong><p>Les litiges ajoutés depuis une fiche laboratoire apparaîtront ici.</p></div>}
              </div>
            </div>
          ) : view === "dossiers" ? (
            selectedProfile ? (
              <LaboratoryProfileEditor
                profile={selectedProfile}
                followups={items}
                onChange={setSelectedProfile}
                onSave={(profile) => void saveProfile(profile ?? selectedProfile)}
                onDelete={() => void deleteProfile(selectedProfile)}
                onClose={() => setSelectedProfile(null)}
                operator={initialOperator}
                onMoveFollowup={openReschedule}
                onDeleteFollowup={(item) => void deleteItem(item)}
                onPrepareFollowup={openVisitPreparation}
                onCompleteFollowup={openLaboratoryReport}
                onFollowDispute={(profile, dispute) => void followDisputeInNotes(profile, dispute)}
                onCreateBriOperation={(profile, operationType) => void createBriOperation(profile, operationType)}
                onSetBriPayment={(profile, entry, paid) => void setBriPayment(profile, entry, paid)}
                onOpenBriOperation={openBriOperation}
                saving={saving}
                onPlan={() => {
                  setForm((current) => ({
                    ...current,
                    laboratory: selectedProfile.name,
                    isLaboratory: true,
                    subject: "Rendez-vous",
                    contactName: selectedProfile.delegateName,
                    contactRole: selectedProfile.delegateRole || "Délégué(e)",
                    phone: selectedProfile.delegatePhone,
                    email: selectedProfile.delegateEmail,
                    pharmacy: selectedProfile.pharmacies[0] ?? initialPharmacy,
                    assignedTo: selectedProfile.assignedTo || initialOperator,
                  }));
                  openComposerAt();
                }}
              />
            ) : (
              <div className="laboratory-directory">
                <header><div><span>ANNUAIRE ACTIF</span><h2>Vos partenaires laboratoire</h2><p>Une fiche courte au quotidien, tous les détails disponibles en un clic.</p></div><button type="button" disabled={saving} onClick={() => void createProfile()}>＋ Nouveau laboratoire</button></header>
                <div className="laboratory-directory-grid">
                  {visibleProfiles.map((profile) => {
                    const profileItems = items.filter((item) => item.laboratory.toLocaleLowerCase("fr") === profile.name.toLocaleLowerCase("fr"));
                    const next = profileItems.filter((item) => item.status === "planned").sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate))[0];
                    const openWork = profile.workstreams.filter((entry) => entry.status === "open").length + profile.disputes.filter((entry) => entry.status === "open").length;
                    return (
                      <article key={profile.id} onClick={() => openProfile(profile)}>
                        <div className="laboratory-contact-logo">{profile.name.slice(0, 2).toLocaleUpperCase("fr")}</div>
                        <div className="laboratory-directory-info"><span>{profile.pharmacies.join(" · ") || "Pharmacie à préciser"}</span><h3>{profile.name}</h3><p>{profile.delegateName || "Contact à compléter"}{profile.delegateRole ? ` · ${profile.delegateRole}` : ""}</p></div>
                        <div className="laboratory-directory-kpis"><span>🗓 {next ? `${followupDate(next.scheduledDate)} ${next.scheduledTime}` : "Aucun RDV"}</span><span className={openWork ? "alert" : ""}>⚑ {openWork} suivi{openWork > 1 ? "s" : ""}</span><span>👤 {profile.assignedTo || "Non attribué"}</span></div>
                        <button type="button">Ouvrir le dossier →</button>
                      </article>
                    );
                  })}
                  {!visibleProfiles.length && <div className="laboratory-empty"><span>📂</span><strong>Aucun dossier dans cette vue</strong><p>Créez un laboratoire puis ajoutez contacts, conditions, documents et rendez-vous.</p><button type="button" onClick={() => void createProfile()}>＋ Créer le premier dossier</button></div>}
                </div>
              </div>
            )
          ) : (
            <div className="laboratory-agenda">
              <div className="laboratory-agenda-head"><div><span>{view === "relances" ? "À TRAITER" : view === "reports" ? "COMPTES RENDUS" : "AGENDA LABO"}</span><h2>{view === "relances" ? "Relances arrivées à échéance" : view === "reports" ? "Comptes rendus laboratoire" : "Rendez-vous laboratoire"}</h2><p>{view === "reports" ? "Retrouvez les visites clôturées, leurs conclusions, pièces jointes et prochaines actions." : view === "agenda" ? "Consultez ou planifiez les rendez-vous partagés avec l’équipe." : "Les actions labo arrivées à échéance sont regroupées ici."}</p></div><b>{filtered.length} élément{filtered.length > 1 ? "s" : ""}</b></div>
              {view === "agenda" && (
                <LaboratoryCalendar
                  items={filtered}
                  mode={calendarMode}
                  selectedDate={calendarDate}
                  onSelectDate={setCalendarDate}
                  onModeChange={setCalendarMode}
                  onCreateSlot={(date, time) => {
                    setCalendarDate(date);
                    openComposerAt(date, time);
                  }}
                />
              )}
              <div className="laboratory-list">
                {filtered.filter((item) => view !== "agenda" || calendarMode === "list" || item.scheduledDate === calendarDate).map((item) => {
                  const late = item.status === "planned" && item.scheduledDate < today;
                  const current = item.status === "planned" && item.scheduledDate === today;
                  return (
                    <article key={item.id} className={`${item.priority} ${late ? "late" : ""} ${item.status}`}>
                      <div className="laboratory-date-block"><strong>{new Date(`${item.scheduledDate}T12:00:00`).toLocaleDateString("fr-FR", { day: "2-digit" })}</strong><span>{new Date(`${item.scheduledDate}T12:00:00`).toLocaleDateString("fr-FR", { month: "short" })}</span><small>{item.scheduledTime || "Journée"}{item.scheduledTime ? `–${laboratoryEndTime(item.scheduledTime, item.durationMinutes)}` : ""}</small></div>
                      <div className="laboratory-main-info">
                        <div className="laboratory-row-top"><span className="laboratory-name">{item.laboratory}</span>{!item.isLaboratory && <span className="laboratory-external-badge">RDV hors labo</span>}<span className={`laboratory-priority ${item.priority}`}>{item.priority === "urgent" ? "Urgent" : item.priority === "important" ? "Important" : "Normal"}</span>{late && <span className="laboratory-overdue">En retard</span>}{current && <span className="laboratory-today">Aujourd’hui</span>}{item.status === "completed" && <span className="laboratory-done">Terminé</span>}</div>
                        <h3>{item.subject}</h3>
                        <p>{[item.contactName, item.contactRole, item.notes].filter(Boolean).join(" · ") || "Aucune note de préparation."}</p>
                        <div className="laboratory-meta"><span>🏥 {item.pharmacy}</span><span>⏱ {item.durationMinutes} min</span><span>👤 {item.assignedTo || "Non attribué"}</span>{item.email && <a href={`mailto:${item.email}?subject=${encodeURIComponent(`${item.laboratory} · ${item.subject}`)}`}>✉ Écrire</a>}{item.nextAction && <span>↗ {item.nextAction}</span>}</div>
                        {item.isLaboratory && item.status === "planned" && <div className="laboratory-preparation-status"><strong>Préparation inventaire & périmés</strong>{(Object.keys(item.preparationStatus).length ? Object.keys(item.preparationStatus) : [item.pharmacy]).map((pharmacy) => <button type="button" key={pharmacy} className={item.preparationStatus[pharmacy] ? "done" : ""} onClick={() => void togglePreparation(item, pharmacy)}>{item.preparationStatus[pharmacy] ? "✓" : "○"} {pharmacy} · {item.preparationStatus[pharmacy] ? "traité" : "pas traité"}</button>)}</div>}
                        {item.outcome && <blockquote><strong>Compte-rendu</strong>{item.outcome}</blockquote>}
                      </div>
                      <div className="laboratory-actions">
                        {item.status === "planned" ? <>
                          {item.scheduledDate > today && <button type="button" className="prepare" onClick={() => openVisitPreparation(item)}>✍ Préparer</button>}
                          {item.scheduledDate <= today && <button type="button" className="complete" onClick={() => openLaboratoryReport(item)}>✓ Compte-rendu</button>}
                          <button type="button" onClick={() => openReschedule(item)}>🗓 Déplacer</button>
                        </> : <button type="button" onClick={() => void updateItem(item, "reopen")}>↺ Rouvrir</button>}
                        <button type="button" className="delete" onClick={() => void deleteItem(item)} aria-label={`Supprimer ${item.laboratory}`}>🗑</button>
                      </div>
                    </article>
                  );
                })}
                {!filtered.filter((item) => view !== "agenda" || calendarMode === "list" || item.scheduledDate === calendarDate).length && <div className="laboratory-empty compact"><span>{loading ? "…" : "✓"}</span><strong>{loading ? "Chargement de l’agenda" : "Aucun rendez-vous ce jour"}</strong><p>{loading ? "Les rendez-vous arrivent." : "Choisissez une autre date ou planifiez le prochain échange."}</p></div>}
              </div>
            </div>
          )}
        </section>
      </div>

      {createdConfirmation && (
        <div className="laboratory-modal-layer laboratory-success-layer" role="dialog" aria-modal="true" aria-labelledby="laboratory-success-title">
          <section>
            <div className="laboratory-success-icon">✓</div>
            <span>AGENDA MIS À JOUR</span>
            <h2 id="laboratory-success-title">Rendez-vous enregistré</h2>
            <p><strong>{createdConfirmation.laboratory}</strong><br />{followupDate(createdConfirmation.scheduledDate)} · {createdConfirmation.scheduledTime}–{laboratoryEndTime(createdConfirmation.scheduledTime, createdConfirmation.durationMinutes)}</p>
            {createdConfirmation.isLaboratory && <small>La préparation inventaire et périmés apparaîtra sur le portail quatre jours avant.</small>}
            <button type="button" onClick={() => setCreatedConfirmation(null)}>Parfait</button>
          </section>
        </div>
      )}

      {disputeReminderProfile && (
        <div className="laboratory-modal-layer laboratory-dispute-reminder" role="dialog" aria-modal="true" aria-labelledby="laboratory-dispute-reminder-title">
          <section>
            <div className="laboratory-warning-icon">!</div>
            <span>LITIGE NON TRAITÉ</span>
            <h2 id="laboratory-dispute-reminder-title">{disputeReminderProfile.name}</h2>
            <p>{disputeReminderProfile.disputes.filter((item) => item.status === "open").length} litige{disputeReminderProfile.disputes.filter((item) => item.status === "open").length > 1 ? "s restent" : " reste"} à suivre sur cette fiche.</p>
            <div>{disputeReminderProfile.disputes.filter((item) => item.status === "open").slice(0, 3).map((item) => <small key={item.id}>⚠️ {item.title || "Litige à préciser"}{item.dueDate ? ` · rappel ${followupDate(item.dueDate)}` : ""}</small>)}</div>
            <footer><button type="button" className="quiet" onClick={() => setDisputeReminderProfile(null)}>Continuer sur la fiche</button><button type="button" onClick={() => { setDisputeReminderProfile(null); setSelectedProfile(null); setView("litiges"); }}>Ouvrir le suivi des litiges</button></footer>
          </section>
        </div>
      )}

      {reschedulingItem && (
        <div className="laboratory-modal-layer" role="dialog" aria-modal="true" aria-labelledby="laboratory-reschedule-title">
          <section>
            <header><div><span>DÉPLACER LE RENDEZ-VOUS</span><h2 id="laboratory-reschedule-title">{reschedulingItem.laboratory} · {reschedulingItem.subject}</h2></div><button type="button" onClick={() => setReschedulingItem(null)}>×</button></header>
            <div className="laboratory-reschedule-fields"><label><span>Nouvelle date</span><input type="date" value={rescheduleDate} onInput={(event) => setRescheduleDate(event.currentTarget.value)} onChange={(event) => setRescheduleDate(event.target.value)} onBlur={(event) => setRescheduleDate(event.currentTarget.value)} /></label><label><span>Nouvelle heure</span><input type="time" value={rescheduleTime} onInput={(event) => setRescheduleTime(event.currentTarget.value)} onChange={(event) => setRescheduleTime(event.target.value)} onBlur={(event) => setRescheduleTime(event.currentTarget.value)} /></label></div>
            <footer><button type="button" className="quiet" onClick={() => setReschedulingItem(null)}>Annuler</button><button type="button" onClick={() => void confirmReschedule()}>✓ Confirmer le déplacement</button></footer>
          </section>
        </div>
      )}

      {preparingItem && (
        <div className="laboratory-modal-layer laboratory-visit-preparation" role="dialog" aria-modal="true" aria-labelledby="laboratory-preparation-title">
          <section>
            <header><div><span>PRÉPARER LA VISITE</span><h2 id="laboratory-preparation-title">{preparingItem.laboratory} · {followupDate(preparingItem.scheduledDate)}</h2></div><button type="button" onClick={() => setPreparingItem(null)}>×</button></header>
            <p>Préparez les points à aborder, produits, périmés, conditions et décisions attendues. Après le rendez-vous, ce contenu préremplira automatiquement le compte rendu.</p>
            {laboratoryExpiryPending(expiryManagement) && <div className="laboratory-expiry-reminder"><span>!</span><p><strong>À reprendre pendant ce rendez-vous</strong><small>Un reliquat, un dispatch ou un paiement laboratoire reste en attente depuis le précédent échange.</small></p></div>}
            <LaboratoryExpiryManagementFields value={expiryManagement} onChange={setExpiryManagement} />
            <label><span>Trame de visite</span><textarea autoFocus value={preparationNotes} onChange={(event) => setPreparationNotes(event.target.value)} placeholder={"Objectifs du rendez-vous\nProduits / référencement à négocier\nPérimés, reprises ou litiges\nConditions commerciales attendues\nQuestions et décisions à obtenir"} /></label>
            <footer><button type="button" className="quiet" onClick={() => setPreparingItem(null)}>Annuler</button><button type="button" onClick={() => void confirmVisitPreparation()}>💾 Enregistrer la préparation</button></footer>
          </section>
        </div>
      )}

      {closingItem && (
        <div className="laboratory-modal-layer laboratory-report-layer" role="dialog" aria-modal="true" aria-labelledby="laboratory-closing-title">
          <section className="laboratory-report-editor">
            <header><div><span>COMPTE-RENDU</span><h2 id="laboratory-closing-title">{closingItem.laboratory} · {closingItem.subject}</h2></div><button type="button" onClick={() => setClosingItem(null)}>×</button></header>
            <p className="laboratory-report-intro">La préparation du rendez-vous est reprise ci-dessous. Complétez-la pendant la visite, puis joignez les documents reçus.</p>
            <div className="laboratory-report-meta"><span><small>Date</small><b>{followupDate(closingItem.scheduledDate)} · {closingItem.scheduledTime}</b></span><span><small>Délégué</small><b>{closingItem.contactName || "Non renseigné"}</b></span><span><small>Opérateur</small><b>{closingItem.assignedTo || initialOperator}</b></span><span><small>Pharmacie</small><b>{closingItem.pharmacy}</b></span></div>
            <LaboratoryExpiryManagementFields value={expiryManagement} onChange={setExpiryManagement} />
            <label className="laboratory-report-notes"><span>🗣 Notes de visite et compte rendu</span><textarea autoFocus value={outcome} onChange={(event) => setOutcome(event.target.value)} placeholder="Produits évoqués, conditions obtenues, décisions, engagements, périmés, points à relancer…" /></label>
            <label><span>Prochaine action</span><input value={nextAction} onChange={(event) => setNextAction(event.target.value)} placeholder="Ex. relancer pour les conditions avant vendredi" /></label>
            <section className="laboratory-report-attachments"><header><div><b>📎 Pièces jointes / scans</b><small>Photos et PDF sont allégés automatiquement. Word, Excel et CSV sont également acceptés.</small></div><label className={reportUploading ? "uploading" : ""}>{reportUploading ? "Compression et envoi…" : "＋ Ajouter des fichiers"}<input type="file" hidden multiple disabled={reportUploading} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv" onChange={(event) => void uploadReportFiles(event.target.files)} /></label></header>{reportAttachments.length > 0 && <div>{reportAttachments.map((file) => <span key={file.id}><a href={file.url} target="_blank" rel="noreferrer">📄 {file.title}</a><button type="button" aria-label={`Retirer ${file.title}`} onClick={() => setReportAttachments((current) => current.filter((item) => item.id !== file.id))}>×</button></span>)}</div>}</section>
            <section className="laboratory-report-options">
              <label className="laboratory-report-check"><input type="checkbox" checked={reportNextAppointment} onChange={(event) => setReportNextAppointment(event.target.checked)} /><span>📅 Un prochain rendez-vous a été fixé pendant cette visite</span></label>
              {reportNextAppointment && <div><label><span>Date</span><input type="date" value={reportNextDate} onChange={(event) => setReportNextDate(event.target.value)} /></label><label><span>Heure</span><input type="time" value={reportNextTime} onChange={(event) => setReportNextTime(event.target.value)} /></label></div>}
              <label className="laboratory-report-check"><input type="checkbox" checked={reportReminderEnabled} onChange={(event) => setReportReminderEnabled(event.target.checked)} /><span>🔔 Ajouter un rappel à une date précise dans un bloc-notes</span></label>
              {reportReminderEnabled && <div><label><span>Date du rappel</span><input type="date" value={reportReminderDate} onChange={(event) => setReportReminderDate(event.target.value)} /></label><label><span>Bloc-notes de</span><select value={reportReminderOwner} onChange={(event) => setReportReminderOwner(event.target.value)}>{missionOperators.map((item) => <option key={`${item.pharmacy}-${item.name}`} value={item.name}>{item.name} · {item.pharmacy}</option>)}</select></label></div>}
            </section>
            <footer><button type="button" className="quiet" onClick={() => setClosingItem(null)}>Annuler</button><button type="button" disabled={reportUploading} onClick={() => void completeItem()}>✓ Enregistrer le compte rendu</button></footer>
          </section>
        </div>
      )}

      {notice && <div className="module-toast">{notice}</div>}
    </main>
  );
}

function normalizeDropSearch(value: string) {
  return value.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLocaleLowerCase("fr").replace(/[^a-z0-9]+/g, " ").trim();
}

function dropCategoryIcon(category: string) {
  if (category === "Collyres") return "👁️";
  if (category === "Pommades ophtalmiques") return "🧴";
  if (category.includes("auriculaires")) return "👂";
  return "💧";
}

function lensAdvice(value: string) {
  if (value === "✓") return { icon: "✓", label: "Compatibles", tone: "ok" };
  if (value.includes("🕒")) return { icon: "◷", label: value.replace("🕒", "").trim() || "Attendre 15 min", tone: "wait" };
  if (value === "X") return { icon: "×", label: "Retirer les lentilles", tone: "no" };
  if (value === "—") return { icon: "–", label: "Sans objet", tone: "neutral" };
  if (value === "Voir la ligne équivalente") return { icon: "↳", label: "Voir l’équivalent", tone: "neutral" };
  return { icon: "i", label: value, tone: "neutral" };
}

function DropProductCard({ product }: { product: DropProduct }) {
  const lens = lensAdvice(product.lenses);
  const isAlias = product.conservation === "Voir la ligne équivalente";
  return (
    <article className={`drop-card${isAlias ? " is-alias" : ""}`}>
      <div className="drop-card-topline">
        <span className="drop-family">{dropCategoryIcon(product.category)} {product.category}</span>
        {product.refrigerated && <span className="drop-cold">❄️ Au froid avant ouverture</span>}
      </div>
      <h3>{product.name}</h3>
      {isAlias ? (
        <div className="drop-alias-note"><span>↳</span><p><b>Présentation équivalente</b><small>Consulter la ligne de la spécialité de référence juste au-dessus.</small></p></div>
      ) : (
        <div className="drop-facts">
          <div><span>Après ouverture</span><strong>{product.conservation}</strong></div>
          <div><span>Contenu du flacon</span><strong>{product.drops}</strong><small>{product.drops.toLocaleLowerCase("fr").includes("unidose") ? "" : "gouttes environ"}</small></div>
        </div>
      )}
      <div className={`drop-lens ${lens.tone}`}>
        <span>{lens.icon}</span>
        <p><small>Lentilles</small><strong>{lens.label}</strong></p>
      </div>
      {product.info && <p className="drop-info"><span>ℹ️</span>{product.info}</p>}
    </article>
  );
}

function DropCountModule({ onBack }: { onBack: () => void }) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<(typeof dropCategories)[number]>("Tous");
  const [lensOnly, setLensOnly] = useState(false);
  const [shortConservation, setShortConservation] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  const results = useMemo(() => {
    const needle = normalizeDropSearch(query);
    const shortPattern = /(jeter|immédiatement|7 j|10 j|15 j|15 jours)/i;
    return dropProducts
      .filter((product) => category === "Tous" || product.category === category)
      .filter((product) => !lensOnly || product.lenses === "✓")
      .filter((product) => !shortConservation || shortPattern.test(product.conservation))
      .filter((product) => !needle || normalizeDropSearch(`${product.name} ${product.category} ${product.info}`).includes(needle))
      .sort((a, b) => {
        if (needle) {
          const aStarts = normalizeDropSearch(a.name).startsWith(needle) ? 0 : 1;
          const bStarts = normalizeDropSearch(b.name).startsWith(needle) ? 0 : 1;
          if (aStarts !== bStarts) return aStarts - bStarts;
        }
        return a.name.localeCompare(b.name, "fr");
      });
  }, [category, lensOnly, query, shortConservation]);

  const visibleResults = showAll || query ? results : results.slice(0, 30);
  const reset = () => {
    setQuery("");
    setCategory("Tous");
    setLensOnly(false);
    setShortConservation(false);
    setShowAll(false);
    window.setTimeout(() => searchRef.current?.focus(), 0);
  };

  return (
    <main className="drop-module">
      <header className="module-header drop-module-header">
        <button className="back-button" onClick={onBack}>← Accueil</button>
        <div className="module-brand"><span className="brand-mark">sp</span><div><strong>saint-laurent</strong><small>PHARMACIES</small></div></div>
        <div className="module-header-actions"><ModuleToolsLink /><span className="module-status"><i />Référentiel 11.2024</span></div>
      </header>

      <section className="drop-hero">
        <div>
          <p className="drop-kicker">💧 RÉFÉRENTIEL COMPTOIR</p>
          <h1>Nombre de gouttes</h1>
          <p>Retrouver en quelques secondes la conservation après ouverture, le contenu du flacon et la conduite à tenir avec les lentilles.</p>
        </div>
        <div className="drop-hero-stats">
          <span><b>{dropProducts.length}</b> références</span>
          <span><b>5</b> familles</span>
          <span><b>1</b> réponse immédiate</span>
        </div>
      </section>

      <section className="drop-search-panel">
        <label htmlFor="drop-search">Quel produit recherchez-vous ?</label>
        <div className="drop-search-row">
          <span>⌕</span>
          <input
            ref={searchRef}
            id="drop-search"
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Ex. Azopt, Tobrex, Otipax, ZymaD…"
          />
          {(query || category !== "Tous" || lensOnly || shortConservation) && <button onClick={reset}>Effacer</button>}
        </div>
        <div className="drop-filter-scroll" aria-label="Filtrer par famille">
          {dropCategories.map((item) => (
            <button key={item} className={category === item ? "active" : ""} onClick={() => { setCategory(item); setShowAll(false); }}>
              {item === "Tous" ? "✨" : dropCategoryIcon(item)} {item}
            </button>
          ))}
        </div>
        <div className="drop-smart-filters">
          <button className={lensOnly ? "active" : ""} onClick={() => setLensOnly((value) => !value)}>✓ Compatible lentilles</button>
          <button className={shortConservation ? "active alert" : ""} onClick={() => setShortConservation((value) => !value)}>⏱️ Conservation courte</button>
          <p><b>{results.length}</b> résultat{results.length > 1 ? "s" : ""}</p>
        </div>
      </section>

      <aside className="drop-safety-note">
        <span>👁️</span>
        <p><strong>Réflexe lentilles</strong> Le port reste déconseillé en cas d’infection oculaire. Le symbole ◷ signifie : attendre au moins 15 minutes avant de les remettre.</p>
      </aside>

      {results.length ? (
        <>
          <section className="drop-results" aria-live="polite">
            {visibleResults.map((product, index) => <DropProductCard key={`${product.category}-${product.name}-${index}`} product={product} />)}
          </section>
          {!showAll && !query && results.length > visibleResults.length && (
            <button className="drop-show-more" onClick={() => setShowAll(true)}>Afficher les {results.length} références</button>
          )}
        </>
      ) : (
        <section className="drop-empty">
          <span>🔎</span><h2>Aucun produit trouvé</h2><p>Essayez une partie du nom ou choisissez une autre famille.</p><button onClick={reset}>Réinitialiser la recherche</button>
        </section>
      )}

      <footer className="drop-source">
        <p><strong>Source :</strong> document « Nombre de goutte », mise à jour @FG 11/2024.</p>
        <p>Outil d’aide au comptoir. En cas de doute, de modification récente ou de situation particulière, vérifier le RCP et les informations du fabricant.</p>
      </footer>
    </main>
  );
}

type MadEquipment = {
  id: number;
  name: string;
  category: string;
  pharmacy: string;
  inventoryCode: string;
  serialNumber: string;
  status: "available" | "rented" | "maintenance" | "retired";
  notes: string;
  createdAt: number;
  updatedAt: number;
};

type MadRental = {
  id: number;
  equipmentId: number;
  materialName: string;
  pharmacy: string;
  patientName: string;
  phone: string;
  startDate: string;
  expectedReturnDate: string;
  prescriptionEndDate: string;
  prescriptionRenewalDate: string;
  prescriptionStatus: "missing" | "received" | "not_required";
  depositCents: number;
  prescriber: string;
  assignedBy: string;
  notes: string;
  status: "active" | "returned";
  returnedAt: number;
  returnedBy: string;
  createdAt: number;
  updatedAt: number;
};

type MadData = { equipment: MadEquipment[]; rentals: MadRental[]; syncedAt: number };

type MadHistoricalTarget = {
  name: string;
  pharmacy: "Square" | "Ragadan" | "Laurentine";
  quantity: number;
  code: string;
};

const madHistoricalPark: MadHistoricalTarget[] = [
  { name: "Aérosol pneumatique", pharmacy: "Square", quantity: 9, code: "MAD-SQU-AER-P" },
  { name: "Aérosol sonique", pharmacy: "Square", quantity: 1, code: "MAD-SQU-AER-S" },
  { name: "Fauteuil roulant", pharmacy: "Square", quantity: 1, code: "MAD-SQU-FAU" },
  { name: "Pied à perfusion", pharmacy: "Square", quantity: 2, code: "MAD-SQU-PIED" },
  { name: "Pousse-seringue pour diffuseur Androne", pharmacy: "Square", quantity: 2, code: "MAD-SQU-AND" },
  { name: "Tire-lait Kitett Minikit Pro", pharmacy: "Square", quantity: 4, code: "MAD-SQU-KIT" },
  { name: "Tire-lait Medela Symphony", pharmacy: "Square", quantity: 1, code: "MAD-SQU-MED" },
  { name: "Pied à perfusion", pharmacy: "Ragadan", quantity: 2, code: "MAD-RAG-PIED" },
  { name: "Pousse-seringue pour diffuseur Androne", pharmacy: "Ragadan", quantity: 4, code: "MAD-RAG-AND" },
  { name: "Tensiomètre", pharmacy: "Ragadan", quantity: 1, code: "MAD-RAG-TEN" },
];

function madParkName(value: string) {
  const normalized = value.toLocaleLowerCase("fr").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  if (normalized.includes("pied") && normalized.includes("perf")) return "Pied à perfusion";
  if (normalized.includes("pousse") || normalized.includes("androne")) return "Pousse-seringue pour diffuseur Androne";
  return value.trim();
}

function normalizeMadEquipment(row: Record<string, unknown>): MadEquipment {
  return {
    id: Number(row.id),
    name: String(row.name ?? ""),
    category: String(row.category ?? "Autre matériel"),
    pharmacy: String(row.pharmacy ?? "Square"),
    inventoryCode: String(row.inventoryCode ?? row.inventory_code ?? ""),
    serialNumber: String(row.serialNumber ?? row.serial_number ?? ""),
    status: (["available", "rented", "maintenance", "retired"].includes(String(row.status)) ? row.status : "available") as MadEquipment["status"],
    notes: String(row.notes ?? ""),
    createdAt: Number(row.createdAt ?? row.created_at ?? 0),
    updatedAt: Number(row.updatedAt ?? row.updated_at ?? 0),
  };
}

function normalizeMadRental(row: Record<string, unknown>): MadRental {
  return {
    id: Number(row.id),
    equipmentId: Number(row.equipmentId ?? row.equipment_id),
    materialName: String(row.materialName ?? row.material_name ?? ""),
    pharmacy: String(row.pharmacy ?? "Square"),
    patientName: String(row.patientName ?? row.patient_name ?? ""),
    phone: String(row.phone ?? ""),
    startDate: String(row.startDate ?? row.start_date ?? ""),
    expectedReturnDate: String(row.expectedReturnDate ?? row.expected_return_date ?? ""),
    prescriptionEndDate: String(row.prescriptionEndDate ?? row.prescription_end_date ?? ""),
    prescriptionRenewalDate: String(row.prescriptionRenewalDate ?? row.prescription_renewal_date ?? ""),
    prescriptionStatus: (["missing", "received", "not_required"].includes(String(row.prescriptionStatus ?? row.prescription_status))
      ? (row.prescriptionStatus ?? row.prescription_status) : "received") as MadRental["prescriptionStatus"],
    depositCents: Number(row.depositCents ?? row.deposit_cents ?? 0),
    prescriber: String(row.prescriber ?? ""),
    assignedBy: String(row.assignedBy ?? row.assigned_by ?? ""),
    notes: String(row.notes ?? ""),
    status: String(row.status) === "returned" ? "returned" : "active",
    returnedAt: Number(row.returnedAt ?? row.returned_at ?? 0),
    returnedBy: String(row.returnedBy ?? row.returned_by ?? ""),
    createdAt: Number(row.createdAt ?? row.created_at ?? 0),
    updatedAt: Number(row.updatedAt ?? row.updated_at ?? 0),
  };
}

function normalizeMadData(payload: Record<string, unknown>): MadData {
  return {
    equipment: (Array.isArray(payload.equipment) ? payload.equipment : []).map((row) => normalizeMadEquipment(row as Record<string, unknown>)),
    rentals: (Array.isArray(payload.rentals) ? payload.rentals : []).map((row) => normalizeMadRental(row as Record<string, unknown>)),
    syncedAt: Number(payload.syncedAt ?? 0),
  };
}

const madCategories = [
  "Aérosol pneumatique", "Aérosol sonique", "Fauteuil roulant", "Pied à perfusion",
  "Pompe pour perfusion", "Pousse-seringue pour diffuseur Androne", "Tensiomètre",
  "Tire-lait Kitett Minikit Pro", "Tire-lait Medela Symphony", "Autre matériel",
];

function madRentalAlert(rental: MadRental, today = localDateIso()) {
  if (rental.status !== "active") return "";
  if (rental.expectedReturnDate && rental.expectedReturnDate < today) return "Retour dépassé";
  if (rental.expectedReturnDate === today) return "Retour aujourd’hui";
  if (rental.prescriptionStatus === "missing") return "Ordonnance manquante";
  if (rental.prescriptionRenewalDate && rental.prescriptionRenewalDate <= today) return "Ordonnance à renouveler";
  return "";
}

function MadRentalModule({
  onBack,
  initialPharmacy,
  initialOperator,
}: {
  onBack: () => void;
  initialPharmacy: string;
  initialOperator: string;
}) {
  const [data, setData] = useState<MadData>({ equipment: [], rentals: [], syncedAt: 0 });
  const [mode, setMode] = useState<"rentals" | "equipment" | "history">("rentals");
  const [pharmacyFilter, setPharmacyFilter] = useState(initialPharmacy);
  const [query, setQuery] = useState("");
  const [panel, setPanel] = useState<"rental" | "equipment" | null>(null);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("");
  const today = localDateIso();
  const inThirtyDays = (() => { const value = new Date(); value.setDate(value.getDate() + 30); return localDateIso(value); })();
  const [equipmentDraft, setEquipmentDraft] = useState({
    category: madCategories[0], pharmacy: initialPharmacy, inventoryCode: "", serialNumber: "", notes: "",
  });
  const [rentalDraft, setRentalDraft] = useState({
    equipmentId: 0, patientName: "", phone: "", startDate: today, expectedReturnDate: inThirtyDays,
    prescriptionEndDate: "", prescriptionRenewalDate: "", prescriptionStatus: "received" as MadRental["prescriptionStatus"],
    depositEuros: "", prescriber: "", assignedBy: initialOperator, notes: "",
  });

  const flash = (message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice(""), 3000);
  };

  const load = async () => {
    try {
      const response = await fetch("/api/mad-rentals", { cache: "no-store" });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      setData(normalizeMadData(payload));
    } catch (error) {
      flash(error instanceof Error ? error.message : "Le suivi MAD est indisponible.");
    }
  };

  useEffect(() => {
    void load();
    const timer = startVisiblePolling(load, PORTAL_LIVE_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, []);

  const request = async (method: "POST" | "PATCH", body: Record<string, unknown>, success: string) => {
    setLoading(true);
    try {
      const response = await fetch("/api/mad-rentals", {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      if (payload.equipment && payload.rentals) setData(normalizeMadData(payload));
      else await load();
      flash(success);
      return true;
    } catch (error) {
      flash(error instanceof Error ? error.message : "L’action n’a pas pu être enregistrée.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const active = data.rentals.filter((item) => item.status === "active");
  const overdue = active.filter((item) => item.expectedReturnDate < today);
  const filteredRentals = data.rentals.filter((item) => {
    const pharmacyMatch = pharmacyFilter === "Toutes" || item.pharmacy === pharmacyFilter;
    const modeMatch = mode === "history" ? item.status === "returned" : item.status === "active";
    const text = `${item.materialName} ${item.patientName} ${item.phone} ${item.notes}`.toLocaleLowerCase("fr");
    return pharmacyMatch && modeMatch && (!query.trim() || text.includes(query.trim().toLocaleLowerCase("fr")));
  });
  const filteredEquipment = data.equipment.filter((item) => {
    const pharmacyMatch = pharmacyFilter === "Toutes" || item.pharmacy === pharmacyFilter;
    const text = `${item.name} ${item.category} ${item.inventoryCode} ${item.serialNumber}`.toLocaleLowerCase("fr");
    return pharmacyMatch && (!query.trim() || text.includes(query.trim().toLocaleLowerCase("fr")));
  });
  const historicalMissing = madHistoricalPark.map((target) => {
    const current = data.equipment.filter((item) => item.status !== "retired"
      && item.pharmacy === target.pharmacy
      && madParkName(item.name || item.category) === target.name).length;
    return { ...target, current, missing: Math.max(0, target.quantity - current) };
  });
  const historicalMissingCount = historicalMissing.reduce((total, item) => total + item.missing, 0);
  const parkByPharmacy = ["Square", "Ragadan", "Laurentine"].map((pharmacy) => ({
    pharmacy,
    count: data.equipment.filter((item) => item.pharmacy === pharmacy && item.status !== "retired").length,
  }));
  const equipmentGroups = [...filteredEquipment.reduce((groups, item) => {
    const name = madParkName(item.name || item.category);
    const key = `${item.pharmacy}::${name}`;
    const group = groups.get(key) ?? { key, name, category: item.category, pharmacy: item.pharmacy, items: [] as MadEquipment[] };
    group.items.push(item);
    groups.set(key, group);
    return groups;
  }, new Map<string, { key: string; name: string; category: string; pharmacy: string; items: MadEquipment[] }>()).values()]
    .sort((left, right) => left.pharmacy.localeCompare(right.pharmacy, "fr") || left.name.localeCompare(right.name, "fr"));

  const addEquipment = async () => {
    const ok = await request(
      "POST",
      { action: "equipment", ...equipmentDraft, name: equipmentDraft.category },
      "Matériel ajouté au parc.",
    );
    if (ok) {
      setEquipmentDraft({ category: madCategories[0], pharmacy: equipmentDraft.pharmacy, inventoryCode: "", serialNumber: "", notes: "" });
      setPanel(null);
    }
  };

  const importHistoricalPark = async () => {
    if (!historicalMissingCount || loading) return;
    setLoading(true);
    let imported = 0;
    try {
      for (const [targetIndex, target] of historicalMissing.entries()) {
        if (!target.missing) continue;
        const occupied = new Set(data.equipment
          .filter((item) => item.pharmacy === target.pharmacy && madParkName(item.name || item.category) === target.name)
          .map((item) => item.inventoryCode));
        const availableUnits = Array.from({ length: target.quantity }, (_, index) => index + 1)
          .filter((unit) => !occupied.has(`${target.code}-${String(unit).padStart(3, "0")}`))
          .slice(0, target.missing);
        for (const unit of availableUnits) {
          const response = await fetch("/api/mad-rentals", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: 2_026_081_000_000 + targetIndex * 100 + unit,
              action: "equipment",
              name: target.name,
              category: target.name,
              pharmacy: target.pharmacy,
              inventoryCode: `${target.code}-${String(unit).padStart(3, "0")}`,
              equipmentStatus: "available",
              notes: "Repris du parc MAD historique v50evv le 10/08/2026.",
            }),
          });
          const payload = await response.json();
          if (!response.ok) throw new Error(payload.error || "La reprise du parc MAD a échoué.");
          imported += 1;
        }
      }
      await load();
      flash(`${imported} appareil${imported > 1 ? "s" : ""} repris du parc MAD historique.`);
    } catch (error) {
      await load();
      flash(error instanceof Error ? error.message : "La reprise du parc MAD a échoué.");
    } finally {
      setLoading(false);
    }
  };

  const addRental = async () => {
    const equipment = data.equipment.find((item) => item.id === rentalDraft.equipmentId);
    const ok = await request("POST", {
      action: "rental",
      ...rentalDraft,
      depositCents: Math.round(Number(rentalDraft.depositEuros.replace(",", ".")) * 100) || 0,
      pharmacy: equipment?.pharmacy,
    }, "Location MAD enregistrée.");
    if (ok) {
      setRentalDraft({
        equipmentId: 0, patientName: "", phone: "", startDate: today, expectedReturnDate: inThirtyDays,
        prescriptionEndDate: "", prescriptionRenewalDate: "", prescriptionStatus: "received",
        depositEuros: "", prescriber: "", assignedBy: initialOperator, notes: "",
      });
      setPanel(null);
    }
  };

  return (
    <main className="module-page mad-module">
      <header className="module-header">
        <button type="button" className="module-back" onClick={onBack}>← <span>Retour à l’accueil</span></button>
        <div className="wordmark module-wordmark"><span className="wordmark-symbol"><i /><i /><i /></span><span><strong>saint-laurent</strong><small>pharmacies</small></span></div>
        <div className="module-header-actions"><ModuleToolsLink /><span className="module-status"><i /> MAD synchronisé</span></div>
      </header>
      <section className="mad-hero">
        <div>
          <p className="module-kicker">🛏️ LOCATION & PRÊT DE MATÉRIEL</p>
          <h1>Location MAD</h1>
          <p>Le parc, les patients, les ordonnances et les retours réunis dans une seule vue.</p>
        </div>
        <div className="mad-hero-actions">
          <button className="secondary-action" type="button" onClick={() => { setMode("equipment"); setPanel(null); }}>⚙️ Parc matériel</button>
          <button className="primary-action" type="button" onClick={() => setPanel("rental")}>＋ Nouvelle location</button>
        </div>
      </section>

      <section className="mad-stats" aria-label="Résumé location MAD">
        <button type="button" onClick={() => setMode("rentals")}><strong>{active.length}</strong><span>locations en cours</span></button>
        <button className={overdue.length ? "danger" : ""} type="button" onClick={() => setMode("rentals")}><strong>{overdue.length}</strong><span>retours dépassés</span></button>
        <button type="button" onClick={() => setMode("equipment")}><strong>{data.equipment.filter((item) => item.status === "available").length}</strong><span>matériels disponibles</span></button>
        <button type="button" onClick={() => setMode("history")}><strong>{data.rentals.filter((item) => item.status === "returned").length}</strong><span>retours tracés</span></button>
      </section>

      {notice && <div className="module-notice">{notice}</div>}

      <section className="mad-toolbar">
        <div className="mad-tabs">
          <button className={mode === "rentals" ? "active" : ""} type="button" onClick={() => setMode("rentals")}>🟢 En cours</button>
          <button className={mode === "equipment" ? "active" : ""} type="button" onClick={() => setMode("equipment")}>🧰 Parc matériel</button>
          <button className={mode === "history" ? "active" : ""} type="button" onClick={() => setMode("history")}>↺ Historique</button>
        </div>
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Rechercher patient, matériel, téléphone…" />
        <select value={pharmacyFilter} onChange={(event) => setPharmacyFilter(event.target.value)}>
          <option>Toutes</option><option>Square</option><option>Ragadan</option><option>Laurentine</option>
        </select>
      </section>

      {panel === "rental" && (
        <section className="mad-form-panel">
          <div className="mad-form-heading">
            <div><span>ÉTAPE UNIQUE</span><h2>Nouvelle location</h2><p>Choisissez un appareil disponible, puis fixez l’échéance.</p></div>
            <button type="button" onClick={() => setPanel(null)}>×</button>
          </div>
          <div className="mad-form-grid">
            <label className="wide">Matériel disponible<select value={rentalDraft.equipmentId} onChange={(event) => setRentalDraft({ ...rentalDraft, equipmentId: Number(event.target.value) })}>
              <option value={0}>Choisir un appareil</option>
              {data.equipment.filter((item) => item.status === "available").map((item) => <option key={item.id} value={item.id}>{item.name} · {item.pharmacy}{item.inventoryCode ? ` · ${item.inventoryCode}` : ""}</option>)}
            </select></label>
            <label>Nom complet du patient<input value={rentalDraft.patientName} onChange={(event) => setRentalDraft({ ...rentalDraft, patientName: event.target.value })} placeholder="Mme Monique Dupont" /></label>
            <label>Téléphone<input value={rentalDraft.phone} onChange={(event) => setRentalDraft({ ...rentalDraft, phone: event.target.value })} placeholder="06…" /></label>
            <label>Départ<input type="date" value={rentalDraft.startDate} onChange={(event) => setRentalDraft({ ...rentalDraft, startDate: event.target.value })} /></label>
            <label>Retour prévu<input type="date" value={rentalDraft.expectedReturnDate} onChange={(event) => setRentalDraft({ ...rentalDraft, expectedReturnDate: event.target.value })} /></label>
            <label>Ordonnance<select value={rentalDraft.prescriptionStatus} onChange={(event) => setRentalDraft({ ...rentalDraft, prescriptionStatus: event.target.value as MadRental["prescriptionStatus"] })}>
              <option value="received">Reçue</option><option value="missing">À récupérer</option><option value="not_required">Non nécessaire</option>
            </select></label>
            <label>Renouveler l’ordonnance le<input type="date" value={rentalDraft.prescriptionRenewalDate} onChange={(event) => setRentalDraft({ ...rentalDraft, prescriptionRenewalDate: event.target.value })} /></label>
            <label>Fin ordonnance<input type="date" value={rentalDraft.prescriptionEndDate} onChange={(event) => setRentalDraft({ ...rentalDraft, prescriptionEndDate: event.target.value })} /></label>
            <label>Caution (€)<input inputMode="decimal" value={rentalDraft.depositEuros} onChange={(event) => setRentalDraft({ ...rentalDraft, depositEuros: event.target.value })} placeholder="0,00" /></label>
            <label>Prescripteur<input value={rentalDraft.prescriber} onChange={(event) => setRentalDraft({ ...rentalDraft, prescriber: event.target.value })} /></label>
            <label>Remis par<select value={rentalDraft.assignedBy} onChange={(event) => setRentalDraft({ ...rentalDraft, assignedBy: event.target.value })}>
              {missionOperators.map((operator) => <option key={operator.name}>{operator.name}</option>)}
            </select></label>
            <label className="wide">Note utile<textarea value={rentalDraft.notes} onChange={(event) => setRentalDraft({ ...rentalDraft, notes: event.target.value })} placeholder="Accessoires remis, état du matériel, consignes…" /></label>
          </div>
          <div className="mad-form-actions"><button type="button" onClick={() => setPanel(null)}>Annuler</button><button className="primary-action" disabled={loading} type="button" onClick={addRental}>✓ Enregistrer la location</button></div>
        </section>
      )}

      {panel === "equipment" && (
        <section className="mad-form-panel equipment">
          <div className="mad-form-heading">
            <div><span>INVENTAIRE</span><h2>Ajouter un appareil</h2><p>Un code interne permet d’identifier chaque unité sans ambiguïté.</p></div>
            <button type="button" onClick={() => setPanel(null)}>×</button>
          </div>
          <div className="mad-form-grid">
            <label className="wide">Catégorie du matériel<select value={equipmentDraft.category} onChange={(event) => setEquipmentDraft({ ...equipmentDraft, category: event.target.value })}>{madCategories.map((category) => <option key={category}>{category}</option>)}</select><small>La catégorie devient automatiquement le nom de l’appareil dans le parc.</small></label>
            <label>Pharmacie<select value={equipmentDraft.pharmacy} onChange={(event) => setEquipmentDraft({ ...equipmentDraft, pharmacy: event.target.value })}><option>Square</option><option>Ragadan</option><option>Laurentine</option></select></label>
            <label>Code inventaire<input value={equipmentDraft.inventoryCode} onChange={(event) => setEquipmentDraft({ ...equipmentDraft, inventoryCode: event.target.value })} placeholder="MAD-RAG-001" /></label>
            <label>Numéro de série<input value={equipmentDraft.serialNumber} onChange={(event) => setEquipmentDraft({ ...equipmentDraft, serialNumber: event.target.value })} /></label>
            <label className="wide">Note<input value={equipmentDraft.notes} onChange={(event) => setEquipmentDraft({ ...equipmentDraft, notes: event.target.value })} placeholder="Accessoires, état, rangement…" /></label>
          </div>
          <div className="mad-form-actions"><button type="button" onClick={() => setPanel(null)}>Annuler</button><button className="primary-action" disabled={loading} type="button" onClick={addEquipment}>＋ Ajouter au parc</button></div>
        </section>
      )}

      {mode === "equipment" ? (
        <section className="mad-equipment-grid">
          <div className="mad-section-title"><div><span>🧰</span><h2>Parc matériel</h2></div><div className="mad-park-actions">{historicalMissingCount > 0 && <button className="secondary-action" disabled={loading} type="button" onClick={() => void importHistoricalPark()}>↻ Reprendre le parc historique ({historicalMissingCount})</button>}<button className="primary-action" type="button" onClick={() => setPanel("equipment")}>＋ Ajouter un appareil</button></div></div>
          <div className="mad-park-pharmacy-summary">{parkByPharmacy.map((item) => <button className={pharmacyFilter === item.pharmacy ? "active" : ""} type="button" key={item.pharmacy} onClick={() => setPharmacyFilter(item.pharmacy)}><span>🏥 {item.pharmacy}</span><strong>{item.count}</strong><small>appareil{item.count > 1 ? "s" : ""}</small></button>)}</div>
          {equipmentGroups.length === 0 ? <div className="empty-state">Aucun matériel dans cette vue.</div> : equipmentGroups.map((group) => {
            const available = group.items.filter((item) => item.status === "available").length;
            const rented = group.items.filter((item) => item.status === "rented").length;
            const maintenance = group.items.filter((item) => item.status === "maintenance").length;
            const codes = group.items.map((item) => item.inventoryCode).filter(Boolean).sort();
            return <article className={`mad-equipment-card ${available ? "available" : rented ? "rented" : maintenance ? "maintenance" : "retired"}`} key={group.key}>
              <div className="mad-equipment-icon">{group.category.includes("Fauteuil") ? "♿" : group.category.includes("Tire-lait") ? "🍼" : group.category.includes("Aérosol") ? "🫁" : "🧰"}</div>
              <div><h3>{group.name}</h3><p>{group.pharmacy} · {group.items.length} appareil{group.items.length > 1 ? "s" : ""}{codes.length ? ` · ${codes.join(", ")}` : ""}</p><span>{available} disponible{available > 1 ? "s" : ""}{rented ? ` · ${rented} en location` : ""}{maintenance ? ` · ${maintenance} en maintenance` : ""}</span></div>
            </article>
          })}
        </section>
      ) : (
        <section className="mad-rental-list">
          <div className="mad-section-title"><div><span>{mode === "history" ? "↺" : "🛏️"}</span><h2>{mode === "history" ? "Locations retournées" : "Locations en cours"}</h2></div><small>{filteredRentals.length} dossier{filteredRentals.length > 1 ? "s" : ""}</small></div>
          {filteredRentals.length === 0 ? <div className="empty-state">Aucune location dans cette vue.</div> : filteredRentals.map((item) => {
            const alert = madRentalAlert(item, today);
            return (
              <article className={`mad-rental-card ${alert ? "alert" : ""}`} key={item.id}>
                <div className="mad-rental-main">
                  <div className="mad-rental-icon">{item.materialName.toLocaleLowerCase("fr").includes("fauteuil") ? "♿" : "🛏️"}</div>
                  <div><div className="mad-rental-title"><h3>{item.patientName}</h3>{alert && <span>{alert}</span>}</div><strong>{item.materialName}</strong><p>{item.pharmacy} · remis par {item.assignedBy || "—"} le {followupDate(item.startDate)}</p>{item.notes && <em>{item.notes}</em>}</div>
                </div>
                <div className="mad-rental-dates">
                  <span>Retour prévu<strong>{followupDate(item.expectedReturnDate)}</strong></span>
                  <span>Ordonnance<strong>{item.prescriptionStatus === "missing" ? "À récupérer" : item.prescriptionRenewalDate ? `Renouveler ${followupDate(item.prescriptionRenewalDate)}` : "Conforme"}</strong></span>
                </div>
                {item.status === "active" && <div className="mad-rental-actions">
                  <button disabled={loading} type="button" onClick={() => request("PATCH", { id: item.id, action: "return", returnedBy: initialOperator }, "Retour enregistré, matériel disponible.")}>✓ Matériel retourné</button>
                  <button disabled={loading} type="button" onClick={() => request("PATCH", { id: item.id, action: "extend", days: 7 }, "Retour prolongé de 7 jours.")}>＋ 7 jours</button>
                  {item.prescriptionStatus === "missing" && <button disabled={loading} type="button" onClick={() => request("PATCH", { id: item.id, action: "prescription_received" }, "Ordonnance reçue.")}>📄 Ordonnance reçue</button>}
                  {item.phone && <a href={`tel:${item.phone}`}>☎ Appeler</a>}
                </div>}
              </article>
            );
          })}
        </section>
      )}
      <p className="mad-sync">● Synchronisé {data.syncedAt ? new Date(data.syncedAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "—"}</p>
    </main>
  );
}

type TeamBasketItem = {
  id: number;
  laboratory: string;
  pharmacy: string;
  product: string;
  quantity: number;
  unit: string;
  francoGoal: number;
  francoUnit: string;
  status: "waiting" | "ordered";
};

const normalizeTeamBasketItem = (row: Record<string, unknown>): TeamBasketItem => ({
  id: Number(row.id),
  laboratory: String(row.laboratory ?? ""),
  pharmacy: String(row.pharmacy ?? ""),
  product: String(row.product ?? ""),
  quantity: Number(row.quantity ?? 0),
  unit: String(row.unit ?? "u."),
  francoGoal: Number(row.franco_goal ?? 0),
  francoUnit: String(row.franco_unit ?? "unités"),
  status: String(row.status ?? "waiting") as TeamBasketItem["status"],
});

const tvQuantity = (value: number) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 }).format(value);

type CounterAlertView = {
  id: number;
  title: string;
  message: string;
  pharmacies: string[];
  scheduledAt: number;
  recurrence: "none" | "daily" | "weekdays" | "weekly";
  channels: { windows: boolean; portal: boolean; tv: boolean };
  occurrenceAt: number;
  occurrenceKey: string;
};

function TeamScreen({
  followups,
  onClose,
  initialPharmacy = "Square",
}: {
  followups: PatientFollowup[];
  onClose: () => void;
  initialPharmacy?: string;
}) {
  const [now, setNow] = useState(new Date());
  const [missionData, setMissionData] = useState<MissionData>({ missions: [], completions: [] });
  const [dailyData, setDailyData] = useState<DailyTaskData>({ tasks: [], notes: [], syncedAt: 0 });
  const [adrenalineItems, setAdrenalineItems] = useState<AdrenalineItem[]>([]);
  const [missingProducts, setMissingProducts] = useState<MissingProduct[]>([]);
  const [stockLabelItems, setStockLabelItems] = useState<StockLabelRequest[]>([]);
  const [laboratoryItems, setLaboratoryItems] = useState<LaboratoryFollowup[]>([]);
  const [laboratoryProfiles, setLaboratoryProfiles] = useState<LaboratoryProfile[]>([]);
  const [madRentals, setMadRentals] = useState<MadRental[]>([]);
  const [mutualizedBasketItems, setMutualizedBasketItems] = useState<TeamBasketItem[]>([]);
  const [handoverData, setHandoverData] = useState<TeamHandoverData>({ messages: [], reads: [], syncedAt: 0 });
  const [counterAlerts, setCounterAlerts] = useState<CounterAlertView[]>([]);
  const [challengeTvData, setChallengeTvData] = useState<ChallengeSummary & { visible?: boolean }>({ challenge: null, updates: [], individuals: [], pharmacyRanking: [], syncedAt: 0, activeChallenges: [] });
  const [screenPharmacy, setScreenPharmacy] = useState(initialPharmacy);
  const [slideIndex, setSlideIndex] = useState(0);
  const brief = useDailyBrief();
  const today = localDateIso(now);
  const pharmacyMatches = (name: string) => name === screenPharmacy;
  const due = followups.filter((item) => item.status === "active" && item.recontactDate <= today && pharmacyMatches(item.pharmacy));
  const dueMissions = missionOccurrences(missionData, now).filter((item) => !item.completion && pharmacyMatches(item.pharmacy));
  const dueTasks = dueDailyTasks(dailyData, now).filter((task) => task.pharmacies.includes(screenPharmacy));
  const dueAdrenaline = adrenalineAlerts(adrenalineItems, now).filter((item) => pharmacyMatches(item.pharmacy));
  const dueMissingProducts = missingProducts.filter((item) => isMissingProductDue(item, now) && item.pharmacies.includes(screenPharmacy));
  const dueStockLabels = stockLabelItems.filter((item) => item.status === "open" && item.pharmacies.includes(screenPharmacy));
  const dueLaboratories = laboratoryItems.filter((item) => laboratoryPreparationDue(item, screenPharmacy, now));
  const dueMadRentals = madRentals.filter((item) => item.pharmacy === screenPharmacy && Boolean(madRentalAlert(item, today)));
  const dueHandovers = handoverData.messages
    .filter((item) => item.pharmacies.includes(screenPharmacy) && isTeamHandoverVisible(item, today))
    .sort((a, b) => (a.priority === "urgent" ? 0 : a.priority === "important" ? 1 : 2) - (b.priority === "urgent" ? 0 : b.priority === "important" ? 1 : 2) || b.updatedAt - a.updatedAt);
  const dueLaboratoryWork = laboratoryProfiles.flatMap((profile) =>
    profile.pharmacies.includes(screenPharmacy)
      ? profile.workstreams.filter((item) => item.status === "open" && item.dueDate <= today).map((item) => ({ profile, item }))
      : [],
  );
  const mutualizedLaboratoryIcons: Record<string, string> = { Podowell: "👟", "Medi-Comfort": "🦶", Donjoy: "🦿", Sigvaris: "🧦" };
  const mutualizedBasketAlerts = ["Podowell", "Medi-Comfort", "Donjoy", "Sigvaris"].flatMap((laboratory) => {
    const items = mutualizedBasketItems.filter((item) => item.status === "waiting" && item.laboratory === laboratory);
    const pharmacyItems = items.filter((item) => item.pharmacy === screenPharmacy);
    if (!pharmacyItems.length) return [];
    const groupTotal = items.reduce((total, item) => total + item.quantity, 0);
    const pharmacyTotal = pharmacyItems.reduce((total, item) => total + item.quantity, 0);
    const goal = Math.max(0, ...items.map((item) => item.francoGoal));
    const remaining = Math.max(0, goal - groupTotal);
    const unit = items.find((item) => item.francoUnit)?.francoUnit || items.find((item) => item.unit)?.unit || "unités";
    const ready = goal > 0 && remaining === 0;
    return [{
      key: `mutualized-order-${laboratory}`,
      className: `mutualized-order ${ready ? "ready" : "progress"}`,
      icon: mutualizedLaboratoryIcons[laboratory] ?? "🧺",
      label: `PANIER MUTUALISÉ · ${screenPharmacy.toLocaleUpperCase("fr")}`,
      title: ready ? `${laboratory} · commande prête` : `${laboratory} · commande groupée`,
      description: `${screenPharmacy} a réservé ${tvQuantity(pharmacyTotal)} ${unit} · total des 3 pharmacies ${tvQuantity(groupTotal)}${goal > 0 ? ` / ${tvQuantity(goal)} ${unit}` : ` ${unit}`}`,
      chip: ready ? "Franco atteint ✓" : goal > 0 ? `Encore ${tvQuantity(remaining)} ${unit}` : `${tvQuantity(groupTotal)} ${unit}`,
    }];
  }).sort((a, b) => Number(b.className.includes("ready")) - Number(a.className.includes("ready")));
  const upcoming = followups
    .filter((item) => item.status === "active" && item.recontactDate > today && pharmacyMatches(item.pharmacy))
    .sort((a, b) => a.recontactDate.localeCompare(b.recontactDate))
    .slice(0, 3);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const loadMissions = async () => {
      try {
        const response = await fetch("/api/recurring-missions", { cache: "no-store" });
        if (response.ok) setMissionData(normalizeMissionData(await response.json()));
      } catch {
        // The patient reminders remain available if the mission feed is offline.
      }
    };
    void loadMissions();
    const timer = startVisiblePolling(loadMissions, PORTAL_TV_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const loadDailyTasks = async () => {
      try {
        const response = await fetch("/api/daily-tasks", { cache: "no-store" });
        if (response.ok) setDailyData(normalizeDailyTaskData(await response.json()));
      } catch {
        // Other team alerts stay available if this feed is offline.
      }
    };
    void loadDailyTasks();
    const timer = startVisiblePolling(loadDailyTasks, PORTAL_TV_REFRESH_MS);
    const unsubscribe = subscribeToDailyTasksUpdates(loadDailyTasks);
    return () => {
      window.clearInterval(timer);
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const loadAdrenaline = async () => {
      try {
        const response = await fetch("/api/adrenaline", { cache: "no-store" });
        if (response.ok) {
          const payload = await response.json();
          setAdrenalineItems((payload.items ?? []).map(normalizeAdrenalineItem));
        }
      } catch {
        // Other alerts remain visible if the safety feed is briefly offline.
      }
    };
    void loadAdrenaline();
    const timer = startVisiblePolling(loadAdrenaline, PORTAL_TV_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const loadMissingProducts = async () => {
      try {
        const response = await fetch("/api/missing-products", { cache: "no-store" });
        if (response.ok) {
          const payload = await response.json();
          setMissingProducts((payload.items ?? []).map(normalizeMissingProduct));
        }
      } catch {
        // Les autres alertes restent visibles si ce flux est brièvement indisponible.
      }
    };
    void loadMissingProducts();
    const timer = startVisiblePolling(loadMissingProducts, PORTAL_TV_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const loadStockLabels = async () => {
      try {
        const response = await fetch("/api/stock-labels", { cache: "no-store" });
        if (response.ok) {
          const payload = await response.json();
          setStockLabelItems((payload.items ?? []).map(normalizeStockLabelRequest));
        }
      } catch {
        // Les autres alertes continuent à tourner.
      }
    };
    void loadStockLabels();
    const timer = startVisiblePolling(loadStockLabels, PORTAL_TV_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const loadLaboratories = async () => {
      try {
        const [followupsResponse, profilesResponse] = await Promise.all([
          fetch("/api/laboratory-followups", { cache: "no-store" }),
          fetch("/api/laboratories", { cache: "no-store" }),
        ]);
        if (followupsResponse.ok) {
          const payload = await followupsResponse.json();
          setLaboratoryItems((payload.items ?? []).map(normalizeLaboratoryFollowup));
        }
        if (profilesResponse.ok) {
          const payload = await profilesResponse.json();
          setLaboratoryProfiles((payload.items ?? []).map(normalizeLaboratoryProfile));
        }
      } catch {
        // Les autres alertes continuent à tourner.
      }
    };
    void loadLaboratories();
    const timer = startVisiblePolling(loadLaboratories, PORTAL_TV_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const loadMadRentals = async () => {
      try {
        const response = await fetch("/api/mad-rentals", { cache: "no-store" });
        if (response.ok) {
          const payload = await response.json();
          setMadRentals((payload.rentals ?? []).map((item: Record<string, unknown>) => normalizeMadRental(item)));
        }
      } catch {
        // Les autres alertes continuent à tourner.
      }
    };
    void loadMadRentals();
    const timer = startVisiblePolling(loadMadRentals, PORTAL_TV_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const loadMutualizedBasket = async () => {
      try {
        const response = await fetch("/api/product-orders", { cache: "no-store" });
        if (response.ok) {
          const payload = await response.json();
          setMutualizedBasketItems((payload.basketItems ?? []).map((item: Record<string, unknown>) => normalizeTeamBasketItem(item)));
        }
      } catch {
        // Les autres alertes continuent à tourner si le panier mutualisé est momentanément indisponible.
      }
    };
    void loadMutualizedBasket();
    const timer = startVisiblePolling(loadMutualizedBasket, PORTAL_TV_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const loadHandovers = async () => {
      try {
        const response = await fetch("/api/team-handovers", { cache: "no-store" });
        if (response.ok) setHandoverData(normalizeTeamHandoverData(await response.json()));
      } catch {
        // La rotation TV continue avec les autres alertes si ce flux est indisponible.
      }
    };
    void loadHandovers();
    const timer = startVisiblePolling(loadHandovers, PORTAL_TV_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const loadCounterAlerts = async () => {
      try {
        const response = await fetch(`/api/counter-alerts?mode=active&channel=tv&pharmacy=${encodeURIComponent(screenPharmacy)}`, { cache: "no-store" });
        if (response.ok) {
          const payload = await response.json() as { items?: CounterAlertView[] };
          setCounterAlerts(payload.items ?? []);
        }
      } catch {
        // Les autres informations TV restent visibles si les alertes programmées sont brièvement indisponibles.
      }
    };
    void loadCounterAlerts();
    const timer = startVisiblePolling(loadCounterAlerts, PORTAL_TV_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [screenPharmacy]);

  useEffect(() => {
    const loadChallengeRelay = async () => {
      try {
        const response = await fetch(`/api/challenges?mode=tv&pharmacy=${encodeURIComponent(screenPharmacy)}`, { cache: "no-store" });
        if (response.ok) {
          const payload = await response.json() as ChallengeSummary & { visible?: boolean };
          setChallengeTvData(payload);
        }
      } catch {
        // L'écran TV continue avec ses autres informations si ce relais est indisponible.
      }
    };
    void loadChallengeRelay();
    const timer = startVisiblePolling(loadChallengeRelay, PORTAL_TV_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [screenPharmacy]);

  const priorityRank = (priority: string) => priority === "urgent" ? 0 : priority === "important" ? 1 : 2;
  const sortedTasks = [...dueTasks].sort((a, b) => priorityRank(a.priority) - priorityRank(b.priority) || a.displayDate.localeCompare(b.displayDate));
  const sortedMissions = [...dueMissions].sort((a, b) => priorityRank(a.mission.priority) - priorityRank(b.mission.priority));
  const alertItems = [
    ...dueAdrenaline.map((item) => {
      const days = daysUntilDate(item.expiryDate, now);
      return {
        key: `adrenaline-${item.id}`,
        className: "adrenaline",
        icon: "⚕️",
        label: `SÉCURITÉ VACCIN · ${item.pharmacy.toLocaleUpperCase("fr")}`,
        title: `${item.product} à renouveler`,
        description: `Lot ${item.batchNumber} · péremption ${followupDate(item.expiryDate)} · responsable ${item.operator}`,
        chip: days < 0 ? "Périmée" : days === 0 ? "Aujourd’hui" : `J−${days}`,
      };
    }),
    ...dueHandovers.slice(0, 8).map((item) => ({
      key: `handover-${item.id}`,
      className: `handover ${item.priority}`,
      icon: item.category === "safety" ? "🛡️" : item.category === "stock" ? "📦" : item.category === "patient" ? "🩺" : "🤲",
      label: `PASSATION ÉQUIPE · ${screenPharmacy.toLocaleUpperCase("fr")}`,
      title: item.title,
      description: `${item.body} · Par ${item.createdBy} · Pour ${item.audience}`,
      chip: item.priority === "urgent" ? "Urgent" : item.requiresAck ? "À lire" : "Info équipe",
    })),
    ...due.map((item) => ({
      key: `patient-${item.id}`,
      className: "",
      icon: patientInitials(item.patient) || "P.",
      label: `RAPPEL PATIENT · ${item.pharmacy.toLocaleUpperCase("fr")}`,
      title: item.patient,
      description: item.note || item.advice,
      chip: item.recontactDate < today ? "En retard" : "Aujourd’hui",
    })),
    ...mutualizedBasketAlerts,
    ...sortedTasks.slice(0, 8).map((task) => ({
      key: `daily-${task.id}`,
      className: `daily ${task.kind} ${task.priority}`,
      icon: task.kind === "delivery" ? "🚚" : "✓",
      label: `${task.kind === "delivery" ? "LIVRAISON" : "À FAIRE"} · ${screenPharmacy.toLocaleUpperCase("fr")}`,
      title: task.title,
      description: task.kind === "delivery" ? [task.recipient, task.address].filter(Boolean).join(" · ") : task.details || (task.assignedTo ? `Pour ${task.assignedTo}` : "Pour toute l’équipe"),
      chip: task.displayDate < today ? "En retard" : task.dueTime ? `Avant ${task.dueTime}` : task.priority === "urgent" ? "Urgent" : "Aujourd’hui",
    })),
    ...sortedMissions.slice(0, 6).map((item) => ({
      key: `mission-${item.mission.id}-${item.pharmacy}`,
      className: `mission ${item.mission.priority}`,
      icon: "🔁",
      label: `MISSION · ${item.pharmacy.toLocaleUpperCase("fr")}`,
      title: item.mission.title,
      description: item.mission.description || item.periodLabel,
      chip: item.mission.priority === "urgent" ? "Urgent" : item.periodLabel,
    })),
    ...dueMissingProducts.slice(0, 6).map((item) => ({
      key: `missing-${item.id}`,
      className: "missing-product",
      icon: "🔎",
      label: `PRODUIT MANQUANT · ${screenPharmacy.toLocaleUpperCase("fr")}`,
      title: item.product,
      description: item.comment || [item.ean ? `EAN ${item.ean}` : "", missingFrequencyLabels[item.frequency]].filter(Boolean).join(" · "),
      chip: "À contrôler",
    })),
    ...dueStockLabels.slice(0, 8).map((item) => ({
      key: `stock-label-${item.id}`,
      className: `stock-label ${item.requestType}`,
      icon: item.requestType === "stock" ? "📦" : "🏷️",
      label: `${item.requestType === "stock" ? "STOCK À CONTRÔLER" : "ÉTIQUETTE À RETIRER"} · ${screenPharmacy.toLocaleUpperCase("fr")}`,
      title: item.product,
      description: [item.ean ? `EAN ${item.ean}` : "", item.comment, `Demandé par ${item.createdBy}`].filter(Boolean).join(" · "),
      chip: item.requestType === "stock" ? "À contrôler" : "À retirer",
    })),
    ...dueMadRentals.slice(0, 8).map((item) => ({
      key: `mad-${item.id}`,
      className: "mad",
      icon: item.materialName.toLocaleLowerCase("fr").includes("fauteuil") ? "♿" : "🛏️",
      label: `LOCATION MAD · ${screenPharmacy.toLocaleUpperCase("fr")}`,
      title: `${item.materialName} · ${item.patientName}`,
      description: [item.phone, item.assignedBy ? `Remis par ${item.assignedBy}` : "", item.notes].filter(Boolean).join(" · "),
      chip: madRentalAlert(item, today),
    })),
    ...dueLaboratories.slice(0, 6).map((item) => ({
      key: `laboratory-${item.id}`,
      className: `laboratory ${item.priority}`,
      icon: "📦",
      label: `PRÉPARATION RDV LABO · ${screenPharmacy.toLocaleUpperCase("fr")}`,
      title: `${item.laboratory} · inventaire et périmés`,
      description: [followupDate(item.scheduledDate), item.scheduledTime, item.notes].filter(Boolean).join(" · "),
      chip: item.scheduledDate < today ? "En retard" : `RDV J−${Math.max(0, daysUntilDate(item.scheduledDate, now))}`,
    })),
    ...dueLaboratoryWork.slice(0, 6).map(({ profile, item }) => ({
      key: `laboratory-work-${profile.id}-${item.id}`,
      className: "laboratory important",
      icon: item.type === "bri" ? "💶" : "🤝",
      label: `${item.type === "bri" ? "SUIVI BRI" : "COOP / TG"} · ${screenPharmacy.toLocaleUpperCase("fr")}`,
      title: `${profile.name} · ${item.title || "Action laboratoire"}`,
      description: [profile.assignedTo ? `Responsable ${profile.assignedTo}` : "", item.note].filter(Boolean).join(" · "),
      chip: item.dueDate < today ? "En retard" : "Aujourd’hui",
    })),
  ];
  const pageSize = 4;
  const categoryForAlert = (item: (typeof alertItems)[number]) => {
    if (item.key.startsWith("handover-")) return "handovers";
    if (item.key.startsWith("daily-") && item.className.includes("delivery")) return "deliveries";
    if (item.key.startsWith("daily-") || item.key.startsWith("mission-") || item.key.startsWith("laboratory-")) return "todo";
    if (item.key.startsWith("patient-") || item.key.startsWith("adrenaline-") || item.key.startsWith("mad-")) return "patients";
    if (item.key.startsWith("missing-") || item.key.startsWith("stock-label-") || item.key.startsWith("mutualized-order-") || item.key.startsWith("generic-")) return "stock";
    return "todo";
  };
  const tvSectionDefinitions = [
    { id: "deliveries", icon: "🚚", label: "Livraisons", subtitle: "Destinataire, adresse et heure limite" },
    { id: "handovers", icon: "🤲", label: "Passations", subtitle: "Les informations à reprendre entre équipes" },
    { id: "todo", icon: "✓", label: "To-do", subtitle: "Les actions concrètes à réaliser aujourd’hui" },
    { id: "stock", icon: "📦", label: "Panier mutualisé & stock", subtitle: "Commandes, contrôles et étiquettes" },
    { id: "patients", icon: "📞", label: "Rappels patients", subtitle: "Patients et suivis arrivés à échéance" },
    { id: "challenges", icon: "🏁", label: "Challenges", subtitle: "Titre, période, récompense, objectifs et derniers classements" },
  ] as const;
  type TvPage = { section: (typeof tvSectionDefinitions)[number]; items: typeof alertItems };
  const tvPages = tvSectionDefinitions.reduce<TvPage[]>((pages, section) => {
    if (section.id === "challenges") {
      pages.push({ section, items: [] });
      return pages;
    }
    const sectionItems = alertItems.filter((item) => categoryForAlert(item) === section.id);
    pages.push(...Array.from({ length: Math.max(1, Math.ceil(sectionItems.length / pageSize)) }, (_, index) => ({
      section,
      items: sectionItems.slice(index * pageSize, index * pageSize + pageSize),
    })));
    return pages;
  }, []);
  const pageCount = Math.max(1, tvPages.length);
  const activeTvPage = tvPages[slideIndex] ?? { section: tvSectionDefinitions[0], items: [] };
  const visibleAlerts = activeTvPage.items;
  const totalAlerts = alertItems.length;
  const sectionAlertCount = activeTvPage.section.id === "challenges" ? (challengeTvData.activeChallenges?.length ?? 0) : alertItems.filter((item) => categoryForAlert(item) === activeTvPage.section.id).length;
  const teamHeadline = sectionAlertCount
    ? activeTvPage.section.id === "challenges" ? `${sectionAlertCount} challenge${sectionAlertCount > 1 ? "s" : ""} en cours` : `${sectionAlertCount} action${sectionAlertCount > 1 ? "s" : ""} à traiter`
    : activeTvPage.section.id === "challenges"
      ? "Les prochains challenges"
    : activeTvPage.section.id === "deliveries"
      ? "Aucune livraison"
      : activeTvPage.section.id === "handovers"
        ? "Aucune passation"
        : activeTvPage.section.id === "todo"
          ? "To-do à jour"
      : activeTvPage.section.id === "stock"
        ? "Stock à jour"
        : "Aucun rappel aujourd’hui";
  const teamBirthday = nextTeamBirthday(screenPharmacy, now);
  const challengeTvCards: ChallengeSummary[] = (challengeTvData.activeChallenges ?? []).map((entry) =>
    "challenge" in entry
      ? entry
      : { ...challengeTvData, challenge: entry, activeChallenges: [] },
  );

  useEffect(() => {
    if (slideIndex >= pageCount) {
      setSlideIndex(0);
      return;
    }
    if (pageCount <= 1) return;
    const timer = window.setTimeout(
      () => setSlideIndex((current) => (current + 1) % pageCount),
      activeTvPage.section.id === "challenges" ? 22_000 : activeTvPage.section.id === "deliveries" ? 18_000 : activeTvPage.section.id === "handovers" ? 16_000 : activeTvPage.section.id === "todo" ? 14_000 : activeTvPage.section.id === "patients" ? 12_000 : 10_000,
    );
    return () => window.clearTimeout(timer);
  }, [activeTvPage.section.id, pageCount, slideIndex]);

  return (
    <main className="team-screen">
      {counterAlerts[0] && <section className="team-counter-alert"><span>⏰</span><div><small>ALERTE COMPTOIR · {screenPharmacy.toLocaleUpperCase("fr")}</small><h2>{counterAlerts[0].title}</h2>{counterAlerts[0].message && <p>{counterAlerts[0].message}</p>}</div></section>}
      <header className="team-screen-header">
        <div className="team-screen-brand">
          <span className="team-brand-mark">S</span>
          <div><strong>Bonjour l’équipe</strong><small>Le point utile, visible en un regard</small></div>
        </div>
        <div className="team-clock">
          <strong>{now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</strong>
          <span>{now.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</span>
          {brief.today && <small className="team-weather-mini">{weatherIcon(brief.today.weatherCode)} Nice {brief.today.current}° · 🌊 {brief.sea?.wave.toFixed(1) ?? "–"} m</small>}
        </div>
        <div className="team-screen-actions">
          <label><span>TV de la pharmacie</span><select value={screenPharmacy} onChange={(event) => setScreenPharmacy(event.target.value)}>{followupPharmacies.map((name) => <option key={name}>{name}</option>)}</select></label>
          <button type="button" onClick={onClose}>Quitter l’écran équipe</button>
        </div>
      </header>

      <section className="team-screen-body">
        <div className="team-screen-intro">
          <span className={`team-live-dot ${sectionAlertCount ? "alert" : ""}`} />
          <div>
            <small>{activeTvPage.section.icon} {activeTvPage.section.label.toLocaleUpperCase("fr")}</small>
            <h1>{teamHeadline}</h1>
            <p>{sectionAlertCount ? activeTvPage.section.subtitle : activeTvPage.section.id === "challenges" ? "Les nouveaux objectifs et classements apparaîtront automatiquement ici." : activeTvPage.section.id === "deliveries" ? "Aucune livraison en attente pour cette pharmacie." : activeTvPage.section.id === "handovers" ? "Aucune information de passation à reprendre." : activeTvPage.section.id === "todo" ? "Aucune tâche ni mission en attente." : activeTvPage.section.id === "stock" ? "Aucune commande mutualisée, rupture ou action de stock en attente." : "Aucun patient à rappeler ni suivi arrivé à échéance."}</p>
          </div>
        </div>

        <div className={`team-alert-grid ${visibleAlerts.length === 1 ? "single" : ""}`}>
          {activeTvPage.section.id === "challenges" && <ChallengeTvPage challenges={challengeTvCards} pharmacy={screenPharmacy} currentTime={now.getTime()} />}
          {activeTvPage.section.id !== "challenges" && visibleAlerts.map((item, index) => (
            <article className={`team-alert-bubble category-${activeTvPage.section.id} ${item.className}`} key={item.key}>
              <span className="team-alert-number">{String(slideIndex * pageSize + index + 1).padStart(2, "0")}</span>
              <div className="team-patient-avatar">{item.icon}</div>
              <div>
                <span className="team-alert-label">{item.label}</span>
                <h2>{item.title}</h2>
                <p>{item.description}</p>
              </div>
              <span className="team-due-chip">{item.chip}</span>
            </article>
          ))}
          {activeTvPage.section.id !== "challenges" && !visibleAlerts.length && (
            <article className="team-empty-state">
              <span>✓</span>
              <div><h2>{teamHeadline}</h2><p>La prochaine échéance de cette rubrique apparaîtra automatiquement ici.</p></div>
            </article>
          )}
          {pageCount > 1 && <div className="team-slide-nav"><span>{activeTvPage.section.icon} {activeTvPage.section.label} · page {slideIndex + 1}/{pageCount}</span><div>{Array.from({ length: pageCount }, (_, index) => <button type="button" aria-label={`Afficher la page ${index + 1}`} className={index === slideIndex ? "active" : ""} key={index} onClick={() => setSlideIndex(index)} />)}</div></div>}
        </div>

        <aside className="team-screen-side">
          <div className="team-side-card next">
            <span>À VENIR</span>
            <h3>Prochains suivis</h3>
            {upcoming.length ? upcoming.map((item) => (
              <div className="team-upcoming" key={item.id}>
                <strong>{item.patient}</strong>
                <span>{followupDate(item.recontactDate)}</span>
              </div>
            )) : <p>Aucune autre relance planifiée.</p>}
          </div>
          <div className="team-side-card pulse">
            <span>VIE D’ÉQUIPE</span>
            <h3>{teamBirthday?.days === 0 ? `🎂 Joyeux anniversaire ${teamBirthday.name} !` : teamBirthday ? `🎈 Prochain anniversaire : ${teamBirthday.name}` : "🎈 Anniversaires d’équipe"}</h3>
            <p>{teamBirthday ? (teamBirthday.days === 0 ? "Une belle journée à célébrer avec l’équipe." : `${teamBirthday.date.toLocaleDateString("fr-FR", { day: "numeric", month: "long" })} · dans ${teamBirthday.days} jour${teamBirthday.days > 1 ? "s" : ""}.`) : "Les dates de cette équipe restent à compléter dans l’administration."}</p>
            {brief.nameDay && <small>✨ Bonne fête aux {brief.nameDay}</small>}
          </div>
        </aside>
      </section>
    </main>
  );
}

function TeamDisplayModule({ onBack, initialPharmacy }: { onBack: () => void; initialPharmacy: string }) {
  const [followups, setFollowups] = useState<PatientFollowup[]>([]);

  useEffect(() => {
    const loadFollowups = async () => {
      try {
        const response = await fetch("/api/patient-followups", { cache: "no-store" });
        if (response.ok) setFollowups(await response.json());
      } catch {
        // Missions and daily actions remain visible if patient follow-ups are briefly offline.
      }
    };
    void loadFollowups();
    const timer = startVisiblePolling(loadFollowups, PORTAL_LIVE_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, []);

  return <TeamScreen followups={followups} onClose={onBack} initialPharmacy={initialPharmacy} />;
}

function FollowupModal({
  initial,
  saving,
  onClose,
  onSave,
}: {
  initial: PatientFollowup | null;
  saving: boolean;
  onClose: () => void;
  onSave: (form: FollowupForm) => void;
}) {
  const [form, setForm] = useState<FollowupForm>(() => initial ? {
    patient: initial.patient,
    phone: initial.phone,
    pharmacy: initial.pharmacy,
    advisor: initial.advisor,
    seenDate: initial.seenDate,
    recontactDate: initial.recontactDate,
    advice: initial.advice,
    note: initial.note,
  } : {
    patient: "",
    phone: "",
    pharmacy: "Ragadan",
    advisor: "Gaëlle — Ragadan",
    seenDate: localDateIso(),
    recontactDate: offsetDate(2),
    advice: "",
    note: "",
  });

  const update = (key: keyof FollowupForm, value: string) => setForm((current) => ({ ...current, [key]: value }));
  const advisors = missionOperators.filter((operator) => operator.pharmacy === form.pharmacy);
  const valid = form.patient.trim() && form.pharmacy && form.advisor && form.seenDate && form.recontactDate && form.advice.trim();

  return (
    <div className="followup-modal-layer" role="presentation" onMouseDown={onClose}>
      <section className="followup-modal" role="dialog" aria-modal="true" aria-labelledby="followup-modal-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div><span>{initial ? "MODIFIER LE SUIVI" : "NOUVEAU SUIVI"}</span><h2 id="followup-modal-title">{initial ? initial.patient : "Nouveau patient à suivre"}</h2></div>
          <button type="button" aria-label="Fermer" onClick={onClose}>×</button>
        </header>
        <div className="followup-modal-grid">
          <label><span>Patient / initiales *</span><input autoFocus value={form.patient} onChange={(event) => update("patient", event.target.value)} placeholder="Ex. Mme D." /></label>
          <label><span>Téléphone</span><input value={form.phone} onChange={(event) => update("phone", event.target.value)} placeholder="Optionnel" inputMode="tel" /></label>
          <label><span>Pharmacie *</span><select value={form.pharmacy} onChange={(event) => {
            const nextPharmacy = event.target.value;
            const firstOperator = missionOperators.find((operator) => operator.pharmacy === nextPharmacy);
            setForm((current) => ({ ...current, pharmacy: nextPharmacy, advisor: firstOperator ? `${firstOperator.name} — ${firstOperator.pharmacy}` : "" }));
          }}>{followupPharmacies.map((name) => <option key={name}>{name}</option>)}</select></label>
          <label><span>Conseillé par *</span><select value={form.advisor} onChange={(event) => update("advisor", event.target.value)}>{advisors.map((operator) => <option key={operator.name} value={`${operator.name} — ${operator.pharmacy}`}>{operator.name} · {operator.role}</option>)}</select></label>
          <label><span>Vu le *</span><input type="date" value={form.seenDate} onChange={(event) => update("seenDate", event.target.value)} /></label>
          <label><span>À recontacter le *</span><input type="date" min={form.seenDate} value={form.recontactDate} onChange={(event) => update("recontactDate", event.target.value)} /></label>
          <label className="wide"><span>Conseil donné / motif *</span><textarea value={form.advice} onChange={(event) => update("advice", event.target.value.slice(0, 500))} placeholder="Ex. vérifier la tolérance du traitement, faire un point sur la douleur…" /></label>
          <label className="wide"><span>Note pour le rappel</span><textarea value={form.note} onChange={(event) => update("note", event.target.value.slice(0, 500))} placeholder="La question précise à poser lors du rappel…" /></label>
        </div>
        <footer>
          <p><span>TV</span> Le jour prévu, le nom complet et la note utile seront visibles sur l’écran équipe.</p>
          <div><button type="button" className="quiet" onClick={onClose}>Annuler</button><button type="button" className="save" disabled={!valid || saving} onClick={() => onSave(form)}>{saving ? "Enregistrement…" : "Enregistrer"}</button></div>
        </footer>
      </section>
    </div>
  );
}

function PatientFollowupModule({ onBack }: { onBack: () => void }) {
  const [followups, setFollowups] = useState<PatientFollowup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"all" | "due" | "active" | "closed">("all");
  const [modal, setModal] = useState<"new" | PatientFollowup | null>(null);
  const [saving, setSaving] = useState(false);
  const [teamMode, setTeamMode] = useState(false);
  const today = localDateIso();

  const refresh = async () => {
    try {
      setError("");
      const response = await fetch("/api/patient-followups", { cache: "no-store" });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);
      setFollowups(result);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Le suivi est momentanément indisponible.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void refresh(); }, []);

  const counts = useMemo(() => ({
    due: followups.filter((item) => item.status === "active" && item.recontactDate <= today).length,
    active: followups.filter((item) => item.status === "active" && item.recontactDate > today).length,
    closed: followups.filter((item) => item.status === "closed").length,
  }), [followups, today]);

  const visible = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("fr");
    return followups
      .filter((item) => {
        if (filter === "due") return item.status === "active" && item.recontactDate <= today;
        if (filter === "active") return item.status === "active" && item.recontactDate > today;
        if (filter === "closed") return item.status === "closed";
        return true;
      })
      .filter((item) => !normalized || `${item.patient} ${item.advice} ${item.note} ${item.pharmacy} ${item.advisor}`.toLocaleLowerCase("fr").includes(normalized))
      .sort((a, b) => a.status.localeCompare(b.status) || a.recontactDate.localeCompare(b.recontactDate));
  }, [filter, followups, query, today]);

  const saveFollowup = async (form: FollowupForm) => {
    setSaving(true);
    try {
      const editing = modal !== null && modal !== "new";
      const response = await fetch("/api/patient-followups", {
        method: editing ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editing ? { id: modal.id, ...form } : form),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);
      setFollowups((current) => editing ? current.map((item) => item.id === result.id ? result : item) : [result, ...current]);
      setModal(null);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Impossible d’enregistrer ce suivi.");
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (item: PatientFollowup) => {
    const response = await fetch("/api/patient-followups", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: item.id, status: item.status === "closed" ? "active" : "closed" }),
    });
    const result = await response.json();
    if (response.ok) setFollowups((current) => current.map((row) => row.id === result.id ? result : row));
    else setError(result.error || "Impossible de modifier ce suivi.");
  };

  const remove = async (item: PatientFollowup) => {
    if (!window.confirm(`Supprimer le suivi de ${item.patient} ?`)) return;
    const response = await fetch(`/api/patient-followups?id=${item.id}`, { method: "DELETE" });
    if (response.ok) setFollowups((current) => current.filter((row) => row.id !== item.id));
    else setError("Impossible de supprimer ce suivi.");
  };

  if (teamMode) return <TeamScreen followups={followups} onClose={() => setTeamMode(false)} />;

  return (
    <main className="followup-app">
      <header className="module-header">
        <button type="button" className="module-back" onClick={onBack}>← <span>Retour à l’accueil</span></button>
        <div className="wordmark module-wordmark"><span className="wordmark-symbol"><i /><i /><i /></span><span><strong>saint-laurent</strong><small>pharmacies</small></span></div>
        <div className="module-header-actions"><ModuleToolsLink /><span className="module-status"><i /> Données synchronisées</span></div>
      </header>

      <section className="followup-page">
        <div className="followup-hero">
          <div><span className="followup-kicker">PATIENTS · RELANCES PLANIFIÉES</span><h1>Patients à suivre</h1><p>Une vue claire pour ne laisser aucun conseil sans nouvelles.</p></div>
          <div className="followup-hero-actions"><button type="button" className="screen-button" onClick={() => setTeamMode(true)}><span>▣</span> Mode écran équipe</button><button type="button" className="new-followup" onClick={() => setModal("new")}>+ Nouveau suivi</button></div>
        </div>

        <div className="followup-stats">
          <button type="button" className={`due ${filter === "due" ? "active" : ""}`} onClick={() => setFilter("due")}><span>{counts.due}</span><div><strong>À rappeler</strong><small>Échéance atteinte</small></div></button>
          <button type="button" className={`active ${filter === "active" ? "active-filter" : ""}`} onClick={() => setFilter("active")}><span>{counts.active}</span><div><strong>En suivi</strong><small>Rappels à venir</small></div></button>
          <button type="button" className={`closed ${filter === "closed" ? "active" : ""}`} onClick={() => setFilter("closed")}><span>{counts.closed}</span><div><strong>Clôturés</strong><small>Suivis terminés</small></div></button>
        </div>

        <section className="followup-list-card">
          <div className="followup-toolbar">
            <label><span>⌕</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Rechercher un patient, un conseil, une pharmacie…" /></label>
            <select value={filter} onChange={(event) => setFilter(event.target.value as typeof filter)}>
              <option value="all">Tout afficher</option><option value="due">À rappeler</option><option value="active">En suivi</option><option value="closed">Clôturés</option>
            </select>
          </div>

          {error && <div className="followup-error"><span>!</span><p>{error}</p><button type="button" onClick={() => void refresh()}>Réessayer</button></div>}
          {loading ? <div className="followup-loading"><i /><p>Chargement des suivis…</p></div> : visible.length ? (
            <div className="followup-list">
              {visible.map((item) => {
                const isDue = item.status === "active" && item.recontactDate <= today;
                return (
                  <article className={`followup-row ${isDue ? "due" : ""} ${item.status === "closed" ? "closed" : ""}`} key={item.id}>
                    <div className="followup-row-main">
                      <span className="patient-avatar">{patientInitials(item.patient) || "P."}</span>
                      <div><h2>{item.patient}</h2><p>{item.pharmacy} · conseillé par {item.advisor.split(" — ")[0]} · vu le {followupDate(item.seenDate)}</p><div className="followup-advice"><strong>Conseil :</strong> {item.advice}</div>{item.note && <small className="followup-note">À demander : {item.note}</small>}</div>
                    </div>
                    <div className="followup-row-status">
                      <span className={`date-chip ${isDue ? "due" : item.status}`}>{item.status === "closed" ? "Clôturé" : isDue ? `${item.recontactDate < today ? "En retard" : "Aujourd’hui"} · ${followupDate(item.recontactDate)}` : `Prévu · ${followupDate(item.recontactDate)}`}</span>
                      <span className="phone-chip">{item.phone ? `☎ ${item.phone}` : "Sans téléphone"}</span>
                    </div>
                    <div className="followup-row-actions">
                      <button type="button" className="done" onClick={() => void changeStatus(item)}>{item.status === "closed" ? "Rouvrir" : "Rappel fait"}</button>
                      <button type="button" className="sms" disabled={!item.phone} onClick={() => { window.location.href = `sms:${item.phone}?body=${encodeURIComponent("Bonjour, votre pharmacie prend de vos nouvelles à la suite de votre passage.")}`; }}>SMS</button>
                      <button type="button" className="edit" onClick={() => setModal(item)}>Modifier</button>
                      <button type="button" className="delete" onClick={() => void remove(item)}>Supprimer</button>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="followup-empty">
              <span>◎</span><h2>{query || filter !== "all" ? "Aucun suivi dans cette vue" : "Votre premier suivi commence ici"}</h2><p>{query || filter !== "all" ? "Modifiez la recherche ou le filtre pour retrouver un patient." : "Planifiez une date de rappel : le portail et l’écran équipe s’occuperont de la rendre visible au bon moment."}</p>
              {!query && filter === "all" && <button type="button" onClick={() => setModal("new")}>Créer un suivi</button>}
            </div>
          )}
        </section>
      </section>

      {modal && <FollowupModal initial={modal === "new" ? null : modal} saving={saving} onClose={() => setModal(null)} onSave={(form) => void saveFollowup(form)} />}
    </main>
  );
}

type MissionFormState = {
  title: string;
  description: string;
  priority: MissionPriority;
  frequency: MissionFrequency;
  time: string;
  time2: string;
  day: string;
  pharmacies: string[];
  assignees: string[];
};

const missionRoleOptions: MissionOperator["role"][] = ["Titulaire", "Pharmacien", "Préparateur", "Apprentie", "Rayonniste"];
const roleAssignee = (role: MissionOperator["role"]) => `role:${role}`;

function missionEligibleOperators(mission: Pick<RecurringMission, "assignees">, pharmacy: string) {
  return missionOperators.filter((operator) =>
    operator.pharmacy === pharmacy &&
    (mission.assignees.includes(operator.name) || mission.assignees.includes(roleAssignee(operator.role))),
  );
}

function missionFormFromItem(item: RecurringMission | null, initialPharmacy = "Square"): MissionFormState {
  const parts = item?.schedule.split(",") ?? [];
  return {
    title: item?.title ?? "",
    description: item?.description ?? "",
    priority: item?.priority ?? "normal",
    frequency: item?.frequency ?? "daily",
    time: item?.frequency === "twice_daily" ? parts[0] ?? "09:00" : item?.frequency === "daily" ? parts[0] ?? "09:00" : parts[1] ?? "09:00",
    time2: item?.frequency === "twice_daily" ? parts[1] ?? "17:00" : "17:00",
    day: item && (item.frequency === "weekly" || item.frequency === "monthly") ? parts[0] ?? "1" : "1",
    pharmacies: item?.pharmacies ?? [initialPharmacy],
    assignees: item?.assignees ?? [],
  };
}

function missionSchedule(form: MissionFormState) {
  if (form.frequency === "twice_daily") return `${form.time},${form.time2}`;
  if (form.frequency === "daily") return form.time;
  return `${form.day},${form.time}`;
}

function MissionModal({
  item,
  initialPharmacy,
  saving,
  onClose,
  onSave,
}: {
  item: RecurringMission | null;
  initialPharmacy: string;
  saving: boolean;
  onClose: () => void;
  onSave: (form: MissionFormState) => void;
}) {
  const [form, setForm] = useState(() => missionFormFromItem(item, initialPharmacy));
  const toggleList = (key: "pharmacies" | "assignees", value: string) => {
    setForm((current) => ({
      ...current,
      [key]: current[key].includes(value) ? current[key].filter((entry) => entry !== value) : [...current[key], value],
    }));
  };
  const visibleOperators = missionOperators.filter((operator) => form.pharmacies.includes(operator.pharmacy));
  const activeRoles = missionRoleOptions.filter((role) => form.assignees.includes(roleAssignee(role)));
  const valid = form.title.trim() && form.pharmacies.length && form.assignees.length;

  return (
    <div className="mission-modal-layer" role="presentation" onMouseDown={onClose}>
      <section className="mission-modal" role="dialog" aria-modal="true" aria-labelledby="mission-modal-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div><span>{item ? "MODIFIER" : "NOUVELLE MISSION"}</span><h2 id="mission-modal-title">{item ? item.title : "Créer une mission récurrente"}</h2></div>
          <button type="button" aria-label="Fermer" onClick={onClose}>×</button>
        </header>
        <div className="mission-form">
          <label className="wide"><span>Nom de la mission *</span><input autoFocus value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} placeholder="Ex. Contrôler la température du réfrigérateur" /></label>
          <fieldset className="wide"><legend>Priorité</legend><div className="mission-choice-row">{(["normal", "important", "urgent"] as MissionPriority[]).map((priority) => <button type="button" key={priority} className={`${priority} ${form.priority === priority ? "active" : ""}`} onClick={() => setForm({ ...form, priority })}><i />{priority === "normal" ? "Normale" : priority === "important" ? "Importante" : "Urgente"}</button>)}</div></fieldset>
          <label><span>Fréquence *</span><select value={form.frequency} onChange={(event) => setForm({ ...form, frequency: event.target.value as MissionFrequency })}><option value="twice_daily">2 fois par jour</option><option value="daily">Quotidienne, sauf dimanche</option><option value="weekly">Hebdomadaire</option><option value="monthly">Mensuelle</option></select></label>
          {(form.frequency === "weekly" || form.frequency === "monthly") && <label><span>{form.frequency === "weekly" ? "Jour de la semaine" : "Jour du mois"}</span><select value={form.day} onChange={(event) => setForm({ ...form, day: event.target.value })}>{form.frequency === "weekly" ? ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"].map((day, index) => <option value={index + 1} key={day}>{day}</option>) : Array.from({ length: 28 }, (_, index) => <option value={index + 1} key={index}>{index + 1}</option>)}</select></label>}
          <label><span>Heure prévue</span><input type="time" value={form.time} onChange={(event) => setForm({ ...form, time: event.target.value })} /></label>
          {form.frequency === "twice_daily" && <label><span>Deuxième passage</span><input type="time" value={form.time2} onChange={(event) => setForm({ ...form, time2: event.target.value })} /></label>}
          <fieldset className="wide"><legend>Pharmacie(s) *</legend><div className="mission-checkboxes pharmacies">{followupPharmacies.map((pharmacy) => <button type="button" key={pharmacy} className={form.pharmacies.includes(pharmacy) ? "selected" : ""} onClick={() => toggleList("pharmacies", pharmacy)}><i>{form.pharmacies.includes(pharmacy) ? "✓" : ""}</i>{pharmacy}</button>)}</div></fieldset>
          <fieldset className="wide mission-responsibility"><legend>Qui peut réaliser cette mission ? *</legend>
            <p className="mission-responsibility-help">Choisissez un ou plusieurs métiers : la mission suivra automatiquement les opérateurs correspondants dans chaque pharmacie.</p>
            <div className="mission-checkboxes roles">{missionRoleOptions.map((role) => <button type="button" key={role} className={activeRoles.includes(role) ? "selected" : ""} onClick={() => toggleList("assignees", roleAssignee(role))}><i>{activeRoles.includes(role) ? "✓" : ""}</i><span>{role}<small>{visibleOperators.filter((operator) => operator.role === role).length} personne(s) concernée(s)</small></span></button>)}</div>
            <div className="mission-people-heading"><strong>Ou désigner précisément une personne</strong><div className="mission-form-links"><button type="button" onClick={() => setForm({ ...form, assignees: [...activeRoles.map(roleAssignee), ...visibleOperators.map((operator) => operator.name)] })}>Toutes les personnes</button><button type="button" onClick={() => setForm({ ...form, assignees: [] })}>Tout décocher</button></div></div>
            <div className="mission-checkboxes operators">{visibleOperators.map((operator) => <button type="button" key={operator.name} className={form.assignees.includes(operator.name) ? "selected" : ""} onClick={() => toggleList("assignees", operator.name)}><i>{form.assignees.includes(operator.name) ? "✓" : ""}</i><span>{operator.name}<small>{operator.pharmacy} · {operator.role}</small></span></button>)}</div>
          </fieldset>
          <label className="wide"><span>Description / procédure</span><textarea value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value.slice(0, 700) })} placeholder="Précisez le contrôle à effectuer ou la procédure à suivre…" /></label>
        </div>
        <footer><p>La mission apparaîtra automatiquement sur l’accueil et la télévision lorsqu’elle sera due.</p><div><button type="button" className="quiet" onClick={onClose}>Annuler</button><button type="button" className="save" disabled={!valid || saving} onClick={() => onSave(form)}>{saving ? "Enregistrement…" : item ? "Enregistrer" : "Créer la mission"}</button></div></footer>
      </section>
    </div>
  );
}

function RecurringMissionsModule({ onBack, initialPharmacy }: { onBack: () => void; initialPharmacy: string }) {
  const [data, setData] = useState<MissionData>({ missions: [], completions: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState<"all" | "due" | MissionFrequency>("all");
  const [modal, setModal] = useState<"new" | RecurringMission | null>(null);
  const [saving, setSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [operatorsByOccurrence, setOperatorsByOccurrence] = useState<Record<string, string>>({});
  const pharmacyData = {
    missions: data.missions.filter((mission) => mission.pharmacies.includes(initialPharmacy)),
    completions: data.completions.filter((completion) => completion.pharmacy === initialPharmacy),
  };
  const occurrences = missionOccurrences(pharmacyData);
  const pending = occurrences.filter((item) => !item.completion);
  const completed = occurrences.filter((item) => item.completion);

  const refresh = async () => {
    try {
      setError("");
      const response = await fetch("/api/recurring-missions", { cache: "no-store" });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      setData(normalizeMissionData(payload));
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Les missions sont indisponibles.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void refresh(); }, []);

  const visible = pharmacyData.missions.filter((mission) => {
    if (filter === "all") return true;
    if (filter === "due") return pending.some((item) => item.mission.id === mission.id);
    return mission.frequency === filter;
  });

  const saveMission = async (form: MissionFormState) => {
    setSaving(true);
    try {
      const editing = modal !== null && modal !== "new";
      const response = await fetch("/api/recurring-missions", {
        method: editing ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...(editing ? { id: modal.id, action: "update" } : {}),
          title: form.title,
          description: form.description,
          priority: form.priority,
          frequency: form.frequency,
          schedule: missionSchedule(form),
          pharmacies: form.pharmacies,
          assignees: form.assignees,
        }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error);
      const mission = normalizeMission(payload);
      setData((current) => ({ ...current, missions: editing ? current.missions.map((item) => item.id === mission.id ? mission : item) : [...current.missions, mission] }));
      setModal(null);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Impossible d’enregistrer la mission.");
    } finally {
      setSaving(false);
    }
  };

  const completeMission = async (mission: RecurringMission, pharmacy: string, periodKey: string) => {
    const key = `${mission.id}-${pharmacy}`;
    const available = missionEligibleOperators(mission, pharmacy);
    const operator = operatorsByOccurrence[key] || available[0]?.name;
    if (!operator) return;
    setSaving(true);
    try {
      const response = await fetch("/api/recurring-missions", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "complete", id: mission.id, pharmacy, operator, periodKey }) });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Impossible de valider la mission.");
      await refresh();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Impossible de valider la mission.");
    } finally { setSaving(false); }
  };

  const undoCompletion = async (completion: MissionCompletion) => {
    setSaving(true);
    try {
      const response = await fetch("/api/recurring-missions", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "uncomplete", completionId: completion.id }) });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Annulation impossible.");
      await refresh();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Annulation impossible.");
    } finally { setSaving(false); }
  };

  const removeMission = async (mission: RecurringMission) => {
    if (!window.confirm(`Supprimer la mission « ${mission.title} » et son historique ?`)) return;
    setSaving(true);
    try {
      const response = await fetch(`/api/recurring-missions?id=${mission.id}`, { method: "DELETE" });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Suppression impossible.");
      await refresh();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Suppression impossible.");
    } finally { setSaving(false); }
  };

  return (
    <main className="missions-app">
      <header className="module-header"><button type="button" className="module-back" onClick={onBack}>← <span>Retour à l’accueil</span></button><div className="wordmark module-wordmark"><span className="wordmark-symbol"><i /><i /><i /></span><span><strong>saint-laurent</strong><small>pharmacies</small></span></div><div className="module-header-actions"><ModuleToolsLink /><span className="module-status"><i /> Missions synchronisées</span></div></header>
      <section className="missions-page">
        <div className="missions-hero"><div><span>ÉQUIPE · RITUELS · {initialPharmacy.toLocaleUpperCase("fr")}</span><h1>Missions récurrentes</h1><p>Uniquement les missions de {initialPharmacy}, avec une validation claire par opérateur.</p></div><div><button type="button" className="mission-history-button" onClick={() => setHistoryOpen((current) => !current)}>Historique</button><button type="button" className="mission-new-button" onClick={() => setModal("new")}>+ Nouvelle mission</button></div></div>
        <div className="mission-stats"><article className="due"><strong>{pending.length}</strong><span>À faire maintenant</span></article><article className="done"><strong>{completed.length}</strong><span>Validées sur la période</span></article><article className="urgent"><strong>{pending.filter((item) => item.mission.priority === "urgent").length}</strong><span>Urgentes</span></article></div>
        <div className="mission-filters">{([["all", "Toutes"], ["due", "À faire"], ["twice_daily", "2× / jour"], ["daily", "Quotidiennes"], ["weekly", "Hebdo"], ["monthly", "Mensuelles"]] as Array<[typeof filter, string]>).map(([value, label]) => <button type="button" key={value} className={filter === value ? "active" : ""} onClick={() => setFilter(value)}>{label}</button>)}</div>
        {error && <div className="mission-error">{error}<button type="button" onClick={() => void refresh()}>Réessayer</button></div>}
        {historyOpen && <section className="mission-history"><div><h2>Historique récent · {initialPharmacy}</h2><button type="button" onClick={() => setHistoryOpen(false)}>Fermer</button></div>{pharmacyData.completions.length ? pharmacyData.completions.slice(0, 30).map((completion) => { const mission = data.missions.find((item) => item.id === completion.missionId); return <p key={completion.id}><strong>{mission?.title ?? "Mission supprimée"}</strong><span>{completion.pharmacy} · {completion.completedBy} · {new Date(completion.completedAt).toLocaleString("fr-FR")}</span></p>; }) : <p>Aucune validation enregistrée pour {initialPharmacy}.</p>}</section>}
        <section className="missions-list">
          {loading ? <div className="missions-empty">Chargement des missions…</div> : visible.length ? visible.map((mission) => {
            const period = missionPeriod(mission);
            return <article className={`mission-card ${mission.priority}`} key={mission.id}>
              <header><div><span className={`mission-priority ${mission.priority}`}>{mission.priority === "normal" ? "Normale" : mission.priority === "important" ? "Importante" : "Urgente"}</span><h2>{mission.title}</h2><p>{mission.description || "Aucune procédure complémentaire."}</p></div><div className="mission-card-actions"><button type="button" disabled={saving} onClick={() => setModal(mission)}>Modifier</button><button type="button" disabled={saving} onClick={() => void removeMission(mission)}>Supprimer</button></div></header>
              <div className="mission-meta"><span>{missionFrequencyLabels[mission.frequency]}</span><span>{period.label}</span><span>{missionEligibleOperators(mission, initialPharmacy).length} personne(s) éligible(s)</span></div>
              <div className="mission-pharmacy-rows">{mission.pharmacies.filter((pharmacy) => pharmacy === initialPharmacy).map((pharmacy) => {
                const completion = data.completions.find((item) => item.missionId === mission.id && item.pharmacy === pharmacy && item.periodKey === period.key);
                const key = `${mission.id}-${pharmacy}`;
                const available = missionEligibleOperators(mission, pharmacy);
                return <div className={`mission-pharmacy-row ${completion ? "completed" : ""}`} key={pharmacy}><span className="mission-pharmacy-name">{pharmacy}</span>{period.due ? completion ? <><p><strong>✓ Réalisée</strong><small>par {completion.completedBy} à {new Date(completion.completedAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</small></p><button type="button" className="undo" disabled={saving} onClick={() => void undoCompletion(completion)}>{saving ? "Enregistrement…" : "Annuler"}</button></> : available.length ? <><select disabled={saving} value={operatorsByOccurrence[key] || available[0]?.name || ""} onChange={(event) => setOperatorsByOccurrence((current) => ({ ...current, [key]: event.target.value }))}>{available.map((operator) => <option key={operator.name}>{operator.name} · {operator.role}</option>)}</select><button type="button" className="complete" disabled={saving} onClick={() => void completeMission(mission, pharmacy, period.key)}>{saving ? "Enregistrement…" : "Marquer comme faite"}</button></> : <p><strong>Aucun opérateur</strong><small>Ajoutez un métier ou une personne correspondant à {pharmacy}.</small></p> : <p><strong>À venir</strong><small>{period.label}</small></p>}</div>;
              })}</div>
              <div className="mission-assignees">{mission.assignees.map((name) => <span key={name}>{name.startsWith("role:") ? `Métier · ${name.slice(5)}` : name}</span>)}</div>
            </article>;
          }) : <div className="missions-empty"><span>✓</span><h2>{filter === "all" ? "Créez votre première mission" : "Aucune mission dans cette vue"}</h2><p>Une mission créée ici apparaîtra automatiquement sur l’accueil et sur la télévision lorsqu’elle sera due.</p>{filter === "all" && <button type="button" onClick={() => setModal("new")}>Créer une mission</button>}</div>}
        </section>
      </section>
      {modal && <MissionModal item={modal === "new" ? null : modal} initialPharmacy={initialPharmacy} saving={saving} onClose={() => setModal(null)} onSave={(form) => void saveMission(form)} />}
    </main>
  );
}

type CleanerPreset = "auto" | "optimal" | "blackwhite" | "soft";
type CleanerView = "cleaned" | "original" | "compare";

function PrescriptionCleanerModule({ onBack }: { onBack: () => void }) {
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const cleanedCanvas = useRef<HTMLCanvasElement>(null);
  const sourceImage = useRef<HTMLImageElement | null>(null);
  const [imageUrl, setImageUrl] = useState("");
  const [fileName, setFileName] = useState("");
  const [preset, setPreset] = useState<CleanerPreset>("auto");
  const [view, setView] = useState<CleanerView>("cleaned");
  const [brightness, setBrightness] = useState(108);
  const [contrast, setContrast] = useState(132);
  const [grayscale, setGrayscale] = useState(false);
  const [sharpen, setSharpen] = useState(true);
  const [rotation, setRotation] = useState(0);
  const [crop, setCrop] = useState({ top: 0, right: 0, bottom: 0, left: 0 });
  const [manualOpen, setManualOpen] = useState(false);
  const [fineTuneOpen, setFineTuneOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [processing, setProcessing] = useState(false);
  const [printData, setPrintData] = useState("");
  const [dragging, setDragging] = useState(false);

  const applyPreset = (next: CleanerPreset) => {
    setPreset(next);
    if (next === "auto") {
      if (sourceImage.current) autoEnhance(sourceImage.current);
      else {
        setBrightness(108);
        setContrast(132);
        setGrayscale(false);
        setSharpen(true);
      }
    } else if (next === "optimal") {
      setBrightness(108);
      setContrast(132);
      setGrayscale(false);
      setSharpen(true);
    } else if (next === "blackwhite") {
      setBrightness(112);
      setContrast(158);
      setGrayscale(true);
      setSharpen(true);
    } else {
      setBrightness(110);
      setContrast(114);
      setGrayscale(false);
      setSharpen(false);
    }
  };

  const autoEnhance = (image: HTMLImageElement) => {
    const sample = document.createElement("canvas");
    const scale = Math.min(1, 360 / Math.max(image.naturalWidth, image.naturalHeight));
    sample.width = Math.max(1, Math.round(image.naturalWidth * scale));
    sample.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = sample.getContext("2d", { willReadFrequently: true });
    if (!context) return;
    context.drawImage(image, 0, 0, sample.width, sample.height);
    const pixels = context.getImageData(0, 0, sample.width, sample.height).data;
    let sum = 0;
    let squareSum = 0;
    let count = 0;
    for (let index = 0; index < pixels.length; index += 16) {
      const light = pixels[index] * .299 + pixels[index + 1] * .587 + pixels[index + 2] * .114;
      sum += light;
      squareSum += light * light;
      count += 1;
    }
    const mean = sum / Math.max(1, count);
    const deviation = Math.sqrt(Math.max(0, squareSum / Math.max(1, count) - mean * mean));
    setBrightness(Math.round(Math.max(92, Math.min(125, 106 + (170 - mean) * .16))));
    setContrast(Math.round(Math.max(112, Math.min(172, 126 + (52 - deviation) * .85))));
    setGrayscale(false);
    setSharpen(true);
    setPreset("auto");
    setMessage("Mode automatique appliqué : luminosité et contraste adaptés à cette ordonnance.");
  };

  const renderCleaned = () => {
    const image = sourceImage.current;
    const canvas = cleanedCanvas.current;
    if (!image || !canvas || !image.naturalWidth) return;

    setProcessing(true);
    window.requestAnimationFrame(() => {
      const sx = Math.round(image.naturalWidth * crop.left / 100);
      const sy = Math.round(image.naturalHeight * crop.top / 100);
      const sw = Math.max(1, Math.round(image.naturalWidth * (100 - crop.left - crop.right) / 100));
      const sh = Math.max(1, Math.round(image.naturalHeight * (100 - crop.top - crop.bottom) / 100));
      const rotated = Math.abs(rotation) % 180 === 90;
      const scale = Math.min(1, 1600 / Math.max(sw, sh));
      const drawWidth = Math.max(1, Math.round(sw * scale));
      const drawHeight = Math.max(1, Math.round(sh * scale));

      canvas.width = rotated ? drawHeight : drawWidth;
      canvas.height = rotated ? drawWidth : drawHeight;
      const context = canvas.getContext("2d", { willReadFrequently: sharpen });
      if (!context) return;
      context.save();
      context.fillStyle = "white";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.translate(canvas.width / 2, canvas.height / 2);
      context.rotate(rotation * Math.PI / 180);
      context.filter = `brightness(${brightness}%) contrast(${contrast}%) grayscale(${grayscale ? 100 : 0}%)`;
      context.drawImage(image, sx, sy, sw, sh, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
      context.restore();
      context.filter = "none";

      if (sharpen && canvas.width * canvas.height < 3_000_000) {
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        const source = new Uint8ClampedArray(imageData.data);
        const target = imageData.data;
        const width = canvas.width;
        const height = canvas.height;
        const amount = .32;
        for (let y = 1; y < height - 1; y += 1) {
          for (let x = 1; x < width - 1; x += 1) {
            const index = (y * width + x) * 4;
            for (let channel = 0; channel < 3; channel += 1) {
              const center = source[index + channel];
              const neighbors =
                source[index - 4 + channel] +
                source[index + 4 + channel] +
                source[index - width * 4 + channel] +
                source[index + width * 4 + channel];
              target[index + channel] = Math.max(0, Math.min(255, center + amount * (center * 4 - neighbors)));
            }
          }
        }
        context.putImageData(imageData, 0, 0);
      }
      setProcessing(false);
    });
  };

  useEffect(() => {
    if (!imageUrl) return;
    const image = new Image();
    image.onload = () => {
      sourceImage.current = image;
      autoEnhance(image);
      renderCleaned();
      window.setTimeout(() => autoCrop(), 60);
    };
    image.onerror = () => setMessage("Ce format n’est pas lisible sur cet appareil. Essayez une photo JPG ou PNG.");
    image.src = imageUrl;
    return () => { image.onload = null; image.onerror = null; };
    // renderCleaned is intentionally triggered again by the adjustment effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl]);

  useEffect(() => {
    if (sourceImage.current) renderCleaned();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brightness, contrast, grayscale, sharpen, rotation, crop, view]);

  useEffect(() => () => {
    if (imageUrl) URL.revokeObjectURL(imageUrl);
  }, [imageUrl]);

  const loadFile = (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setMessage("Pour un résultat fiable, importez une photo ou une image JPG, PNG, WebP ou HEIC compatible avec votre appareil.");
      return;
    }
    if (file.size > 24 * 1024 * 1024) {
      setMessage("L’image dépasse 24 Mo. Réduisez-la ou reprenez une photo en qualité standard.");
      return;
    }
    if (imageUrl) URL.revokeObjectURL(imageUrl);
    sourceImage.current = null;
    setImageUrl(URL.createObjectURL(file));
    setFileName(file.name || "Image collée");
    setMessage("");
    setCrop({ top: 0, right: 0, bottom: 0, left: 0 });
    setRotation(0);
    applyPreset("auto");
    setView("cleaned");
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLElement>) => {
    const file = Array.from(event.clipboardData.files).find((item) => item.type.startsWith("image/"));
    if (file) {
      event.preventDefault();
      loadFile(file);
    } else {
      setMessage("Le presse-papiers ne contient pas d’image.");
    }
  };

  const autoCrop = () => {
    const image = sourceImage.current;
    if (!image) return;
    const detector = document.createElement("canvas");
    const scale = Math.min(1, 520 / Math.max(image.naturalWidth, image.naturalHeight));
    detector.width = Math.max(1, Math.round(image.naturalWidth * scale));
    detector.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = detector.getContext("2d", { willReadFrequently: true });
    if (!context) return;
    context.drawImage(image, 0, 0, detector.width, detector.height);
    const data = context.getImageData(0, 0, detector.width, detector.height).data;
    const rowScore = Array(detector.height).fill(0);
    const columnScore = Array(detector.width).fill(0);
    for (let y = 0; y < detector.height; y += 1) {
      for (let x = 0; x < detector.width; x += 1) {
        const index = (y * detector.width + x) * 4;
        const light = (data[index] + data[index + 1] + data[index + 2]) / 3 > 172;
        if (light) { rowScore[y] += 1; columnScore[x] += 1; }
      }
    }
    const top = rowScore.findIndex((score) => score / detector.width > .24);
    const bottomFromEnd = [...rowScore].reverse().findIndex((score) => score / detector.width > .24);
    const left = columnScore.findIndex((score) => score / detector.height > .24);
    const rightFromEnd = [...columnScore].reverse().findIndex((score) => score / detector.height > .24);
    if (top < 0 || bottomFromEnd < 0 || left < 0 || rightFromEnd < 0) {
      setMessage("La feuille n’a pas été détectée. Ouvrez le recadrage manuel pour ajuster les bords.");
      setManualOpen(true);
      return;
    }
    const next = {
      top: Math.max(0, Math.round(top / detector.height * 100) - 1),
      bottom: Math.max(0, Math.round(bottomFromEnd / detector.height * 100) - 1),
      left: Math.max(0, Math.round(left / detector.width * 100) - 1),
      right: Math.max(0, Math.round(rightFromEnd / detector.width * 100) - 1),
    };
    const useful = Object.values(next).some((value) => value >= 2);
    setCrop(useful ? next : { top: 0, right: 0, bottom: 0, left: 0 });
    setMessage(useful ? "Bords de la feuille détectés. Vérifiez l’aperçu avant d’exporter." : "La feuille occupe déjà toute l’image.");
  };

  const reset = () => {
    if (imageUrl) URL.revokeObjectURL(imageUrl);
    setImageUrl("");
    setFileName("");
    setMessage("");
    setCrop({ top: 0, right: 0, bottom: 0, left: 0 });
    setRotation(0);
    setPrintData("");
    sourceImage.current = null;
    applyPreset("auto");
  };

  const download = () => {
    const canvas = cleanedCanvas.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${fileName.replace(/\.[^.]+$/, "") || "ordonnance"}-nettoyee.jpg`;
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 500);
    }, "image/jpeg", .94);
  };

  const print = () => {
    const canvas = cleanedCanvas.current;
    if (!canvas) return;
    setPrintData(canvas.toDataURL("image/jpeg", .96));
    window.setTimeout(() => window.print(), 120);
  };

  return (
    <main
      className={`cleaner-app ${dragging ? "dragging" : ""}`}
      onPaste={handlePaste}
      onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
      onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
      onDragLeave={(event) => { if (event.currentTarget === event.target) setDragging(false); }}
      onDrop={(event) => { event.preventDefault(); setDragging(false); loadFile(event.dataTransfer.files[0]); }}
    >
      <header className="module-header">
        <button type="button" className="module-back" onClick={onBack}>← <span>Retour à l’accueil</span></button>
        <div className="wordmark module-wordmark"><span className="wordmark-symbol"><i /><i /><i /></span><span><strong>saint-laurent</strong><small>pharmacies</small></span></div>
        <div className="module-header-actions"><ModuleToolsLink /><span className="module-status private"><i /> Traitement local</span></div>
      </header>

      <section className="cleaner-page">
        <div className="cleaner-hero">
          <div><span>ORDONNANCE · LISIBILITÉ</span><h1>Nettoyer<br />une ordonnance</h1><p>Photographiez, rendez le texte net et imprimez une copie propre — sans envoyer le document ailleurs.</p></div>
          <div className="cleaner-privacy"><strong>100 % local</strong><small>L’image reste sur cet appareil</small></div>
        </div>

        <div className="cleaner-steps" aria-label="Étapes du nettoyage">
          <span className={imageUrl ? "done" : "active"}><i>{imageUrl ? "✓" : "1"}</i>Importer</span>
          <b />
          <span className={imageUrl ? "active" : ""}><i>2</i>Recadrer</span>
          <b />
          <span><i>3</i>Nettoyer</span>
          <b />
          <span><i>4</i>Exporter</span>
        </div>

        <div className="cleaner-layout">
          <section className="cleaner-controls">
            <div className="cleaner-control-heading"><span>01</span><div><h2>Votre ordonnance</h2><p>Photo, fichier ou image copiée.</p></div></div>
            <input ref={fileInput} hidden type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" onChange={(event) => loadFile(event.target.files?.[0])} />
            <input ref={cameraInput} hidden type="file" accept="image/*" capture="environment" onChange={(event) => loadFile(event.target.files?.[0])} />

            {!imageUrl ? (
              <div
                className="cleaner-dropzone"
                tabIndex={0}
                role="button"
                onClick={() => fileInput.current?.click()}
                onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") fileInput.current?.click(); }}
                onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={(event) => { event.preventDefault(); event.stopPropagation(); setDragging(false); loadFile(event.dataTransfer.files[0]); }}
              >
                <span className="cleaner-camera">⌁</span>
                <h3>Déposez l’ordonnance ici</h3>
                <p>ou choisissez la méthode la plus rapide</p>
                <div>
                  <button type="button" onClick={(event) => { event.stopPropagation(); cameraInput.current?.click(); }}>Prendre une photo</button>
                  <button type="button" className="secondary" onClick={(event) => { event.stopPropagation(); fileInput.current?.click(); }}>Choisir un fichier</button>
                </div>
                <small>JPG, PNG, WebP et formats photo compatibles · 24 Mo maximum</small>
                <kbd>Astuce : cliquez ici puis collez avec ⌘V / Ctrl+V</kbd>
              </div>
            ) : (
              <div className="cleaner-file-ready">
                <span>✓</span><div><strong>{fileName}</strong><small>Image chargée et prête à être améliorée</small></div><button type="button" onClick={() => fileInput.current?.click()}>Remplacer</button>
              </div>
            )}

            {message && <p className="cleaner-message">{message}</p>}

            <section className={`cleaner-crop-priority ${!imageUrl ? "disabled" : ""}`}>
              <div className="cleaner-control-heading crop-heading"><span>02</span><div><h2>Isoler parfaitement la feuille</h2><p>Le recadrage est appliqué automatiquement dès l’import.</p></div></div>
              <button type="button" className="cleaner-auto-crop" disabled={!imageUrl} onClick={autoCrop}>
                <span>⌗</span><div><strong>Relancer le recadrage automatique</strong><small>Détecte les quatre bords et retire la table ou le fond</small></div><b>→</b>
              </button>
              <div className="cleaner-rotate-tools">
                <button type="button" disabled={!imageUrl} onClick={() => setRotation((current) => current - 90)}><span>↶</span>Tourner à gauche</button>
                <button type="button" disabled={!imageUrl} onClick={() => setRotation((current) => current + 90)}><span>↷</span>Tourner à droite</button>
              </div>
              <button type="button" className="cleaner-manual-toggle crop-toggle" disabled={!imageUrl} onClick={() => setManualOpen((current) => !current)}>
                <span>Corriger les bords à la main</span><small>{manualOpen ? "Les quatre contrôles sont ouverts" : "Haut, bas, gauche et droite"}</small><b>{manualOpen ? "−" : "+"}</b>
              </button>
              {manualOpen && (
                <div className="cleaner-manual crop-manual">
                  <div className="cleaner-crop">
                    <div><strong>Ajuster les quatre bords</strong><button type="button" onClick={() => setCrop({ top: 0, right: 0, bottom: 0, left: 0 })}>Réinitialiser</button></div>
                    {(Object.keys(crop) as Array<keyof typeof crop>).map((side) => (
                      <label key={side}><span>{{ top: "Haut", right: "Droite", bottom: "Bas", left: "Gauche" }[side]} <b>{crop[side]} %</b></span><input type="range" min="0" max="35" value={crop[side]} onChange={(event) => setCrop((current) => ({ ...current, [side]: Number(event.target.value) }))} /></label>
                    ))}
                  </div>
                </div>
              )}
            </section>

            <div className={`cleaner-adjustments ${!imageUrl ? "disabled" : ""}`}>
              <div className="cleaner-control-heading small"><span>03</span><div><h2>Rendre le texte lisible</h2><p>Après le recadrage, choisissez simplement le rendu.</p></div></div>
              <div className="cleaner-presets">
                <button type="button" className={preset === "auto" ? "active auto" : "auto"} disabled={!imageUrl} onClick={() => applyPreset("auto")}><span>⚡</span><strong>Automatique</strong><small>Analyse chaque photo et corrige lumière + contraste</small></button>
                <button type="button" className={preset === "optimal" ? "active" : ""} disabled={!imageUrl} onClick={() => applyPreset("optimal")}><span>✦</span><strong>Lisible</strong><small>Le meilleur choix dans la plupart des cas</small></button>
                <button type="button" className={preset === "blackwhite" ? "active" : ""} disabled={!imageUrl} onClick={() => applyPreset("blackwhite")}><span>◐</span><strong>Noir & blanc</strong><small>Idéal pour un texte pâle</small></button>
                <button type="button" className={preset === "soft" ? "active" : ""} disabled={!imageUrl} onClick={() => applyPreset("soft")}><span>◌</span><strong>Doux</strong><small>Conserve davantage la photo</small></button>
              </div>
              <button type="button" className="cleaner-manual-toggle" disabled={!imageUrl} onClick={() => setFineTuneOpen((current) => !current)}>
                <span>Ajuster lumière et contraste</span><small>{fineTuneOpen ? "Masquer les réglages" : "Pour les ordonnances très pâles"}</small><b>{fineTuneOpen ? "−" : "+"}</b>
              </button>
              {fineTuneOpen && (
                <div className="cleaner-manual">
                  <div className="cleaner-sliders">
                    <label><span>Luminosité <b>{brightness - 100 > 0 ? "+" : ""}{brightness - 100}</b></span><input type="range" min="75" max="135" value={brightness} onChange={(event) => { setPreset("optimal"); setBrightness(Number(event.target.value)); }} /></label>
                    <label><span>Contraste <b>{contrast - 100 > 0 ? "+" : ""}{contrast - 100}</b></span><input type="range" min="80" max="180" value={contrast} onChange={(event) => { setPreset("optimal"); setContrast(Number(event.target.value)); }} /></label>
                  </div>
                  <div className="cleaner-checks">
                    <label><input type="checkbox" checked={grayscale} onChange={(event) => setGrayscale(event.target.checked)} />Noir & blanc</label>
                    <label><input type="checkbox" checked={sharpen} onChange={(event) => setSharpen(event.target.checked)} />Renforcer le texte</label>
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="cleaner-preview">
            <div className="cleaner-preview-head">
              <div><span>04</span><div><h2>Vérifier le cadrage</h2><p>Contrôlez les quatre bords avant d’imprimer.</p></div></div>
              {imageUrl && <div className="cleaner-view-tabs">{(["cleaned", "original", "compare"] as CleanerView[]).map((item) => <button type="button" key={item} className={view === item ? "active" : ""} onClick={() => setView(item)}>{item === "cleaned" ? "Nettoyé" : item === "original" ? "Original" : "Comparer"}</button>)}</div>}
            </div>

            <div className={`cleaner-stage ${view} ${imageUrl ? "ready" : ""}`}>
              {!imageUrl ? (
                <div className="cleaner-stage-empty"><span>⌁</span><h3>L’aperçu apparaîtra ici</h3><p>Importez une ordonnance pour commencer.</p></div>
              ) : (
                <>
                  {(view === "original" || view === "compare") && <figure className="cleaner-original"><figcaption>AVANT</figcaption><img src={imageUrl} alt="Ordonnance originale" /></figure>}
                  {(view === "cleaned" || view === "compare") && <figure className="cleaner-result"><figcaption>APRÈS</figcaption><div className="cleaner-canvas-wrap">{processing && <span className="cleaner-processing">Nettoyage…</span>}<canvas ref={cleanedCanvas} aria-label="Ordonnance nettoyée" /></div></figure>}
                  {view === "original" && <canvas ref={cleanedCanvas} className="cleaner-hidden-canvas" aria-hidden="true" />}
                </>
              )}
            </div>

            <div className="cleaner-export">
              <div><span className="cleaner-ready-dot" /><p><strong>{imageUrl ? "Prêt à exporter" : "En attente d’une image"}</strong><small>{imageUrl ? "Vérifiez les bords et la lisibilité du texte." : "Aucun document n’est conservé."}</small></p></div>
              <div>
                {imageUrl && <button type="button" className="cleaner-reset" onClick={reset}>Recommencer</button>}
                <button type="button" disabled={!imageUrl || processing} onClick={download}>Télécharger</button>
                <button type="button" className="primary" disabled={!imageUrl || processing} onClick={print}>Imprimer / PDF</button>
              </div>
            </div>
          </section>
        </div>
      </section>

      {dragging && <div className="cleaner-global-drop" aria-hidden="true"><span>↓</span><strong>Déposez l’ordonnance</strong><small>Le recadrage et la correction automatique démarrent immédiatement</small></div>}

      <section className="cleaner-print" aria-hidden="true">{printData && <img src={printData} alt="" />}</section>
    </main>
  );
}

type TeamContentItem = { id: number; title: string; details?: string; dueDate?: string; status?: string; created_at?: number };

function TeamContentModule({
  onBack,
  title,
  eyebrow,
  description,
  endpoint,
  itemLabel,
}: {
  onBack: () => void;
  title: string;
  eyebrow: string;
  description: string;
  endpoint: string;
  itemLabel: string;
}) {
  const [items, setItems] = useState<TeamContentItem[]>([]);
  const [draft, setDraft] = useState({ title: "", details: "", dueDate: "" });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);

  const load = async () => {
    try {
      const response = await fetch(endpoint, { cache: "no-store" });
      const payload = await response.json() as { items?: TeamContentItem[]; error?: string };
      if (!response.ok) throw new Error(payload.error || "Chargement impossible.");
      setItems(payload.items ?? []);
      setMessage("");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Chargement impossible.");
    }
  };

  useEffect(() => { void load(); }, [endpoint]);

  const save = async () => {
    if (!draft.title.trim() || busy) return;
    setBusy(true);
    try {
      const response = await fetch(endpoint, {
        method: editingId ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...draft, id: editingId || undefined, title: draft.title.trim(), status: editingId ? undefined : "active" }),
      });
      const payload = await response.json() as TeamContentItem & { error?: string };
      if (!response.ok) throw new Error(payload.error || "Enregistrement impossible.");
      setItems((current) => editingId
        ? current.map((item) => item.id === editingId ? payload : item)
        : [payload, ...current]);
      setDraft({ title: "", details: "", dueDate: "" });
      setEditingId(null);
      setMessage(`${itemLabel} ${editingId ? "modifié" : "enregistré"}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Enregistrement impossible.");
    } finally {
      setBusy(false);
    }
  };

  const edit = (item: TeamContentItem) => {
    setEditingId(item.id);
    setDraft({ title: item.title, details: item.details || "", dueDate: item.dueDate || "" });
    setMessage(`Modification de « ${item.title} ».`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleStatus = async (item: TeamContentItem) => {
    if (busy) return;
    setBusy(true);
    try {
      const status = item.status === "inactive" ? "active" : "inactive";
      const response = await fetch(endpoint, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...item, status }),
      });
      const payload = await response.json() as TeamContentItem & { error?: string };
      if (!response.ok) throw new Error(payload.error || "Modification impossible.");
      setItems((current) => current.map((entry) => entry.id === item.id ? payload : entry));
      setMessage(`${itemLabel} ${status === "active" ? "réactivé" : "désactivé"}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Modification impossible.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: number) => {
    if (pendingDelete !== id) return setPendingDelete(id);
    setBusy(true);
    try {
      const response = await fetch(`${endpoint}?id=${id}`, { method: "DELETE" });
      if (!response.ok) throw new Error("Suppression impossible.");
      setItems((current) => current.filter((item) => item.id !== id));
      setPendingDelete(null);
      setMessage(`${itemLabel} supprimé.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Suppression impossible.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="team-content-module">
      <header className="module-header"><button type="button" onClick={onBack}>←</button><div><span>{eyebrow}</span><h1>{title}</h1><p>{description}</p></div></header>
      <section className="team-content-composer">
        <label><span>Titre *</span><input value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} placeholder={`Titre · ${itemLabel.toLocaleLowerCase("fr")}`} /></label>
        <label><span>Date utile</span><input type="date" value={draft.dueDate} onChange={(event) => setDraft({ ...draft, dueDate: event.target.value })} /></label>
        <label className="wide"><span>Détails</span><textarea value={draft.details} onChange={(event) => setDraft({ ...draft, details: event.target.value })} placeholder="Informations visibles par l’équipe" /></label>
        <button type="button" disabled={!draft.title.trim() || busy} onClick={() => void save()}>{busy ? "Enregistrement…" : editingId ? "✓ Enregistrer les modifications" : "＋ Ajouter"}</button>
        {editingId && <button type="button" className="secondary" disabled={busy} onClick={() => { setEditingId(null); setDraft({ title: "", details: "", dueDate: "" }); }}>Annuler</button>}
      </section>
      {message && <p className="team-content-message">{message}</p>}
      <section className="team-content-list">
        {items.map((item) => <article key={item.id} className={item.status === "inactive" ? "inactive" : ""}><div><span>{item.status === "inactive" ? "Désactivé" : item.dueDate || "À diffuser"}</span><h2>{item.title}</h2><p>{item.details || "Aucun détail complémentaire."}</p></div><div className="team-content-actions"><button type="button" disabled={busy} onClick={() => edit(item)}>Modifier</button><button type="button" disabled={busy} onClick={() => void toggleStatus(item)}>{item.status === "inactive" ? "Réactiver" : "Désactiver"}</button><button type="button" className={pendingDelete === item.id ? "confirm" : "delete"} disabled={busy} onClick={() => void remove(item.id)}>{pendingDelete === item.id ? "Confirmer" : "🗑 Supprimer"}</button></div></article>)}
        {!items.length && <div className="team-content-empty"><span>✓</span><strong>Aucun élément pour le moment</strong><p>Ajoutez le premier contenu avec le formulaire ci-dessus.</p></div>}
      </section>
    </main>
  );
}

type TitulaireQuickAction = "order" | "stock" | "label" | "clearance";

function TitulaireQuickAdd({ operator, initialPharmacy, onClose, onSaved }: { operator: string; initialPharmacy: string; onClose: () => void; onSaved: (message: string) => void }) {
  const officeNames = ["Square", "Ragadan", "Laurentine"];
  const [lookup, setLookup] = useState("");
  const [product, setProduct] = useState("");
  const [ean, setEan] = useState("");
  const [action, setAction] = useState<TitulaireQuickAction>("order");
  const [selectedPharmacies, setSelectedPharmacies] = useState<string[]>(initialPharmacy ? [initialPharmacy] : [...officeNames]);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const pickProduct = (suggestion: CatalogSuggestion) => {
    setLookup(suggestion.ean);
    setEan(suggestion.ean);
    setProduct(suggestion.product);
    setError("");
  };

  const save = async () => {
    let resolvedProduct = product.trim();
    let resolvedEan = ean.trim() || extractGtinFromScan(lookup);
    if (!resolvedProduct && resolvedEan) {
      try {
        const suggestion = await resolveCatalogEan(resolvedEan);
        resolvedProduct = suggestion?.product || "";
        resolvedEan = suggestion?.ean || resolvedEan;
      } catch {
        // Le message de validation ci-dessous reste explicite.
      }
    }
    if (!resolvedProduct) return setError("Choisissez le produit proposé par le catalogue.");
    if (!selectedPharmacies.length) return setError("Choisissez au moins une pharmacie.");
    setSaving(true);
    setError("");
    try {
      let response: Response;
      if (action === "order") {
        response = await fetch("/api/product-orders", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ kind: "request", pharmacy: selectedPharmacies[0], pharmacies: selectedPharmacies, requestedBy: operator, requestDate: localDateIso(), product: resolvedProduct, ean: resolvedEan, comment, status: "new" }) });
      } else if (action === "stock" || action === "label") {
        response = await fetch("/api/stock-labels", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ items: [{ ean: resolvedEan, product: resolvedProduct, requestType: action, pharmacies: selectedPharmacies, comment, createdBy: operator }] }) });
      } else {
        const stocks: Record<string, number> = { Square: 0, Ragadan: 0, Laurentine: 0 };
        if (resolvedEan) {
          const catalogResponse = await fetch(`/api/product-catalog?ean=${encodeURIComponent(resolvedEan)}&limit=12`, { cache: "no-store" });
          if (catalogResponse.ok) {
            const catalog = await catalogResponse.json() as { items?: CatalogSuggestion[] };
            for (const item of catalog.items ?? []) if (officeNames.includes(item.pharmacy)) stocks[item.pharmacy] = Number(item.stock) || 0;
          }
        }
        response = await fetch("/api/clearance-products", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ean: resolvedEan, product: resolvedProduct, pharmacies: selectedPharmacies, stocks, expiryRisk: false, slowMoving: true, maximumPriority: false, note: comment, triggers: [], status: "active", soldCount: 0, createdBy: operator }) });
      }
      const payload = await response.json().catch(() => ({})) as { error?: string; detail?: string };
      if (!response.ok) throw new Error([payload.error, payload.detail].filter(Boolean).join(" · ") || "Enregistrement impossible.");
      const labels: Record<TitulaireQuickAction, string> = { order: "ajouté aux produits à commander", stock: "ajouté au contrôle de stock", label: "ajouté aux étiquettes à retirer", clearance: "ajouté aux produits à écouler" };
      onSaved(`${resolvedProduct} ${labels[action]}.`);
      onClose();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  return <div className="titulaire-quick-layer" role="presentation" onMouseDown={onClose}>
    <section className="titulaire-quick-panel" role="dialog" aria-modal="true" aria-labelledby="titulaire-quick-title" onMouseDown={(event) => event.stopPropagation()}>
      <header><div><span>AJOUT UNIVERSEL</span><h2 id="titulaire-quick-title">Une saisie, puis la bonne destination</h2><p>Scannez ou collez l’EAN : le produit est reconnu avant l’enregistrement.</p></div><button type="button" onClick={onClose} aria-label="Fermer">×</button></header>
      <label className="titulaire-quick-search"><span>EAN ou nom du produit</span><CatalogAutocomplete value={lookup} onChange={(value) => { setLookup(value); setEan(extractGtinFromScan(value)); if (!value.trim()) setProduct(""); }} onPick={pickProduct} placeholder="Scanner un EAN ou rechercher un produit" inputMode="search" /></label>
      <label><span>Produit reconnu</span><input value={product} onChange={(event) => setProduct(event.target.value)} placeholder="La dénomination apparaît ici" /></label>
      <fieldset className="titulaire-quick-actions"><legend>Que faut-il faire ?</legend>{([["order", "📦", "À commander"], ["stock", "🔎", "Contrôler le stock"], ["label", "🏷️", "Retirer l’étiquette"], ["clearance", "📣", "À écouler"]] as const).map(([value, icon, label]) => <button key={value} type="button" className={action === value ? "active" : ""} onClick={() => setAction(value)}><i>{icon}</i><span>{label}</span></button>)}</fieldset>
      <fieldset className="titulaire-quick-pharmacies"><legend>Pharmacies concernées</legend>{officeNames.map((name) => <button type="button" key={name} className={selectedPharmacies.includes(name) ? "active" : ""} onClick={() => setSelectedPharmacies((current) => current.includes(name) ? current.filter((item) => item !== name) : [...current, name])}><i>{selectedPharmacies.includes(name) ? "✓" : ""}</i>{name}</button>)}</fieldset>
      <label><span>Précision <small>facultatif</small></span><textarea value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Contexte ou consigne utile…" /></label>
      {error && <p className="titulaire-quick-error" role="alert">{error}</p>}
      <footer><button type="button" className="secondary" onClick={onClose}>Annuler</button><button type="button" className="primary" disabled={saving || (!lookup.trim() && !product.trim()) || !selectedPharmacies.length} onClick={() => void save()}>{saving ? "Enregistrement…" : "✓ Enregistrer"}</button></footer>
    </section>
  </div>;
}

export default function Home({ authenticatedEmail = "" }: { authenticatedEmail?: string }) {
  const titulaireAuthorized = authenticatedEmail.trim().toLocaleLowerCase("fr") === TITULAIRE_ACCOUNT_EMAIL;
  const [activeTab, setActiveTab] = useState("Aujourd’hui");
  const [pharmacy, setPharmacy] = useState(pharmacies[0]);
  const [activeOperator, setActiveOperator] = useState(titulaireAuthorized ? "Vincent B" : "");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("Tout");
  const [launcherOpen, setLauncherOpen] = useState(false);
  const [titulaireMode, setTitulaireMode] = useState(false);
  const [titulaireRailCollapsed, setTitulaireRailCollapsed] = useState(false);
  const [titulaireQuickOpen, setTitulaireQuickOpen] = useState(false);
  const [titulaireNotesExpanded, setTitulaireNotesExpanded] = useState(false);
  const [selectedTool, setSelectedTool] = useState<Tool | null>(null);
  const [completed, setCompleted] = useState<number[]>([]);
  const [toast, setToast] = useState("");
  const [homeMissionData, setHomeMissionData] = useState<MissionData>({ missions: [], completions: [] });
  const [homeDailyData, setHomeDailyData] = useState<DailyTaskData>({ tasks: [], notes: [], syncedAt: 0 });
  const [homeOperatorNotes, setHomeOperatorNotes] = useState<OperatorNote[]>([]);
  const [homeAdrenalineItems, setHomeAdrenalineItems] = useState<AdrenalineItem[]>([]);
  const [homeCpamItems, setHomeCpamItems] = useState<CpamOverpayment[]>([]);
  const [homeMissingProducts, setHomeMissingProducts] = useState<MissingProduct[]>([]);
  const [homeStockLabels, setHomeStockLabels] = useState<StockLabelRequest[]>([]);
  const [homeLaboratoryItems, setHomeLaboratoryItems] = useState<LaboratoryFollowup[]>([]);
  const [homeLaboratoryProfiles, setHomeLaboratoryProfiles] = useState<LaboratoryProfile[]>([]);
  const [homeMadRentals, setHomeMadRentals] = useState<MadRental[]>([]);
  const [homeHandoverData, setHomeHandoverData] = useState<TeamHandoverData>({ messages: [], reads: [], syncedAt: 0 });
  const [homePatientFollowups, setHomePatientFollowups] = useState<PatientFollowup[]>([]);
  const [homeCounterAlerts, setHomeCounterAlerts] = useState<CounterAlertView[]>([]);
  const [dismissedCounterAlertKeys, setDismissedCounterAlertKeys] = useState<string[]>([]);
  const [operatorRelayPinned, setOperatorRelayPinned] = useState(true);
  const [operatorRelayExpanded, setOperatorRelayExpanded] = useState(true);
  const [homeView, setHomeView] = useState<"pilot" | "team">("team");
  const [portalTheme, setPortalTheme] = useState<(typeof portalThemes)[number]["id"]>("jade");
  const [toolCategoryOrder, setToolCategoryOrder] = useState<Tool["category"][]>(toolCategories);
  const [homeNow, setHomeNow] = useState(new Date());
  const [, setOperatorRevision] = useState(0);
  const dailyBrief = useDailyBrief();

  useEffect(() => {
    const savedTheme = window.localStorage.getItem("portal-theme");
    if (portalThemes.some((theme) => theme.id === savedTheme)) {
      setPortalTheme(savedTheme as (typeof portalThemes)[number]["id"]);
    }
    void fetch("/api/operators", { cache: "no-store" })
      .then((response) => response.ok ? response.json() : null)
      .then((payload: { operators?: Array<{ name?: string; pharmacy?: string; role?: string; birthDate?: string }> } | null) => {
        if (!payload?.operators?.length) return;
        applySharedOperators(payload.operators);
        setOperatorRevision((current) => current + 1);
      })
      .catch(() => undefined);
    const savedPharmacy = window.localStorage.getItem("portal-active-pharmacy");
    if (savedPharmacy && pharmacies.includes(savedPharmacy)) {
      setPharmacy(savedPharmacy);
    }
    try {
      const savedOrder = JSON.parse(window.localStorage.getItem("tool-category-order") ?? "[]") as Tool["category"][];
      if (savedOrder.length === toolCategories.length && toolCategories.every((item) => savedOrder.includes(item))) setToolCategoryOrder(savedOrder);
    } catch {
      // Conserve l’ordre par défaut si la préférence locale est illisible.
    }
    const requestedTool = new URLSearchParams(window.location.search).get("tool");
    setTitulaireRailCollapsed(window.localStorage.getItem("titulaire-rail-collapsed") === "1");
    if (requestedTool) {
      const match = tools.find((tool) => tool.name.toLocaleLowerCase("fr") === requestedTool.toLocaleLowerCase("fr"));
      if (match) setSelectedTool(match);
    }
  }, []);

  useEffect(() => {
    setActiveOperator(titulaireAuthorized ? "Vincent B" : "");
    setTitulaireMode(titulaireAuthorized);
    if (!titulaireAuthorized) {
      setTitulaireQuickOpen(false);
      setTitulaireNotesExpanded(false);
    }
  }, [titulaireAuthorized]);

  useEffect(() => {
    window.localStorage.setItem("portal-active-pharmacy", pharmacy);
  }, [pharmacy]);

  const setTitulaireWorkspace = (enabled: boolean) => {
    setTitulaireMode(enabled && titulaireAuthorized);
  };

  const toggleTitulaireRail = () => {
    setTitulaireRailCollapsed((current) => {
      window.localStorage.setItem("titulaire-rail-collapsed", current ? "0" : "1");
      return !current;
    });
  };

  useEffect(() => {
    document.documentElement.dataset.portalTheme = portalTheme;
    window.localStorage.setItem("portal-theme", portalTheme);
  }, [portalTheme]);

  useEffect(() => {
    const selected = pharmacy.includes("Square") ? "Square" : pharmacy.includes("Ragadan") ? "Ragadan" : pharmacy.includes("Laurentine") ? "Laurentine" : "Square";
    const loadCounterAlerts = async () => {
      try {
        const response = await fetch(`/api/counter-alerts?mode=active&channel=portal&limit=25&pharmacy=${encodeURIComponent(selected)}`, { cache: "no-store" });
        if (response.ok) {
          const payload = await response.json() as { items?: CounterAlertView[] };
          setHomeCounterAlerts(payload.items ?? []);
        }
      } catch {
        // Le portail reste disponible si le flux d’alertes programmées est brièvement indisponible.
      }
    };
    void loadCounterAlerts();
  }, [pharmacy]);

  useEffect(() => {
    const parameters = new URLSearchParams(window.location.search);
    if (parameters.get("tools") === "1") {
      setLauncherOpen(true);
      window.history.replaceState({}, "", window.location.pathname);
    } else if (parameters.get("tool") === "drive") {
      const driveTool = tools.find((tool) => tool.name === "Drive équipe");
      if (driveTool) setSelectedTool(driveTool);
      window.history.replaceState({}, "", window.location.pathname);
    }
    const timer = window.setInterval(() => setHomeNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setLauncherOpen(true);
        window.setTimeout(() => document.getElementById("global-search")?.focus(), 50);
      }
      if (event.key === "Escape") {
        setLauncherOpen(false);
        setSelectedTool(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    const loadHomeStockLabels = async () => {
      try {
        const response = await fetch("/api/stock-labels?limit=50", { cache: "no-store" });
        if (response.ok) {
          const payload = await response.json();
          setHomeStockLabels((payload.items ?? []).map(normalizeStockLabelRequest));
        }
      } catch {
        // Le reste du portail reste disponible.
      }
    };
    void loadHomeStockLabels();
  }, []);

  useEffect(() => {
    const loadHomeLaboratories = async () => {
      try {
        const [followupsResponse, profilesResponse] = await Promise.all([
          fetch("/api/laboratory-followups?limit=50", { cache: "no-store" }),
          fetch("/api/laboratories?limit=50", { cache: "no-store" }),
        ]);
        if (followupsResponse.ok) {
          const payload = await followupsResponse.json();
          setHomeLaboratoryItems((payload.items ?? []).map(normalizeLaboratoryFollowup));
        }
        if (profilesResponse.ok) {
          const payload = await profilesResponse.json();
          setHomeLaboratoryProfiles((payload.items ?? []).map(normalizeLaboratoryProfile));
        }
      } catch {
        // Le reste du portail reste utilisable si l’agenda labo est brièvement indisponible.
      }
    };
    void loadHomeLaboratories();
  }, []);

  useEffect(() => {
    const loadHomeMadRentals = async () => {
      try {
        const response = await fetch("/api/mad-rentals?limit=50", { cache: "no-store" });
        if (response.ok) {
          const payload = await response.json();
          setHomeMadRentals((payload.rentals ?? []).map((item: Record<string, unknown>) => normalizeMadRental(item)));
        }
      } catch {
        // Le cockpit reste disponible si le suivi MAD est brièvement indisponible.
      }
    };
    void loadHomeMadRentals();
  }, []);

  useEffect(() => {
    const loadHomePatientFollowups = async () => {
      try {
        const response = await fetch("/api/patient-followups?limit=50", { cache: "no-store" });
        if (response.ok) setHomePatientFollowups(await response.json() as PatientFollowup[]);
      } catch {
        // L’accueil reste disponible si le suivi patient est brièvement hors ligne.
      }
    };
    void loadHomePatientFollowups();
  }, []);

  useEffect(() => {
    const loadHomeMissions = async () => {
      try {
        const response = await fetch("/api/recurring-missions?limit=50", { cache: "no-store" });
        if (response.ok) setHomeMissionData(normalizeMissionData(await response.json()));
      } catch {
        // The rest of the home page stays available if the mission feed is offline.
      }
    };
    void loadHomeMissions();
  }, []);

  useEffect(() => {
    const loadHomeHandovers = async () => {
      try {
        const response = await fetch("/api/team-handovers?limit=50", { cache: "no-store" });
        if (response.ok) setHomeHandoverData(normalizeTeamHandoverData(await response.json()));
      } catch {
        // L’accueil reste disponible si les passations sont brièvement hors ligne.
      }
    };
    void loadHomeHandovers();
  }, []);

  useEffect(() => {
    const loadHomeDaily = async () => {
      try {
        const response = await fetch("/api/daily-tasks?limit=50", { cache: "no-store" });
        if (response.ok) setHomeDailyData(normalizeDailyTaskData(await response.json()));
      } catch {
        // The portal remains available if the live list is briefly offline.
      }
    };
    void loadHomeDaily();
    return subscribeToDailyTasksUpdates(loadHomeDaily);
  }, []);

  useEffect(() => {
    const loadHomeOperatorNotes = async () => {
      try {
        const response = await fetch("/api/operator-notes?limit=50", { cache: "no-store" });
        if (response.ok) {
          const payload = await response.json();
          setHomeOperatorNotes((payload.notes ?? []).map(normalizeOperatorNote));
        }
      } catch {
        // A personal relay outage must never block the pharmacy cockpit.
      }
    };
    void loadHomeOperatorNotes();
  }, []);

  useEffect(() => {
    const loadHomeAdrenaline = async () => {
      try {
        const response = await fetch("/api/adrenaline?limit=50", { cache: "no-store" });
        if (response.ok) {
          const payload = await response.json();
          setHomeAdrenalineItems((payload.items ?? []).map(normalizeAdrenalineItem));
        }
      } catch {
        // The cockpit remains usable if the safety feed is briefly offline.
      }
    };
    void loadHomeAdrenaline();
  }, []);

  useEffect(() => {
    const loadHomeCpam = async () => {
      try {
        const response = await fetch("/api/cpam-overpayments?limit=50", { cache: "no-store" });
        if (response.ok) {
          const payload = await response.json();
          setHomeCpamItems((payload.items ?? []).map(normalizeCpamOverpayment));
        }
      } catch {
        // Financial details stay inside the module if this summary feed is offline.
      }
    };
    void loadHomeCpam();
  }, []);

  useEffect(() => {
    const loadHomeMissingProducts = async () => {
      try {
        const response = await fetch("/api/missing-products?limit=50", { cache: "no-store" });
        if (response.ok) {
          const payload = await response.json();
          setHomeMissingProducts((payload.items ?? []).map(normalizeMissingProduct));
        }
      } catch {
        // Le cockpit reste utilisable si le suivi des ruptures est brièvement indisponible.
      }
    };
    void loadHomeMissingProducts();
  }, []);

  useEffect(() => {
    const preference = window.localStorage.getItem(`operator-relay:${activeOperator}`);
    if (preference) {
      try {
        const saved = JSON.parse(preference) as { pinned?: boolean; expanded?: boolean };
        setOperatorRelayPinned(saved.pinned !== false);
        setOperatorRelayExpanded(saved.expanded !== false);
        return;
      } catch {
        // Use the visible default if a browser preference is malformed.
      }
    }
    setOperatorRelayPinned(true);
    setOperatorRelayExpanded(true);
  }, [activeOperator]);

  useEffect(() => {
    const preference = window.localStorage.getItem("home-view");
    if (preference === "pilot" || preference === "team") setHomeView(preference);
  }, []);

  const filteredTools = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("fr");
    return tools.filter((tool) => {
      const categoryMatch = category === "Tout" || category === tool.category;
      const queryMatch = !normalized || `${tool.name} ${tool.description} ${tool.category}`.toLocaleLowerCase("fr").includes(normalized);
      return categoryMatch && queryMatch;
    });
  }, [category, query]);

  const completeAction = (id: number, title: string) => {
    setCompleted((current) => [...current, id]);
    setToast(`« ${title} » marqué comme terminé`);
    window.setTimeout(() => setToast(""), 2600);
  };

  const openTool = (tool: Tool) => {
    setSelectedTool(tool);
    setLauncherOpen(false);
  };

  const saveRelayPreference = (pinned: boolean, expanded: boolean) => {
    setOperatorRelayPinned(pinned);
    setOperatorRelayExpanded(expanded);
    window.localStorage.setItem(`operator-relay:${activeOperator}`, JSON.stringify({ pinned, expanded }));
  };

  const completeHomeOperatorNote = async (note: OperatorNote) => {
    setHomeOperatorNotes((current) => current.map((item) => item.id === note.id ? { ...item, status: "completed", completedAt: Date.now() } : item));
    try {
      const response = await fetch("/api/operator-notes", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: note.id, action: "complete" }),
      });
      if (!response.ok) throw new Error();
      setToast(`« ${note.title} » marqué comme fait`);
      window.setTimeout(() => setToast(""), 2200);
    } catch {
      setHomeOperatorNotes((current) => current.map((item) => item.id === note.id ? note : item));
      setToast("La note n’a pas pu être validée");
      window.setTimeout(() => setToast(""), 2200);
    }
  };

  if (selectedTool?.name === "Mémo conseil patient") {
    return <MemoModule onBack={() => setSelectedTool(null)} />;
  }

  if (selectedTool?.name === "Fiche conseil patient") {
    const initialPharmacy = pharmacy.includes("Square") ? "Square" : pharmacy.includes("Ragadan") ? "Ragadan" : "Laurentine";
    return <AdviceSheetModule onBack={() => setSelectedTool(null)} initialOperator={activeOperator} initialPharmacy={initialPharmacy} />;
  }

  if (selectedTool?.name === "Droits de prescription") {
    return <PrescriptionRightsModule onBack={() => setSelectedTool(null)} />;
  }

  if (selectedTool?.name === "Ventes associées") {
    const initialPharmacy = pharmacy.includes("Square") ? "Square" : pharmacy.includes("Ragadan") ? "Ragadan" : pharmacy.includes("Laurentine") ? "Laurentine" : "Square";
    return <AssociatedSalesModule onBack={() => setSelectedTool(null)} initialOperator={activeOperator} initialPharmacy={initialPharmacy} />;
  }

  if (selectedTool?.name === "Nombre de gouttes") {
    return <DropCountModule onBack={() => setSelectedTool(null)} />;
  }

  if (selectedTool?.name === "Équivalences pansements") {
    return <DressingEquivalenceModule onBack={() => setSelectedTool(null)} />;
  }

  if (selectedTool?.name === "Assistant prix") {
    const initialPharmacy = pharmacy.includes("Square") ? "Square" : pharmacy.includes("Ragadan") ? "Ragadan" : pharmacy.includes("Laurentine") ? "Laurentine" : "Square";
    return <PricingAssistantModule onBack={() => setSelectedTool(null)} initialPharmacy={initialPharmacy} />;
  }

  if (selectedTool?.name === "BRI") {
    const editId = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("briEdit") : "";
    const source = editId ? `/bri/bri-reglements.html?edit=${encodeURIComponent(editId)}` : "/bri/bri-module.html";
    return <main className="bri-module-shell"><header><button type="button" onClick={() => setSelectedTool(null)}>← Accueil</button><div><span>BACK-OFFICE LABORATOIRE</span><h1>Module BRI</h1></div><b>Synchronisé avec les fiches laboratoire</b></header><iframe src={source} title="Module BRI" /></main>;
  }

  if (selectedTool?.name === "Challenge équipe") {
    const initialPharmacy = pharmacy.includes("Square") ? "Square" : pharmacy.includes("Ragadan") ? "Ragadan" : pharmacy.includes("Laurentine") ? "Laurentine" : "Square";
    return <ChallengeModule onBack={() => setSelectedTool(null)} initialPharmacy={initialPharmacy} initialOperator={activeOperator} />;
  }

  if (selectedTool?.name === "PharmaMerch") {
    const initialPharmacy = pharmacy.includes("Square") ? "Square" : pharmacy.includes("Ragadan") ? "Ragadan" : pharmacy.includes("Laurentine") ? "Laurentine" : "Square";
    return <MerchandisingModule onBack={() => setSelectedTool(null)} initialPharmacy={initialPharmacy} initialOperator={activeOperator} />;
  }

  if (selectedTool?.name === "Création Affiche promo") {
    const initialPharmacy = pharmacy.includes("Square") ? "Square" : pharmacy.includes("Ragadan") ? "Ragadan" : pharmacy.includes("Laurentine") ? "Laurentine" : "Square";
    return <PromoPosterModule onBack={() => setSelectedTool(null)} initialPharmacy={initialPharmacy} />;
  }

  if (selectedTool?.name === "Formation équipe") {
    return <TeamContentModule
      onBack={() => setSelectedTool(null)}
      title="Formation équipe"
      eyebrow="ORGANISATION · COMPÉTENCES"
      description="Planifiez les formations et conservez les consignes utiles à l’équipe."
      endpoint="/api/team-training"
      itemLabel="Formation"
    />;
  }

  if (selectedTool?.name === "Une semaine, un conseil") {
    return <TeamContentModule
      onBack={() => setSelectedTool(null)}
      title="Une semaine, un conseil"
      eyebrow="ANIMATION DU COMPTOIR"
      description="Préparez le conseil court qui sera partagé cette semaine."
      endpoint="/api/weekly-advice"
      itemLabel="Conseil"
    />;
  }

  if (selectedTool?.name === "Promotions") {
    return <TeamContentModule
      onBack={() => setSelectedTool(null)}
      title="Promotions"
      eyebrow="CAMPAGNES · ÉQUIPE"
      description="Centralisez les opérations promotionnelles actives et leurs dates utiles."
      endpoint="/api/promotions"
      itemLabel="Promotion"
    />;
  }

  if (selectedTool?.name === "PERFADOM") {
    const initialPharmacy = pharmacy.includes("Square") ? "Square" : pharmacy.includes("Ragadan") ? "Ragadan" : pharmacy.includes("Laurentine") ? "Laurentine" : "Square";
    return <PerfadomModule
      onBack={() => setSelectedTool(null)}
      onOpenMad={() => setSelectedTool(tools.find((tool) => tool.name === "Location MAD") ?? null)}
      initialPharmacy={initialPharmacy}
      initialOperator={activeOperator}
      operators={missionOperators.map(({ name, pharmacy: operatorPharmacy }) => ({ name, pharmacy: operatorPharmacy }))}
    />;
  }

  if (selectedTool?.name === "Patients à suivre") {
    return <PatientFollowupModule onBack={() => setSelectedTool(null)} />;
  }

  if (selectedTool?.name === "Nettoyer une ordonnance") {
    return <PrescriptionCleanerModule onBack={() => setSelectedTool(null)} />;
  }

  if (selectedTool?.name === "Missions récurrentes") {
    const initialPharmacy = pharmacy.includes("Square") ? "Square" : pharmacy.includes("Ragadan") ? "Ragadan" : pharmacy.includes("Laurentine") ? "Laurentine" : "Square";
    return <RecurringMissionsModule onBack={() => setSelectedTool(null)} initialPharmacy={initialPharmacy} />;
  }

  if (selectedTool?.name === "To-do & livraisons") {
    const initialPharmacy = pharmacy.includes("Square") ? "Square" : pharmacy.includes("Ragadan") ? "Ragadan" : pharmacy.includes("Laurentine") ? "Laurentine" : "Square";
    return <DailyTasksModule onBack={() => setSelectedTool(null)} initialPharmacy={initialPharmacy} initialOperator={activeOperator} />;
  }

  if (selectedTool?.name === "Bloc-notes opérateur") {
    return <OperatorNotesModule onBack={() => setSelectedTool(null)} initialOperator={activeOperator} />;
  }

  if (selectedTool?.name === "Suivi adrénaline") {
    return <AdrenalineModule onBack={() => setSelectedTool(null)} />;
  }

  if (selectedTool?.name === "Indus CPAM") {
    return <CpamOverpaymentsModule onBack={() => setSelectedTool(null)} />;
  }

  if (selectedTool?.name === "Courses") {
    const initialPharmacy = pharmacy.includes("Square") ? "Square" : pharmacy.includes("Ragadan") ? "Ragadan" : pharmacy.includes("Laurentine") ? "Laurentine" : "Square";
    return <ShoppingListModule onBack={() => setSelectedTool(null)} initialPharmacy={initialPharmacy} initialOperator={activeOperator} />;
  }

  if (selectedTool?.name === "Produits manquants à contrôler au quotidien") {
    return <MissingProductsModule onBack={() => setSelectedTool(null)} initialOperator={activeOperator} />;
  }

  if (selectedTool?.name === "Génériques manquants") {
    return <GenericShortagesModule onBack={() => setSelectedTool(null)} initialOperator={activeOperator} />;
  }

  if (selectedTool?.name === "Stock & étiquettes") {
    const initialPharmacy = pharmacy.includes("Square") ? "Square" : pharmacy.includes("Ragadan") ? "Ragadan" : pharmacy.includes("Laurentine") ? "Laurentine" : "Square";
    return <StockLabelsModule onBack={() => setSelectedTool(null)} initialOperator={activeOperator} initialPharmacy={initialPharmacy} />;
  }

  if (selectedTool?.name === "Produits à écouler") {
    const initialPharmacy = pharmacy.includes("Square") ? "Square" : pharmacy.includes("Ragadan") ? "Ragadan" : pharmacy.includes("Laurentine") ? "Laurentine" : "Square";
    return <ClearanceProductsModule onBack={() => setSelectedTool(null)} initialOperator={activeOperator} initialPharmacy={initialPharmacy} />;
  }

  if (selectedTool?.name === "Produits à commander") {
    const initialPharmacy = pharmacy.includes("Square") ? "Square" : pharmacy.includes("Ragadan") ? "Ragadan" : pharmacy.includes("Laurentine") ? "Laurentine" : "Square";
    return <ProductsToOrderModule
      onBack={() => setSelectedTool(null)}
      initialPharmacy={initialPharmacy}
      initialOperator={activeOperator}
      operators={missionOperators.map(({ name, pharmacy: operatorPharmacy }) => ({ name, pharmacy: operatorPharmacy }))}
    />;
  }

  if (selectedTool?.name === "Drive équipe") {
    const initialPharmacy = pharmacy.includes("Square") ? "Square" : pharmacy.includes("Ragadan") ? "Ragadan" : pharmacy.includes("Laurentine") ? "Laurentine" : "Square";
    return <DriveModule onBack={() => setSelectedTool(null)} initialPharmacy={initialPharmacy} initialOperator={activeOperator} />;
  }

  if (selectedTool?.name === "Produits dormants") {
    const initialPharmacy = pharmacy.includes("Square") ? "Square" : pharmacy.includes("Ragadan") ? "Ragadan" : pharmacy.includes("Laurentine") ? "Laurentine" : "Square";
    return <DormantProductsModule
      onBack={() => setSelectedTool(null)}
      onOpenClearance={() => setSelectedTool(tools.find((tool) => tool.name === "Produits à écouler") ?? null)}
      initialPharmacy={initialPharmacy}
      initialOperator={activeOperator}
    />;
  }

  if (selectedTool?.name === "Laboratoires") {
    const initialPharmacy = pharmacy.includes("Square") ? "Square" : pharmacy.includes("Ragadan") ? "Ragadan" : pharmacy.includes("Laurentine") ? "Laurentine" : "Square";
    return <LaboratoryModule onBack={() => setSelectedTool(null)} initialOperator={activeOperator} initialPharmacy={initialPharmacy} />;
  }

  if (selectedTool?.name === "Passation équipe") {
    const initialPharmacy = pharmacy.includes("Square") ? "Square" : pharmacy.includes("Ragadan") ? "Ragadan" : pharmacy.includes("Laurentine") ? "Laurentine" : "Square";
    return <TeamHandoverModule
      onBack={() => setSelectedTool(null)}
      initialPharmacy={initialPharmacy}
      initialOperator={activeOperator}
      operators={missionOperators.map(({ name, pharmacy: operatorPharmacy, role }) => ({ name, pharmacy: operatorPharmacy, role }))}
    />;
  }

  if (selectedTool?.name === "Location MAD") {
    const initialPharmacy = pharmacy.includes("Square") ? "Square" : pharmacy.includes("Ragadan") ? "Ragadan" : pharmacy.includes("Laurentine") ? "Laurentine" : "Square";
    return <MadRentalModule onBack={() => setSelectedTool(null)} initialOperator={activeOperator} initialPharmacy={initialPharmacy} />;
  }

  if (selectedTool?.name === "Écran équipe TV") {
    const initialPharmacy = pharmacy.includes("Square") ? "Square" : pharmacy.includes("Ragadan") ? "Ragadan" : pharmacy.includes("Laurentine") ? "Laurentine" : "Square";
    return <TeamDisplayModule onBack={() => setSelectedTool(null)} initialPharmacy={initialPharmacy} />;
  }

  const selectedPharmacyKey = pharmacy.includes("Square") ? "Square" : pharmacy.includes("Ragadan") ? "Ragadan" : pharmacy.includes("Laurentine") ? "Laurentine" : "";
  const visibleCounterAlert = homeCounterAlerts.find((item) => !dismissedCounterAlertKeys.includes(item.occurrenceKey));
  const homeMissionOccurrences = missionOccurrences(homeMissionData)
    .filter((item) => !item.completion && (!selectedPharmacyKey || item.pharmacy === selectedPharmacyKey))
    .sort((a, b) => {
      const priority = (value: MissionPriority) => value === "urgent" ? 0 : value === "important" ? 1 : 2;
      return priority(a.mission.priority) - priority(b.mission.priority) || a.mission.title.localeCompare(b.mission.title, "fr");
    })
    .slice(0, 6);
  const dailyTool = tools.find((tool) => tool.name === "To-do & livraisons")!;
  const operatorNotesTool = tools.find((tool) => tool.name === "Bloc-notes opérateur")!;
  const pricingTool = tools.find((tool) => tool.name === "Assistant prix")!;
  const adrenalineTool = tools.find((tool) => tool.name === "Suivi adrénaline")!;
  const cpamTool = tools.find((tool) => tool.name === "Indus CPAM")!;
  const tvTool = tools.find((tool) => tool.name === "Écran équipe TV")!;
  const missingProductsTool = tools.find((tool) => tool.name === "Produits manquants à contrôler au quotidien")!;
  const genericShortagesTool = tools.find((tool) => tool.name === "Génériques manquants")!;
  const stockLabelsTool = tools.find((tool) => tool.name === "Stock & étiquettes")!;
  const laboratoryTool = tools.find((tool) => tool.name === "Laboratoires")!;
  const handoverTool = tools.find((tool) => tool.name === "Passation équipe")!;
  const madTool = tools.find((tool) => tool.name === "Location MAD")!;
  const perfadomTool = tools.find((tool) => tool.name === "PERFADOM")!;
  const recurringMissionsTool = tools.find((tool) => tool.name === "Missions récurrentes")!;
  const patientFollowupTool = tools.find((tool) => tool.name === "Patients à suivre")!;
  const orderingTool = tools.find((tool) => tool.name === "Produits à commander")!;
  const clearanceTool = tools.find((tool) => tool.name === "Produits à écouler")!;
  const dormantTool = tools.find((tool) => tool.name === "Produits dormants")!;
  const homeDailyTasks = dueDailyTasks(homeDailyData)
    .filter((task) => !selectedPharmacyKey || task.pharmacies.includes(selectedPharmacyKey))
    .slice(0, 6);
  const homePatientDue = homePatientFollowups
    .filter((item) => item.status === "active" && item.recontactDate <= localDateIso(homeNow) && (!selectedPharmacyKey || item.pharmacy === selectedPharmacyKey))
    .sort((a, b) => a.recontactDate.localeCompare(b.recontactDate) || a.patient.localeCompare(b.patient, "fr"));
  const activeOperatorProfile = missionOperators.find((item) => item.name === activeOperator);
  const showTitulaire = titulaireAuthorized && titulaireMode;
  const canPinOperatorRelay = activeOperatorProfile?.role === "Titulaire";
  const operatorNotesDue = homeOperatorNotes
    .filter((note) => note.owner === activeOperator && note.status === "open" && note.dueDate <= localDateIso())
    .slice(0, 4);
  const homeAdrenalineAlerts = adrenalineAlerts(homeAdrenalineItems)
    .filter((item) => !selectedPharmacyKey || item.pharmacy === selectedPharmacyKey);
  const homeCpamPending = homeCpamItems
    .filter((item) => (item.decision === "to_process" || item.decision === "contested") && (!selectedPharmacyKey || item.pharmacy === selectedPharmacyKey));
  const homeCpamAmount = homeCpamPending.reduce((sum, item) => sum + item.amountCents, 0);
  const homeMissingDue = homeMissingProducts
    .filter((item) => isMissingProductDue(item) && (!selectedPharmacyKey || item.pharmacies.includes(selectedPharmacyKey)));
  const homeStockLabelOpen = homeStockLabels
    .filter((item) => item.status === "open" && (!selectedPharmacyKey || item.pharmacies.includes(selectedPharmacyKey)));
  const homeLaboratoryDue = homeLaboratoryItems
    .filter((item) => selectedPharmacyKey
      ? laboratoryPreparationDue(item, selectedPharmacyKey, homeNow)
      : (Object.keys(item.preparationStatus).length ? Object.keys(item.preparationStatus) : [item.pharmacy]).some((name) => laboratoryPreparationDue(item, name, homeNow)));
  const homeLaboratoryWorkDue = homeLaboratoryProfiles.flatMap((profile) =>
    (!selectedPharmacyKey || profile.pharmacies.includes(selectedPharmacyKey))
      ? profile.workstreams.filter((item) => item.status === "open" && item.dueDate <= localDateIso(homeNow)).map((item) => ({ profile, item }))
      : [],
  );
  const homeLaboratoryAlertCount = homeLaboratoryDue.length + homeLaboratoryWorkDue.length;
  const homeMadDue = homeMadRentals
    .filter((item) => (!selectedPharmacyKey || item.pharmacy === selectedPharmacyKey) && Boolean(madRentalAlert(item, localDateIso(homeNow))));
  const homeNextLaboratory = homeLaboratoryItems
    .filter((item) => item.isLaboratory && item.status === "planned" && item.scheduledDate >= localDateIso(homeNow) && (!selectedPharmacyKey || Object.prototype.hasOwnProperty.call(item.preparationStatus, selectedPharmacyKey) || item.pharmacy === selectedPharmacyKey))
    .sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate) || a.scheduledTime.localeCompare(b.scheduledTime))[0];
  const homeBirthday = nextTeamBirthday(selectedPharmacyKey || "Square", homeNow);
  const homeHandovers = homeHandoverData.messages
    .filter((item) => isTeamHandoverVisible(item, localDateIso(homeNow)) && (!selectedPharmacyKey || item.pharmacies.includes(selectedPharmacyKey)))
    .sort((a, b) => (a.priority === "urgent" ? 0 : a.priority === "important" ? 1 : 2) - (b.priority === "urgent" ? 0 : b.priority === "important" ? 1 : 2) || b.updatedAt - a.updatedAt);
  const handoverIsRead = (message: TeamHandoverMessage) => homeHandoverData.reads.some((read) =>
    read.handoverId === message.id && read.operator === activeOperator && (!selectedPharmacyKey || read.pharmacy === selectedPharmacyKey),
  );
  const unreadHomeHandovers = homeHandovers.filter((item) => !handoverIsRead(item));
  const moveToolCategory = (group: Tool["category"], direction: -1 | 1) => {
    setToolCategoryOrder((current) => {
      const index = current.indexOf(group);
      const target = index + direction;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      window.localStorage.setItem("tool-category-order", JSON.stringify(next));
      return next;
    });
  };
  const readHomeHandover = async (message: TeamHandoverMessage) => {
    const readPharmacy = selectedPharmacyKey || message.pharmacies[0];
    if (!readPharmacy || handoverIsRead(message)) return;
    const optimisticRead = { id: -Date.now(), handoverId: message.id, pharmacy: readPharmacy, operator: activeOperator, readAt: Date.now() };
    setHomeHandoverData((current) => ({ ...current, reads: [optimisticRead, ...current.reads] }));
    const response = await fetch("/api/team-handovers", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: message.id, action: "read", pharmacy: readPharmacy, operator: activeOperator }),
    });
    if (!response.ok) {
      setHomeHandoverData((current) => ({ ...current, reads: current.reads.filter((item) => item.id !== optimisticRead.id) }));
      setToast("La confirmation de lecture n’a pas été enregistrée");
    }
  };

  return (
    <main className={`app-shell home-presentation-${homeView === "team" ? "classic" : "clean"} ${showTitulaire ? `titulaire-mode ${titulaireRailCollapsed ? "titulaire-rail-is-collapsed" : ""}` : ""}`}>
      {showTitulaire && <aside className={`titulaire-sidebar ${titulaireRailCollapsed ? "collapsed" : ""}`} aria-label="Navigation du bureau titulaire">
        <header><span className="titulaire-mark">◆</span><div><strong>Bureau titulaire</strong><small>{activeOperator}</small></div><button className="titulaire-sidebar-toggle" type="button" onClick={toggleTitulaireRail} aria-label={titulaireRailCollapsed ? "Déplier la barre latérale" : "Replier la barre latérale"} title={titulaireRailCollapsed ? "Déplier la barre latérale" : "Replier la barre latérale"}><span aria-hidden="true">{titulaireRailCollapsed ? "»" : "‹"}</span></button></header>
        <nav>
          <button type="button" className="active" onClick={() => { setActiveTab("Aujourd’hui"); window.scrollTo({ top: 0, behavior: "smooth" }); }}><span>⌂</span><b>Accueil titulaire</b></button>
          <button type="button" className="quick" onClick={() => setTitulaireQuickOpen(true)}><span>＋</span><b>Ajout rapide</b></button>
          <small>PILOTAGE PERSONNEL</small>
          <button type="button" onClick={() => openTool(laboratoryTool)}><span>🧪</span><b>Laboratoires</b>{homeLaboratoryAlertCount > 0 && <i>{homeLaboratoryAlertCount}</i>}</button>
          <button type="button" onClick={() => openTool(operatorNotesTool)}><span>📌</span><b>Bloc-notes</b>{operatorNotesDue.length > 0 && <i>{operatorNotesDue.length}</i>}</button>
          <button type="button" onClick={() => openTool(pricingTool)}><span>✨</span><b>Assistant prix</b></button>
          <small>GESTION STOCK</small>
          <button type="button" onClick={() => openTool(orderingTool)}><span>📦</span><b>À commander</b></button>
          <button type="button" onClick={() => openTool(stockLabelsTool)}><span>🏷️</span><b>Stock / étiquettes</b>{homeStockLabelOpen.length > 0 && <i>{homeStockLabelOpen.length}</i>}</button>
          <button type="button" onClick={() => openTool(clearanceTool)}><span>📣</span><b>À écouler</b></button>
          <button type="button" onClick={() => openTool(dormantTool)}><span>🌙</span><b>Produits dormants</b></button>
          <button type="button" onClick={() => openTool(missingProductsTool)}><span>🔎</span><b>Manquants à contrôler</b>{homeMissingDue.length > 0 && <i>{homeMissingDue.length}</i>}</button>
          <button type="button" onClick={() => openTool(genericShortagesTool)}><span>💊</span><b>Génériques manquants</b></button>
          <button type="button" onClick={() => setLauncherOpen(true)}><span>▦</span><b>Tous les modules</b></button>
        </nav>
        <footer><a href="/admin"><span>⚙</span><b>Administration</b></a><button type="button" onClick={() => setTitulaireWorkspace(false)}><span>↙</span><b>Quitter le mode</b></button></footer>
      </aside>}
      {visibleCounterAlert && <div className="portal-counter-alert-layer"><section className="portal-counter-alert"><span>⏰ ALERTE COMPTOIR · {selectedPharmacyKey || "RÉSEAU"}</span><h2>{visibleCounterAlert.title}</h2>{visibleCounterAlert.message && <p>{visibleCounterAlert.message}</p>}<footer><small>{new Date(visibleCounterAlert.occurrenceAt).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</small><button type="button" onClick={() => setDismissedCounterAlertKeys((current) => [...current, visibleCounterAlert.occurrenceKey])}>✓ J’ai vu</button></footer></section></div>}
      <header className="global-header">
        <button className="wordmark" type="button" onClick={() => setActiveTab("Aujourd’hui")} aria-label="Accueil Saint-Laurent">
          <span className="wordmark-symbol"><i /><i /><i /></span>
          <span><strong>saint-laurent</strong><small>pharmacies</small></span>
        </button>

        <nav className="main-tabs home-header-center" aria-label="Navigation principale">
          <button className="header-current-pharmacy" type="button" onClick={() => setActiveTab("Aujourd’hui")}>
            <span>{selectedPharmacyKey || "3"}</span><strong>{selectedPharmacyKey || "Les trois pharmacies"}</strong><em>aujourd’hui</em>
          </button>
          <button className="header-applications" type="button" onClick={() => setLauncherOpen(true)}>
            <span>▦</span><b>Applications</b>
          </button>
          <button className="header-laboratory" type="button" aria-label="Labo" title="Ouvrir les laboratoires" onClick={() => openTool(laboratoryTool)}>
            <span>🧪</span><b>Labo</b>
          </button>
          {titulaireAuthorized && !showTitulaire && <button className="header-titulaire" type="button" onClick={() => setTitulaireWorkspace(true)}><span>◆</span><b>Bureau titulaire</b></button>}
        </nav>

        <div className="header-right">
          <button className="header-tv-shortcut" type="button" aria-label="Ouvrir le mode TV" title="Mode TV" onClick={() => openTool(tvTool)}>📺</button>
          <label className="pharmacy-switcher">
            <span className="sr-only">Pharmacie active</span>
            <i className="live-pulse" />
            <select value={pharmacy} onChange={(event) => setPharmacy(event.target.value)}>
              {pharmacies.map((name) => <option key={name} value={name}>{pharmacyShortLabels[name] ?? name}</option>)}
            </select>
          </label>
          <button className="round-button" type="button" aria-label="Notifications" onClick={() => setToast("Aucune nouvelle alerte critique")}>
            ◌<span>2</span>
          </button>
          <a className="admin-entry header-admin-entry" href="/admin">⚙️ Admin</a>
        </div>
        <div className="header-context">
          <span className="context-date" suppressHydrationWarning>📅 {homeNow.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} · {homeNow.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
          <span>✨ {dailyBrief.nameDay ? `Fête : ${dailyBrief.nameDay}` : "Fête du jour indisponible"}</span>
          <span>{dailyBrief.today ? <>{weatherIcon(dailyBrief.today.weatherCode)} Nice aujourd’hui <b>{dailyBrief.today.current}°</b> · {dailyBrief.today.min}/{dailyBrief.today.max}°</> : <>🌦️ Météo Nice momentanément indisponible</>}</span>
          {dailyBrief.tomorrow && <span>{weatherIcon(dailyBrief.tomorrow.weatherCode)} Demain <b>{dailyBrief.tomorrow.min}/{dailyBrief.tomorrow.max}°</b></span>}
          {dailyBrief.sea && <span>🌊 Mer <b>{dailyBrief.sea.wave.toFixed(1)} m</b> · {dailyBrief.sea.temperature ? `${dailyBrief.sea.temperature.toFixed(0)}°` : "temp. indisponible"}</span>}
          <span className={homeBirthday?.days === 0 ? "birthday today" : "birthday"}>🎂 {homeBirthday ? (homeBirthday.days === 0 ? `${homeBirthday.name} aujourd’hui` : `${homeBirthday.name} · ${homeBirthday.date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}`) : "Anniversaires à compléter"}</span>
          <div className="header-presentation-controls">
            <button type="button" className={homeView === "team" ? "active" : ""} onClick={() => { setHomeView("team"); window.localStorage.setItem("home-view", "team"); }}>▦ Ancien portail</button>
            <button type="button" className={homeView === "pilot" ? "active" : ""} onClick={() => { setHomeView("pilot"); window.localStorage.setItem("home-view", "pilot"); }}>◇ Épuré</button>
            <div className="header-theme-picker" role="group" aria-label="Couleur du portail">{portalThemes.map((theme) => <button key={theme.id} type="button" className={portalTheme === theme.id ? "active" : ""} onClick={() => setPortalTheme(theme.id)} aria-label={`Couleur ${theme.label}`} aria-pressed={portalTheme === theme.id} title={theme.label}><i style={{ background: theme.color }} /></button>)}</div>
          </div>
        </div>
      </header>

      <section className="page">
        {showTitulaire && <section className="titulaire-dashboard">
          <header><div><span>ACCUEIL DÉDIÉ TITULAIRE</span><h1>Bonjour {activeOperator.split(" ")[0]}, votre bureau essentiel.</h1><p>Labo, notes, prix, gestion du stock et administration — sans parcourir tout le portail.</p></div><button type="button" onClick={() => setTitulaireQuickOpen(true)}>＋ Ajout rapide</button></header>
          <section className={`titulaire-notes-banner ${titulaireNotesExpanded ? "expanded" : "collapsed"}`}>
            <button type="button" className="titulaire-notes-toggle" onClick={() => setTitulaireNotesExpanded((current) => !current)} aria-expanded={titulaireNotesExpanded}>
              <span className="titulaire-notes-icon">📌</span>
              <span><small>MON BLOC-NOTES</small><strong>{activeOperator}</strong><em>{operatorNotesDue.length ? `${operatorNotesDue.length} note${operatorNotesDue.length > 1 ? "s" : ""} à reprendre` : "Aucune note urgente aujourd’hui"}</em></span>
              <b>{operatorNotesDue.length}</b>
              <i>{titulaireNotesExpanded ? "Plier ⌃" : "Déplier ⌄"}</i>
            </button>
            {titulaireNotesExpanded && <div className="titulaire-notes-body">
              <div className="titulaire-notes-list">
                {operatorNotesDue.map((note) => <article key={note.id} className={note.priority}>
                  <button type="button" aria-label={`Terminer ${note.title}`} onClick={() => void completeHomeOperatorNote(note)}>✓</button>
                  <div><strong>{note.title}</strong><small>{followupDate(note.dueDate)}{note.details ? ` · ${note.details}` : ""}</small></div>
                  {note.priority !== "normal" && <span>{note.priority === "urgent" ? "Urgent" : "Important"}</span>}
                </article>)}
                {!operatorNotesDue.length && <div className="titulaire-notes-empty"><span>✓</span><p><strong>Bloc-notes à jour</strong><small>Aucune note personnelle à traiter aujourd’hui.</small></p></div>}
              </div>
              <button type="button" className="titulaire-notes-open" onClick={() => openTool(operatorNotesTool)}>Ouvrir mon bloc-notes <span>→</span></button>
            </div>}
          </section>
          <div className="titulaire-dashboard-featured">
            <button type="button" onClick={() => openTool(laboratoryTool)}><span className="rose">🧪</span><p><small>LABORATOIRES</small><strong>Rendez-vous, fiches et comptes rendus</strong><em>{homeLaboratoryAlertCount} suivi{homeLaboratoryAlertCount > 1 ? "s" : ""} à traiter</em></p><b>Ouvrir →</b></button>
            <button type="button" onClick={() => openTool(operatorNotesTool)}><span className="sand">📌</span><p><small>MON BLOC-NOTES</small><strong>Priorités et rappels personnels</strong><em>{operatorNotesDue.length} note{operatorNotesDue.length > 1 ? "s" : ""} à reprendre</em></p><b>Ouvrir →</b></button>
            <button type="button" onClick={() => openTool(pricingTool)}><span className="apricot">✨</span><p><small>PILOTAGE PRIX</small><strong>Assistant prix</strong><em>Calculs et décisions par officine</em></p><b>Ouvrir →</b></button>
            <a href="/admin"><span className="ink">⚙️</span><p><small>PARAMÉTRAGE</small><strong>Administration du portail</strong><em>Compagnons, alertes et réglages</em></p><b>Administrer →</b></a>
          </div>
          <div className="titulaire-dashboard-section-title"><span>GESTION STOCK</span><strong>Les 6 modules utiles au quotidien</strong></div>
          <div className="titulaire-dashboard-grid">
            <button type="button" onClick={() => openTool(orderingTool)}><span className="apricot">📦</span><p><small>DEMANDES COMMUNES</small><strong>Produits à commander</strong><em>Les trois pharmacies réunies</em></p><b>Ouvrir →</b></button>
            <button type="button" onClick={() => openTool(stockLabelsTool)}><span className="sky">🏷️</span><p><small>EN ATTENTE</small><strong>{homeStockLabelOpen.length} stock / étiquette{homeStockLabelOpen.length > 1 ? "s" : ""}</strong><em>À contrôler ou retirer</em></p><b>Traiter →</b></button>
            <button type="button" onClick={() => openTool(clearanceTool)}><span className="rose">📣</span><p><small>PRIORITÉS COMPTOIR</small><strong>Produits à écouler</strong><em>Déclencheurs et ventes associées</em></p><b>Traiter →</b></button>
            <button type="button" onClick={() => openTool(dormantTool)}><span className="mint">🌙</span><p><small>ANALYSE RÉSEAU</small><strong>Produits dormants</strong><em>Comparer les trois officines</em></p><b>Analyser →</b></button>
            <button type="button" onClick={() => openTool(missingProductsTool)}><span className="sand">🔎</span><p><small>CONTRÔLE QUOTIDIEN</small><strong>Produits manquants</strong><em>{homeMissingDue.length} contrôle{homeMissingDue.length > 1 ? "s" : ""} à faire</em></p><b>Contrôler →</b></button>
            <button type="button" onClick={() => openTool(genericShortagesTool)}><span className="sky">💊</span><p><small>SUIVI DES RUPTURES</small><strong>Génériques manquants</strong><em>Molécules, labos et retours</em></p><b>Ouvrir →</b></button>
          </div>
        </section>}
        <div className="home-operational-grid">
          <section className={`home-handover-relay ${homeHandovers.length === 0 ? "is-empty" : ""}`}>
            <header>
              <div className="home-handover-title">
                <span>🤲</span>
                <div><small>PASSATION ÉQUIPE · TEMPS RÉEL</small><h2>À reprendre entre équipes</h2><p>{selectedPharmacyKey || "Toutes les pharmacies"} · {homeHandovers.length > 0 ? `${unreadHomeHandovers.length} information${unreadHomeHandovers.length > 1 ? "s" : ""} à lire pour ${activeOperator}` : "aucune passation en attente"}</p></div>
              </div>
              <button type="button" onClick={() => openTool(handoverTool)}>Ouvrir la passation <span>→</span></button>
            </header>
            <div className="home-handover-cards">
              {homeHandovers.length === 0 ? (
                <article className="home-handover-empty-card">
                  <div className="home-handover-card-top"><span>✅</span><b>À jour</b></div>
                  <h3>Tout est transmis</h3>
                  <p>La prochaine information d’équipe apparaîtra ici immédiatement.</p>
                </article>
              ) : homeHandovers.slice(0, 3).map((message) => {
                const read = handoverIsRead(message);
                return <article key={message.id} className={`${message.priority} ${read ? "read" : "unread"}`}>
                  <div className="home-handover-card-top"><span>{message.category === "safety" ? "🛡️" : message.category === "stock" ? "📦" : message.category === "patient" ? "🩺" : "💬"}</span><b>{message.priority === "urgent" ? "Urgent" : message.priority === "important" ? "Important" : read ? "Lu" : "À lire"}</b></div>
                  <h3>{message.title}</h3>
                  <p>{message.body}</p>
                  <footer><small>Par {message.createdBy} · {message.audience}</small>{!read && message.requiresAck ? <button type="button" onClick={() => void readHomeHandover(message)}>✓ Vu et pris en compte</button> : <span className="home-handover-read">{read ? "✓ Lu" : "Information"}</span>}</footer>
                </article>;
              })}
            </div>
          </section>

          {homeView === "team" && (
            <section className="home-glance">
            <header><div><span>⚡ PRIORITÉS ÉQUIPE</span><h2>Ce qu’il faut voir maintenant</h2></div><b>{homeMissionOccurrences.length + homeDailyTasks.length} action{homeMissionOccurrences.length + homeDailyTasks.length > 1 ? "s" : ""}</b></header>
            <div className="home-glance-grid">
              <article className="daily">
                <div className="home-glance-heading"><span>🚚</span><div><small>TEMPS RÉEL</small><h3>To-do & livraisons</h3></div><button type="button" onClick={() => openTool(dailyTool)}>Tout voir →</button></div>
                <div className="home-glance-list">
                  {homeDailyTasks.length ? homeDailyTasks.map((task) => <button type="button" key={task.id} className={`${task.kind} ${task.priority}`} onClick={() => openTool(dailyTool)}><i>{task.kind === "delivery" ? "▣" : "✓"}</i><span><strong>{task.title}</strong><small>{task.kind === "delivery" ? task.recipient : task.assignedTo || "Toute l’équipe"} · {task.dueTime || (task.displayDate < localDateIso() ? "En retard" : "Aujourd’hui")}</small></span><b>{task.kind === "delivery" ? "Livraison" : task.priority === "urgent" ? "Urgent" : "À faire"}</b></button>) : <button type="button" className="empty" onClick={() => openTool(dailyTool)}><i>✓</i><span><strong>Liste à jour</strong><small>Aucune action ou livraison en attente.</small></span></button>}
                </div>
              </article>
              <article className={`missions ${homeMissionOccurrences.some((item) => item.mission.priority === "urgent") ? "has-urgent" : ""}`}>
                <div className="home-glance-heading"><span className="home-glance-count">{homeMissionOccurrences.length}</span><div><small>🔁 RÉCURRENT · À VALIDER</small><h3>Missions du jour</h3></div><button type="button" onClick={() => openTool(recurringMissionsTool)}>Ouvrir les missions →</button></div>
                <div className="home-glance-list">
                  {homeMissionOccurrences.length ? homeMissionOccurrences.map((item) => <button type="button" key={`${item.mission.id}-${item.pharmacy}`} className={item.mission.priority} onClick={() => openTool(recurringMissionsTool)}><i>✓</i><span><strong>{item.mission.title}</strong><small>{item.pharmacy} · {item.periodLabel}</small></span><b>{item.mission.priority === "urgent" ? "Urgent" : item.mission.priority === "important" ? "Important" : "À faire"}</b></button>) : <button type="button" className="empty" onClick={() => openTool(recurringMissionsTool)}><i>✓</i><span><strong>Missions à jour</strong><small>Rien à valider maintenant.</small></span></button>}
                </div>
              </article>
            </div>
            </section>
          )}
        </div>

        <button className="command-bar" type="button" onClick={() => setLauncherOpen(true)}>
          <span className="command-icon">⌕</span>
          <span><strong>Que voulez-vous faire ?</strong><small>Un patient, une procédure, un outil, une action…</small></span>
          <kbd>⌘ K</kbd>
        </button>

        {canPinOperatorRelay && (
          operatorRelayPinned ? (
            <section className={`operator-relay ${operatorRelayExpanded ? "expanded" : "collapsed"}`}>
              <header>
                <button type="button" className="operator-relay-main" onClick={() => saveRelayPreference(true, !operatorRelayExpanded)} aria-expanded={operatorRelayExpanded}>
                  <span className="operator-relay-icon">▤</span>
                  <span><small>BLOC-NOTES PERSONNEL</small><strong>{activeOperator}</strong></span>
                  <b>{operatorNotesDue.length}</b>
                  <em>{operatorRelayExpanded ? "Plier" : "Déplier"} {operatorRelayExpanded ? "⌃" : "⌄"}</em>
                </button>
                <button type="button" className="operator-relay-pin active" onClick={() => saveRelayPreference(false, false)} aria-label="Retirer le bloc-notes de l’accueil">● Épinglé</button>
              </header>
              {operatorRelayExpanded && (
                <div className="operator-relay-body">
                  <div className="operator-relay-list">
                    {operatorNotesDue.map((note) => <article key={note.id} className={note.priority}>
                      <button type="button" aria-label={`Terminer ${note.title}`} onClick={() => void completeHomeOperatorNote(note)}>✓</button>
                      <div><strong>{note.title}</strong><small>{followupDate(note.dueDate)}{note.details ? ` · ${note.details}` : ""}</small></div>
                      {note.priority !== "normal" && <span>{note.priority === "urgent" ? "Urgent" : "Important"}</span>}
                    </article>)}
                    {!operatorNotesDue.length && <div className="operator-relay-empty"><span>✓</span><p><strong>Bloc-notes à jour</strong><small>Aucune note personnelle à traiter aujourd’hui.</small></p></div>}
                  </div>
                  <button type="button" className="operator-relay-open" onClick={() => openTool(operatorNotesTool)}>Ouvrir mon bloc-notes <span>→</span></button>
                </div>
              )}
            </section>
          ) : (
            <button type="button" className="operator-relay-unpinned" onClick={() => saveRelayPreference(true, true)}>
              <span>▤</span><span><strong>Bloc-notes personnel masqué</strong><small>Réafficher le relais de {activeOperator} sur l’accueil</small></span><b>Épingler</b>
            </button>
          )
        )}

        <section className="priority-strip" aria-label="Résumé des priorités">
          <button type="button" onClick={() => setActiveTab("Aujourd’hui")}>
            <span className="priority-number urgent">4</span>
            <span><strong>À faire maintenant</strong><small>1 action urgente</small></span>
            <i>→</i>
          </button>
          <button type="button" onClick={() => openTool(patientFollowupTool)}>
            <span className="priority-number patient">{homePatientDue.length}</span>
            <span><strong>Patients à suivre</strong><small>Échéance aujourd’hui</small></span>
            <i>→</i>
          </button>
          <button type="button" onClick={() => openTool(orderingTool)}>
            <span className="priority-number stock">6</span>
            <span><strong>À commander</strong><small>3 pharmacies réunies</small></span>
            <i>→</i>
          </button>
          <button type="button" onClick={() => openTool(laboratoryTool)}>
            <span className="priority-number agenda">{homeLaboratoryAlertCount}</span>
            <span><strong>Laboratoires</strong><small>{homeLaboratoryAlertCount ? `${homeLaboratoryAlertCount} suivi${homeLaboratoryAlertCount > 1 ? "s" : ""} à traiter` : homeNextLaboratory ? `${homeNextLaboratory.laboratory} · ${followupDate(homeNextLaboratory.scheduledDate)}${homeNextLaboratory.scheduledTime ? ` à ${homeNextLaboratory.scheduledTime}` : ""}` : "Aucun rendez-vous planifié"}</small></span>
            <i>→</i>
          </button>
        </section>

        {homePatientDue.length > 0 && (
          <section className="home-patient-alert">
            <div className="home-patient-symbol">☎</div>
            <div>
              <span>SUIVI PATIENT · RAPPEL À FAIRE</span>
              <h2>{homePatientDue.length === 1 ? homePatientDue[0].patient : `${homePatientDue.length} patients à rappeler`}</h2>
              <p>{homePatientDue.slice(0, 3).map((item) => `${item.patient} · ${item.note || item.advice}`).join("  •  ")}{homePatientDue.length > 3 ? `  •  +${homePatientDue.length - 3}` : ""}</p>
            </div>
            <button type="button" onClick={() => openTool(patientFollowupTool)}>Ouvrir le suivi <span>→</span></button>
          </section>
        )}

        {homeAdrenalineAlerts.length > 0 && (
          <section className="home-adrenaline-alert">
            <div className="home-adrenaline-symbol">ϟ</div>
            <div>
              <span>SÉCURITÉ VACCINATION</span>
              <h2>{homeAdrenalineAlerts.length === 1 ? `${homeAdrenalineAlerts[0].product} à renouveler · ${homeAdrenalineAlerts[0].pharmacy}` : `${homeAdrenalineAlerts.length} adrénalines à renouveler`}</h2>
              <p>{homeAdrenalineAlerts.map((item) => `${item.pharmacy} · ${daysUntilDate(item.expiryDate) < 0 ? "périmée" : `J−${daysUntilDate(item.expiryDate)}`}`).join("  •  ")}</p>
            </div>
            <button type="button" onClick={() => openTool(adrenalineTool)}>Voir le suivi <span>→</span></button>
          </section>
        )}

        {homeCpamPending.length > 0 && (
          <section className="home-cpam-summary">
            <div className="home-cpam-symbol">€</div>
            <div><span>SUIVI ADMINISTRATIF · CONFIDENTIEL</span><h2>{homeCpamPending.length} indu{homeCpamPending.length > 1 ? "s" : ""} à suivre</h2><p>{formatEuro(homeCpamAmount)} en cours de traitement ou de contestation.</p></div>
            <button type="button" onClick={() => openTool(cpamTool)}>Ouvrir le suivi <span>→</span></button>
          </section>
        )}

        {homeMissingDue.length > 0 && (
          <section className="home-missing-alert">
            <div className="home-missing-symbol">🔎</div>
            <div>
              <span>STOCK · CONTRÔLE À FAIRE</span>
              <h2>{homeMissingDue.length} produit{homeMissingDue.length > 1 ? "s" : ""} manquant{homeMissingDue.length > 1 ? "s" : ""} à revérifier</h2>
              <p>{homeMissingDue.slice(0, 3).map((item) => item.product).join("  •  ")}{homeMissingDue.length > 3 ? `  •  +${homeMissingDue.length - 3}` : ""}</p>
            </div>
            <button type="button" onClick={() => openTool(missingProductsTool)}>Contrôler maintenant <span>→</span></button>
          </section>
        )}

        {homeStockLabelOpen.length > 0 && (
          <section className="home-stock-label-alert">
            <div className="home-stock-label-symbol">🏷️</div>
            <div>
              <span>RAYON · DEMANDES EN ATTENTE</span>
              <h2>{homeStockLabelOpen.length} contrôle{homeStockLabelOpen.length > 1 ? "s" : ""} stock / étiquette à traiter</h2>
              <p>{homeStockLabelOpen.slice(0, 3).map((item) => `${item.requestType === "stock" ? "Stock" : "Étiquette"} · ${item.product}`).join("  •  ")}{homeStockLabelOpen.length > 3 ? `  •  +${homeStockLabelOpen.length - 3}` : ""}</p>
            </div>
            <button type="button" onClick={() => openTool(stockLabelsTool)}>Traiter les demandes <span>→</span></button>
          </section>
        )}

        {homeMadDue.length > 0 && (
          <section className="home-mad-alert">
            <div className="home-mad-symbol">🛏️</div>
            <div>
              <span>MAD · LOCATION À SUIVRE</span>
              <h2>{homeMadDue.length === 1 ? `${homeMadDue[0].materialName} · ${homeMadDue[0].patientName}` : `${homeMadDue.length} locations MAD à traiter`}</h2>
              <p>{homeMadDue.slice(0, 3).map((item) => `${item.patientName} · ${madRentalAlert(item, localDateIso(homeNow)).toLocaleLowerCase("fr")}`).join("  •  ")}{homeMadDue.length > 3 ? `  •  +${homeMadDue.length - 3}` : ""}</p>
            </div>
            <button type="button" onClick={() => openTool(madTool)}>Ouvrir le suivi <span>→</span></button>
          </section>
        )}

        {homeLaboratoryAlertCount > 0 && (
          <section className="home-laboratory-alert">
            <div className="home-laboratory-symbol">🤝</div>
            <div>
              <span>LABORATOIRES · PRÉPARATION J−4</span>
              <h2>{homeLaboratoryAlertCount === 1 && homeLaboratoryDue[0] ? `${homeLaboratoryDue[0].laboratory} · inventaire et périmés` : homeLaboratoryAlertCount === 1 && homeLaboratoryWorkDue[0] ? `${homeLaboratoryWorkDue[0].profile.name} · ${homeLaboratoryWorkDue[0].item.title || "suivi commercial"}` : `${homeLaboratoryAlertCount} préparations / suivis labo à traiter`}</h2>
              <p>{[...homeLaboratoryDue.map((item) => `${selectedPharmacyKey || item.pharmacy} : pas traité · RDV ${followupDate(item.scheduledDate)}${item.scheduledTime ? ` à ${item.scheduledTime}` : ""}`), ...homeLaboratoryWorkDue.map(({ profile, item }) => `${profile.name} · ${item.type === "bri" ? "BRI" : "COOP / TG"}`)].slice(0, 3).join("  •  ")}</p>
            </div>
            <button type="button" onClick={() => openTool(laboratoryTool)}>Ouvrir l’agenda <span>→</span></button>
          </section>
        )}

        {homeView === "pilot" && <section className="home-daily">
          <div className="home-daily-heading"><div><span className="section-kicker">TEMPS RÉEL</span><h2>To-do & livraisons</h2></div><button type="button" onClick={() => openTool(dailyTool)}>Ouvrir la liste <span>→</span></button></div>
          {homeDailyTasks.length ? <div className="home-daily-grid">{homeDailyTasks.map((task) => <button type="button" key={task.id} className={`${task.kind} ${task.priority}`} onClick={() => openTool(dailyTool)}><span>{task.kind === "delivery" ? "▣" : "✓"}</span><div><strong>{task.title}</strong><small>{task.kind === "delivery" ? task.recipient : task.assignedTo || "Toute l’équipe"} · {task.dueTime || (task.displayDate < localDateIso() ? "En retard" : "Aujourd’hui")}</small></div><b>{task.kind === "delivery" ? "Livraison" : task.priority === "urgent" ? "Urgent" : "À faire"}</b></button>)}</div> : <button type="button" className="home-feed-empty daily" onClick={() => openTool(dailyTool)}><span>✓</span><p><strong>To-do et livraisons à jour</strong><small>Les actions prévues aujourd’hui ou en retard apparaîtront ici.</small></p><b>Consulter →</b></button>}
        </section>}

        {homeView === "pilot" && <section className="home-missions">
          <div className="home-missions-heading"><div><span className="section-kicker">MISSIONS DU JOUR</span><h2>{homeMissionOccurrences.length} mission{homeMissionOccurrences.length > 1 ? "s" : ""} à réaliser</h2></div><button type="button" onClick={() => openTool(recurringMissionsTool)}>Ouvrir les missions <span>→</span></button></div>
          {homeMissionOccurrences.length ? <div className="home-mission-grid">{homeMissionOccurrences.map((item) => <button type="button" className={item.mission.priority} key={`${item.mission.id}-${item.pharmacy}`} onClick={() => openTool(recurringMissionsTool)}><span className="home-mission-check">✓</span><div><strong>{item.mission.title}</strong><small>{item.pharmacy} · {item.periodLabel}</small></div><b>{item.mission.priority === "urgent" ? "Urgent" : item.mission.priority === "important" ? "Important" : "À faire"}</b></button>)}</div> : <button type="button" className="home-feed-empty" onClick={() => openTool(recurringMissionsTool)}><span>✓</span><p><strong>Missions à jour</strong><small>La prochaine mission apparaîtra automatiquement à son échéance.</small></p><b>Consulter →</b></button>}
        </section>}

        <div className="main-grid">
          <aside className="continuity-card compact">
            <div className="continuity-icon">✚</div>
            <div className="continuity-mini-copy">
              <span>PERFADOM</span>
              <h2>Dossier en cours</h2>
              <p>Étape 2/4 · Matériel & actes</p>
            </div>
            <button type="button" onClick={() => openTool(perfadomTool)}>Reprendre <span>→</span></button>
          </aside>

          <section className="tools-panel">
            <div className="section-heading">
              <div>
                <span className="section-kicker">ACCÈS DIRECT</span>
                <h2>Vos outils essentiels</h2>
              </div>
              <button type="button" onClick={() => setLauncherOpen(true)}>Tous les outils <span>→</span></button>
            </div>
            <div className="essential-grid">
              {tools.slice(0, 8).map((tool) => (
                <button type="button" key={tool.name} className="essential-tool" onClick={() => openTool(tool)}>
                  <span className={`tool-icon ${tool.color}`}>{tool.icon}</span>
                  <span><strong>{tool.name}</strong><small>{tool.description}</small></span>
                  {tool.badge && <b>{tool.badge}</b>}
                  <span className="tool-action">Ouvrir →</span>
                </button>
              ))}
            </div>
          </section>

        </div>

        <section className="network-bar">
          <div><span className="network-mark">S</span><p><strong>Réseau Saint-Laurent</strong><small>Les 3 pharmacies sont synchronisées</small></p></div>
          <div className="site-statuses">
            <span><i /> Square</span><span><i /> Ragadan</span><span><i /> Laurentine</span>
          </div>
          <span className="sync-time">Dernière synchro · à l’instant</span>
        </section>
      </section>

      {launcherOpen && (
        <div className="overlay" role="presentation" onMouseDown={() => setLauncherOpen(false)}>
          <section className="launcher" role="dialog" aria-modal="true" aria-labelledby="launcher-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="launcher-search">
              <span>⌕</span>
              <label htmlFor="global-search" className="sr-only">Rechercher un outil</label>
              <input
                autoFocus
                id="global-search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Rechercher un outil ou une action…"
              />
              <kbd>ESC</kbd>
            </div>
            <div className="launcher-body">
              <div className="launcher-head">
                <div><span className="section-kicker">CENTRE D’OUTILS</span><h2 id="launcher-title">{filteredTools.length} module{filteredTools.length > 1 ? "s" : ""}, classé{filteredTools.length > 1 ? "s" : ""} par usage</h2></div>
                <div className="category-pills">
                  {["Tout", ...toolCategoryOrder].map((item) => (
                    <button type="button" key={item} className={category === item ? "active" : ""} onClick={() => setCategory(item)}>{item}</button>
                  ))}
                </div>
              </div>
              <div className="launcher-groups">
                {toolCategoryOrder.map((group, groupIndex) => {
                  const groupTools = filteredTools.filter((tool) => tool.category === group);
                  if (!groupTools.length) return null;
                  return <section className="launcher-group" key={group}>
                    <header>
                      <span>{group}</span><b>{groupTools.length}</b>
                      <div className="launcher-order-controls">
                        <button type="button" disabled={groupIndex === 0} onClick={() => moveToolCategory(group, -1)} aria-label={`Remonter ${group}`}>↑</button>
                        <button type="button" disabled={groupIndex === toolCategoryOrder.length - 1} onClick={() => moveToolCategory(group, 1)} aria-label={`Descendre ${group}`}>↓</button>
                      </div>
                    </header>
                    <div className="launcher-grid">
                      {groupTools.map((tool) => (
                        <button type="button" key={tool.name} onClick={() => openTool(tool)}>
                          <span className={`tool-icon ${tool.color}`}>{tool.icon}</span>
                          <span><strong>{tool.name}</strong><small>{tool.description}</small></span>
                          {tool.badge && <b>{tool.badge}</b>}
                          <span className="tool-action">Ouvrir →</span>
                        </button>
                      ))}
                    </div>
                  </section>;
                })}
              </div>
              {filteredTools.length === 0 && <div className="no-result">Aucun outil ne correspond à « {query} ».</div>}
            </div>
          </section>
        </div>
      )}

      {selectedTool && (
        <div className="overlay tool-overlay" role="presentation" onMouseDown={() => setSelectedTool(null)}>
          <section className="tool-drawer" role="dialog" aria-modal="true" aria-labelledby="tool-title" onMouseDown={(event) => event.stopPropagation()}>
            <button className="drawer-close" type="button" aria-label="Fermer" onClick={() => setSelectedTool(null)}>×</button>
            <span className={`drawer-icon ${selectedTool.color}`}>{selectedTool.icon}</span>
            <span className="section-kicker">{selectedTool.category}</span>
            <h2 id="tool-title">{selectedTool.name}</h2>
            <p>{selectedTool.description}. Ce module s’ouvrira ici sans rechargement complet et réutilisera le contexte de la pharmacie, de l’opérateur et du dossier en cours.</p>
            <div className="drawer-benefits">
              <div><span>01</span><p><strong>Contexte conservé</strong><small>Pharmacie et opérateur déjà identifiés</small></p></div>
              <div><span>02</span><p><strong>Saisie unique</strong><small>Les données utiles suivent le parcours</small></p></div>
              <div><span>03</span><p><strong>Retour instantané</strong><small>Revenez au cockpit sans perdre votre travail</small></p></div>
            </div>
            <button className="drawer-primary" type="button" onClick={() => { setSelectedTool(null); setToast(`${selectedTool.name} prêt à être raccordé aux données existantes`); window.setTimeout(() => setToast(""), 2800); }}>
              Ouvrir le module <span>→</span>
            </button>
          </section>
        </div>
      )}

      {showTitulaire && titulaireQuickOpen && <TitulaireQuickAdd operator={activeOperator} initialPharmacy={selectedPharmacyKey || "Square"} onClose={() => setTitulaireQuickOpen(false)} onSaved={(message) => { setToast(message); window.setTimeout(() => setToast(""), 2800); }} />}

      {toast && <div className="toast" role="status"><span>✓</span>{toast}</div>}
    </main>
  );
}
